import { createInertiaApp } from "@inertiajs/react";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  Bell,
  Bookmark,
  Bot,
  BriefcaseBusiness,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  CircleUserRound,
  Clapperboard,
  Compass,
  Camera,
  CreditCard,
  Download,
  Eye,
  EyeOff,
  FileText,
  Filter,
  Flag,
  Globe2,
  Hash,
  Heart,
  House,
  Images,
  Inbox,
  KeyRound,
  LifeBuoy,
  Link2,
  ListChecks,
  Lock,
  Mail,
  MapPin,
  Megaphone,
  MessageCircle,
  MonitorCog,
  Moon,
  MoreHorizontal,
  MousePointerClick,
  Paperclip,
  PencilLine,
  Plus,
  RefreshCcw,
  Reply,
  Search,
  Send,
  Settings,
  Share2,
  ShieldCheck,
  Sun,
  ThumbsUp,
  Trash2,
  Upload,
  UserCheck,
  UserPlus,
  UserX,
  UserRound,
  Users,
  Wallet,
  X,
  type LucideIcon
} from "lucide-react";
import { Virtuoso, VirtuosoGrid, type Components as VirtuosoComponents, type GridComponents as VirtuosoGridComponents } from "react-virtuoso";
import { createRoot, hydrateRoot } from "react-dom/client";
import { forwardRef, memo, useCallback, useEffect, useId, useMemo, useRef, useState, type ComponentPropsWithoutRef, type FormEvent, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent, type ReactElement, type ReactNode, type RefObject, type UIEvent } from "react";
import type { I18nDictionary, InertiaPage, OpenSNNotificationStreamConfig, ViewCatalogItem } from "../core/types";
import { openSNCoreViewForPath, openSNInitialDataPayload } from "../core/seo";
import { extendOpenSNDictionary } from "../i18n/index";
import { getCommunityMessages } from "../i18n/community";
import { affiliateCopy } from "../i18n/affiliates";
import { AffiliateView, AffiliateOpsView } from "./affiliates";
import { memberCopy } from "../i18n/member";
import { MemberContentView, MemberVerificationView, MemberExportSettings, MemberSessionSettings, MemberEmailSettings, MemberDeleteAccountSettings, MemberWithdrawalForm, MemberTransactionList, MemberPaymentStatus, useMemberPolling, type MemberViewProps, type MemberRequest } from "./member-views";
import { localizeOpenSNText, type OpenSNFieldOption, type OpenSNMemberField, type OpenSNRelationshipType, type OpenSNSocialDefinition } from "../schema/index";
import "../styles/social.css";

type PageLocale = { code: string; nativeName: string; flag: string; direction: "ltr" | "rtl"; translationStatus?: "ready" | "pending" };
type OpenSNMediaDefaults = {
  profilePhotoLight?: string;
  profilePhotoDark?: string;
  coverPhotoLight?: string;
  coverPhotoDark?: string;
  default_profile_photo_light?: string;
  default_profile_photo_dark?: string;
  default_cover_photo_light?: string;
  default_cover_photo_dark?: string;
};

type PageProps = InertiaPage["props"] & {
  dictionary: I18nDictionary;
  auth: { user: null | OpenSNAuthUser };
  brand?: { name?: string; initials?: string; homeHref?: string; ariaLabel?: string; authVisualAssets?: Partial<Record<"login" | "signup", PeopleAssetPictureProps>>; mediaDefaults?: OpenSNMediaDefaults };
  socialDefinition?: OpenSNSocialDefinition;
  notificationStream?: OpenSNNotificationStreamConfig;
  initialData?: Record<string, unknown>;
  locale: PageLocale;
  locales: PageLocale[];
  module: string;
  queues: string[];
  currentUrl: string;
  currentQuery: Record<string, string>;
  view: ViewCatalogItem;
};

type SocialPageComponent = (props: PageProps) => ReactElement;

type ApiResult = {
  data?: unknown;
  rows: Array<Record<string, unknown>>;
  nextCursor?: string | null;
  total?: number | null;
  page?: number | null;
  perPage?: number | null;
  status: "idle" | "loading" | "ready" | "error" | "permission";
  statusCode?: number;
  error?: string;
  reload: () => void;
  loadMore: () => void;
  loadingNext?: boolean;
  nextError?: string;
  loadingPrevious?: boolean;
  previousError?: string;
  hasPrevious?: boolean;
  firstItemIndex?: number;
  loadPrevious?: () => void;
};

type OpsSortDirection = "asc" | "desc";

type OpsTableColumn = {
  key: string;
  label: string;
  width: number;
  valueKeys?: string[];
  minWidth?: number;
  sortable?: boolean;
  editable?: boolean;
  input?: "text" | "textarea" | "select" | "number" | "datetime";
  options?: string[];
};

type OpsTableSettings = {
  columnWidths?: Record<string, number>;
  sortKey?: string;
  sortDirection?: OpsSortDirection;
  perPage?: number;
};

type OpsDrawerState = {
  mode: "create" | "edit";
  row: Record<string, unknown>;
} | null;

type SubmitState = {
  status: "idle" | "saving" | "success" | "error" | "permission";
  message: string;
  payload?: Record<string, unknown>;
};

type ClientRoute = {
  currentUrl: string;
  currentQuery: Record<string, string>;
};
type ChatOptimisticMessage = Record<string, unknown> & {
  id: string;
  room_id: string;
  body: string;
  created_at: string;
  mine: 1;
  local_status?: "sending" | "failed";
  server_id?: string;
};
type LocaleOption = PageProps["locales"][number];
type TopbarMenu = "notifications" | "language" | "user";
type OpenSNThemePreference = "light" | "dark" | "system";

const notificationsChangedEvent = "opensn:notifications-changed";
const routeNavigationEvent = "opensn:route-navigation";

let activeDictionary: I18nDictionary | null = null;
let activeLocaleCode = "en";
let activeSocialDefinition: OpenSNSocialDefinition | null = null;
let activeMediaDefaults: OpenSNMediaDefaults = {};
let socialAppMounted = false;
let configuredViewResolver: ((pathname: string) => ViewCatalogItem | null) | undefined;

const localeNameCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
const imageUploadAccept = "image/*,.png,.jpg,.jpeg,.gif,.webp,.avif,.avifs,.heic,.heif,.tif,.tiff,.bmp,.dib,.ico,.svg,.jfif,.jxl,.apng,.psd,.tga,.jp2,.j2k,.jpf,.jpm,.jpx,.pnm,.pbm,.pgm,.ppm,.pam,.dds,.exr,.hdr,.dng,.cr2,.cr3,.nef,.arw,.orf,.rw2";
const romanianLocaleMarkers = new Set(["acasa", "alatura-te", "articole", "autentificare", "cauta", "confidentialitate", "grupuri", "invitatii", "notificari", "pagini", "portofel", "prieteni", "scrie o postare", "sectiuni setari", "setari"]);

function sortedLocaleOptions(locales: LocaleOption[]): LocaleOption[] {
  return [...locales].sort((first, second) => localeNameCollator.compare(first.nativeName, second.nativeName) || first.code.localeCompare(second.code));
}

function flagSymbolFromEmoji(flag: string): string | null {
  const points = Array.from(flag.trim()).map((character) => character.codePointAt(0) ?? 0);
  if (points.length === 2 && points.every((point) => point >= 0x1F1E6 && point <= 0x1F1FF)) {
    return String.fromCharCode(...points.map((point) => point - 0x1F1E6 + 65)).toLowerCase();
  }
  return null;
}

function languageFlagHref(flag: string): string | null {
  const symbol = flagSymbolFromEmoji(flag);
  return symbol ? `/assets/flags/${symbol}.avif` : null;
}

function assetSrcSet(base: string, widths: number[]): string {
  return widths.map((width) => `${base}/${width}.avif ${width}w`).join(", ");
}

function isImageUploadFile(file: File): boolean {
  return file.type.startsWith("image/") || /\.(?:avif|avifs|bmp|dib|gif|heic|heif|ico|jpe?g|jfif|jxl|png|apng|svg|tiff?|webp|psd|tga|jp2|j2k|jpf|jpm|jpx|pnm|pbm|pgm|ppm|pam|dds|exr|hdr|dng|cr2|cr3|nef|arw|orf|rw2)$/i.test(file.name);
}

function avifImageSrc(src: string): string {
  const origin = typeof window === "undefined" ? "https://throuple.social" : window.location.origin;
  if (!src.startsWith("/media/") && !src.startsWith(`${origin}/media/`)) return src;
  const url = new URL(src, origin);
  url.searchParams.set("avif", "1");
  return src.startsWith("/") ? `${url.pathname}${url.search}${url.hash}` : url.toString();
}

type PictureImageProps = {
  src: string;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  loading?: "eager" | "lazy";
  decoding?: "async" | "auto" | "sync";
  fetchPriority?: "high" | "low" | "auto";
  ariaHidden?: boolean;
  sources?: Array<{ type: string; srcSet: string; sizes: string }>;
};

function PictureImage({ src, alt, className, width, height, loading = "lazy", decoding = "async", fetchPriority, ariaHidden, sources = [] }: PictureImageProps) {
  return (
    <picture className={className} aria-hidden={ariaHidden ? "true" : undefined}>
      {sources.map((source) => <source key={`${source.type}-${source.srcSet}`} type={source.type} srcSet={source.srcSet} sizes={source.sizes} />)}
      <img src={avifImageSrc(src)} alt={alt} width={width} height={height} loading={loading} decoding={decoding} fetchPriority={fetchPriority} />
    </picture>
  );
}

type PeopleAssetPictureProps = {
  slug: string;
  widths: number[];
  width: number;
  height: number;
  className?: string;
  sizes: string;
  alt?: string;
  loading?: "eager" | "lazy";
  fetchPriority?: "high" | "low" | "auto";
};

const AUTH_VISUAL_ASSETS = {
  login: {
    slug: "new2-community-laptop",
    widths: [320, 480, 640, 750, 828, 960, 1200, 1400],
    width: 1400,
    height: 920
  },
  signup: {
    slug: "new2-team-meeting",
    widths: [320, 480, 640, 750, 828, 900],
    width: 900,
    height: 600
  }
};

const DEFAULT_MEMBER_RELATIONSHIP: OpenSNRelationshipType = {
  id: "friend",
  label: { en: "Friend", ro: "Prieten" },
  actionLabel: { en: "Add friend", ro: "Adauga prieten" },
  successLabel: { en: "Friends", ro: "Prieteni" },
  titleMode: "none",
  reciprocal: true
};

type SelectOption = { value: string; label: string; description?: string; regionCode?: string };
type OpenSNUserPrivacySettings = {
  profileVisibility?: string;
  messagesFrom?: string;
  commentsOnPosts?: boolean;
  searchable?: boolean;
  showOnline?: boolean;
  friendListVisibility?: string;
  friendActivityVisibility?: string;
};
type OpenSNUserNotificationSettings = {
  emailEnabled?: boolean;
  pushEnabled?: boolean;
  digestEnabled?: boolean;
  chatEnabled?: boolean;
  friendRequestsEnabled?: boolean;
  friendAcceptedEnabled?: boolean;
  friendActivityEnabled?: boolean;
  mutualFriendsEnabled?: boolean;
  commentsEnabled?: boolean;
  messagesEnabled?: boolean;
  safetyAlertsEnabled?: boolean;
  followsEnabled?: boolean;
};
type OpenSNUserSettings = {
  privacy?: OpenSNUserPrivacySettings;
  notifications?: OpenSNUserNotificationSettings;
  preferredLocale?: string;
  themePreference?: OpenSNThemePreference;
  videoAutoplay?: boolean;
};
type OpenSNAuthUser = {
  id: string;
  display_name: string;
  username: string;
  profile_slug?: string;
  profileSlug?: string;
  public_id?: string;
  publicId?: string;
  locale: string;
  role: string;
  email?: string;
  has_password?: boolean;
  gender?: string;
  bio?: string;
  current_city?: string;
  current_country?: string;
  current_country_code?: string;
  current_city_id?: number | null;
  birth_date?: string;
  age?: number;
  adult_profile_complete?: boolean;
  public_profile_mode?: "minimal" | "complete";
  work_sector?: string;
  avatar_object_key?: string;
  avatar_url?: string;
  cover_object_key?: string;
  cover_url?: string;
  preferredLocale?: string;
  preferred_locale?: string;
  themePreference?: OpenSNThemePreference;
  theme_preference?: OpenSNThemePreference;
  videoAutoplay?: boolean;
  video_autoplay?: boolean;
  settings?: OpenSNUserSettings;
  privacySettings?: OpenSNUserPrivacySettings;
  privacy_settings?: OpenSNUserPrivacySettings;
  notificationSettings?: OpenSNUserNotificationSettings;
  notification_settings?: OpenSNUserNotificationSettings;
};

const DEFAULT_USER_SETTINGS: { privacy: Required<OpenSNUserPrivacySettings>; notifications: Required<OpenSNUserNotificationSettings> } = {
  privacy: {
    profileVisibility: "friends",
    messagesFrom: "friends",
    commentsOnPosts: true,
    searchable: true,
    showOnline: true,
    friendListVisibility: "friends",
    friendActivityVisibility: "friends"
  },
  notifications: {
    emailEnabled: true,
    pushEnabled: false,
    digestEnabled: true,
    chatEnabled: true,
    friendRequestsEnabled: true,
    friendAcceptedEnabled: true,
    friendActivityEnabled: true,
    mutualFriendsEnabled: true,
    commentsEnabled: true,
    messagesEnabled: true,
    safetyAlertsEnabled: true,
    followsEnabled: true
  }
};

const WORK_IN_EUROPE_INDUSTRY_VALUES = [
  "Agriculture",
  "Automotive",
  "Care work",
  "Cleaning",
  "Construction",
  "Domestic work",
  "Education",
  "Food delivery",
  "Healthcare",
  "Hospitality",
  "IT and digital",
  "Logistics",
  "Manufacturing",
  "Retail",
  "Seasonal work",
  "Security",
  "Transport",
  "Warehousing"
];

function workIndustryOptions(dictionary: I18nDictionary): SelectOption[] {
  const labels: Record<string, string> = {
    Agriculture: dictionary.categoryLabels.groupWorkJobs,
    Automotive: dictionary.categoryLabels.groupTransport,
    "Care work": dictionary.categoryLabels.topicTaxHealthSocialSecurity,
    Cleaning: dictionary.categoryLabels.groupWorkJobs,
    Construction: dictionary.categoryLabels.groupWorkJobs,
    "Domestic work": dictionary.categoryLabels.groupWorkJobs,
    Education: dictionary.categoryLabels.pageTraining,
    "Food delivery": dictionary.categoryLabels.groupTransport,
    Healthcare: dictionary.categoryLabels.topicTaxHealthSocialSecurity,
    Hospitality: dictionary.categoryLabels.groupWorkJobs,
    "IT and digital": dictionary.categoryLabels.topicOnlineServicesAppointments,
    Logistics: dictionary.categoryLabels.groupTransport,
    Manufacturing: dictionary.categoryLabels.groupWorkJobs,
    Retail: dictionary.categoryLabels.groupWorkJobs,
    "Seasonal work": dictionary.categoryLabels.groupWorkJobs,
    Security: dictionary.categoryLabels.groupSafety,
    Transport: dictionary.categoryLabels.groupTransport,
    Warehousing: dictionary.categoryLabels.groupWorkJobs
  };
  return WORK_IN_EUROPE_INDUSTRY_VALUES.map((value) => ({ value, label: labels[value] ?? dictionary.social.ui.workSector }));
}

function extraSignupInterestOptions(dictionary: I18nDictionary): SelectOption[] {
  const { categoryLabels } = dictionary;
  const { ui } = dictionary.social;
  return [
    { value: "community", label: categoryLabels.groupCommunity },
    { value: "support", label: ui.generalSupport },
    { value: "wallet", label: ui.walletTools },
    { value: "transport", label: categoryLabels.groupTransport },
    { value: "healthcare", label: categoryLabels.topicTaxHealthSocialSecurity },
    { value: "language-learning", label: categoryLabels.groupLanguage },
    { value: "rights-and-legal-help", label: categoryLabels.groupSafety },
    { value: "schools", label: categoryLabels.pageTraining },
    { value: "taxes", label: categoryLabels.topicTaxHealthSocialSecurity },
    { value: "banking", label: ui.walletTools },
    { value: "events", label: categoryLabels.groupCommunity },
    { value: "family", label: categoryLabels.topicFamilyMembers }
  ];
}

function PeopleAssetPicture({ slug, widths, width, height, className, sizes, alt = "", loading = "lazy", fetchPriority }: PeopleAssetPictureProps) {
  const base = `/assets/people/${slug}`;
  return (
    <PictureImage
      src={`${base}/${width}.avif`}
      alt={alt}
      width={width}
      height={height}
      className={className}
      loading={loading}
      fetchPriority={fetchPriority}
      ariaHidden={!alt}
      sources={[
        { type: "image/avif", srcSet: assetSrcSet(base, widths), sizes }
      ]}
    />
  );
}

function SignupSelectField({ label, name, options, placeholder, autoComplete, defaultValue = "", required = false, value, onChange }: { label: string; name: string; options: SelectOption[]; placeholder: string; autoComplete?: string; defaultValue?: string; required?: boolean; value?: string; onChange?: (value: string) => void }) {
  return (
    <label>
      <span>{label}</span>
      <select name={name} {...(value === undefined ? { defaultValue } : { value })} autoComplete={autoComplete} required={required} onChange={(event) => onChange?.(event.currentTarget.value)}>
        <option value="">{placeholder}</option>
        {options.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}
      </select>
    </label>
  );
}

type LocationCity = { geonameId: number; name: string; admin1Name: string | null };
type LocationCountry = { code: string; name: string; cities: LocationCity[] };
type LocationCatalog = { countries: LocationCountry[] };

function locationFormPayload(form: FormData): Record<string, unknown> {
  const cityId = String(form.get("currentCityId") ?? "");
  return {
    currentCountryCode: String(form.get("currentCountryCode") ?? ""),
    currentCountry: String(form.get("currentCountry") ?? ""),
    currentCity: String(form.get("currentCity") ?? ""),
    currentCityId: cityId ? Number(cityId) : null
  };
}

function GlobalLocationFields({ ui, user, filter = false }: { ui: I18nDictionary["social"]["ui"]; user?: PageProps["auth"]["user"]; filter?: boolean }) {
  const catalog = useApi("/assets/locations/world.json");
  const countries = (catalog.data as LocationCatalog | undefined)?.countries;
  const [countryCode, setCountryCode] = useState(user?.current_country_code ?? "");
  const [cityText, setCityText] = useState(user?.current_city ?? "");
  const cityInput = useRef<HTMLInputElement | null>(null);
  const edited = useRef(false);
  const country = countries?.find((item) => item.code === countryCode);
  const countryOptions = useMemo(() => {
    const names = new Intl.DisplayNames([activeLocaleCode], { type: "region" });
    return (countries ?? []).map((item) => ({ value: item.code, label: names.of(item.code) ?? item.name }))
      .sort((first, second) => first.label.localeCompare(second.label, activeLocaleCode));
  }, [countries, activeLocaleCode]);
  const cityOptions = useMemo(() => {
    const nameCounts = new Map<string, number>();
    for (const city of country?.cities ?? []) nameCounts.set(city.name, (nameCounts.get(city.name) ?? 0) + 1);
    return (country?.cities ?? []).map((city) => ({
      city,
      value: nameCounts.get(city.name) === 1 ? city.name : `${city.name} (${city.admin1Name ?? country?.name})`
    }));
  }, [country]);
  const matchingCities = cityOptions.filter((option) => option.value === cityText);
  const selectedCity = matchingCities.length === 1 ? matchingCities[0].city : undefined;
  const city = matchingCities[0]?.city.name ?? cityText;
  const listId = filter ? "filter-location-cities" : "profile-location-cities";

  useEffect(() => {
    if (!countries || edited.current) return;
    const savedCountry = countries.find((item) => item.code === user?.current_country_code || item.name === user?.current_country);
    if (savedCountry) setCountryCode(savedCountry.code);
  }, [countries, user?.current_country_code, user?.current_country]);
  useEffect(() => {
    if (edited.current || !user?.current_city_id) return;
    const savedCity = cityOptions.find((option) => option.city.geonameId === user.current_city_id);
    if (savedCity) setCityText(savedCity.value);
  }, [cityOptions, user?.current_city_id]);
  useEffect(() => {
    const form = cityInput.current?.form;
    const reset = () => {
      edited.current = false;
      setCountryCode(user?.current_country_code ?? countries?.find((item) => item.name === user?.current_country)?.code ?? "");
      setCityText(user?.current_city ?? "");
    };
    form?.addEventListener("reset", reset);
    return () => form?.removeEventListener("reset", reset);
  }, [countries, user?.current_country_code, user?.current_country, user?.current_city]);

  return (
    <>
      <SignupSelectField label={ui.currentCountry} name={filter ? "countryCode" : "currentCountryCode"} options={countryOptions} placeholder={catalog.status === "loading" || catalog.status === "idle" ? ui.loading : filter ? getCommunityMessages(activeLocaleCode).any : ui.selectCountry} autoComplete="country" value={countryCode} required={!filter} onChange={(value) => { edited.current = true; setCountryCode(value); setCityText(""); }} />
      {!filter && <input type="hidden" name="currentCountry" value={country?.name ?? user?.current_country ?? ""} />}
      <label>
        <span>{ui.currentCity}</span>
        <input ref={cityInput} list={listId} value={cityText} onChange={(event) => { edited.current = true; setCityText(event.currentTarget.value); }} required={!filter} maxLength={120} autoComplete="address-level2" placeholder={ui.currentCityPlaceholder} />
        <datalist id={listId}>{cityOptions.map((option) => <option value={option.value} label={option.city.admin1Name ?? undefined} key={option.city.geonameId} />)}</datalist>
      </label>
      <input type="hidden" name={filter ? "city" : "currentCity"} value={filter && selectedCity ? "" : city} />
      <input type="hidden" name={filter ? "cityId" : "currentCityId"} value={selectedCity?.geonameId ?? ""} />
      {catalog.status === "error" && <div className="form-status error" role="alert">{ui.requestFailed} <BaseButton type="button" variant="transparent" onClick={catalog.reload}>{ui.retry}</BaseButton></div>}
    </>
  );
}

function genderOptions(ui: I18nDictionary["social"]["ui"]): SelectOption[] {
  return [
    { value: "female", label: ui.genderFemale },
    { value: "male", label: ui.genderMale },
    { value: "non_binary", label: ui.genderNonBinary },
    { value: "self_described", label: ui.genderSelfDescribed },
    { value: "prefer_not_to_say", label: ui.genderPreferNotToSay }
  ];
}

function SignupInterestTagSelect({
  label,
  name,
  options,
  selectedValues,
  placeholder,
  onChange
}: {
  label: string;
  name: string;
  options: SelectOption[];
  selectedValues: string[];
  placeholder: string;
  onChange: (values: string[]) => void;
}) {
  const selectedSet = new Set(selectedValues);
  const selectedOptions = selectedValues
    .map((value) => options.find((option) => option.value === value))
    .filter((option): option is SelectOption => Boolean(option));
  const availableOptions = options.filter((option) => !selectedSet.has(option.value));
  const selectId = `${name}-select`;
  function addValue(value: string) {
    if (!value || selectedSet.has(value)) return;
    onChange([...selectedValues, value]);
  }
  function removeValue(value: string) {
    onChange(selectedValues.filter((item) => item !== value));
  }
  return (
    <div className="tag-select-field">
      <label htmlFor={selectId}>
        <span>{label}</span>
        <select id={selectId} value="" onChange={(event) => addValue(event.currentTarget.value)}>
          <option value="">{placeholder}</option>
          {availableOptions.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}
        </select>
      </label>
      <input type="hidden" name={name} value={selectedValues.join(", ")} />
      {selectedOptions.length > 0 && (
        <div className="selected-tag-list" aria-label={label}>
          {selectedOptions.map((option) => (
            <button className="selected-tag" type="button" key={option.value} onClick={() => removeValue(option.value)} aria-label={`Remove ${option.label}`}>
              <span>{option.label}</span>
              <X size={14} aria-hidden="true" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function openIdProviderOptions(rows: Array<Record<string, unknown>>): Array<{ provider: string; label: string }> {
  return rows
    .map((row) => ({
      provider: String(row.provider ?? row.id ?? "").trim(),
      label: String(row.label ?? row.provider ?? row.id ?? "").trim()
    }))
    .filter((row) => row.provider && row.label);
}

function LocaleFlag({ locale }: { locale: Pick<PageLocale, "flag" | "nativeName"> }) {
  const flagHref = languageFlagHref(locale.flag);
  return (
    <span className="language-flag-frame" aria-hidden="true">
      {flagHref ? <img className="language-flag-svg" src={flagHref} alt="" width={64} height={48} /> : <Globe2 size={17} />}
    </span>
  );
}

type ProfileMediaControls = {
  avatarFile: File | null;
  coverFile: File | null;
  removeAvatar: boolean;
  removeCover: boolean;
  avatarInputRef: RefObject<HTMLInputElement | null>;
  coverInputRef: RefObject<HTMLInputElement | null>;
  chooseAvatar: (files: FileList | null) => void;
  chooseCover: (files: FileList | null) => void;
  clearAvatar: (removeExisting?: boolean) => void;
  clearCover: (removeExisting?: boolean) => void;
  requestAvatar: () => void;
  requestCover: () => void;
};

const iconByModule: Record<string, LucideIcon> = {
  ads: Megaphone,
  ai: Bot,
  articles: FileText,
  auth: Lock,
  chat: MessageCircle,
  dating: Heart,
  clips: Clapperboard,
  creator: BadgeCheck,
  documents: FileText,
  events: CalendarDays,
  feed: House,
  groups: Globe2,
  membership: BadgeCheck,
  network: Users,
  ops: MonitorCog,
  pages: BriefcaseBusiness,
  profile: CircleUserRound,
  public: Globe2,
  search: Search,
  saved: Bookmark,
  settings: Settings,
  support: LifeBuoy,
  system: AlertTriangle,
  wallet: Wallet
};

const moduleLinks = [
  ["feed", "/timeline", "feed"],
  ["network", "/network", "network"],
  ["dating", "/dating", "dating"],
  ["chat", "/chat", "chat"],
  ["groups", "/groups", "groups"],
  ["pages", "/pages", "pages"],
  ["articles", "/articles", "articles"],
  ["clips", "/clips", "clips"],
  ["documents", "/documents", "documents"],
  ["saved", "/saved", "saved"],
  ["wallet", "/wallet", "wallet"],
  ["membership", "/membership", "membership"],
  ["ops", "/admin", "ops"]
] as const;

const settingsLinks = [
  ["Settings/Profile", "/settings/profile", "settingsProfile"],
  ["Settings/Privacy", "/settings/privacy", "settingsPrivacy"],
  ["Settings/Notifications", "/settings/notifications", "settingsNotifications"],
  ["Settings/Email", "/settings/email", "settingsEmail"],
  ["Settings/Password", "/settings/password", "settingsPassword"],
  ["Settings/Two Factor", "/settings/two_factor", "settingsTwoFactor"],
  ["Settings/Display", "/settings/display", "settingsDisplay"],
  ["Settings/Data Download", "/settings/download", "settingsDataDownload"],
  ["Settings/Account", "/settings/account", "settingsAccount"]
] as const;

type OpsSectionGroupKey = "overview" | "content" | "community" | "engagement" | "communication" | "moderation" | "monetization" | "configuration" | "operations";

const opsSectionGroupOrder: OpsSectionGroupKey[] = ["overview", "content", "community", "engagement", "communication", "moderation", "monetization", "configuration", "operations"];

const opsSections = [
  { group: "overview", labelKey: "overview", href: "/admin", endpoint: null },
  { group: "operations", labelKey: "queues", href: "/admin/queues", endpoint: "/api/ops/queues" },
  { group: "moderation", labelKey: "reports", href: "/admin/reports", endpoint: "/api/ops/reports" },
  { group: "moderation", labelKey: "photoReports", href: "/admin/photo-reports", endpoint: "/api/ops/photo-reports" },
  { group: "moderation", labelKey: "supportTickets", href: "/admin/support-tickets", endpoint: "/api/ops/support-tickets" },
  { group: "community", labelKey: "users", href: "/admin/users", endpoint: "/api/ops/users" },
  { group: "community", labelKey: "follows", href: "/admin/follows", endpoint: "/api/ops/follows" },
  { group: "content", labelKey: "posts", href: "/admin/posts", endpoint: "/api/ops/posts" },
  { group: "content", labelKey: "comments", href: "/admin/comments", endpoint: "/api/ops/comments" },
  { group: "content", labelKey: "replies", href: "/admin/replies", endpoint: "/api/ops/replies" },
  { group: "engagement", labelKey: "likes", href: "/admin/likes", endpoint: "/api/ops/likes" },
  { group: "engagement", labelKey: "shares", href: "/admin/shares", endpoint: "/api/ops/shares" },
  { group: "content", labelKey: "hashtags", href: "/admin/hashtags", endpoint: "/api/ops/hashtags" },
  { group: "configuration", labelKey: "categories", href: "/admin/categories", endpoint: "/api/ops/categories" },
  { group: "content", labelKey: "stories", href: "/admin/stories", endpoint: "/api/ops/stories" },
  { group: "content", labelKey: "media", href: "/admin/media", endpoint: "/api/ops/media" },
  { group: "content", labelKey: "clips", href: "/admin/clips", endpoint: "/api/ops/clips" },
  { group: "content", labelKey: "articles", href: "/admin/articles", endpoint: "/api/ops/articles" },
  { group: "content", labelKey: "documents", href: "/admin/documents", endpoint: "/api/ops/documents" },
  { group: "monetization", labelKey: "ads", href: "/admin/ads", endpoint: "/api/ops/ads" },
  { group: "community", moduleKey: "groups", href: "/admin/groups", endpoint: "/api/ops/groups" },
  { group: "community", moduleKey: "events", href: "/admin/events", endpoint: "/api/ops/events" },
  { group: "community", moduleKey: "pages", href: "/admin/pages", endpoint: "/api/ops/pages" },
  { group: "communication", labelKey: "notifications", href: "/admin/notifications", endpoint: "/api/ops/notifications" },
  { group: "communication", labelKey: "messages", href: "/admin/messages", endpoint: "/api/ops/messages" },
  { group: "communication", labelKey: "conversations", href: "/admin/chat-rooms", endpoint: "/api/ops/chat-rooms" },
  { group: "community", labelKey: "invites", href: "/admin/invites", endpoint: "/api/ops/invites" },
  { group: "engagement", labelKey: "savedItems", href: "/admin/saved-items", endpoint: "/api/ops/saved-items" },
  { group: "configuration", labelKey: "openidProviders", href: "/admin/openid-providers", endpoint: "/api/ops/openid-providers" },
  { group: "operations", labelKey: "jobs", href: "/admin/jobs", endpoint: "/api/ops/jobs" },
  { group: "operations", labelKey: "deadLetters", href: "/admin/dead-letters", endpoint: "/api/ops/dead-letters" },
  { group: "operations", labelKey: "cron", href: "/admin/crons", endpoint: "/api/ops/crons" },
  { group: "operations", labelKey: "outbox", href: "/admin/outbox", endpoint: "/api/ops/outbox" },
  { group: "operations", labelKey: "webhooks", href: "/admin/webhooks", endpoint: "/api/ops/webhooks" },
  { group: "operations", labelKey: "locks", href: "/admin/locks", endpoint: "/api/ops/locks" },
  { group: "operations", labelKey: "mediaProcessing", href: "/admin/media-processing", endpoint: "/api/ops/media-processing" },
  { group: "configuration", labelKey: "mediaDefaults", href: "/admin/media-defaults", endpoint: "/api/ops/media-defaults", kind: "media-defaults" },
  { group: "operations", labelKey: "dataExports", href: "/admin/data-exports", endpoint: "/api/ops/data-exports" },
  { group: "operations", labelKey: "accountDeletes", href: "/admin/account-deletes", endpoint: "/api/ops/account-deletes" },
  { group: "operations", labelKey: "retention", href: "/admin/retention", endpoint: "/api/ops/retention-policies" },
  { group: "configuration", labelKey: "languages", href: "/admin/languages", endpoint: "/api/ops/languages" },
  { group: "configuration", labelKey: "roles", href: "/admin/roles", endpoint: "/api/ops/roles" }
] as const;

const opsOverviewPanels = [
  { titleKey: "queues", endpoint: "/api/ops/queues" },
  { titleKey: "jobs", endpoint: "/api/ops/jobs" },
  { titleKey: "deadLetters", endpoint: "/api/ops/dead-letters" },
  { titleKey: "cron", endpoint: "/api/ops/crons" }
] as const;

function ContractView(props: PageProps) {
  const propsQueryKey = queryRecordKey(props.currentQuery);
  const [clientRoute, setClientRoute] = useState(() => currentOpenSNRoute(props.currentUrl, props.currentQuery));
  useEffect(() => {
    setClientRoute(currentOpenSNRoute(props.currentUrl, props.currentQuery));
  }, [props.currentUrl, propsQueryKey]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const currentHref = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const canonicalHref = canonicalProfileRouteHref(currentHref);
    if (!canonicalHref || canonicalHref === currentHref || !coreRouteViewForPath(canonicalHref)) return;
    window.history.replaceState({}, "", canonicalHref);
    setClientRoute(routeFromHref(canonicalHref, {}));
  }, [props.currentUrl, propsQueryKey]);
  useEffect(() => {
    function syncFromLocation() {
      setClientRoute(currentOpenSNRoute(props.currentUrl, props.currentQuery));
    }
    function syncFromNavigation(event: Event) {
      const href = (event as CustomEvent<{ href?: string }>).detail?.href;
      if (href) setClientRoute(routeFromHref(href, props.currentQuery));
    }
    window.addEventListener("popstate", syncFromLocation);
    window.addEventListener(routeNavigationEvent, syncFromNavigation);
    return () => {
      window.removeEventListener("popstate", syncFromLocation);
      window.removeEventListener(routeNavigationEvent, syncFromNavigation);
    };
  }, [props.currentUrl, propsQueryKey]);
  const currentUrl = clientRoute.currentUrl || props.currentUrl;
  const currentQuery = clientRoute.currentQuery;
  const routeView = coreRouteViewForPath(currentUrl);
  const view = routeView && !viewMatchesRoute(props.view, routeView) ? routeView : props.view;
  const resolvedProps: PageProps = { ...props, currentUrl, currentQuery, dictionary: extendOpenSNDictionary(props.locale.code, props.dictionary), module: view.module, view };
  activeDictionary = resolvedProps.dictionary;
  activeLocaleCode = resolvedProps.locale.code;
  activeSocialDefinition = resolvedProps.socialDefinition ?? null;
  activeMediaDefaults = resolvedProps.brand?.mediaDefaults ?? {};
  let content: ReactElement;
  if (resolvedProps.view.name === "Affiliate Operations") {
    content = <Shell {...resolvedProps}><AffiliateOpsView localeCode={resolvedProps.locale.code} /></Shell>;
  } else if (resolvedProps.view.name === "Affiliates") {
    content = <Shell {...resolvedProps}><AffiliateView localeCode={resolvedProps.locale.code} /></Shell>;
  } else if (resolvedProps.view.name === "Couple Invitation") {
    content = <Shell {...resolvedProps}><CoupleInvitationView props={resolvedProps} /></Shell>;
  } else if (resolvedProps.view.layoutTemplate === "auth-card" || resolvedProps.view.module === "auth" || resolvedProps.view.module === "public") {
    content = <AuthContractView {...resolvedProps} />;
  } else if (resolvedProps.view.layoutTemplate === "support-form-card" || resolvedProps.view.module === "support") {
    content = (
      <Shell {...resolvedProps}>
        <SupportView {...resolvedProps} />
      </Shell>
    );
  } else if (resolvedProps.view.layoutTemplate === "settings-split-form" || resolvedProps.view.module === "settings") {
    content = (
      <Shell {...resolvedProps}>
        <SettingsView {...resolvedProps} />
      </Shell>
    );
  } else if (["Invites Overview", "Your Referrals"].includes(resolvedProps.view.name)) {
    content = (
      <Shell {...resolvedProps}>
        <InviteNetworkView {...resolvedProps} />
      </Shell>
    );
  } else if (resolvedProps.view.name === "Invite") {
    content = (
      <Shell {...resolvedProps}>
        <InviteFormView {...resolvedProps} />
      </Shell>
    );
  } else if (resolvedProps.view.module === "ads") {
    content = (
      <Shell {...resolvedProps}>
        <AdsManagerView {...resolvedProps} />
      </Shell>
    );
  } else if (["articles", "clips", "documents"].includes(resolvedProps.view.module) && resolvedProps.view.name !== "Articles Editor") {
    content = <Shell {...resolvedProps}><MemberContentRoute {...resolvedProps} /></Shell>;
  } else if (resolvedProps.view.module === "verification") {
    content = <Shell {...resolvedProps}>{resolvedProps.auth.user ? <MemberVerificationView {...memberViewProps(resolvedProps)} /> : <PermissionPanel title={resolvedProps.dictionary.social.ui.logInToContinueTitle} body={resolvedProps.dictionary.social.ui.logInToContinueBody} actionLabel={resolvedProps.dictionary.social.ui.logIn} />}</Shell>;
  } else if (resolvedProps.view.layoutTemplate === "simple-form-card") {
    content = (
      <Shell {...resolvedProps}>
        <EntityFormView {...resolvedProps} />
      </Shell>
    );
  } else if (resolvedProps.view.layoutTemplate === "chat-shell" || resolvedProps.view.module === "chat") {
    content = (
      <Shell {...resolvedProps}>
        <ChatView {...resolvedProps} />
      </Shell>
    );
  } else if (resolvedProps.view.module === "dating") {
    content = (
      <Shell {...resolvedProps}>
        <DatingView {...resolvedProps} />
      </Shell>
    );
  } else if (resolvedProps.view.name === "Profile Overview" || resolvedProps.view.module === "profile") {
    content = (
      <Shell {...resolvedProps}>
        <ProfileView {...resolvedProps} />
      </Shell>
    );
  } else if (resolvedProps.view.layoutTemplate === "wallet-membership") {
    content = (
      <Shell {...resolvedProps}>
        <WalletMembershipView {...resolvedProps} />
      </Shell>
    );
  } else if (resolvedProps.view.layoutTemplate === "ops-table" || resolvedProps.view.module === "ops") {
    content = (
      <Shell {...resolvedProps}>
        <OpsView {...resolvedProps} />
      </Shell>
    );
  } else if (resolvedProps.view.layoutTemplate.includes("modal")) {
    content = (
      <Shell {...resolvedProps}>
        <ModalContractView {...resolvedProps} />
      </Shell>
    );
  } else {
    content = (
      <Shell {...resolvedProps}>
        <SocialContractView {...resolvedProps} />
      </Shell>
    );
  }
  return content;
}

function coreRouteViewForPath(value: string): ViewCatalogItem | null {
  if (configuredViewResolver) return configuredViewResolver(normalizeAppPath(value));
  return openSNCoreViewForPath(value);
}

function viewMatchesRoute(view: ViewCatalogItem, routeView: ViewCatalogItem): boolean {
  return view.name === routeView.name || view.slug === routeView.slug;
}

function memberViewProps(props: PageProps): MemberViewProps {
  return { locale: props.locale.code, ui: props.dictionary.social.ui, userId: props.auth.user?.id ?? "", request: memberRequest, upload: uploadFiles, navigate: navigateMemberHref };
}

function navigateMemberHref(href: string): void {
  if (!navigateOpenSNRoute(href)) window.location.assign(href);
}

function MemberContentRoute(props: PageProps) {
  const kind = props.view.module as "articles" | "clips" | "documents";
  const segment = normalizeAppPath(props.currentUrl).split("/")[2] ?? "";
  const mode = segment === "create" ? "create" : segment ? "detail" : props.currentQuery.mine === "1" && props.currentQuery.status === "draft" ? "drafts" : "list";
  if (!props.auth.user && (mode === "create" || mode === "drafts")) return <PermissionPanel title={props.dictionary.social.ui.logInToContinueTitle} body={props.dictionary.social.ui.logInToContinueBody} actionLabel={props.dictionary.social.ui.logIn} />;
  return <MemberContentView {...memberViewProps(props)} kind={kind} mode={mode} identifier={segment ? decodeURIComponent(segment) : undefined} />;
}

function Shell({ children, ...props }: PageProps & { children: ReactNode }) {
  const user = props.auth.user;
  const ui = props.dictionary.social.ui;
  const brand = brandProps(props);
  const displayName = user?.display_name ?? ui.guestMember;
  const [activeTopbarMenu, setActiveTopbarMenu] = useState<TopbarMenu | null>(null);
  const [topbarScrolled, setTopbarScrolled] = useState(false);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const [themePreference, setThemePreference] = useState<OpenSNThemePreference>(() => themePreferenceFromUser(user));
  const [themeState, setThemeState] = useState<SubmitState>({ status: "idle", message: "" });
  const [logoutState, setLogoutState] = useState<SubmitState>({ status: "idle", message: "" });
  const topbarRef = useRef<HTMLElement | null>(null);
  const notificationsButtonRef = useRef<HTMLButtonElement | null>(null);
  const userMenuButtonRef = useRef<HTMLButtonElement | null>(null);
  const mobileMoreButtonRef = useRef<HTMLButtonElement | null>(null);
  const mobileMorePanelRef = useRef<HTMLDivElement | null>(null);
  useModalLifecycle(mobileMoreOpen, mobileMorePanelRef);
  const notificationsResult = useApi(user ? "/api/notifications" : null);
  useMemberPolling(notificationsResult.reload, Boolean(user));
  const visibleModuleLinks = moduleLinks.filter(([module]) => isModuleVisible(module, user));
  const mobilePrimaryModules = new Set(["feed", "network", "dating", "chat"]);
  const mobilePrimaryLinks = visibleModuleLinks.filter(([module]) => mobilePrimaryModules.has(module));
  const mobileMoreLinks = visibleModuleLinks.filter(([module]) => !mobilePrimaryModules.has(module));
  const mobileMoreActive = mobileMoreLinks.some(([module, href]) => isModuleLinkActive(props.currentUrl, props.module, module, href));
  const searchLabel = ui.searchPlaceholder || ui.search || props.dictionary.social.modules.search;
  const socialSearchLabel = ui.socialSearch || searchLabel;
  const searchQuery = searchQueryFromRoute(props.currentUrl, props.currentQuery);
  const notificationsLabel = props.dictionary.social.modules.notifications;
  const notificationsUnreadCount = notificationUnreadCount(notificationsResult);
  const notificationsUnread = notificationsUnreadCount > 0;
  const notificationButtonLabel = notificationsUnread ? `${notificationsLabel}: ${notificationBadgeText(notificationsUnreadCount)} ${ui.notificationUnread}` : notificationsLabel;
  const themeLabel = uiLabel(ui, "theme", "Theme");
  const themeOptions: Array<{ value: OpenSNThemePreference; label: string; Icon: LucideIcon }> = [
    { value: "light", label: uiLabel(ui, "themeLight", "Light"), Icon: Sun },
    { value: "dark", label: uiLabel(ui, "themeDark", "Dark"), Icon: Moon },
    { value: "system", label: uiLabel(ui, "themeSystem", "System"), Icon: MonitorCog }
  ];
  const notificationsMenuId = "topbar-notifications-menu";
  const languageMenuId = "topbar-language-menu";
  const userMenuId = "topbar-user-menu";
  const mobileMorePanelId = "mobile-more-panel";
  const profileHref = user ? profileHrefFromRow(user) || profileHrefFromIdentifier("me") : "/login";
  const showGuestAuthOverlay = !user && guestAuthOverlayAppliesToView(props.view, props.currentUrl);
  const shellAuthRedirect = currentRouteHref(props.currentUrl, props.currentQuery);
  const shellLoginHref = authRouteHrefForRedirect("/login", shellAuthRedirect);
  const shellSignupHref = authRouteHrefForRedirect("/signup", shellAuthRedirect);
  const handleNotificationDelivery = useCallback(() => {
    window.dispatchEvent(new Event(notificationsChangedEvent));
  }, []);
  useNotificationDelivery(user?.id ?? null, props.notificationStream, handleNotificationDelivery);
  useEffect(() => {
    setThemePreference(themePreferenceFromUser(user));
  }, [user?.id, user?.settings?.themePreference]);
  useEffect(() => {
    applyOpenSNTheme(themePreference);
    if (themePreference !== "system" || typeof window === "undefined" || !window.matchMedia) return;
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const updateSystemTheme = () => applyOpenSNTheme("system");
    media.addEventListener("change", updateSystemTheme);
    return () => media.removeEventListener("change", updateSystemTheme);
  }, [themePreference]);
  async function handleThemePreferenceChange(value: OpenSNThemePreference) {
    if (value === themePreference && themeState.status !== "error") return;
    const previous = themePreference;
    setThemePreference(value);
    setThemeState({ status: "saving", message: ui.savingSettings });
    try {
      await submitJson("/api/settings/display", "PATCH", { themePreference: value });
      setThemeState({ status: "success", message: ui.settingsSaved });
    } catch (error) {
      setThemePreference(previous);
      setThemeState({ status: "error", message: localizedErrorMessage(error, ui.settingsRequestFailed) });
    }
  }
  function handleShellRouteClick(event: ReactMouseEvent<HTMLAnchorElement>, href: string, onNavigated?: () => void) {
    handleOpenSNRouteClick(event, href, onNavigated);
  }
  function handleTopbarRouteClick(event: ReactMouseEvent<HTMLAnchorElement>, href: string) {
    handleShellRouteClick(event, href, () => setActiveTopbarMenu(null));
  }
  function handleModuleRouteClick(event: ReactMouseEvent<HTMLAnchorElement>, href: string) {
    handleShellRouteClick(event, href, () => {
      setActiveTopbarMenu(null);
      setMobileMoreOpen(false);
    });
  }
  function handleMobileRouteClick(event: ReactMouseEvent<HTMLAnchorElement>, href: string) {
    handleModuleRouteClick(event, href);
  }
  async function handleLogout() {
    setLogoutState({ status: "saving", message: ui.logOut });
    try {
      await submitJson("/api/auth/logout", "POST", {});
      window.location.assign(shellLoginHref);
    } catch (error) {
      setLogoutState({ status: "error", message: localizedErrorMessage(error, ui.requestFailed) });
    }
  }
  function handleBrandRouteClick(event: ReactMouseEvent<HTMLAnchorElement>) {
    handleShellRouteClick(event, brand.homeHref, () => {
      setActiveTopbarMenu(null);
      setMobileMoreOpen(false);
    });
  }
  function handleSearchSubmit(event: FormEvent<HTMLFormElement>) {
    if (event.defaultPrevented) return;
    const form = new FormData(event.currentTarget);
    const query = String(form.get("q") ?? "").trim();
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    const serialized = params.toString();
    const href = `/search${serialized ? `?${serialized}` : ""}`;
    if (!navigateOpenSNRoute(href)) return;
    event.preventDefault();
    setActiveTopbarMenu(null);
    setMobileMoreOpen(false);
  }
  useEffect(() => {
    if (!activeTopbarMenu) return;
    const closeOnPointer = (event: PointerEvent) => {
      if (topbarRef.current && !topbarRef.current.contains(event.target as Node)) setActiveTopbarMenu(null);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeTopbarMenu(true, activeTopbarMenu);
    };
    document.addEventListener("pointerdown", closeOnPointer);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnPointer);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [activeTopbarMenu]);
  useEffect(() => {
    const updateScrolledState = () => {
      setTopbarScrolled(Math.max(window.scrollY, document.documentElement.scrollTop) > 4);
    };
    updateScrolledState();
    window.addEventListener("scroll", updateScrolledState, { passive: true });
    document.addEventListener("scroll", updateScrolledState, { capture: true, passive: true });
    return () => {
      window.removeEventListener("scroll", updateScrolledState);
      document.removeEventListener("scroll", updateScrolledState, { capture: true });
    };
  }, []);
  useEffect(() => {
    window.addEventListener(notificationsChangedEvent, notificationsResult.reload);
    return () => window.removeEventListener(notificationsChangedEvent, notificationsResult.reload);
  }, [notificationsResult.reload]);
  useEffect(() => {
    if (!mobileMoreOpen) return;
    window.requestAnimationFrame(() => mobileMorePanelRef.current?.focus({ preventScroll: true }));
    const closeOnOutsidePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (mobileMorePanelRef.current?.contains(target) || mobileMoreButtonRef.current?.contains(target)) return;
      closeMobileMore();
    };
    document.addEventListener("pointerdown", closeOnOutsidePointerDown);
    return () => document.removeEventListener("pointerdown", closeOnOutsidePointerDown);
  }, [mobileMoreOpen]);
  function toggleTopbarMenu(menu: TopbarMenu) {
    setActiveTopbarMenu((current) => (current === menu ? null : menu));
  }
  function handleTopbarMenuTriggerKeyDown(event: ReactKeyboardEvent<HTMLButtonElement>, menu: TopbarMenu, menuId: string) {
    if (!["Enter", " ", "ArrowDown", "ArrowUp"].includes(event.key)) return;
    event.preventDefault();
    const menuOpen = activeTopbarMenu === menu;
    if (menuOpen && (event.key === "Enter" || event.key === " ")) {
      setActiveTopbarMenu(null);
      return;
    }
    if (!menuOpen) setActiveTopbarMenu(menu);
    focusMenuItem(menuId, event.key === "ArrowUp");
  }
  function closeTopbarMenu(restoreFocus = false, menu: TopbarMenu | null = activeTopbarMenu) {
    setActiveTopbarMenu(null);
    if (!restoreFocus) return;
    const trigger = menu === "notifications" ? notificationsButtonRef.current : menu === "user" ? userMenuButtonRef.current : null;
    window.requestAnimationFrame(() => trigger?.focus({ preventScroll: true }));
  }
  function closeMobileMore(restoreFocus = false) {
    setMobileMoreOpen(false);
    if (restoreFocus) window.requestAnimationFrame(() => mobileMoreButtonRef.current?.focus({ preventScroll: true }));
  }
  function handleMobileMorePanelKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      closeMobileMore(true);
      return;
    }
    trapFocusTab(event, mobileMorePanelRef.current);
  }
  return (
    <main className={`social-shell module-${props.module}`} dir={props.locale.direction}>
      <header ref={topbarRef} className={`topline shell-topline ${user ? "is-authenticated" : "is-guest"} ${topbarScrolled ? "is-scrolled" : ""}`} aria-label={socialSearchLabel} aria-hidden={showGuestAuthOverlay ? "true" : undefined} inert={showGuestAuthOverlay ? true : undefined}>
        <a className="brand top-brand" href={brand.homeHref} aria-label={brand.ariaLabel} onClick={handleBrandRouteClick}>
          <BrandIdentity />
        </a>
        <form className="searchbar" action="/search" method="get" onSubmit={handleSearchSubmit}>
          <button className="search-submit" type="submit" title={searchLabel} aria-label={searchLabel}>
            <Search size={18} />
          </button>
          <FloatingField label={searchLabel} className="search-field">
            <input key={searchQuery} name="q" defaultValue={searchQuery} aria-label={searchLabel} placeholder={searchLabel} />
          </FloatingField>
        </form>
        <div className="top-actions">
          <button ref={mobileMoreButtonRef} className={`icon-button mobile-menu-button ${mobileMoreOpen || mobileMoreActive ? "active" : ""}`} type="button" aria-label={ui.more} aria-haspopup="dialog" aria-expanded={mobileMoreOpen} aria-controls={mobileMorePanelId} onClick={() => setMobileMoreOpen((open) => !open)}><MoreHorizontal size={20} /></button>
          {!user && (
            <div className="top-language-switcher">
              <LanguageSlider props={props} variant="social" panelId={languageMenuId} controlledOpen={activeTopbarMenu === "language"} onOpenChange={(open) => setActiveTopbarMenu(open ? "language" : null)} />
            </div>
          )}
          {user && (
            <div className="top-menu-wrap">
              <button ref={notificationsButtonRef} className={`icon-button ${activeTopbarMenu === "notifications" ? "active" : ""}`} type="button" title={notificationButtonLabel} aria-label={notificationButtonLabel} aria-haspopup="menu" aria-expanded={activeTopbarMenu === "notifications"} aria-controls={notificationsMenuId} onKeyDown={(event) => handleTopbarMenuTriggerKeyDown(event, "notifications", notificationsMenuId)} onClick={() => toggleTopbarMenu("notifications")}>
                <Bell size={19} />
                {notificationsUnread && <span className="topbar-unread-badge" aria-hidden="true">{notificationBadgeText(notificationsUnreadCount)}</span>}
              </button>
              {activeTopbarMenu === "notifications" && <TopbarNotificationsMenu id={notificationsMenuId} result={notificationsResult} ui={ui} label={notificationsLabel} loginLabel={props.dictionary.public.nav.login} loginHref={shellLoginHref} onRouteClick={handleTopbarRouteClick} />}
            </div>
          )}
          <div className="top-menu-wrap user-menu-wrap">
            <button ref={userMenuButtonRef} className="identity" type="button" aria-label={displayName} aria-haspopup="menu" aria-expanded={activeTopbarMenu === "user"} aria-controls={userMenuId} onKeyDown={(event) => handleTopbarMenuTriggerKeyDown(event, "user", userMenuId)} onClick={() => toggleTopbarMenu("user")}>
              {user ? <span className="identity-avatar" aria-hidden="true"><ProfileAvatar row={user} size="sm" /></span> : <CircleUserRound size={20} />}
              <span className="identity-name">{displayName}</span>
              <ChevronDown size={15} />
            </button>
            {activeTopbarMenu === "user" && (
              <div id={userMenuId} className="user-menu topbar-menu" role="menu" onKeyDown={(event) => trapFocusTab(event, event.currentTarget)}>
                {user ? (
                  <>
                    <a href={profileHref} role="menuitem" onClick={(event) => handleTopbarRouteClick(event, profileHref)}>{ui.profile}</a>
                    <a href="/invites" role="menuitem" onClick={(event) => handleTopbarRouteClick(event, "/invites")}>{ui.invites}</a>
                    <a href="/affiliates" role="menuitem" onClick={(event) => handleTopbarRouteClick(event, "/affiliates")}>{affiliateCopy(props.locale.code).title}</a>
                    {["owner", "admin"].includes(user.role) && <a href="/ops/affiliates" role="menuitem" onClick={(event) => handleTopbarRouteClick(event, "/ops/affiliates")}>{affiliateCopy(props.locale.code).opsTitle}</a>}
                    <a href="/membership" role="menuitem" onClick={(event) => handleTopbarRouteClick(event, "/membership")}>{ui.membership}</a>
                    <a href="/wallet" role="menuitem" onClick={(event) => handleTopbarRouteClick(event, "/wallet")}>{props.dictionary.social.modules.wallet}</a>
                    <a href="/verification" role="menuitem" onClick={(event) => handleTopbarRouteClick(event, "/verification")}>{ui.verify}</a>
                    <a href="/support" role="menuitem" onClick={(event) => handleTopbarRouteClick(event, "/support")}>{props.dictionary.public.nav.support}</a>
                    <a href="/settings/profile" role="menuitem" onClick={(event) => handleTopbarRouteClick(event, "/settings/profile")}>{props.dictionary.social.modules.settings}</a>
                    <div className="theme-menu-group" role="group" aria-label={themeLabel}>
                      <div>
                        {themeOptions.map(({ value, label, Icon }) => (
                          <button type="button" role="menuitemradio" aria-label={label} title={label} aria-checked={themePreference === value} className={themePreference === value ? "active" : ""} disabled={themeState.status === "saving"} onClick={() => handleThemePreferenceChange(value)} key={value}>
                            <Icon size={17} aria-hidden="true" />
                          </button>
                        ))}
                      </div>
                      {themeState.status === "error" && <InlineStatus state={themeState} />}
                    </div>
                    <button type="button" role="menuitem" disabled={logoutState.status === "saving"} onClick={handleLogout}>{ui.logOut}</button>
                    {logoutState.status === "error" && <InlineStatus state={logoutState} />}
                  </>
                ) : (
                  <>
                    <a href={shellLoginHref} role="menuitem" onClick={(event) => handleTopbarRouteClick(event, shellLoginHref)}>{props.dictionary.public.nav.login}</a>
                    <a href={shellSignupHref} role="menuitem" onClick={(event) => handleTopbarRouteClick(event, shellSignupHref)}>{ui.createAccount}</a>
                    <a href="/support" role="menuitem" onClick={(event) => handleTopbarRouteClick(event, "/support")}>{props.dictionary.public.nav.support}</a>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </header>
      <aside className="sidebar" aria-hidden={showGuestAuthOverlay ? "true" : undefined} inert={showGuestAuthOverlay ? true : undefined}>
        {user && <a className="sidebar-profile" href={profileHref} onClick={(event) => handleModuleRouteClick(event, profileHref)}><ProfileAvatar row={user} size="md" /><span className="sidebar-profile-copy"><strong>{displayName}</strong><small>@{user.username}</small></span><ChevronDown size={14} /></a>}
        <nav className="module-nav" aria-label={ui.primaryNavigation}>
          {visibleModuleLinks.map(([module, href, labelKey]) => {
            const Icon = iconByModule[module] ?? House;
            const active = isModuleLinkActive(props.currentUrl, props.module, module, href);
            return (
              <a className={active ? "active" : ""} href={href} aria-current={active ? "page" : undefined} onClick={(event) => handleModuleRouteClick(event, href)} key={`${module}-${href}`}>
                <Icon size={19} />
                <span>{socialNavLabel(props.dictionary, labelKey)}</span>
              </a>
            );
          })}
        </nav>
        <div className="sidebar-footer">
          <a className="sidebar-invite" href="/invites" onClick={(event) => handleModuleRouteClick(event, "/invites")}><UserPlus size={18} /><span>{ui.invitePeople}</span><ArrowRight size={16} /></a>
          {user && <a className="sidebar-invite" href="/affiliates" aria-current={props.module === "affiliates" ? "page" : undefined} onClick={(event) => handleModuleRouteClick(event, "/affiliates")}><Share2 size={18} /><span>{affiliateCopy(props.locale.code).title}</span><ArrowRight size={16} /></a>}
          <div className="sidebar-footer-links"><a href="/settings/profile" onClick={(event) => handleModuleRouteClick(event, "/settings/profile")}><Settings size={15} />{props.dictionary.social.modules.settings}</a><a href="/support" onClick={(event) => handleModuleRouteClick(event, "/support")}><LifeBuoy size={15} />{props.dictionary.public.nav.support}</a></div>
          <small>{brand.name}</small>
        </div>
      </aside>
      <section className="workspace" aria-hidden={showGuestAuthOverlay ? "true" : undefined} inert={showGuestAuthOverlay ? true : undefined}>
        {children}
      </section>
      <nav className="mobile-bottom-nav" aria-label={ui.primaryNavigation} aria-hidden={showGuestAuthOverlay ? "true" : undefined} inert={showGuestAuthOverlay ? true : undefined}>
        {mobilePrimaryLinks.map(([module, href, labelKey]) => {
          const Icon = iconByModule[module] ?? House;
          const active = isModuleLinkActive(props.currentUrl, props.module, module, href);
          return (
            <a className={active ? "active" : ""} href={href} aria-current={active ? "page" : undefined} onClick={(event) => handleMobileRouteClick(event, href)} key={`mobile-${module}-${href}`}>
              <Icon size={19} />
              <span>{socialNavLabel(props.dictionary, labelKey)}</span>
            </a>
          );
        })}
        <a className={props.module === "profile" ? "active" : ""} href={profileHref} aria-current={props.module === "profile" ? "page" : undefined} onClick={(event) => handleMobileRouteClick(event, profileHref)}><CircleUserRound size={19} /><span>{ui.profile}</span></a>
      </nav>
      {mobileMoreOpen && (
        <div id={mobileMorePanelId} className="mobile-more-panel" role="dialog" aria-label={ui.more} aria-hidden={showGuestAuthOverlay ? "true" : undefined} inert={showGuestAuthOverlay ? true : undefined} tabIndex={-1} ref={mobileMorePanelRef} onKeyDown={handleMobileMorePanelKeyDown}>
          {mobileMoreLinks.map(([module, href, labelKey]) => {
            const Icon = iconByModule[module] ?? House;
            const active = isModuleLinkActive(props.currentUrl, props.module, module, href);
            return (
              <a className={active ? "active" : ""} href={href} aria-current={active ? "page" : undefined} onClick={(event) => handleMobileRouteClick(event, href)} key={`mobile-more-${module}-${href}`}>
                <Icon size={18} />
                <span>{socialNavLabel(props.dictionary, labelKey)}</span>
              </a>
            );
          })}
          <a href="/settings/profile" onClick={(event) => handleMobileRouteClick(event, "/settings/profile")}><Settings size={18} /><span>{props.dictionary.social.modules.settings}</span></a>
          <a href="/support" onClick={(event) => handleMobileRouteClick(event, "/support")}><LifeBuoy size={18} /><span>{props.dictionary.public.nav.support}</span></a>
        </div>
      )}
      {showGuestAuthOverlay && <GuestAuthOverlay props={props} />}
    </main>
  );
}

function themePreferenceFromUser(user: PageProps["auth"]["user"]): OpenSNThemePreference {
  return themePreferenceFromSettings(user?.settings ?? {});
}

function themePreferenceFromSettings(settings: OpenSNUserSettings): OpenSNThemePreference {
  return normalizeThemePreference(settings.themePreference ?? (settings as Record<string, unknown>).theme_preference);
}

function normalizeThemePreference(value: unknown): OpenSNThemePreference {
  return value === "light" || value === "dark" || value === "system" ? value : "system";
}

function applyOpenSNTheme(value: OpenSNThemePreference): void {
  if (typeof document === "undefined") return;
  const preference = normalizeThemePreference(value);
  const systemDark = typeof window !== "undefined" && window.matchMedia ? window.matchMedia("(prefers-color-scheme: dark)").matches : false;
  const resolved = preference === "dark" || (preference === "system" && systemDark) ? "dark" : "light";
  document.documentElement.dataset.opensnTheme = preference;
  document.documentElement.dataset.opensnResolvedTheme = resolved;
  document.documentElement.style.colorScheme = resolved;
}

let modalScrollLockCount = 0;
let modalBodyOverflow = "";
let modalRootOverflow = "";
let modalWorkspaceScrollState: Array<{
  element: HTMLElement;
  overflow: string;
  overscrollBehavior: string;
  scrollTop: number;
  scrollLeft: number;
}> = [];
const modalInertState = new WeakMap<HTMLElement, { count: number; inert: boolean; ariaHidden: string | null }>();

function acquireModalScrollLock() {
  if (modalScrollLockCount === 0) {
    modalBodyOverflow = document.body.style.overflow;
    modalRootOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    modalWorkspaceScrollState = Array.from(document.querySelectorAll<HTMLElement>(".workspace")).map((element) => ({
      element,
      overflow: element.style.overflow,
      overscrollBehavior: element.style.overscrollBehavior,
      scrollTop: element.scrollTop,
      scrollLeft: element.scrollLeft
    }));
    modalWorkspaceScrollState.forEach(({ element }) => {
      element.style.overflow = "hidden";
      element.style.overscrollBehavior = "none";
    });
  }
  modalScrollLockCount += 1;
}

function releaseModalScrollLock() {
  modalScrollLockCount = Math.max(0, modalScrollLockCount - 1);
  if (modalScrollLockCount !== 0) return;
  document.body.style.overflow = modalBodyOverflow;
  document.documentElement.style.overflow = modalRootOverflow;
  modalWorkspaceScrollState.forEach(({ element, overflow, overscrollBehavior, scrollTop, scrollLeft }) => {
    element.style.overflow = overflow;
    element.style.overscrollBehavior = overscrollBehavior;
    element.scrollTop = scrollTop;
    element.scrollLeft = scrollLeft;
  });
  modalWorkspaceScrollState = [];
}

function modalBackgroundElements(dialog: HTMLElement): HTMLElement[] {
  const elements = new Set<HTMLElement>();
  let current: HTMLElement = dialog;
  while (current.parentElement) {
    const parent = current.parentElement;
    Array.from(parent.children).forEach((child) => {
      if (child === current || !(child instanceof HTMLElement) || ["SCRIPT", "STYLE", "LINK"].includes(child.tagName))
        return;
      elements.add(child);
    });
    if (parent === document.body) break;
    current = parent;
  }
  return [...elements];
}

function acquireModalInert(elements: HTMLElement[]) {
  elements.forEach((element) => {
    const state = modalInertState.get(element);
    if (state) {
      state.count += 1;
      return;
    }
    modalInertState.set(element, {
      count: 1,
      inert: element.hasAttribute("inert"),
      ariaHidden: element.getAttribute("aria-hidden")
    });
    element.setAttribute("inert", "");
    element.setAttribute("aria-hidden", "true");
  });
}

function releaseModalInert(elements: HTMLElement[]) {
  elements.forEach((element) => {
    const state = modalInertState.get(element);
    if (!state) return;
    state.count -= 1;
    if (state.count > 0) return;
    if (!state.inert) element.removeAttribute("inert");
    if (state.ariaHidden === null) element.removeAttribute("aria-hidden");
    else element.setAttribute("aria-hidden", state.ariaHidden);
    modalInertState.delete(element);
  });
}

function useModalLifecycle<T extends HTMLElement>(
  open: boolean,
  dialogRef: RefObject<T | null>,
  initialFocusRef?: RefObject<HTMLElement | null>,
  inertBackground = true,
  returnFocusRef?: RefObject<HTMLElement | null>
) {
  useEffect(() => {
    if (!open || typeof document === "undefined") return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    const returnFocus =
      returnFocusRef?.current ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    const background = inertBackground ? modalBackgroundElements(dialog) : [];
    acquireModalScrollLock();
    acquireModalInert(background);
    const frame = window.requestAnimationFrame(() => {
      const focusTarget = initialFocusRef?.current ?? focusableElements(dialog)[0] ?? dialog;
      focusTarget.focus({ preventScroll: true });
    });
    return () => {
      window.cancelAnimationFrame(frame);
      releaseModalInert(background);
      releaseModalScrollLock();
      if (returnFocus?.isConnected) window.requestAnimationFrame(() => returnFocus.focus({ preventScroll: true }));
    };
  }, [dialogRef, inertBackground, initialFocusRef, open, returnFocusRef]);
}


function trapFocusTab(event: ReactKeyboardEvent<HTMLElement>, container: HTMLElement | null) {
  if (event.key !== "Tab") return;
  const focusable = focusableElements(container);
  if (!focusable.length) {
    event.preventDefault();
    container?.focus({ preventScroll: true });
    return;
  }
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = document.activeElement;
  if (event.shiftKey && active === first) {
    event.preventDefault();
    last.focus({ preventScroll: true });
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus({ preventScroll: true });
  } else if (!focusable.includes(active as HTMLElement)) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus({ preventScroll: true });
  }
}

function focusMenuItem(menuId: string | undefined, last = false) {
  if (!menuId) return;
  window.requestAnimationFrame(() => {
    const items = focusableElements(document.getElementById(menuId));
    const target = last ? items[items.length - 1] : items[0];
    target?.focus({ preventScroll: true });
  });
}

function focusableElements(container: HTMLElement | null): HTMLElement[] {
  return Array.from(
    container?.querySelectorAll<HTMLElement>(
      "summary, a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex='-1'])"
    ) ?? []
  ).filter((item) => {
    const style = window.getComputedStyle(item);
    return (
      !item.hasAttribute("hidden") &&
      item.getAttribute("aria-hidden") !== "true" &&
      !item.closest("[inert]") &&
      style.display !== "none" &&
      style.visibility !== "hidden" &&
      item.getClientRects().length > 0
    );
  });
}

function handleMenuPanelKeyDown(event: ReactKeyboardEvent<HTMLElement>, container: HTMLElement | null) {
  if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
    const items = focusableElements(container);
    if (!items.length) return;
    event.preventDefault();
    const activeIndex = items.findIndex((item) => item === document.activeElement);
    const baseIndex = activeIndex >= 0 ? activeIndex : 0;
    const nextIndex = event.key === "Home"
      ? 0
      : event.key === "End"
        ? items.length - 1
        : event.key === "ArrowUp"
          ? (baseIndex - 1 + items.length) % items.length
          : (baseIndex + 1) % items.length;
    items[nextIndex]?.focus({ preventScroll: true });
    return;
  }
  trapFocusTab(event, container);
}

function GuestAuthOverlay({ props }: { props: PageProps }) {
  const ui = props.dictionary.social.ui;
  const redirect = currentRouteHref(props.currentUrl, props.currentQuery);
  const loginHref = authRouteHrefForRedirect("/login", redirect);
  const signupHref = authRouteHrefForRedirect("/signup", redirect);
  const dialogRef = useRef<HTMLElement | null>(null);
  const loginRef = useRef<HTMLAnchorElement | null>(null);
  useModalLifecycle(true, dialogRef, loginRef);
  useEffect(() => {
    window.requestAnimationFrame(() => {
      loginRef.current?.focus({ preventScroll: true });
    });
  }, [loginHref]);
  function handleDialogKeyDown(event: ReactKeyboardEvent<HTMLElement>) {
    trapFocusTab(event, dialogRef.current);
  }
  return (
    <div className="guest-auth-overlay">
      <section className="guest-auth-dialog" role="dialog" aria-modal="true" aria-labelledby="guest-auth-title" aria-describedby="guest-auth-description" tabIndex={-1} ref={dialogRef} onKeyDown={handleDialogKeyDown}>
        <Lock size={22} aria-hidden="true" />
        <h2 id="guest-auth-title">{ui.logInToContinueTitle}</h2>
        <p id="guest-auth-description">{ui.logInToContinueBody}</p>
        <div className="guest-auth-actions">
          <a className="base-button primary" href={loginHref} ref={loginRef} onClick={(event) => handleOpenSNRouteClick(event, loginHref)}>{ui.logIn}</a>
          <a className="base-button outlined" href={signupHref} onClick={(event) => handleOpenSNRouteClick(event, signupHref)}>{ui.signUp || ui.createAccount}</a>
        </div>
      </section>
    </div>
  );
}

function TopbarNotificationsMenu({ id, result, ui, label, loginLabel, loginHref, onRouteClick }: { id: string; result: ApiResult; ui: I18nDictionary["social"]["ui"]; label: string; loginLabel: string; loginHref: string; onRouteClick: (event: ReactMouseEvent<HTMLAnchorElement>, href: string) => void }) {
  const [state, setState] = useState<SubmitState>({ status: "idle", message: "" });
  const rows = result.rows.slice(0, 10);
  const hasRows = rows.length > 0;
  const hasUnread = notificationUnreadCount(result) > 0;
  async function markAllRead() {
    setState({ status: "saving", message: ui.markAllRead });
    try {
      await submitJson("/api/notifications/read-all", "POST", {});
      setState({ status: "success", message: ui.done });
      window.dispatchEvent(new Event(notificationsChangedEvent));
      result.reload();
    } catch (error) {
      setState({ status: "error", message: localizedErrorMessage(error, ui.actionFailed) });
    }
  }
  return (
    <div id={id} className="topbar-menu notifications-menu" role="menu" aria-label={label} onKeyDown={(event) => trapFocusTab(event, event.currentTarget)}>
      {result.status === "loading" && !hasRows && <div className="topbar-menu-state">{ui.loadingResults}</div>}
      {result.status === "permission" && (
        <div className="topbar-menu-state">
          <strong>{ui.logInToContinueTitle}</strong>
          <a href={loginHref} role="menuitem" onClick={(event) => onRouteClick(event, loginHref)}>{loginLabel}</a>
        </div>
      )}
      {result.status === "error" && (
        <div className="topbar-menu-state">
          <strong>{ui.unableToLoadView}</strong>
          <span>{displayErrorMessage(result.error, ui.retry)}</span>
        </div>
      )}
      {result.status === "ready" && !hasRows && (
        <div className="topbar-menu-state">
          <strong>{ui.allClear}</strong>
          <span>{ui.notificationsEmpty}</span>
        </div>
      )}
      {hasRows && (
        <div className="topbar-notification-list">
          {rows.map((row, index) => {
            const unread = !row.read_at && !row.readAt;
            const title = notificationTitle(row, ui);
            const body = notificationBody(row, ui);
            const when = timeLabel(row.created_at ?? row.createdAt);
            const href = notificationHref(row);
            return (
              <a className={`topbar-notification-item ${unread ? "unread" : ""}`} href={href} role="menuitem" onClick={(event) => onRouteClick(event, href)} key={String(row.id ?? index)}>
                <span className="topbar-notification-dot" aria-hidden="true" />
                <span className="topbar-notification-copy">
                  <strong>{title}</strong>
                  {body && <span>{body}</span>}
                  {when && <time>{when}</time>}
                </span>
              </a>
            );
          })}
        </div>
      )}
      {hasUnread && <button className="topbar-menu-more" type="button" role="menuitem" disabled={state.status === "saving"} onClick={markAllRead}>{ui.markAllRead}</button>}
      <InlineStatus state={state} />
      <a className="topbar-menu-more" href="/notifications" role="menuitem" onClick={(event) => onRouteClick(event, "/notifications")}>{ui.more}</a>
    </div>
  );
}

function canAccessOps(user: PageProps["auth"]["user"]): boolean {
  return false;
}

function isModuleVisible(module: string, user: PageProps["auth"]["user"]): boolean {
  if (module === "ads") return false;
  if (module === "ops") return false;
  return true;
}

function isModuleLinkActive(currentUrl: string, currentModule: string, module: string, href: string): boolean {
  const firstSegment = href.split("/")[1];
  const hrefRoot = firstSegment ? `/${firstSegment}` : href;
  if (module === "feed" && currentModule === "feed" && currentUrl.startsWith("/post/")) return true;
  if (module === "search" && currentModule === "search" && (currentUrl === "/hashtags" || currentUrl.startsWith("/hashtags/"))) return true;
  if (module === "network" && currentModule === "network" && (currentUrl === "/network" || currentUrl.startsWith("/network/") || currentUrl === "/users" || currentUrl.startsWith("/users/"))) return true;
  return currentUrl === href || (currentModule === module && currentUrl.startsWith(hrefRoot));
}

function SocialContractView(props: PageProps) {
  const [queryVisible, setQueryVisible] = useState(false);
  const currentQueryFilter = searchQueryFromRoute(props.currentUrl, props.currentQuery);
  const [filterState, setFilterState] = useState(() => ({ query: currentQueryFilter, value: currentQueryFilter }));
  const filter = filterState.query === currentQueryFilter ? filterState.value : currentQueryFilter;
  function updateFilter(value: string) {
    setFilterState({ query: currentQueryFilter, value });
  }
  const isHome = props.view.name === "Home Feed";
  const isPostDetail = props.view.name === "Post Detail";
  const endpoint = endpointForCurrentView(props, filter);
  const result = useApi(endpoint, initialApiData(props, endpoint));
  useMemberPolling(result.reload, props.view.module === "notifications" && Boolean(props.auth.user));
  const hasSearchQuery = props.view.name === "Search" && Boolean(filter.trim());
  const panelEmpty = hasSearchQuery ? uiLabel(props.dictionary.social.ui, "searchNoResultsBody", "No results matched this search.") : emptyText(props.view, props.dictionary.social.ui);
  const panelEmptyTitle = hasSearchQuery ? uiLabel(props.dictionary.social.ui, "searchNoResultsTitle", "No results") : undefined;
  useEffect(() => {
    if (props.view.module !== "notifications") return;
    window.addEventListener(notificationsChangedEvent, result.reload);
    return () => window.removeEventListener(notificationsChangedEvent, result.reload);
  }, [props.view.module, result.reload]);
  const isGrid = props.view.layoutTemplate === "grid-directory";
  const isNetworkDirectory = props.view.module === "network";
  const isEventDirectory = props.view.module === "events";
  const isEntityDetail = Boolean(directoryEntityEndpointForView(props.view, props.currentUrl));
  const isEventDetail = isEventDirectory && isEntityDetail;
  const isDirectoryGrid = (isGrid || isNetworkDirectory || isEventDirectory) && !isEntityDetail;
  const viewActions = actionsForCurrentView(props.view, props.currentUrl);
  const notificationsReadyWithoutUnread = props.view.name === "Notifications" && result.status === "ready" && notificationUnreadCount(result) === 0;
  const visibleActions = notificationsReadyWithoutUnread ? viewActions.filter((action) => !action.toLowerCase().includes("mark all read")) : viewActions;
  const showRail = !isHome && visibleActions.length > 0;
  return (
    <section className={`contract-page social-contract-page ${isHome ? "home-feed-page" : ""}`}>
      {!isHome && <ContractHero props={props} onRefresh={result.reload} onSearch={() => setQueryVisible(true)} />}
      {props.view.name === "Network" && <nav className="profile-tabs" aria-label={props.dictionary.social.modules.network}>
        <a href="/network" className={props.currentQuery.connections !== "1" ? "active" : ""} onClick={(event) => handleOpenSNRouteClick(event, "/network")}>{memberCopy(props.locale.code).discoverPeople}</a>
        <a href="/network?connections=1" className={props.currentQuery.connections === "1" ? "active" : ""} onClick={(event) => handleOpenSNRouteClick(event, "/network?connections=1")}>{memberCopy(props.locale.code).myConnections}</a>
      </nav>}
      {queryVisible && (
        <form className="filter-strip" action={filterFormAction(props)} method="get">
          <Search size={18} />
          <FloatingField label={props.dictionary.social.ui.search} className="filter-field">
            <input name="q" value={filter} onChange={(event) => updateFilter(event.currentTarget.value)} placeholder={props.dictionary.social.ui.filterResults} aria-label={props.dictionary.social.ui.filterResults} />
          </FloatingField>
          <BaseButton type="button" variant="transparent" size="sm" onClick={() => updateFilter("")}>
            {props.dictionary.social.ui.clear}
          </BaseButton>
        </form>
      )}
      {!isEntityDetail && (isGrid || isEventDirectory) && isCategoryDirectoryView(props.view) && <DirectoryCategoryFilter props={props} />}
      <div className={isHome ? "feed-layout" : `contract-grid ${showRail ? "" : "no-rail"}`}>
        <section className="contract-main">
          {isHome && <Composer authReady={Boolean(props.auth.user)} authUser={props.auth.user} onPublished={result.reload} dictionary={props.dictionary} localeCode={props.locale.code} />}
          <DataPanel
            result={result}
            title={localizedViewTitle(props.view, props.dictionary)}
            view={props.view}
            empty={panelEmpty}
            emptyTitle={panelEmptyTitle}
            renderRow={isHome || isPostDetail || props.view.module === "saved" ? (row) => renderPostRow(row, props.dictionary.social.ui, props.auth.user, props.locale.code, isPostDetail) : props.view.module === "notifications" ? (row) => <NotificationRow row={row} ui={props.dictionary.social.ui} /> : props.view.module === "search" ? (row) => <SearchResultRow row={row} props={props} /> : props.view.module === "events" ? (row) => <EventDirectoryRow row={row} view={props.view} ui={props.dictionary.social.ui} categoryLabels={props.dictionary.categoryLabels} authUser={props.auth.user} detail={isEventDetail} /> : isDirectoryGrid || isEntityDetail ? (row) => <DirectoryRow row={row} view={props.view} ui={props.dictionary.social.ui} categoryLabels={props.dictionary.categoryLabels} authUser={props.auth.user} detail={isEntityDetail} /> : (row) => renderGenericRow(row, props.dictionary.social.ui)}
            density={isDirectoryGrid ? "grid" : "list"}
            emptyActions={!isHome && !showRail}
            ui={props.dictionary.social.ui}
          />
        </section>
        {showRail && (
          <aside className="contract-rail">
            <ActionPanel view={props.view} actions={visibleActions} onRefresh={result.reload} onSearch={() => setQueryVisible(true)} currentUrl={props.currentUrl} dictionary={props.dictionary} />
          </aside>
        )}
      </div>
    </section>
  );
}

function SearchResultRow({ row, props }: { row: Record<string, unknown>; props: PageProps }) {
  const type = String(row.result_type ?? "");
  const ui = props.dictionary.social.ui;
  if (type === "post") return renderPostRow(row, ui, props.auth.user, props.locale.code);
  if (type === "user" || type === "group" || type === "page") {
    const module = type === "user" ? "network" : type === "group" ? "groups" : "pages";
    const view = { ...props.view, module, endpoint: module === "network" ? "/api/users" : `/api/${module}` };
    return <DirectoryRow row={row} view={view} ui={ui} categoryLabels={props.dictionary.categoryLabels} authUser={props.auth.user} />;
  }
  const href = String(row.href ?? "");
  const title = String(row.title ?? row.tag ?? row.name ?? "");
  return <div className="directory-card"><strong><a href={href} onClick={(event) => handleOpenSNRouteClick(event, href)}>{title}</a></strong>{row.body ? <p>{String(row.body)}</p> : null}</div>;
}

function filterFormAction(props: PageProps): string {
  if (props.view.name === "Search") return "/search";
  return canonicalProfileRouteHref(normalizeAppPath(props.currentUrl)) || normalizeAppPath(props.currentUrl);
}

function StoriesTray({ ui, authUser }: { ui: I18nDictionary["social"]["ui"]; authUser: PageProps["auth"]["user"] }) {
  const result = useApi("/api/stories");
  const [composerOpen, setComposerOpen] = useState(false);
  if (result.status === "error" && result.rows.length === 0) return null;
  if (!authUser && result.rows.length === 0) return null;
  return (
    <section className="stories-tray" aria-label={ui.stories}>
      {authUser && <StoryCreateCard user={authUser} ui={ui} onOpen={() => setComposerOpen(true)} />}
      {result.status === "loading" && result.rows.length === 0 && <StoryLoadingSkeleton label={ui.loadingResults} />}
      {result.rows.map((row, index) => <StoryCard row={row} ui={ui} key={String(row.id ?? row.story_id ?? index)} />)}
      {composerOpen && authUser && <StoryComposerDialog user={authUser} ui={ui} onClose={() => setComposerOpen(false)} onPublished={result.reload} />}
    </section>
  );
}

function FeedScopeTabs({ props }: { props: PageProps }) {
  const ui = props.dictionary.social.ui;
  const currentScope = feedScopeFromQuery(currentQueryFromRoute(props.currentUrl, props.currentQuery));
  const items: Array<{ id: FeedScope; label: string; href: string }> = [
    { id: "public", label: ui.public, href: feedScopeHref("public") },
    { id: "friends", label: ui.friends, href: feedScopeHref("friends") }
  ];
  return (
    <nav className="feed-scope-tabs" aria-label={localizedViewTitle(props.view, props.dictionary)}>
      {items.map((item) => (
        <a className={item.id === currentScope ? "active" : ""} href={item.href} aria-current={item.id === currentScope ? "page" : undefined} onClick={(event) => handleOpenSNRouteClick(event, item.href)} key={item.id}>
          {item.label}
        </a>
      ))}
    </nav>
  );
}

function StoryCreateCard({ user, ui, onOpen }: { user: NonNullable<PageProps["auth"]["user"]>; ui: I18nDictionary["social"]["ui"]; onOpen: () => void }) {
  return (
    <button className="story-card story-create-card" type="button" onClick={onOpen}>
      <span className="story-create-media">
        <ProfileAvatar row={user} size="lg" />
      </span>
      <span className="story-create-plus"><Plus size={18} /></span>
      <span className="story-title">{ui.createStory}</span>
    </button>
  );
}

function StoryLoadingSkeleton({ label }: { label: string }) {
  return (
    <>
      {[0, 1, 2].map((index) => (
        <div className="story-card story-skeleton-card" role={index === 0 ? "status" : undefined} aria-label={index === 0 ? label : undefined} aria-hidden={index === 0 ? undefined : "true"} key={index}>
          <span className="story-skeleton-avatar" />
          <span className="story-skeleton-title" />
        </div>
      ))}
    </>
  );
}

function StoryComposerDialog({ user, ui, onClose, onPublished }: { user: NonNullable<PageProps["auth"]["user"]>; ui: I18nDictionary["social"]["ui"]; onClose: () => void; onPublished: () => void }) {
  const [body, setBody] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState<SubmitState>({ status: "idle", message: "" });
  const dialogRef = useRef<HTMLFormElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const textInputRef = useRef<HTMLTextAreaElement | null>(null);
  useModalLifecycle(true, dialogRef, textInputRef);
  const canPublish = Boolean(body.trim() || file);
  useEffect(() => {
    window.requestAnimationFrame(() => textInputRef.current?.focus());
  }, []);
  function chooseFile(files: FileList | null) {
    const image = Array.from(files ?? []).find(isImageUploadFile) ?? null;
    setFile(image);
    setStatus(image ? { status: "idle", message: "" } : { status: "error", message: ui.chooseImageFiles });
  }
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmedBody = body.trim();
    if (!trimmedBody && !file) {
      setStatus({ status: "error", message: ui.storyRequiresTextOrPhoto });
      return;
    }
    setStatus({ status: "saving", message: file ? ui.uploadingMedia : ui.publishing });
    try {
      const uploaded = file ? await uploadFiles([file], { visibility: "public" }) : [];
      const fileId = String(uploaded[0]?.id ?? "");
      if (file && !fileId) throw new Error(ui.chooseImageFiles);
      await submitJson("/api/stories", "POST", { body: trimmedBody, fileId: fileId || undefined, type: fileId ? "photo" : "text" });
      setStatus({ status: "success", message: ui.storyPublished });
      setBody("");
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      onPublished();
      onClose();
    } catch (error) {
      setStatus({ status: "error", message: localizedErrorMessage(error, ui.storyFailed) });
    }
  }
  function handleDialogKeyDown(event: ReactKeyboardEvent<HTMLFormElement>) {
    if (event.key === "Escape" && status.status !== "saving") onClose();
    trapFocusTab(event, dialogRef.current);
  }
  return (
    <div className="composer-dialog-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget && status.status !== "saving") onClose(); }}>
      <form ref={dialogRef} className="composer-expanded-panel composer-dialog story-dialog" role="dialog" aria-modal="true" aria-label={ui.createStory} tabIndex={-1} onSubmit={submit} onKeyDown={handleDialogKeyDown}>
        <header className="composer-dialog-header">
          <span aria-hidden="true" />
          <strong>{ui.createStory}</strong>
          <button className="icon-only" type="button" aria-label={ui.cancel} onClick={onClose} disabled={status.status === "saving"}><X size={18} /></button>
        </header>
        <div className="composer-dialog-body story-dialog-body">
          <div className="composer-entry">
            <ProfileAvatar row={user} size="md" className="composer-avatar" />
            <div className="composer-body-field story-body-field">
              <textarea ref={textInputRef} value={body} onChange={(event) => setBody(event.currentTarget.value)} aria-label={ui.storyTextPlaceholder} placeholder={ui.storyTextPlaceholder} maxLength={500} />
            </div>
          </div>
          <input ref={fileInputRef} className="composer-file-input" type="file" accept={imageUploadAccept} onChange={(event) => chooseFile(event.currentTarget.files)} />
          <div className="story-create-actions">
            <button type="button" onClick={() => fileInputRef.current?.click()}><Images size={17} /><span>{ui.photo}</span></button>
            {file && (
              <span className="story-selected-file">
                <span>{file.name}</span>
                <button type="button" aria-label={`${ui.clear}: ${file.name}`} onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}><X size={13} /></button>
              </span>
            )}
          </div>
          {file && <PublicMediaConsent />}
        </div>
        <div className="composer-actions">
          <BaseButton type="submit" variant="primary" disabled={!canPublish} loading={status.status === "saving"}><Send size={18} /> {ui.shareStory}</BaseButton>
        </div>
        <InlineStatus state={status} />
      </form>
    </div>
  );
}

function StoryCard({ row, ui }: { row: Record<string, unknown>; ui: I18nDictionary["social"]["ui"] }) {
  const href = storyHref(row);
  const src = storyMediaSrc(row);
  const title = String(row.display_name ?? row.author_display_name ?? row.username ?? row.title ?? ui.stories).trim() || ui.stories;
  const body = String(row.body ?? row.text ?? row.caption ?? "").trim();
  const viewed = booleanValue(row.viewed_by_me ?? row.seen_by_me);
  const background = String(row.background_color ?? row.background ?? "").trim();
  const content = (
    <>
      <span className={`story-media ${src ? "has-image" : ""}`} style={!src && background ? { background } : undefined}>
        {src ? <PictureImage src={src} alt="" /> : body ? <span>{body}</span> : <ProfileAvatar row={authorProfileRow(row)} size="lg" />}
      </span>
      <span className="story-avatar-wrap">
        <ProfileAvatar row={authorProfileRow(row)} size="sm" className={viewed ? "viewed" : ""} />
      </span>
      <span className="story-title">{title}</span>
    </>
  );
  return href ? <a className={`story-card ${viewed ? "viewed" : ""}`} href={href} onClick={(event) => handleOpenSNRouteClick(event, href)}>{content}</a> : <div className={`story-card ${viewed ? "viewed" : ""}`}>{content}</div>;
}

function brandProps(props: PageProps): { name: string; initials: string; homeHref: string; ariaLabel: string } {
  const name = String(props.brand?.name ?? "Throuple.Social").trim() || "Throuple.Social";
  const initials = String(props.brand?.initials ?? "TS").trim().slice(0, 4) || "TS";
  const homeHref = String(props.brand?.homeHref ?? "/timeline").trim() || "/timeline";
  const ariaLabel = String(props.brand?.ariaLabel ?? name).trim() || name;
  return { name, initials, homeHref, ariaLabel };
}

function BrandIdentity() {
  return <><img className="brand-symbol" src="/assets/brand-mark.avif" width={42} height={42} alt="" aria-hidden="true" /><span className="brand-wordmark">throuple<span>.social</span></span></>;
}

function AuthContractView(props: PageProps) {
  const isSystem = props.view.layoutTemplate === "system-state-page" || props.view.name === "Public/Unsubscribe";
  const ui = props.dictionary.social.ui;
  const brand = brandProps(props);
  const authVisualVariant = props.view.name === "Auth/Signup" ? "signup" : "login";
  const authVisualAsset = props.brand?.authVisualAssets?.[authVisualVariant] ?? AUTH_VISUAL_ASSETS[authVisualVariant];
  return (
    <main className="auth-page" dir={props.locale.direction}>
      <section className={`auth-visual ${authVisualVariant}`}>
        <a className="auth-brand" href={brand.homeHref} aria-label={brand.ariaLabel} onClick={(event) => handleOpenSNRouteClick(event, brand.homeHref)}>
          <BrandIdentity />
        </a>
        <PeopleAssetPicture
          {...authVisualAsset}
          className="auth-visual-photo"
          sizes="(max-width: 640px) calc(100vw - 48px), (max-width: 1060px) calc(100vw - 84px), 42vw"
          loading="eager"
          fetchPriority="high"
        />
      </section>
      <section className="auth-content">
        <div className="auth-content-stack">
          <LanguageSlider props={props} variant="auth" />
          {isSystem ? <SystemStateView {...props} /> : <AuthForm view={props.view} currentQuery={props.currentQuery} localeCode={props.locale.code} authReady={Boolean(props.auth.user)} authUser={props.auth.user} dictionary={props.dictionary} socialDefinition={props.socialDefinition} />}
        </div>
      </section>
    </main>
  );
}

function LanguageSlider({ props, variant, panelId = variant === "auth" ? "auth-language-menu" : "topbar-language-menu", controlledOpen, onOpenChange }: { props: PageProps; variant: "auth" | "social"; panelId?: string; controlledOpen?: boolean; onOpenChange?: (open: boolean) => void }) {
  const ui = props.dictionary.social.ui;
  const languageOptions = useMemo(() => sortedLocaleOptions(props.locales), [props.locales]);
  const [status, setStatus] = useState<SubmitState>({ status: "idle", message: "" });
  const [internalOpen, setInternalOpen] = useState(false);
  const pickerRef = useRef<HTMLElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const controlled = typeof controlledOpen === "boolean";
  const open = controlled ? Boolean(controlledOpen) : internalOpen;
  const currentLocale = props.locale.code;
  const activeLocale = props.locales.find((locale) => locale.code === currentLocale) ?? props.locale;
  useEffect(() => {
    if (languageOptions.length <= 1 || controlled || !open) return;
    const closeOnPointer = (event: PointerEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) setPickerOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") closePicker(true);
    };
    document.addEventListener("pointerdown", closeOnPointer);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnPointer);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [controlled, languageOptions.length, open]);
  if (languageOptions.length <= 1) return null;
  function setPickerOpen(nextOpen: boolean) {
    if (controlled) {
      onOpenChange?.(nextOpen);
      return;
    }
    setInternalOpen(nextOpen);
  }
  function closePicker(restoreFocus = false) {
    setPickerOpen(false);
    if (restoreFocus) window.requestAnimationFrame(() => triggerRef.current?.focus({ preventScroll: true }));
  }
  function focusLanguageOption(last = false) {
    window.requestAnimationFrame(() => {
      const items = focusableElements(panelRef.current);
      const target = last ? items[items.length - 1] : items[0];
      target?.focus({ preventScroll: true });
    });
  }
  function handleTriggerKeyDown(event: ReactKeyboardEvent<HTMLButtonElement>) {
    if (!["Enter", " ", "ArrowDown", "ArrowUp"].includes(event.key)) return;
    event.preventDefault();
    if (open && (event.key === "Enter" || event.key === " ")) {
      closePicker();
      return;
    }
    if (!open) setPickerOpen(true);
    focusLanguageOption(event.key === "ArrowUp");
  }
  function handlePickerKeyDown(event: ReactKeyboardEvent<HTMLElement>) {
    if (!open) return;
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      closePicker(true);
      return;
    }
    trapFocusTab(event, panelRef.current);
  }
  async function changeLanguage(localeCode: string) {
    const href = localeHref(props.currentUrl, props.currentQuery, localeCode);
    closePicker();
    if (!props.auth.user) {
      window.location.assign(href);
      return;
    }
    if (localeCode === currentLocale) return;
    setStatus({ status: "saving", message: ui.changingLanguage });
    try {
      await submitJson("/api/me/settings", "PATCH", { preferredLocale: localeCode });
      window.location.assign(href);
    } catch (error) {
      setStatus({ status: "error", message: localizedErrorMessage(error, ui.languageChangeFailed) });
    }
  }
  function renderLocaleAction(locale: LocaleOption) {
    const active = locale.code === currentLocale;
    const ready = locale.translationStatus !== "pending";
    const href = localeHref(props.currentUrl, props.currentQuery, locale.code);
    const className = `language-option ${active ? "active" : ""} ${ready ? "" : "pending"}`;
    const label = (
      <>
        <LocaleFlag locale={locale} />
        <span className="language-option-name">{locale.nativeName}</span>
      </>
    );
    return props.auth.user ? (
      <button
        className={className}
        type="button"
        data-locale={locale.code}
        role="menuitemradio"
        aria-checked={active}
        aria-current={active ? "true" : undefined}
        disabled={!ready || (status.status === "saving" && !active)}
        onClick={() => changeLanguage(locale.code)}
        key={locale.code}
      >
        {label}
      </button>
    ) : !ready ? (
      <span className={className} data-locale={locale.code} role="menuitemradio" aria-checked={active} aria-disabled="true" key={locale.code}>
        {label}
      </span>
    ) : (
      <a className={className} data-locale={locale.code} role="menuitemradio" aria-checked={active} aria-current={active ? "true" : undefined} href={href} onClick={() => closePicker()} key={locale.code}>
        {label}
      </a>
    );
  }
  const panelClassName = variant === "social" ? "language-picker-panel topbar-menu language-menu" : "language-picker-panel";
  return (
    <section className={`language-slider ${variant}`} aria-label={ui.languagePicker} ref={pickerRef} onKeyDown={handlePickerKeyDown}>
      <button ref={triggerRef} className="language-picker-trigger" type="button" aria-label={`${ui.chooseLanguage}: ${activeLocale.nativeName}`} aria-haspopup="menu" aria-expanded={open} aria-controls={panelId} onKeyDown={handleTriggerKeyDown} onClick={() => setPickerOpen(!open)}>
        <LocaleFlag locale={activeLocale} />
        <ChevronDown size={15} aria-hidden="true" />
      </button>
      {open && (
        <div id={panelId} className={panelClassName} role="menu" aria-label={ui.chooseLanguage} ref={panelRef}>
          <div className="language-options" aria-label={ui.availableLanguages}>{languageOptions.map(renderLocaleAction)}</div>
          {status.status === "error" && <p className="language-status">{status.message}</p>}
        </div>
      )}
    </section>
  );
}

type SignupFieldValue = string | string[];
type SignupFieldValues = Record<string, SignupFieldValue>;

const CORE_GENDER_SIGNUP_FIELD_KEYS = new Set(["gender", "gen"]);

function normalizedSignupFieldKey(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]/g, "");
}

function localizedTextValues(value: OpenSNMemberField["label"]): string[] {
  return typeof value === "string" ? [value] : Object.values(value);
}

function isCoreGenderSignupField(field: OpenSNMemberField): boolean {
  return [field.id, field.name, ...localizedTextValues(field.label)].some((value) => CORE_GENDER_SIGNUP_FIELD_KEYS.has(normalizedSignupFieldKey(value)));
}

function localizedFieldOptions(options: OpenSNFieldOption[] | undefined, localeCode: string): SelectOption[] {
  return (options ?? []).map((option) => ({
    value: option.value,
    label: localizeOpenSNText(option.label, localeCode, option.value),
    description: localizeOpenSNText(option.description, localeCode)
  }));
}

function initialSignupFieldValue(field: OpenSNMemberField): SignupFieldValue {
  if (Array.isArray(field.defaultValue)) return field.defaultValue;
  if (typeof field.defaultValue === "string") return field.defaultValue;
  if (field.kind === "multiselect") return [];
  if (field.kind === "choice" && field.options?.[0]) return field.options[0].value;
  return "";
}

function initialSignupFieldValues(fields: OpenSNMemberField[]): SignupFieldValues {
  return Object.fromEntries(fields.map((field) => [field.name, initialSignupFieldValue(field)]));
}

function signupFieldString(value: SignupFieldValue | undefined): string {
  return Array.isArray(value) ? value.join(", ") : String(value ?? "");
}

function signupFieldArray(value: SignupFieldValue | undefined): string[] {
  if (Array.isArray(value)) return value;
  return String(value ?? "").split(",").map((item) => item.trim()).filter(Boolean);
}

function signupFieldVisible(field: OpenSNMemberField, values: SignupFieldValues): boolean {
  if (!field.visibleWhen) return true;
  const currentValue = values[field.visibleWhen.field];
  const currentValues = Array.isArray(currentValue) ? currentValue : [String(currentValue ?? "")];
  const expectedValues = Array.isArray(field.visibleWhen.equals) ? field.visibleWhen.equals : [field.visibleWhen.equals];
  return expectedValues.some((expected) => currentValues.includes(expected));
}

function collectSignupMemberProfile(fields: OpenSNMemberField[], values: SignupFieldValues): Record<string, SignupFieldValue> {
  const entries = fields
    .filter((field) => signupFieldVisible(field, values))
    .map((field) => [field.name, field.kind === "multiselect" ? signupFieldArray(values[field.name]) : signupFieldString(values[field.name])] as const)
    .filter(([, value]) => Array.isArray(value) ? value.length > 0 : value.trim().length > 0);
  return Object.fromEntries(entries);
}

function ConfiguredSignupField({ field, localeCode, values, onChange }: { field: OpenSNMemberField; localeCode: string; values: SignupFieldValues; onChange: (name: string, value: SignupFieldValue) => void }) {
  if (!signupFieldVisible(field, values)) return null;
  const label = localizeOpenSNText(field.label, localeCode, field.name);
  const placeholder = localizeOpenSNText(field.placeholder, localeCode, getCommunityMessages(localeCode).selectOption);
  const value = values[field.name] ?? initialSignupFieldValue(field);
  const options = localizedFieldOptions(field.options, localeCode);
  if (field.kind === "choice") {
    return (
      <fieldset className="signup-choice-group">
        <legend>{label}</legend>
        <div className="signup-choice-grid">
          {options.map((option) => (
            <label className="signup-choice-card" key={option.value}>
              <input type="radio" name={field.name} value={option.value} checked={signupFieldString(value) === option.value} required={field.required} onChange={(event) => onChange(field.name, event.currentTarget.value)} />
              <span>
                <strong>{option.label}</strong>
                {option.description && <small>{option.description}</small>}
              </span>
            </label>
          ))}
        </div>
      </fieldset>
    );
  }
  if (field.kind === "multiselect") {
    return (
      <SignupInterestTagSelect
        label={label}
        name={field.name}
        options={options}
        selectedValues={signupFieldArray(value)}
        placeholder={placeholder}
        onChange={(nextValues) => onChange(field.name, nextValues)}
      />
    );
  }
  if (field.kind === "select") {
    return (
      <label>
        <span>{label}</span>
        <select name={field.name} value={signupFieldString(value)} autoComplete={field.autocomplete} required={field.required} onChange={(event) => onChange(field.name, event.currentTarget.value)}>
          <option value="">{placeholder}</option>
          {options.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}
        </select>
      </label>
    );
  }
  if (field.kind === "textarea") {
    return (
      <label>
        <span>{label}</span>
        <textarea name={field.name} value={signupFieldString(value)} required={field.required} minLength={field.minLength} maxLength={field.maxLength} placeholder={placeholder} onChange={(event) => onChange(field.name, event.currentTarget.value)} />
      </label>
    );
  }
  return (
    <label>
      <span>{label}</span>
      <input name={field.name} type={field.kind === "email" ? "email" : "text"} value={signupFieldString(value)} autoComplete={field.autocomplete} required={field.required} minLength={field.minLength} maxLength={field.maxLength} placeholder={placeholder} onChange={(event) => onChange(field.name, event.currentTarget.value)} />
    </label>
  );
}

function AuthForm({ view, currentQuery, localeCode, authReady, authUser, dictionary, socialDefinition, embedded = false }: { embedded?: boolean; view: ViewCatalogItem; currentQuery: PageProps["currentQuery"]; localeCode: string; authReady: boolean; authUser: PageProps["auth"]["user"]; dictionary: I18nDictionary; socialDefinition?: OpenSNSocialDefinition }) {
  const ui = dictionary.social.ui;
  const [showPassword, setShowPassword] = useState(false);
  const [submitState, setSubmitState] = useState<SubmitState>({ status: "idle", message: "" });
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [removeAvatar, setRemoveAvatar] = useState(false);
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const signupFields = useMemo(() => (socialDefinition?.member.signup.fields ?? []).filter((field) => !["gender", "sexuality", "accountKind", "partnerGender", "partnerSexuality", "partnerName", "partnerEmail", "coupleChatPolicy"].includes(field.name)), [socialDefinition]);
  const [signupFieldValues, setSignupFieldValues] = useState<SignupFieldValues>(() => initialSignupFieldValues(signupFields));
  const [twoFactorChallenge, setTwoFactorChallenge] = useState<Record<string, unknown> | null>(null);
  const avatarInputRef = useRef<HTMLInputElement | null>(null);
  const isLogin = view.name === "Auth/Login";
  const isSignup = view.name === "Auth/Signup";
  const activationToken = isSignup ? String(currentQuery.token ?? "").trim() : "";
  const isRecover = view.name === "Auth/Recover Password";
  const isSocial = view.name === "Auth/Social Consent";
  const isOnboarding = view.name === "Onboarding/First Login";
  const openIdProviders = useApi((isLogin || isSignup || isSocial) ? "/api/auth/openid/providers" : null);
  const inviteCode = String(currentQuery.invite ?? "").trim();
  const recoverToken = isRecover ? String(currentQuery.token ?? "").trim() : "";
  const isRecoverRequest = isRecover && !recoverToken;
  const isRecoverReset = isRecover && Boolean(recoverToken);
  const workSectorOptions = useMemo(() => workIndustryOptions(dictionary), [dictionary]);

  useEffect(() => {
    if (!isLogin || currentQuery.twoFactor !== "1") return;
    let active = true;
    fetch("/api/auth/two-factor/challenge", { headers: apiHeaders(), credentials: "same-origin", cache: "no-store" })
      .then(async (response) => {
        const challenge = await response.json();
        if (!response.ok) throw new Error(ui.requestFailed);
        if (active && isRecord(challenge)) setTwoFactorChallenge(challenge);
      })
      .catch((error) => { if (active) setSubmitState({ status: "error", message: localizedErrorMessage(error, ui.requestFailed) }); });
    return () => { active = false; };
  }, [isLogin, currentQuery.twoFactor, ui.requestFailed]);

  useEffect(() => {
    setSignupFieldValues(initialSignupFieldValues(signupFields));
  }, [signupFields]);

  function updateSignupFieldValue(name: string, value: SignupFieldValue) {
    setSignupFieldValues((current) => ({ ...current, [name]: value }));
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setSubmitState({ status: "saving", message: ui.working });
    try {
      if (isLogin) {
        const response = twoFactorChallenge
          ? await submitJson("/api/auth/two-factor/login", "POST", { challengeId: twoFactorChallenge.challengeId, challengeToken: twoFactorChallenge.challengeToken, code: String(form.get("code")) })
          : await submitJson("/api/auth/login", "POST", { email: String(form.get("email")), password: String(form.get("password")), locale: localeCode });
        if (isRecord(response) && response.twoFactorRequired === true) {
          setTwoFactorChallenge(response);
          setSubmitState({ status: "idle", message: ui.twoFactorRequired });
          return;
        }
        const destination = new URL(safeAuthRedirect(currentQuery.redirect), window.location.origin);
        if (isRecord(response) && isRecord(response.user) && typeof response.user.locale === "string") destination.searchParams.set("lang", response.user.locale);
        window.location.assign(`${destination.pathname}${destination.search}${destination.hash}`);
        return;
      }
      if (isSignup) {
        const baseSignupPayload = {
          email: String(form.get("email")),
          displayName: String(form.get("displayName")),
          birthDate: String(form.get("birthDate") ?? ""),
          ...locationFormPayload(form),
          password: String(form.get("password")),
          locale: localeCode,
          inviteCode: String(form.get("inviteCode") ?? "").trim() || undefined,
          termsAccepted: Boolean(form.get("termsAccepted")),
          adultConfirmed: Boolean(form.get("adultConfirmed"))
        };
        await submitJson(activationToken ? "/api/auth/activate" : "/api/auth/signup", "POST", {
          ...baseSignupPayload,
          ...(activationToken ? { token: activationToken } : {}),
          memberProfileDefinitionVersion: socialDefinition?.version,
          memberProfile: collectSignupMemberProfile(signupFields, signupFieldValues)
        });
        const onboardingHref = firstLoginHref(currentQuery.redirect);
        window.location.assign(await currentAuthSessionReady() ? onboardingHref : authRouteHrefForRedirect("/login", onboardingHref));
        return;
      }
      if (isRecover) {
        if (isRecoverReset) {
          await submitJson("/api/auth/reset-password", "POST", { token: recoverToken, password: String(form.get("password")) });
        } else {
          await submitJson("/api/auth/recover-password", "POST", { email: String(form.get("email")) });
        }
        setSubmitState({ status: "success", message: isRecoverReset ? ui.passwordResetComplete : ui.recoveryEmailQueued });
        return;
      }
      if (isSocial) {
        if (!authReady) {
          setSubmitState({ status: "permission", message: ui.logInFirstSocialConsent });
          return;
        }
        const provider = String(form.get("provider") ?? "").trim();
        if (!provider) {
          setSubmitState({ status: "error", message: ui.openIdRequestFailed });
          return;
        }
        await submitJson("/api/auth/social-consent", "POST", { provider, scopes: ["openid", "email", "profile"] });
        setSubmitState({ status: "success", message: ui.saved });
        return;
      }
      if (isOnboarding) {
        if (!authReady) {
          setSubmitState({ status: "permission", message: ui.logInAfterSignupToFinishSetup });
          return;
        }
        await submitJson("/api/onboarding/first-login", "POST", {
          headline: String(form.get("headline") ?? ""),
          birthDate: String(form.get("birthDate") ?? ""),
          bio: String(form.get("bio") ?? ""),
          ...locationFormPayload(form),
          workSector: String(form.get("workSector") ?? ""),
          languages: splitCsv(form.get("languages")),
          interests: splitCsv(form.get("interests")),
          termsAccepted: Boolean(form.get("termsAccepted")),
          adultConfirmed: Boolean(form.get("adultConfirmed")),
          removeAvatar,
          steps: ["profile", "interests", "recommendations", "privacy"]
        });
        if (avatarFile) {
          const uploadedAvatar = await uploadFiles([avatarFile], { visibility: "public" });
          if (uploadedAvatar[0]?.id) await submitJson("/api/settings/profile", "PATCH", { avatarFileId: String(uploadedAvatar[0].id) });
        }
        window.location.assign(safeAuthRedirect(currentQuery.redirect));
        return;
      }
    } catch (error) {
      const fallback = isLogin && isApiStatus(error, 401) ? invalidCredentialsMessage(dictionary) : ui.requestFailed;
      const message = localizedErrorMessage(error, fallback);
      setSubmitState({ status: "error", message: isLogin && isApiStatus(error, 401) && message === dictionary.errors.unauthorized ? fallback : message });
    }
  }

  async function startOpenId(provider: string) {
    setSubmitState({ status: "saving", message: ui.startingOpenId });
    try {
      const redirect = safeAuthRedirect(currentQuery.redirect);
      const response = await submitJson("/api/auth/openid/start", "POST", { provider, redirect });
      const authorizationUrl = isRecord(response) ? String(response.authorizationUrl ?? "") : "";
      if (!authorizationUrl) throw new Error(ui.openIdRequestFailed);
      window.location.assign(authorizationUrl);
    } catch (error) {
      setSubmitState({ status: "error", message: localizedErrorMessage(error, ui.openIdRequestFailed) });
    }
  }

  const formRef = useRef<HTMLFormElement>(null);
  const title = localizedViewTitle(view, dictionary);
  const Title = embedded ? "h2" : "h1";
  const signupDisplayNameLabel = localizeOpenSNText(socialDefinition?.member.signup.displayNameLabel, localeCode, ui.displayName);
  const signupDisplayNamePlaceholder = localizeOpenSNText(socialDefinition?.member.signup.displayNamePlaceholder, localeCode, ui.displayNamePlaceholder);
  const signupInterestOptions = useMemo<SelectOption[]>(() => [
    { value: "housing", label: ui.topicHousing },
    { value: "documents", label: ui.topicDocuments },
    { value: "jobs", label: ui.topicJobs },
    { value: "safety", label: ui.topicSafety },
    ...extraSignupInterestOptions(dictionary)
  ], [dictionary, ui]);
  function chooseAvatar(files: FileList | null) {
    const file = Array.from(files ?? []).find(isImageUploadFile) ?? null;
    setAvatarFile(file);
    setRemoveAvatar(false);
    if (file) setSubmitState({ status: "idle", message: "" });
  }
  function clearAvatar(removeExisting = false) {
    setAvatarFile(null);
    setRemoveAvatar(removeExisting);
    if (avatarInputRef.current) avatarInputRef.current.value = "";
    setSubmitState({ status: removeExisting ? "success" : "idle", message: removeExisting ? ui.avatarRemovedOnboarding : "" });
  }
  return (
    <form className="auth-card contract-auth-card" onSubmit={submit} ref={formRef}>
      <div className="auth-card-title">
        <KeyRound size={21} />
        <div>
          <Title>{title}</Title>
        </div>
      </div>
      {((isLogin && !twoFactorChallenge) || isSignup || isRecoverRequest) && (
        <label>
          <span>{ui.email}</span>
          <input name="email" type="email" autoComplete="email" required placeholder={ui.emailPlaceholder} />
        </label>
      )}
      {isSignup && (
        <>
          {inviteCode && <input type="hidden" name="inviteCode" value={inviteCode} />}
          <label>
            <span>{signupDisplayNameLabel}</span>
            <input name="displayName" required minLength={2} placeholder={signupDisplayNamePlaceholder} />
          </label>
          <AdultIdentityFields ui={ui} />
          {signupFields.map((field) => <ConfiguredSignupField key={field.id} field={field} localeCode={localeCode} values={signupFieldValues} onChange={updateSignupFieldValue} />)}
        </>
      )}
      {((isLogin && !twoFactorChallenge) || isSignup || isRecoverReset) && (
        <label>
          <span>{isRecoverReset ? ui.resetPasswordWithToken : ui.password}</span>
          <div className="input-action">
            <input name="password" type={showPassword ? "text" : "password"} required minLength={10} placeholder={isRecoverReset ? ui.newPassword : ui.enterPassword} autoComplete={isRecoverReset ? "new-password" : isLogin ? "current-password" : "new-password"} />
            <button className="icon-only" type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? ui.hidePassword : ui.showPassword}>
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </label>
      )}
      {isLogin && twoFactorChallenge && (
        <label>
          <span>{ui.verificationCode}</span>
          <input key="two-factor-code" name="code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" minLength={6} maxLength={6} required autoFocus />
        </label>
      )}
      {isSignup && (
        <SignupLegalConsent ui={ui} />
      )}
      {isSocial && (
        <label>
          <span>{ui.provider}</span>
          <select name="provider">
            {openIdProviderOptions(openIdProviders.rows).map((provider) => <option value={provider.provider} key={provider.provider}>{provider.label}</option>)}
          </select>
        </label>
      )}
      {isOnboarding && (
        <>
          <div className="stepper-progress" aria-label={ui.initialSetupSteps}>
            <span className="active">{ui.profile}</span>
            <span>{ui.interests}</span>
            <span>{ui.recommendations}</span>
            <span>{ui.privacy}</span>
          </div>
          <div className="avatar-row">
            <CircleUserRound size={50} />
            <input ref={avatarInputRef} hidden type="file" accept={imageUploadAccept} onChange={(event) => chooseAvatar(event.currentTarget.files)} />
            <BaseButton type="button" variant="outlined" onClick={() => avatarInputRef.current?.click()}><Upload size={16} /> {ui.uploadAvatar}</BaseButton>
            <BaseButton type="button" variant="transparent" onClick={() => clearAvatar(true)}>{ui.removeAvatar}</BaseButton>
          </div>
          {(avatarFile || removeAvatar) && (
            <div className="selected-file-row">
              <span>{avatarFile ? avatarFile.name : ui.avatarWillBeRemoved}</span>
              {avatarFile && <button type="button" onClick={() => clearAvatar()} aria-label={ui.clearSelectedAvatar}><X size={14} /></button>}
            </div>
          )}
          {avatarFile && <PublicMediaConsent />}
          <label><span>{ui.headline}</span><input name="headline" maxLength={160} placeholder={ui.headlinePlaceholder} /></label>
          <label><span>{ui.bio}</span><textarea name="bio" maxLength={2000} placeholder={ui.bioSupportPlaceholder} /></label>
          <AdultIdentityFields ui={ui} user={authUser} />
          {authUser?.adult_profile_complete === false && <SignupLegalConsent ui={ui} />}
          <SignupSelectField label={ui.workSector} name="workSector" options={workSectorOptions} placeholder={ui.selectIndustry} />
          <label><span>{ui.languages}</span><input name="languages" placeholder={ui.languagesPlaceholder} /></label>
          <SignupInterestTagSelect label={ui.interestsAndHashtags} name="interests" options={signupInterestOptions} selectedValues={selectedTopics} placeholder={ui.selectInterests} onChange={setSelectedTopics} />
        </>
      )}
      <div className="form-actions">
        <BaseButton type="submit" variant="primary" fluid loading={submitState.status === "saving"}>
          {twoFactorChallenge ? ui.verify : isRecoverReset ? ui.resetPassword : isRecoverRequest ? ui.sendResetLink : primaryActionLabel(view, ui)}
        </BaseButton>
        {(isSignup || (isLogin && !twoFactorChallenge)) && openIdProviderOptions(openIdProviders.rows).map((provider) => (
          <BaseButton type="button" variant="transparent" onClick={() => startOpenId(provider.provider)} key={provider.provider}>{`${ui.continueWithOpenId}: ${provider.label}`}</BaseButton>
        ))}
      </div>
      <AuthLinks view={view} ui={ui} currentQuery={currentQuery} />
      <InlineStatus state={submitState} />
    </form>
  );
}

function SignupLegalConsent({ ui }: { ui: I18nDictionary["social"]["ui"] }) {
  const legalLink = (path: string, label: ReactNode) => {
    const href = `${path}?lang=${encodeURIComponent(activeLocaleCode)}`;
    return <a href={href} onClick={(event) => handleOpenSNRouteClick(event, href)}>{label}</a>;
  };
  return (
    <div className="check-stack legal-consent-stack">
      <label><input type="checkbox" name="adultConfirmed" required /><span>{getCommunityMessages(activeLocaleCode).adultConfirmed}</span></label>
      <label>
        <input type="checkbox" name="termsAccepted" required />
        <span>{ui.termsConsentIntro} {legalLink("/terms", ui.terms)}.</span>
      </label>
    </div>
  );
}

function SystemStateView(props: PageProps) {
  const ui = props.dictionary.social.ui;
  const [state, setState] = useState<SubmitState>({ status: "idle", message: "" });
  async function submitToken(endpoint: string, payload: Record<string, unknown>) {
    setState({ status: "saving", message: ui.working });
    try {
      await submitJson(endpoint, "POST", payload);
      setState({ status: "success", message: ui.requestCompleted });
    } catch (error) {
      setState({ status: "error", message: localizedErrorMessage(error, ui.requestFailed) });
    }
  }
  return (
    <section className="system-card">
      <div className="system-icon"><AlertTriangle size={28} /></div>
      <h1>{localizedViewTitle(props.view, props.dictionary)}</h1>
      {props.view.name === "Auth/Email Confirm" && (
        <form className="inline-form" onSubmit={(event) => { event.preventDefault(); submitToken("/api/auth/email/confirm", { token: String(new FormData(event.currentTarget).get("token")) }); }}>
          <FloatingField label={ui.emailToken}>
            <input name="token" required placeholder={ui.emailToken} />
          </FloatingField>
          <BaseButton type="submit" variant="primary" loading={state.status === "saving"}>{ui.verify}</BaseButton>
        </form>
      )}
      <ActionLinks view={props.view} ui={props.dictionary.social.ui} />
      <InlineStatus state={state} />
    </section>
  );
}

function SupportView(props: PageProps) {
  const ui = props.dictionary.social.ui;
  const [state, setState] = useState<SubmitState>({ status: "idle", message: "" });
  const [files, setFiles] = useState<File[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const category = supportCategoryFor(props.view.name);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setState({ status: "saving", message: ui.createSupportTicket });
    try {
      const uploaded = props.auth.user ? await uploadFiles(files, { visibility: "private" }) : [];
      if (files.length > 0 && !props.auth.user) {
        setState({ status: "permission", message: ui.loginToAttachFiles });
        return;
      }
      const payload = {
        category,
        subject: String(form.get("subject")),
        body: String(form.get("body")),
        email: String(form.get("email") || props.auth.user?.username || ""),
        metadata: {
          contract: props.view.name,
          attachments: uploaded,
          section: String(form.get("section") ?? category)
        }
      };
      const response = await submitJson("/api/support/tickets", "POST", payload);
      setState({ status: "success", message: ui.ticketCreated, payload: isRecord(response) ? response : undefined });
    } catch (error) {
      setState({ status: "error", message: localizedErrorMessage(error, ui.supportRequestFailed) });
    }
  }
  return (
    <section className="contract-page">
      <ContractHero props={props} />
      <div className={`support-layout ${state.payload ? "" : "no-rail"}`}>
        <form className="support-form-card" onSubmit={submit} onReset={() => { setFiles([]); setState({ status: "idle", message: "" }); if (inputRef.current) inputRef.current.value = ""; }}>
          <label>
            <span>{ui.section}</span>
            <select name="section" defaultValue={category}>
              {supportSections(ui).map((section) => <option value={section.value} key={section.value}>{section.label}</option>)}
            </select>
          </label>
          <label><span>{ui.email}</span><input name="email" type="email" required placeholder={ui.emailPlaceholder} /></label>
          <label><span>{ui.subject}</span><input name="subject" required minLength={3} placeholder={localizedViewTitle(props.view, props.dictionary)} /></label>
          <label><span>{ui.description}</span><textarea name="body" required minLength={3} placeholder={ui.describeWhatHappened} /></label>
          <input ref={inputRef} hidden type="file" multiple onChange={(event) => setFiles([...event.currentTarget.files ?? []])} />
          <div className="form-actions">
            <BaseButton type="submit" variant="primary" fluid loading={state.status === "saving"}>{primaryActionLabel(props.view, ui)}</BaseButton>
            <BaseButton type="button" variant="outlined" onClick={() => { setState({ status: "permission", message: ui.chooseFiles }); inputRef.current?.click(); }}><Paperclip size={17} /> {ui.attachFiles}</BaseButton>
            <BaseButton type="reset" variant="secondary">{ui.clearForm}</BaseButton>
          </div>
          <InlineStatus state={state} />
        </form>
        {state.payload ? (
          <aside className="contract-rail">
            <div className="state-card success">
              <CheckCircle2 size={20} />
              <strong>{ui.ticketId}</strong>
              <code>{String(state.payload.id)}</code>
            </div>
          </aside>
        ) : null}
      </div>
    </section>
  );
}

function SettingsView(props: PageProps) {
  const ui = props.dictionary.social.ui;
  const copy = getCommunityMessages(props.locale.code);
  const [pendingProfileForm, setPendingProfileForm] = useState<FormData | null>(null);
  const [state, setState] = useState<SubmitState>({ status: "idle", message: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [removeAvatar, setRemoveAvatar] = useState(false);
  const [removeCover, setRemoveCover] = useState(false);
  const profileAvatarInputRef = useRef<HTMLInputElement | null>(null);
  const profileCoverInputRef = useRef<HTMLInputElement | null>(null);
  const twoFactorMethods = useApi(props.auth.user && props.view.name === "Settings/Two Factor" ? "/api/auth/two-factor" : null);
  const [twoFactorSetup, setTwoFactorSetup] = useState<Record<string, unknown> | null>(null);
  const twoFactorActive = twoFactorMethods.rows.some((row) => row.status === "active");
  const persistedSettingsRequired = settingsViewReadsUserSettings(props.view.name);
  const userSettingsResult = useApi(props.auth.user && persistedSettingsRequired ? "/api/me/settings" : null);
  const workSectorOptions = useMemo(() => workIndustryOptions(props.dictionary), [props.dictionary]);
  const userSettings = useMemo(() => currentUserSettings(props.auth.user, userSettingsResult.data), [props.auth.user, userSettingsResult.data]);
  if (!props.auth.user) {
    return <PermissionPanel title={ui.settingsRequireLoginTitle} body={ui.settingsRequireLoginBody} actionLabel={props.dictionary.public.nav.login} />;
  }
  const settingsLoading = persistedSettingsRequired && (userSettingsResult.status === "idle" || userSettingsResult.status === "loading");
  const settingsPermission = persistedSettingsRequired && userSettingsResult.status === "permission";
  const settingsError = persistedSettingsRequired && userSettingsResult.status === "error";
  const settingsReady = !persistedSettingsRequired || userSettingsResult.status === "ready";
  const separateSettings = ["Settings/Email", "Settings/Data Download", "Settings/Account"].includes(props.view.name);
  const accountProps = { ...memberViewProps(props), currentUrl: currentRouteHref(props.currentUrl, props.currentQuery), hasPassword: props.auth.user.has_password === true, reauthenticated: props.currentQuery.reauthenticated === "1" };
  function chooseAvatar(files: FileList | null) {
    const file = Array.from(files ?? []).find(isImageUploadFile) ?? null;
    setAvatarFile(file);
    setRemoveAvatar(false);
    if (file) setState({ status: "idle", message: "" });
  }
  function chooseCover(files: FileList | null) {
    const file = Array.from(files ?? []).find(isImageUploadFile) ?? null;
    setCoverFile(file);
    setRemoveCover(false);
    if (file) setState({ status: "idle", message: "" });
  }
  function clearAvatar(removeExisting = false) {
    setAvatarFile(null);
    setRemoveAvatar(removeExisting);
    if (profileAvatarInputRef.current) profileAvatarInputRef.current.value = "";
    setState({ status: removeExisting ? "success" : "idle", message: removeExisting ? ui.profilePhotoRemoved : "" });
  }
  function clearCover(removeExisting = false) {
    setCoverFile(null);
    setRemoveCover(removeExisting);
    if (profileCoverInputRef.current) profileCoverInputRef.current.value = "";
    setState({ status: removeExisting ? "success" : "idle", message: removeExisting ? ui.coverImageRemoved : "" });
  }
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    if (props.view.name === "Settings/Profile" && (avatarFile || coverFile)) {
      setPendingProfileForm(form);
      return;
    }
    void saveSettings(form);
  }
  async function saveSettings(form: FormData) {
    if (state.status === "saving") return;
    setState({ status: "saving", message: avatarFile || coverFile ? ui.uploadProfileMedia : ui.savingSettings });
    try {
      if (props.view.name === "Settings/Two Factor") {
        if (twoFactorActive) {
          await submitJson("/api/auth/two-factor", "DELETE", { code: String(form.get("code")) });
          setTwoFactorSetup(null);
          setState({ status: "success", message: ui.twoFactorDisabled });
          twoFactorMethods.reload();
        } else if (twoFactorSetup) {
          await submitJson("/api/auth/two-factor/verify", "POST", { id: twoFactorSetup.id, code: String(form.get("code")) });
          setTwoFactorSetup(null);
          setState({ status: "success", message: ui.twoFactorMethodVerified });
          twoFactorMethods.reload();
        } else {
          const setup = await submitJson("/api/auth/two-factor/setup", "POST", { method: "app" });
          if (!isRecord(setup) || !setup.secret || !setup.id) throw new Error(ui.settingsRequestFailed);
          setTwoFactorSetup(setup);
          setState({ status: "success", message: ui.twoFactorMethodCreated });
        }
        return;
      }
      const uploadedAvatar = props.view.name === "Settings/Profile" && avatarFile ? await uploadFiles([avatarFile], { visibility: "public" }) : [];
      const uploadedCover = props.view.name === "Settings/Profile" && coverFile ? await uploadFiles([coverFile], { visibility: "public" }) : [];
      await submitJson(
        settingsEndpoint(props.view.name),
        settingsMethod(props.view.name),
        settingsPayload(props.view.name, form, {
          avatarFileId: uploadedAvatar[0]?.id ? String(uploadedAvatar[0].id) : undefined,
          coverFileId: uploadedCover[0]?.id ? String(uploadedCover[0].id) : undefined,
          removeAvatar: props.view.name === "Settings/Profile" ? removeAvatar : false,
          removeCover: props.view.name === "Settings/Profile" ? removeCover : false
        }),
        maybeIdempotency(props.view.name)
      );
      if (props.view.name === "Settings/Display") {
        window.location.assign(localeHref(props.currentUrl, props.currentQuery, String(form.get("locale") ?? props.locale.code)));
        return;
      }
      setState({ status: "success", message: ui.settingsSaved });
      setPendingProfileForm(null);
      setAvatarFile(null);
      setCoverFile(null);
      setRemoveAvatar(false);
      setRemoveCover(false);
      if (profileAvatarInputRef.current) profileAvatarInputRef.current.value = "";
      if (profileCoverInputRef.current) profileCoverInputRef.current.value = "";
      if (props.view.name === "Settings/Profile" && props.auth.user?.adult_profile_complete === false) window.location.assign(props.currentQuery.redirect ? safeAuthRedirect(props.currentQuery.redirect) : props.currentUrl);
    } catch (error) {
      setState({ status: "error", message: localizedErrorMessage(error, ui.settingsRequestFailed) });
    }
  }
  const profileMedia: ProfileMediaControls = {
    avatarFile,
    coverFile,
    removeAvatar,
    removeCover,
    avatarInputRef: profileAvatarInputRef,
    coverInputRef: profileCoverInputRef,
    chooseAvatar,
    chooseCover,
    clearAvatar,
    clearCover,
    requestAvatar: () => {
      if (props.auth.user?.adult_profile_complete === false) { setState({ status: "permission", message: getCommunityMessages(props.locale.code).complete_adult_profile }); return; }
      setState({ status: "permission", message: ui.chooseImageFile });
      profileAvatarInputRef.current?.click();
    },
    requestCover: () => {
      if (props.auth.user?.adult_profile_complete === false) { setState({ status: "permission", message: getCommunityMessages(props.locale.code).complete_adult_profile }); return; }
      setState({ status: "permission", message: ui.chooseCoverImageFile });
      profileCoverInputRef.current?.click();
    }
  };
  return (
    <section className="contract-page">
      {pendingProfileForm && <DeleteConfirmDialog idBase="settings-public-media" title={ui.uploadProfileMedia} body={copy.publicMediaConfirm} confirmLabel={ui.save} busyLabel={ui.uploadProfileMedia} cancelLabel={ui.cancel} busy={state.status === "saving"} icon={Globe2} confirmVariant="primary" error={state.status === "error" ? state.message : undefined} onCancel={() => setPendingProfileForm(null)} onConfirm={() => { if (pendingProfileForm) void saveSettings(pendingProfileForm); }} />}
      <ContractHero props={props} />
      <div className="settings-shell">
        <nav className="settings-nav" aria-label={ui.settingsSections}>
          {settingsLinks.map(([name, href, labelKey]) => (
            <a className={props.view.name === name ? "active" : ""} href={href} aria-current={props.view.name === name ? "page" : undefined} onClick={(event) => handleOpenSNRouteClick(event, href)} key={name}>{ui[labelKey]}</a>
          ))}
        </nav>
        {separateSettings ? <div className="settings-form">
          {props.view.name === "Settings/Email" && <MemberEmailSettings {...accountProps} email={props.auth.user.email ?? ""} confirmationToken={props.currentQuery.token} />}
          {props.view.name === "Settings/Data Download" && <MemberExportSettings {...memberViewProps(props)} />}
          {props.view.name === "Settings/Account" && <><MemberSessionSettings {...memberViewProps(props)} /><MemberDeleteAccountSettings {...accountProps} /></>}
        </div> : <form className="settings-form" onSubmit={submit} onReset={() => { setState({ status: "idle", message: "" }); setAvatarFile(null); setCoverFile(null); setRemoveAvatar(false); setRemoveCover(false); if (profileAvatarInputRef.current) profileAvatarInputRef.current.value = ""; if (profileCoverInputRef.current) profileCoverInputRef.current.value = ""; }}>
          {settingsLoading && <LoadingState label={ui.loadingResults} />}
          {settingsPermission && <PermissionPanel title={ui.logInToContinueTitle} body={ui.logInToContinueBody} compact actionLabel={ui.logIn} />}
          {settingsError && <EmptyState icon={LifeBuoy} title={ui.unableToLoadView} body={displayErrorMessage(userSettingsResult.error, ui.retry)} showBody action={<BaseButton type="button" variant="outlined" onClick={userSettingsResult.reload}>{ui.retry}</BaseButton>} />}
          {settingsReady && (
            <>
              {props.view.name === "Settings/Two Factor" ? (
                <>
                  <p>{twoFactorActive ? ui.twoFactorMethodVerified : ui.authenticatorSetupHelp}</p>
                  {twoFactorSetup && <label><span>{ui.authenticatorSecret}</span><input value={String(twoFactorSetup.secret)} readOnly autoComplete="off" spellCheck={false} onFocus={(event) => event.currentTarget.select()} /></label>}
                  {(twoFactorSetup || twoFactorActive) && <label><span>{ui.verificationCode}</span><input name="code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" minLength={6} maxLength={6} required /></label>}
                  {twoFactorMethods.status === "error" && <p role="alert">{displayErrorMessage(twoFactorMethods.error, ui.settingsRequestFailed)}</p>}
                </>
              ) : <SettingsFields view={props.view} user={props.auth.user} settings={userSettings} showPassword={showPassword} setShowPassword={setShowPassword} locales={props.locales} profileMedia={profileMedia} ui={ui} workSectorOptions={workSectorOptions} />}
              <div className="form-actions sticky-actions">
                <BaseButton type="submit" variant={props.view.name === "Settings/Account" ? "danger" : "primary"} fluid loading={state.status === "saving"}>
                  {props.view.name === "Settings/Two Factor" ? twoFactorActive ? ui.disableTwoFactor : twoFactorSetup ? ui.verify : ui.settingsTwoFactor : primaryActionLabel(props.view, ui)}
                </BaseButton>
                <BaseButton type="reset" variant="outlined">{ui.cancel}</BaseButton>
              </div>
            </>
          )}
          {!pendingProfileForm && <InlineStatus state={state} />}
        </form>}
      </div>
    </section>
  );
}

function SettingsFields({ view, user, settings, showPassword, setShowPassword, locales, profileMedia, ui, workSectorOptions }: { view: ViewCatalogItem; user: NonNullable<PageProps["auth"]["user"]>; settings: OpenSNUserSettings; showPassword: boolean; setShowPassword: (value: boolean) => void; locales: PageProps["locales"]; profileMedia: ProfileMediaControls; ui: I18nDictionary["social"]["ui"]; workSectorOptions: SelectOption[] }) {
  if (view.name === "Settings/Privacy") {
    const privacy = settings.privacy ?? DEFAULT_USER_SETTINGS.privacy;
    const profileVisibility = privacyAudienceSetting(privacy.profileVisibility, DEFAULT_USER_SETTINGS.privacy.profileVisibility);
    const messagesFrom = privacyAudienceSetting(privacy.messagesFrom, DEFAULT_USER_SETTINGS.privacy.messagesFrom);
    const friendListVisibility = privacyAudienceSetting(privacy.friendListVisibility, DEFAULT_USER_SETTINGS.privacy.friendListVisibility);
    const friendActivityVisibility = privacyAudienceSetting(privacy.friendActivityVisibility, DEFAULT_USER_SETTINGS.privacy.friendActivityVisibility);
    return (
      <>
        <label><span>{ui.profileVisibility}</span><select name="profileVisibility" defaultValue={profileVisibility}><option value="public">{ui.public}</option><option value="friends">{ui.friends}</option><option value="private">{ui.onlyMe}</option></select></label>
        <label><span>{ui.messagesFrom}</span><select name="messagesFrom" defaultValue={messagesFrom}><option value="everyone">{ui.everyone}</option><option value="friends">{ui.friends}</option><option value="none">{ui.nobody}</option></select></label>
        <label><span>{ui.friendListVisibility}</span><select name="friendListVisibility" defaultValue={friendListVisibility}><option value="public">{ui.public}</option><option value="friends">{ui.friends}</option><option value="private">{ui.onlyMe}</option></select></label>
        <label><span>{ui.friendActivityVisibility}</span><select name="friendActivityVisibility" defaultValue={friendActivityVisibility}><option value="public">{ui.public}</option><option value="friends">{ui.friends}</option><option value="private">{ui.onlyMe}</option></select></label>
        <ToggleRow name="commentsOnPosts" label={ui.allowCommentsOnPosts} defaultChecked={booleanSetting(privacy.commentsOnPosts, DEFAULT_USER_SETTINGS.privacy.commentsOnPosts)} />
        <ToggleRow name="searchable" label={ui.searchableProfile} defaultChecked={booleanSetting(privacy.searchable, DEFAULT_USER_SETTINGS.privacy.searchable)} />
        <ToggleRow name="showOnline" label={ui.showOnlineStatus} defaultChecked={booleanSetting(privacy.showOnline, DEFAULT_USER_SETTINGS.privacy.showOnline)} />
        <label><span>{ui.blockedUserId}</span><input name="blockedUserId" placeholder={ui.optionalUserId} /></label>
      </>
    );
  }
  if (view.name === "Settings/Notifications") {
    const notifications = settings.notifications ?? DEFAULT_USER_SETTINGS.notifications;
    return (
      <>
        <ToggleRow name="followsEnabled" label={memberCopy(activeLocaleCode).inAppConnections} defaultChecked={booleanSetting(notifications.followsEnabled, DEFAULT_USER_SETTINGS.notifications.followsEnabled)} />
        <ToggleRow name="commentsEnabled" label={ui.commentNotifications} defaultChecked={booleanSetting(notifications.commentsEnabled, DEFAULT_USER_SETTINGS.notifications.commentsEnabled)} />
        <ToggleRow name="messagesEnabled" label={ui.messageNotifications} defaultChecked={booleanSetting(notifications.messagesEnabled, DEFAULT_USER_SETTINGS.notifications.messagesEnabled)} />
        <ToggleRow name="safetyAlertsEnabled" label={ui.safetyAlerts} defaultChecked={booleanSetting(notifications.safetyAlertsEnabled, DEFAULT_USER_SETTINGS.notifications.safetyAlertsEnabled)} />
      </>
    );
  }
  if (view.name === "Settings/Password" || view.name === "Settings/Add Email Password") {
    return (
      <>
        {view.name === "Settings/Add Email Password" && <label><span>{ui.email}</span><input name="email" type="email" required /></label>}
        <label><span>{ui.currentPassword}</span><PasswordInput name="currentPassword" show={showPassword} setShow={setShowPassword} placeholder={ui.currentPassword} ui={ui} /></label>
        <label><span>{ui.newPassword}</span><PasswordInput name="password" show={showPassword} setShow={setShowPassword} minLength={10} placeholder={ui.newPassword} ui={ui} /></label>
      </>
    );
  }
  if (view.name === "Settings/Display") {
    const currentTheme = themePreferenceFromSettings(settings);
    return (
      <>
        {locales.length > 1 && <label><span>{ui.language}</span><select name="locale" defaultValue={stringSetting(settings.preferredLocale, user.locale)}>{locales.map((locale) => <option value={locale.code} disabled={locale.translationStatus === "pending"} key={locale.code}>{locale.nativeName}</option>)}</select></label>}
        <label><span>{uiLabel(ui, "theme", "Theme")}</span><select name="themePreference" defaultValue={currentTheme}><option value="light">{uiLabel(ui, "themeLight", "Light")}</option><option value="dark">{uiLabel(ui, "themeDark", "Dark")}</option><option value="system">{uiLabel(ui, "themeSystem", "System")}</option></select></label>
        <ToggleRow name="autoplay" label={ui.autoplayMedia} defaultChecked={booleanSetting(settings.videoAutoplay, false)} />
      </>
    );
  }
  const selectedAvatarLabel = profileMedia.avatarFile?.name ?? (profileMedia.removeAvatar ? ui.profilePhotoRemoved : "");
  const selectedCoverLabel = profileMedia.coverFile?.name ?? (profileMedia.removeCover ? ui.coverImageRemoved : "");
  return (
    <>
      <div className="avatar-row">
        <CircleUserRound size={50} />
        <input ref={profileMedia.avatarInputRef} hidden type="file" accept={imageUploadAccept} onChange={(event) => profileMedia.chooseAvatar(event.currentTarget.files)} />
        <BaseButton type="button" variant="outlined" onClick={profileMedia.requestAvatar}><Upload size={16} /> {ui.changeProfilePhoto}</BaseButton>
        <BaseButton type="button" variant="transparent" onClick={() => profileMedia.clearAvatar(true)}>{ui.removeImage}</BaseButton>
      </div>
      {selectedAvatarLabel && (
        <div className="selected-file-row">
          <span>{selectedAvatarLabel}</span>
          {profileMedia.avatarFile && <button type="button" onClick={() => profileMedia.clearAvatar()} aria-label={ui.clearSelectedProfilePhoto}><X size={14} /></button>}
        </div>
      )}
      <div className="avatar-row">
        <Images size={50} />
        <input ref={profileMedia.coverInputRef} hidden type="file" accept={imageUploadAccept} onChange={(event) => profileMedia.chooseCover(event.currentTarget.files)} />
        <BaseButton type="button" variant="outlined" onClick={profileMedia.requestCover}><Upload size={16} /> {ui.changeCoverImage}</BaseButton>
        <BaseButton type="button" variant="transparent" onClick={() => profileMedia.clearCover(true)}>{ui.removeCover}</BaseButton>
      </div>
      {selectedCoverLabel && (
        <div className="selected-file-row">
          <span>{selectedCoverLabel}</span>
          {profileMedia.coverFile && <button type="button" onClick={() => profileMedia.clearCover()} aria-label={ui.clearSelectedCoverImage}><X size={14} /></button>}
        </div>
      )}
      {(profileMedia.avatarFile || profileMedia.coverFile) && <PublicMediaConsent />}
      <label><span>{ui.displayName}</span><input name="displayName" defaultValue={user.display_name} required /></label>
      <label><span>{ui.slug}</span><input name="username" defaultValue={user.username} pattern="[A-Za-z0-9._-]{2,80}" required /></label>
      <SignupSelectField label={ui.gender} name="gender" options={genderOptions(ui)} placeholder={ui.selectGender} defaultValue={stringSetting(user.gender, "")} />
      <label><span>{ui.bio}</span><textarea name="bio" defaultValue={user.bio} placeholder={ui.bioPlaceholder} /></label>
      <AdultIdentityFields ui={ui} user={user} />
      {user.adult_profile_complete === false && <SignupLegalConsent ui={ui} />}
      <label><span>{getCommunityMessages(activeLocaleCode).publicProfileMode}</span><select name="publicProfileMode" defaultValue={user.public_profile_mode ?? "minimal"}><option value="minimal">{getCommunityMessages(activeLocaleCode).minimal}</option><option value="complete">{getCommunityMessages(activeLocaleCode).complete}</option></select></label>
      <SignupSelectField label={ui.workSector} name="workSector" options={workSectorOptions} placeholder={ui.selectIndustry} defaultValue={user.work_sector} />
    </>
  );
}

function settingsViewReadsUserSettings(name: string): boolean {
  return ["Settings/Privacy", "Settings/Notifications", "Settings/Display"].includes(name);
}

function currentUserSettings(user: PageProps["auth"]["user"], data: unknown): OpenSNUserSettings {
  const fromEndpoint = settingsFromApi(data);
  const fromUser = user ? settingsFromUser(user) : {};
  return mergeUserSettings(DEFAULT_USER_SETTINGS, fromUser, fromEndpoint);
}

function settingsFromApi(data: unknown): OpenSNUserSettings {
  const source = settingsSource(data);
  if (!isRecord(source)) return {};
  const settings: OpenSNUserSettings = {
    privacy: privacySetting(source.privacy),
    notifications: notificationSetting(source.notifications)
  };
  const preferredLocale = stringSetting(source.preferredLocale ?? source.preferred_locale, "");
  if (preferredLocale) settings.preferredLocale = preferredLocale;
  const rawThemePreference = source.themePreference ?? source.theme_preference;
  if (rawThemePreference !== undefined) settings.themePreference = normalizeThemePreference(rawThemePreference);
  if (source.videoAutoplay !== undefined || source.video_autoplay !== undefined) settings.videoAutoplay = booleanSetting(source.videoAutoplay ?? source.video_autoplay, false);
  return settings;
}

function settingsFromUser(user: OpenSNAuthUser): OpenSNUserSettings {
  const source = user.settings ?? {};
  const serializedSource = source as Record<string, unknown>;
  return {
    privacy: source.privacy ?? user.privacySettings ?? user.privacy_settings,
    notifications: source.notifications ?? user.notificationSettings ?? user.notification_settings,
    preferredLocale: source.preferredLocale ?? (serializedSource.preferred_locale as string | undefined) ?? user.preferredLocale ?? user.preferred_locale ?? user.locale,
    themePreference: normalizeThemePreference(source.themePreference ?? serializedSource.theme_preference ?? user.themePreference ?? user.theme_preference),
    videoAutoplay: source.videoAutoplay ?? (serializedSource.video_autoplay as boolean | undefined) ?? user.videoAutoplay ?? user.video_autoplay
  };
}

function settingsSource(data: unknown): unknown {
  if (!isRecord(data)) return data;
  if (isRecord(data.data)) return data.data;
  if (Array.isArray(data.rows) && data.rows.length > 0) return data.rows[0];
  return data;
}

function mergeUserSettings(...sources: OpenSNUserSettings[]): OpenSNUserSettings {
  return sources.reduce<OpenSNUserSettings>((merged, source) => ({
    ...merged,
    ...source,
    privacy: { ...merged.privacy, ...source.privacy },
    notifications: { ...merged.notifications, ...source.notifications }
  }), {});
}

function privacySetting(value: unknown): OpenSNUserPrivacySettings | undefined {
  return isRecord(value) ? value as OpenSNUserPrivacySettings : undefined;
}

function notificationSetting(value: unknown): OpenSNUserNotificationSettings | undefined {
  return isRecord(value) ? value as OpenSNUserNotificationSettings : undefined;
}

function stringSetting(value: unknown, defaultValue: string): string {
  const normalized = String(value ?? "").trim();
  return normalized || defaultValue;
}

function privacyAudienceSetting(value: unknown, defaultValue: string): string {
  const normalized = stringSetting(value, defaultValue);
  return normalized.toLowerCase() === "network" ? "friends" : normalized;
}

function booleanSetting(value: unknown, defaultValue: boolean): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["1", "true", "yes", "on"].includes(normalized)) return true;
    if (["0", "false", "no", "off"].includes(normalized)) return false;
  }
  return defaultValue;
}

function EntityFormView(props: PageProps & { embedded?: boolean }) {
  const ui = props.dictionary.social.ui;
  const [state, setState] = useState<SubmitState>({ status: "idle", message: "" });
  const articleId = props.view.name.includes("Article") ? String(props.currentQuery.id ?? "") : "";
  const article = useApi(articleId ? `/api/articles/${encodeURIComponent(articleId)}` : null);
  const articleRow = article.rows[0] ?? {};
  const articleEditable = !articleId || String(articleRow.user_id ?? articleRow.author_id) === props.auth.user?.id;
  const requestKeyRef = useRef<{ signature: string; key: string } | null>(null);
  const actions = actionsForCurrentView(props.view, props.currentUrl);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!props.auth.user && requiresAuth(props.view)) {
      setState({ status: "permission", message: ui.loginBeforeSubmitting });
      return;
    }
    const form = new FormData(event.currentTarget);
    setState({ status: "saving", message: ui.submitting });
    try {
      const payload = entityPayload(props.view.name, form, props.locale.code);
      const endpoint = articleId ? `/api/articles/${encodeURIComponent(articleId)}` : submitEndpoint(props.view);
      const requestKey = persistentIdempotency(requestKeyRef, props.view.name, payload);
      const response = await submitJson(endpoint, articleId ? "PATCH" : "POST", payload, requestKey);
      if (isRecord(response) && typeof response.checkoutUrl === "string") {
        window.location.assign(response.checkoutUrl);
        return;
      }
      if (props.view.name.includes("Deposit") && isRecord(response) && response.id) {
        setState({ status: "saving", message: memberCopy(props.locale.code).payment_pending, payload: response });
        navigateMemberHref(`/wallet?paymentId=${encodeURIComponent(String(response.id))}`);
        return;
      }
      requestKeyRef.current = null;
      if (props.view.name.includes("Article") && isRecord(response)) {
        navigateMemberHref(payload.status === "draft" ? "/articles?mine=1&status=draft" : `/articles/${encodeURIComponent(String(response.slug ?? articleRow.slug ?? response.id ?? articleId))}`);
        return;
      }
      setState({ status: "success", message: ui.saved, payload: isRecord(response) ? response : undefined });
    } catch (error) {
      setState({ status: "error", message: localizedErrorMessage(error, ui.submitFailed) });
    }
  }
  const content = (
      <div className={`form-layout ${actions.length ? "" : "no-rail"}`}>
        {articleId && article.status === "loading" && <LoadingState label={ui.loadingResults} />}
        {articleId && article.status === "error" && <p role="alert">{displayErrorMessage(article.error, ui.requestFailed)}</p>}
        {articleId && (article.status === "permission" || article.status === "ready" && !articleEditable) && <p role="alert">{props.dictionary.errors.forbidden}</p>}
        {(!articleId || article.status === "ready" && articleEditable) && <form className="simple-form-card" onSubmit={submit} onReset={() => setState({ status: "idle", message: "" })}>
          <EntityFields key={`${articleId}:${String(articleRow.updated_at ?? "")}`} view={props.view} ui={ui} walletLabel={props.dictionary.social.modules.wallet} categoryLabels={props.dictionary.categoryLabels} initialValues={articleRow} />
          <div className="form-actions">
            <BaseButton type="submit" variant="primary" fluid loading={state.status === "saving"}>{primaryActionLabel(props.view, ui)}</BaseButton>
            <BaseButton type="reset" variant="outlined">{ui.cancel}</BaseButton>
          </div>
          <InlineStatus state={state} />
        </form>}
        {actions.length > 0 && (
          <aside className="contract-rail">
            <ActionPanel view={props.view} currentUrl={props.currentUrl} dictionary={props.dictionary} />
          </aside>
        )}
      </div>
  );
  if (props.embedded) return content;
  return (
    <section className="contract-page">
      <ContractHero props={props} />
      {content}
    </section>
  );
}

function EntityFields({ view, ui, walletLabel, categoryLabels, initialValues = {} }: { view: ViewCatalogItem; ui: I18nDictionary["social"]["ui"]; walletLabel: string; categoryLabels: I18nDictionary["categoryLabels"]; initialValues?: Record<string, unknown> }) {
  const categoryEndpoint = categoryEndpointForView(view);
  const categories = useApi(categoryEndpoint);
  if (view.name.includes("Ads")) {
    return (
      <>
        <label><span>{ui.name}</span><input name="name" required minLength={2} placeholder={ui.campaignNameExample} /></label>
        <label><span>{ui.objective}</span><select name="objective"><option value="traffic">{ui.traffic}</option><option value="reach">{ui.reach}</option><option value="messages">{ui.messages}</option></select></label>
        <label><span>{ui.budgetEur}</span><input name="budget" type="number" min="1" defaultValue="25" /></label>
        <label><span>{ui.audience}</span><input name="audience" placeholder={ui.audiencePlaceholder} /></label>
        <label><span>{ui.creative}</span><textarea name="description" placeholder={ui.creativePlaceholder} /></label>
      </>
    );
  }
  if (view.name.includes("Wallet")) {
    if (view.name.includes("Transfer")) {
      return (
        <>
          <label><span>{ui.recipientId}</span><input name="recipientId" required placeholder={ui.recipientPlaceholder} /></label>
          <label><span>{ui.amountEur}</span><input name="amount" type="number" min="1" step="1" required /></label>
        </>
      );
    }
    if (view.name.includes("Deposit") || view.name.includes("Withdraw")) {
      return (
        <>
          <label><span>{ui.amountEur}</span><input name="amount" type="number" min="1" step="1" required /></label>
          {view.name.includes("Deposit") && <input type="hidden" name="provider" value="stripe" />}
        </>
      );
    }
    return (
      <>
        <label><span>{ui.amountEur}</span><input name="amount" type="number" min="1" step="1" required /></label>
      </>
    );
  }
  if (view.name.includes("Article")) {
    return (
      <>
        <label><span>{ui.title}</span><input name="title" required minLength={2} maxLength={180} defaultValue={String(initialValues.title ?? "")} /></label>
        <label><span>{ui.slug}</span><input name="slug" defaultValue={String(initialValues.slug ?? "")} /></label>
        <label><span>{ui.excerpt}</span><input name="excerpt" defaultValue={String(initialValues.excerpt ?? "")} /></label>
        <label><span>{ui.body}</span><textarea name="body" required minLength={20} maxLength={50000} defaultValue={String(initialValues.body ?? "")} /></label>
        <label><span>{ui.visibility}</span><select name="visibility" defaultValue={String(initialValues.visibility ?? "public")}><option value="public">{ui.public}</option><option value="friends">{uiLabel(ui, "friendsOnly", ui.friends)}</option><option value="private">{ui.onlyMe}</option></select></label>
        <label><span>{ui.status}</span><select name="status" defaultValue={String(initialValues.status ?? "draft")}><option value="draft">{ui.draft}</option><option value="published">{ui.published}</option></select></label>
      </>
    );
  }
  if (view.name.includes("Events")) {
    return (
      <>
        <label><span>{ui.eventName}</span><input name="title" required minLength={2} maxLength={180} placeholder={ui.eventNamePlaceholder} /></label>
        <label><span>{ui.slug}</span><input name="slug" placeholder={ui.optionalSlug} /></label>
        <div className="form-two">
          <label><span>{ui.eventFormat}</span><select name="format" defaultValue="in_person"><option value="in_person">{ui.inPersonEvent}</option><option value="online">{ui.onlineEvent}</option></select></label>
          <label><span>{ui.privacy}</span><select name="visibility" defaultValue="public"><option value="public">{ui.public}</option><option value="friends">{uiLabel(ui, "friendsOnly", ui.friends)}</option><option value="private">{ui.private}</option></select></label>
        </div>
        <div className="form-two">
          <label><span>{ui.startDate}</span><input name="startsAt" type="datetime-local" required /></label>
          <label><span>{ui.endDate}</span><input name="endsAt" type="datetime-local" /></label>
        </div>
        <label><span>{ui.eventLocation}</span><input name="location" placeholder={ui.eventLocationPlaceholder} /></label>
        <label><span>{ui.eventLink}</span><input name="onlineUrl" type="url" placeholder={ui.eventLinkPlaceholder} /></label>
        <label><span>{ui.description}</span><textarea name="description" required minLength={10} maxLength={5000} placeholder={ui.eventDescriptionPlaceholder} /></label>
        <div className="form-two">
          <label><span>{ui.eventHosts}</span><input name="hosts" placeholder={ui.eventHostsPlaceholder} /></label>
          <label><span>{ui.eventCapacity}</span><input name="capacity" type="number" min="1" step="1" placeholder={ui.eventCapacityPlaceholder} /></label>
        </div>
        <label><span>{ui.invitePeople}</span><input name="invites" placeholder={ui.eventInvitesPlaceholder} /></label>
        {categoryEndpoint && <CategoryField categories={categories} ui={ui} categoryLabels={categoryLabels} required={false} />}
      </>
    );
  }
  return (
    <>
      <label><span>{ui.name}</span><input name="name" required minLength={2} /></label>
      <label><span>{ui.slug}</span><input name="slug" placeholder={ui.optionalSlug} /></label>
      <label><span>{ui.description}</span><textarea name="description" /></label>
      <label><span>{ui.privacy}</span><select name="privacy"><option value="public">{ui.public}</option><option value="private">{ui.private}</option></select></label>
      {categoryEndpoint && <CategoryField categories={categories} ui={ui} categoryLabels={categoryLabels} />}
    </>
  );
}

function AdsManagerView(props: PageProps) {
  const copy = props.dictionary.social.ui;
  const campaigns = useApi(props.auth.user ? "/api/ads" : null);
  const reportAdId = adIdFromPath(props.currentUrl);
  const report = useApi(props.auth.user && props.view.name === "Ads Report" && reportAdId ? `/api/ads/${encodeURIComponent(reportAdId)}/report` : null);
  const stats = useMemo(() => adCampaignStats(campaigns.rows), [campaigns.rows]);
  const createFocused = props.view.name === "Ads Create";
  if (!props.auth.user) {
    return (
      <section className="contract-page ads-page">
        <ContractHero props={props} />
        <PermissionPanel title={copy.loginRequiredTitle} body={copy.loginRequiredBody} actionLabel={props.dictionary.public.nav.login} />
      </section>
    );
  }
  const form = <AdCampaignForm props={props} copy={copy} onCreated={campaigns.reload} />;
  const list = (
    <DataPanel
      result={campaigns}
      title={copy.campaigns}
      view={props.view}
      empty={copy.emptyCampaigns}
      renderRow={(row) => <AdCampaignRow row={row} copy={copy} onChanged={campaigns.reload} />}
      ui={props.dictionary.social.ui}
      emptyActions={false}
    />
  );
  return (
    <section className="contract-page ads-page">
      <ContractHero props={props} onRefresh={() => { campaigns.reload(); report.reload(); }} rightSlot={<AdsHeroStats stats={stats} copy={copy} />} />
      <nav className="ads-tabs" aria-label={copy.adsNavigation}>
        <a className={!createFocused && props.view.name !== "Ads Report" ? "active" : ""} href="/ads" aria-current={!createFocused && props.view.name !== "Ads Report" ? "page" : undefined} onClick={(event) => handleOpenSNRouteClick(event, "/ads")}>{copy.campaigns}</a>
        <a className={createFocused ? "active" : ""} href="/ads/create" aria-current={createFocused ? "page" : undefined} onClick={(event) => handleOpenSNRouteClick(event, "/ads/create")}>{copy.createCampaign}</a>
      </nav>
      <div className={`ads-workspace ${createFocused ? "create-focus" : ""}`}>
        {createFocused ? (
          <>
            <section className="contract-main">{form}</section>
            <aside className="contract-rail">{list}</aside>
          </>
        ) : (
          <>
            <section className="contract-main">
              {list}
              {props.view.name === "Ads Report" && (
                <DataPanel
                  result={report}
                  title={copy.report}
                  view={props.view}
                  empty={copy.emptyReport}
                  renderRow={(row) => <AdReportRow row={row} copy={copy} />}
                  ui={props.dictionary.social.ui}
                  compact
                  emptyActions={false}
                />
              )}
            </section>
            <aside className="contract-rail">{form}</aside>
          </>
        )}
      </div>
    </section>
  );
}

function AdsHeroStats({ stats, copy }: { stats: AdCampaignStats; copy: AdsCopy }) {
  return (
    <div className="ads-hero-stats">
      <span><strong>{stats.active}</strong>{copy.active}</span>
      <span><strong>{formatMoney(stats.spentCents, stats.currency)}</strong>{copy.spent}</span>
      <span><strong>{formatMoney(stats.budgetCents, stats.currency)}</strong>{copy.budget}</span>
    </div>
  );
}

function AdCampaignForm({ props, copy, onCreated }: { props: PageProps; copy: AdsCopy; onCreated?: () => void }) {
  const [state, setState] = useState<SubmitState>({ status: "idle", message: "" });
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const submitter = (event.nativeEvent as SubmitEvent).submitter;
    const requestedStatus = submitter instanceof HTMLButtonElement && submitter.value === "active" ? "active" : "draft";
    const name = String(form.get("name") ?? "").trim();
    const budgetCents = Math.round(Number(form.get("budget") ?? 0) * 100);
    if (!name || budgetCents <= 0) {
      setState({ status: "error", message: copy.formInvalid });
      return;
    }
    setState({ status: "saving", message: requestedStatus === "active" ? copy.launchingCampaign : copy.savingDraft });
    try {
      await submitJson("/api/ads", "POST", {
        name,
        objective: String(form.get("objective") ?? "traffic"),
        budgetCents,
        currency: "EUR",
        status: requestedStatus,
        startsAt: optionalFormString(form, "startsAt"),
        endsAt: optionalFormString(form, "endsAt"),
        targeting: {
          audience: String(form.get("audience") ?? "").trim(),
          placement: String(form.get("placement") ?? "feed"),
          creative: String(form.get("creative") ?? "").trim()
        }
      }, maybeIdempotency("ads"));
      setState({ status: "success", message: requestedStatus === "active" ? copy.campaignLaunched : copy.campaignSaved });
      formElement.reset();
      onCreated?.();
    } catch (error) {
      setState({ status: "error", message: localizedErrorMessage(error, copy.campaignFailed) });
    }
  }
  return (
    <form className="simple-form-card ads-campaign-form" onSubmit={submit} onReset={() => setState({ status: "idle", message: "" })}>
      <header>
        <Megaphone size={22} />
        <h2>{copy.createCampaign}</h2>
      </header>
      <label><span>{copy.campaignName}</span><input name="name" required minLength={2} maxLength={180} placeholder={copy.campaignNameExample} /></label>
      <label>
        <span>{copy.objective}</span>
        <select name="objective" defaultValue="traffic">
          {adObjectiveOptions(copy).map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}
        </select>
      </label>
      <div className="form-two">
        <label><span>{copy.budgetEur}</span><input name="budget" type="number" min="1" step="1" defaultValue="25" required /></label>
        <label>
          <span>{copy.placement}</span>
          <select name="placement" defaultValue="feed">
            <option value="feed">{copy.placementFeed}</option>
            <option value="clips">{copy.placementClips}</option>
            <option value="pages">{copy.placementPages}</option>
          </select>
        </label>
      </div>
      <label><span>{copy.audience}</span><input name="audience" placeholder={copy.audiencePlaceholder} /></label>
      <label><span>{copy.creative}</span><textarea name="creative" placeholder={copy.creativePlaceholder} /></label>
      <div className="form-two">
        <label><span>{copy.startDate}</span><input name="startsAt" type="datetime-local" /></label>
        <label><span>{copy.endDate}</span><input name="endsAt" type="datetime-local" /></label>
      </div>
      <div className="form-actions">
        <BaseButton type="submit" name="intent" value="draft" variant="outlined" loading={state.status === "saving"}>{copy.saveDraft}</BaseButton>
        <BaseButton type="submit" name="intent" value="active" variant="primary" loading={state.status === "saving"}>{copy.launchCampaign}</BaseButton>
        <BaseButton type="reset" variant="transparent">{props.dictionary.social.ui.clear}</BaseButton>
      </div>
      <InlineStatus state={state} />
    </form>
  );
}

function AdCampaignRow({ row, copy, onChanged }: { row: Record<string, unknown>; copy: AdsCopy; onChanged?: () => void }) {
  const id = String(row.id ?? "");
  const status = String(row.status ?? "draft");
  const currency = String(row.currency ?? "EUR");
  const budgetCents = numberValue(row.budget_cents);
  const spentCents = numberValue(row.reported_spend_cents ?? row.spent_cents);
  const impressions = numberValue(row.impressions);
  const clicks = numberValue(row.clicks);
  const ctr = impressions > 0 ? `${((clicks / impressions) * 100).toFixed(1)}%` : "0%";
  const canToggle = Boolean(id) && !["cancelled", "completed"].includes(status);
  const nextStatus = status === "active" ? "paused" : "active";
  const reportHref = id ? `/ads/${encodeURIComponent(id)}/report` : "";
  return (
    <div className="ad-campaign-row">
      <header>
        <div>
          <strong>{String(row.name ?? copy.untitledCampaign)}</strong>
          <p>{adTargetingSummary(row, copy)}</p>
        </div>
        <span className={`ad-status ${cssClassToken(status)}`}>{adStatusLabel(status, copy)}</span>
      </header>
      <div className="ad-metrics" aria-label={copy.campaignMetrics}>
        <span><Eye size={16} /><strong>{formatCompactNumber(impressions)}</strong><small>{copy.impressions}</small></span>
        <span><MousePointerClick size={16} /><strong>{formatCompactNumber(clicks)}</strong><small>{copy.clicks}</small></span>
        <span><Users size={16} /><strong>{ctr}</strong><small>{copy.ctr}</small></span>
        <span><Wallet size={16} /><strong>{formatMoney(spentCents, currency)}</strong><small>{copy.spent}</small></span>
        <span><CreditCard size={16} /><strong>{formatMoney(budgetCents, currency)}</strong><small>{copy.budget}</small></span>
      </div>
      <footer className="inline-actions">
        {reportHref && <a className="base-button outlined sm" href={reportHref} onClick={(event) => handleOpenSNRouteClick(event, reportHref)}>{copy.report}</a>}
        {canToggle && <AdStatusButton id={id} nextStatus={nextStatus} label={nextStatus === "active" ? copy.launch : copy.pause} onChanged={onChanged} copy={copy} />}
        {id && !["cancelled", "completed"].includes(status) && <AdStatusButton id={id} nextStatus="cancelled" label={copy.cancelCampaign} variant="danger" onChanged={onChanged} copy={copy} />}
      </footer>
    </div>
  );
}

function AdStatusButton({ id, nextStatus, label, copy, variant = "secondary", onChanged }: { id: string; nextStatus: string; label: string; copy: AdsCopy; variant?: "primary" | "outlined" | "secondary" | "transparent" | "danger"; onChanged?: () => void }) {
  const [state, setState] = useState<SubmitState>({ status: "idle", message: "" });
  async function updateStatus() {
    setState({ status: "saving", message: label });
    try {
      await submitJson(`/api/ads/${encodeURIComponent(id)}`, "PATCH", { status: nextStatus });
      setState({ status: "success", message: copy.campaignUpdated });
      onChanged?.();
    } catch (error) {
      setState({ status: "error", message: localizedErrorMessage(error, copy.campaignFailed) });
    }
  }
  return <span className="action-stack"><BaseButton type="button" size="sm" variant={variant} loading={state.status === "saving"} onClick={updateStatus}>{label}</BaseButton><InlineStatus state={state} /></span>;
}

function AdReportRow({ row, copy }: { row: Record<string, unknown>; copy: AdsCopy }) {
  const impressions = numberValue(row.impressions);
  const clicks = numberValue(row.clicks);
  const ctr = impressions > 0 ? `${((clicks / impressions) * 100).toFixed(1)}%` : "0%";
  return (
    <div className="ads-report-row">
      <strong>{String(row.report_date ?? row.created_at ?? copy.report)}</strong>
      <span>{formatCompactNumber(impressions)} {copy.impressions}</span>
      <span>{formatCompactNumber(clicks)} {copy.clicks}</span>
      <span>{ctr} {copy.ctr}</span>
      <span>{formatMoney(numberValue(row.spend_cents), "EUR")} {copy.spent}</span>
    </div>
  );
}

function InviteNetworkView(props: PageProps) {
  const ui = props.dictionary.social.ui;
  const [queryVisible, setQueryVisible] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const inviteButtonRef = useRef<HTMLButtonElement | null>(null);
  const inviteDialogRef = useRef<HTMLElement | null>(null);
  const endpoint = `/api/invites${filter.trim() ? `?q=${encodeURIComponent(filter.trim())}` : ""}`;
  const result = useApi(endpoint, initialApiData(props, endpoint));
  useEffect(() => {
    if (!inviteModalOpen) return undefined;
    inviteDialogRef.current?.focus();
  }, [inviteModalOpen]);
  function openInviteModal() {
    setInviteModalOpen(true);
  }
  function closeInviteModal() {
    setInviteModalOpen(false);
    window.requestAnimationFrame(() => inviteButtonRef.current?.focus({ preventScroll: true }));
  }
  function handleInviteDialogKeyDown(event: ReactKeyboardEvent<HTMLElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      closeInviteModal();
      return;
    }
    trapFocusTab(event, inviteDialogRef.current);
  }
  return (
    <section className="contract-page invite-network-page">
      <ContractHero
        props={props}
        onRefresh={result.reload}
        onSearch={() => setQueryVisible(true)}
        rightSlot={
          <button ref={inviteButtonRef} className="base-button primary sm" type="button" onClick={openInviteModal}>
            <Send size={16} /> {ui.sendInvite}
          </button>
        }
      />
      {queryVisible && (
        <form className="filter-strip" action="/invites" method="get">
          <Search size={18} />
          <FloatingField label={ui.search} className="filter-field">
            <input name="q" value={filter} onChange={(event) => setFilter(event.currentTarget.value)} placeholder={ui.filterInvitedPeople} aria-label={ui.filterInvitedPeople} />
          </FloatingField>
          <BaseButton type="button" variant="transparent" size="sm" onClick={() => setFilter("")}>
            {ui.clear}
          </BaseButton>
        </form>
      )}
      <section className="contract-main">
        <DataPanel
          result={result}
          title={localizedViewTitle(props.view, props.dictionary)}
          view={props.view}
          empty={ui.invitedPeopleEmpty}
          renderRow={(row) => <InviteRow row={row} ui={ui} />}
          ui={ui}
          emptyActions={false}
        />
      </section>
      {inviteModalOpen && (
        <div className="post-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closeInviteModal(); }}>
          <section className="post-likers-modal invite-modal" role="dialog" aria-modal="true" aria-label={ui.sendInvite} tabIndex={-1} ref={inviteDialogRef} onKeyDown={handleInviteDialogKeyDown}>
            <header>
              <div>
                <strong>{ui.sendInvite}</strong>
              </div>
              <button className="icon-only" type="button" aria-label={ui.cancel} onClick={closeInviteModal}><X size={18} /></button>
            </header>
            <InviteForm props={props} onCreated={result.reload} onCancel={closeInviteModal} />
          </section>
        </div>
      )}
    </section>
  );
}

function InviteFormView(props: PageProps) {
  const ui = props.dictionary.social.ui;
  const result = useApi("/api/invites");
  return (
    <section className="contract-page">
      <ContractHero props={props} />
      <div className="form-layout">
        <InviteForm props={props} onCreated={result.reload} />
        <aside className="contract-rail">
          <DataPanel
            result={result}
            title={ui.invitedPeople}
            view={props.view}
            empty={ui.invitedPeopleShortEmpty}
            renderRow={(row) => <InviteRow row={row} ui={ui} />}
            ui={ui}
            compact
            emptyActions={false}
            hideTitle
          />
        </aside>
      </div>
    </section>
  );
}

function InviteForm({ props, onCreated, onCancel }: { props: PageProps; onCreated?: () => void; onCancel?: () => void }) {
  const ui = props.dictionary.social.ui;
  const [state, setState] = useState<SubmitState>({ status: "idle", message: "" });
  function cancel() {
    setState({ status: "idle", message: "" });
    onCancel?.();
  }
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const email = String(form.get("email") ?? "").trim();
    const phone = String(form.get("phone") ?? "").trim();
    if (!email && !phone) {
      setState({ status: "error", message: ui.addEmailOrPhone });
      return;
    }
    setState({ status: "saving", message: ui.sendingInvite });
    try {
      const response = await submitJson("/api/invites", "POST", { email: email || undefined, phone: phone || undefined });
      const inviteLink = inviteLinkFromResponse(response);
      setState({ status: "success", message: inviteLink ? ui.inviteQueuedLinkReady : ui.inviteQueued, payload: isRecord(response) ? response : undefined });
      formElement.reset();
      onCreated?.();
    } catch (error) {
      setState({ status: "error", message: localizedErrorMessage(error, ui.inviteRequestFailed) });
    }
  }
  async function copyRegistrationLink() {
    setState({ status: "saving", message: ui.creatingRegistrationLink });
    try {
      const response = await submitJson("/api/invites/link", "POST", {});
      const inviteLink = inviteLinkFromResponse(response);
      if (!inviteLink) throw new Error(ui.inviteLinkMissing);
      await copyTextToClipboard(inviteLink, ui.clipboardUnavailable);
      setState({ status: "success", message: ui.registrationLinkCopied, payload: isRecord(response) ? response : undefined });
      onCreated?.();
    } catch (error) {
      setState({ status: "error", message: localizedErrorMessage(error, ui.inviteLinkRequestFailed) });
    }
  }
  const readyLink = inviteLinkFromResponse(state.payload);
  return (
    <form className="simple-form-card" onSubmit={submit} onReset={() => setState({ status: "idle", message: "" })}>
      <label>
        <span>{ui.email}</span>
        <input name="email" type="email" placeholder={ui.emailPlaceholder} />
      </label>
      <label>
        <span>{ui.phone}</span>
        <input name="phone" type="tel" placeholder={ui.phone} />
      </label>
      {readyLink && (
        <label>
          <span>{ui.registrationLink}</span>
          <input readOnly value={readyLink} onFocus={(event) => event.currentTarget.select()} />
        </label>
      )}
      <div className="form-actions">
        <BaseButton type="submit" variant="primary" fluid loading={state.status === "saving"}>
          <Send size={16} /> {ui.sendInvite}
        </BaseButton>
        <BaseButton type="button" variant="outlined" fluid loading={state.status === "saving"} onClick={copyRegistrationLink}>
          <Link2 size={16} /> {ui.copyRegistrationLink}
        </BaseButton>
        <BaseButton type={onCancel ? "button" : "reset"} variant="transparent" onClick={onCancel ? cancel : undefined}>{ui.cancel}</BaseButton>
      </div>
      <InlineStatus state={state} />
    </form>
  );
}

function InviteRow({ row, ui }: { row: Record<string, unknown>; ui: I18nDictionary["social"]["ui"] }) {
  const recipient = String(row.email ?? row.phone ?? row.recipient ?? ui.registrationLink);
  const status = String(row.status ?? "pending");
  const acceptedName = String(row.accepted_display_name ?? row.accepted_username ?? "").trim();
  const inviteLink = String(row.invite_link ?? row.inviteLink ?? "");
  const acceptedUsername = String(row.accepted_username ?? row.acceptedUsername ?? "").trim();
  const acceptedProfileIdentifier = firstProfileIdentifier(row.accepted_profile_slug, row.acceptedProfileSlug, row.accepted_public_id, row.acceptedPublicId, acceptedUsername, row.accepted_user_id, row.acceptedUserId);
  const acceptedProfileHref = profileHrefFromIdentifier(acceptedProfileIdentifier);
  const [state, setState] = useState<SubmitState>({ status: "idle", message: "" });
  async function copyInvite() {
    if (!inviteLink) return;
    setState({ status: "saving", message: ui.copyingLink });
    try {
      await copyTextToClipboard(inviteLink, ui.clipboardUnavailable);
      setState({ status: "success", message: ui.linkCopied });
    } catch (error) {
      setState({ status: "permission", message: `${ui.linkReady}: ${inviteLink}` });
    }
  }
  return (
    <div className="directory-card invite-card">
      <header>
        <CircleUserRound size={42} />
        <div>
          <strong>{recipient}</strong>
          <p>
            {acceptedName ? (
              <>{ui.acceptedBy} <ProfileNameLink row={{ profile_slug: acceptedProfileIdentifier, username: acceptedUsername, display_name: acceptedName }}>{acceptedName}</ProfileNameLink></>
            ) : `${localizedMachineText(status, ui)} ${ui.invites.toLowerCase()}`}
          </p>
        </div>
      </header>
      <div className="inline-actions">
        {inviteLink && <BaseButton type="button" variant="outlined" size="sm" loading={state.status === "saving"} onClick={copyInvite}><Link2 size={15} /> {ui.copyLink}</BaseButton>}
        {acceptedProfileHref && <a className="base-button primary sm" href={acceptedProfileHref} onClick={(event) => handleOpenSNRouteClick(event, acceptedProfileHref)}>{ui.openProfile}</a>}
      </div>
      <InlineStatus state={state} />
    </div>
  );
}

function CategoryField({ categories, ui, categoryLabels, required = true }: { categories: ApiResult; ui: I18nDictionary["social"]["ui"]; categoryLabels: I18nDictionary["categoryLabels"]; required?: boolean }) {
  const rows = categories.rows;
  const unavailable = categories.status === "error" || categories.status === "permission" || (categories.status === "ready" && rows.length === 0);
  if (!required && unavailable) return null;
  const disabled = categories.status === "loading" || categories.status === "idle" || categories.status === "error" || rows.length === 0;
  return (
    <label>
      <span>{ui.category}</span>
      <select name="categoryId" required={required} disabled={disabled} defaultValue="">
        <option value="" disabled={required}>{ui.chooseCategory}</option>
        {rows.map((row) => (
          <option value={String(row.id)} key={String(row.id)}>{localizedCategoryLabel(row, categoryLabels)}</option>
        ))}
      </select>
    </label>
  );
}

function isCategoryDirectoryView(view: ViewCatalogItem): boolean {
  return view.layoutTemplate === "grid-directory" && (view.module === "groups" || view.module === "events" || view.module === "pages");
}

function DirectoryCategoryFilter({ props }: { props: PageProps }) {
  const ui = props.dictionary.social.ui;
  const endpoint = categoryEndpointForView(props.view);
  const categories = useApi(endpoint);
  const currentQuery = currentQueryFromRoute(props.currentUrl, props.currentQuery);
  const currentCategory = String(currentQuery.category ?? "").trim().toLowerCase();
  if (!endpoint) return null;
  if ((categories.status === "error" || categories.status === "permission") && categories.rows.length === 0) return null;
  if (categories.status === "ready" && categories.rows.length === 0) return null;
  const allCategoriesHref = categoryFilterHref(props.currentUrl, currentQuery);
  return (
    <nav className="directory-category-strip" aria-label={`${ui.category}: ${localizedViewTitle(props.view, props.dictionary)}`}>
      <strong>{ui.category}</strong>
      <a
        className={currentCategory ? "" : "active"}
        href={allCategoriesHref}
        aria-current={currentCategory ? undefined : "page"}
        onClick={(event) => handleOpenSNRouteClick(event, allCategoriesHref)}
      >
        {ui.allCategories}
      </a>
      {categories.rows.map((row) => {
        const id = String(row.id ?? "").trim();
        const slug = String(row.slug ?? id).trim();
        const normalizedSlug = slug.toLowerCase();
        const normalizedId = id.toLowerCase();
        const active = Boolean(currentCategory && (currentCategory === normalizedSlug || currentCategory === normalizedId));
        const href = categoryFilterHref(props.currentUrl, currentQuery, slug || id);
        return (
          <a
            className={active ? "active" : ""}
            href={href}
            aria-current={active ? "page" : undefined}
            onClick={(event) => handleOpenSNRouteClick(event, href)}
            key={id || slug}
          >
            {localizedCategoryLabel(row, props.dictionary.categoryLabels)}
          </a>
        );
      })}
      {categories.status === "loading" && categories.rows.length === 0 && <span>{ui.loadingCategories}</span>}
    </nav>
  );
}

function categoryFilterHref(currentUrl: string, currentQuery: Record<string, string>, category?: string): string {
  const params = new URLSearchParams();
  Object.entries(currentQuery).forEach(([key, value]) => {
    if (key !== "category" && key !== "cursor" && value) params.set(key, value);
  });
  if (category) params.set("category", category);
  const query = params.toString();
  return `${normalizeAppPath(currentUrl) || "/"}${query ? `?${query}` : ""}`;
}

const categoryLabelKeysBySlug: Record<string, keyof I18nDictionary["categoryLabels"]> = {
  "work-jobs": "groupWorkJobs",
  "arrival-europe": "groupArrivalEurope",
  "hiring-alerts": "groupHiringAlerts",
  "contracts-payslips": "groupContractsPayslips",
  housing: "groupHousing",
  documents: "groupDocuments",
  "language-learning": "groupLanguage",
  transport: "groupTransport",
  "community-support": "groupCommunity",
  "rights-safety": "groupSafety",
  "individual-worker": "pageIndividualWorker",
  employer: "pageEmployer",
  "recruitment-agency": "pageRecruitmentAgency",
  company: "pageCompany",
  "public-service": "pagePublicService",
  ngo: "pageNgo",
  training: "pageTraining",
  "housing-service": "pageHousingService",
  "work-service": "pageWorkService",
  "community-page": "pageCommunity",
  abuse: "reportAbuse",
  scam: "reportScam",
  "illegal-content": "reportIllegal",
  "safety-risk": "reportSafety",
  spam: "reportSpam",
  sensitive: "warningSensitive",
  violence: "warningViolence",
  adult: "warningAdult",
  "medical-legal": "warningMedicalLegal"
};

const categoryLabelKeysByEnglishLabel: Record<string, keyof I18nDictionary["categoryLabels"]> = {
  "work and jobs": "groupWorkJobs",
  "arrival in europe": "groupArrivalEurope",
  "hiring alerts": "groupHiringAlerts",
  "contracts and payslips": "groupContractsPayslips",
  "housing and rooms": "groupHousing",
  "documents and bureaucracy": "groupDocuments",
  "language learning": "groupLanguage",
  "transport and commuting": "groupTransport",
  "community support": "groupCommunity",
  "rights and safety": "groupSafety",
  "individual worker page": "pageIndividualWorker",
  employer: "pageEmployer",
  "employer / hiring company": "pageEmployer",
  "recruitment agency": "pageRecruitmentAgency",
  "work agency": "pageRecruitmentAgency",
  company: "pageCompany",
  "public service": "pagePublicService",
  "ngo and support organization": "pageNgo",
  "training and language school": "pageTraining",
  "housing service": "pageHousingService",
  "work support service": "pageWorkService",
  "community page": "pageCommunity",
  "abuse or harassment": "reportAbuse",
  "scam or fraud": "reportScam",
  "illegal content": "reportIllegal",
  "safety risk": "reportSafety",
  "spam or misleading content": "reportSpam",
  "sensitive content": "warningSensitive",
  violence: "warningViolence",
  "adult content": "warningAdult",
  "medical or legal advice": "warningMedicalLegal"
};

function localizedCategoryLabel(row: Record<string, unknown>, categoryLabels: I18nDictionary["categoryLabels"]): string {
  const slug = String(row.slug ?? "").trim().toLowerCase();
  const label = String(row.label ?? "").trim();
  const normalizedLabel = label.toLowerCase();
  const key = categoryLabelKeysBySlug[slug] ?? categoryLabelKeysByEnglishLabel[normalizedLabel];
  return key ? categoryLabels[key] : label || slug;
}

function AdultIdentityFields({ ui, user }: { ui: I18nDictionary["social"]["ui"]; user?: PageProps["auth"]["user"] }) {
  const copy = getCommunityMessages(activeLocaleCode);
  const maximum = new Date();
  maximum.setUTCFullYear(maximum.getUTCFullYear() - 18);
  return <><div className="form-two adult-identity-fields"><label><span>{copy.birthDate}</span><input name="birthDate" type="date" required max={maximum.toISOString().slice(0, 10)} defaultValue={user?.birth_date ?? ""} autoComplete="bday" /></label></div><div className="form-two"><GlobalLocationFields ui={ui} user={user} /></div></>;
}

function PublicMediaConsent() {
  return <label className="community-consent"><input type="checkbox" required /><span>{getCommunityMessages(activeLocaleCode).publicMediaConfirm}</span></label>;
}

function AdultProfilePrompt({ props, returnTo }: { props: PageProps; returnTo?: string }) {
  if (props.auth.user?.adult_profile_complete !== false) return null;
  const href = `/settings/profile?redirect=${encodeURIComponent(returnTo ?? props.currentUrl)}`;
  return <div className="community-notice"><ShieldCheck size={19} /><p>{getCommunityMessages(props.locale.code).complete_adult_profile}</p><a className="base-button outlined" href={href} onClick={(event) => handleOpenSNRouteClick(event, href)}>{props.dictionary.social.ui.profile}</a></div>;
}

function useCommunityAction(onDone?: () => void) {
  const [state, setState] = useState<SubmitState>({ status: "idle", message: "" });
  async function run(action: () => Promise<unknown>, refresh = true) {
    setState({ status: "saving", message: "" });
    try {
      const response = await action();
      setState({ status: "idle", message: "" });
      if (refresh) onDone?.();
      if (refresh) window.dispatchEvent(new Event(notificationsChangedEvent));
      return response;
    } catch (error) {
      setState({ status: "error", message: localizedErrorMessage(error, activeDictionary?.social.ui.requestFailed ?? "") });
      return undefined;
    }
  }
  return { state, run, busy: state.status === "saving" };
}

function apiObject(result: ApiResult): Record<string, unknown> {
  const payload = result.data;
  return isRecord(payload) && isRecord(payload.data) ? payload.data : result.rows[0] ?? {};
}

function couplePeople(row: Record<string, unknown>): Array<Record<string, unknown>> {
  return [row.partner_one, row.partner_two].filter(isRecord);
}

function CommunityDisclosure({ enabled, title, summary, icon: Icon, children }: { enabled: boolean; title: string; summary: string; icon: LucideIcon; children: ReactNode }) {
  if (!enabled) return <>{children}</>;
  return <details className="community-disclosure" name="dating-profile-sections"><summary className="community-disclosure-summary"><Icon size={19} /><span className="community-disclosure-title">{title}</span><span className="community-disclosure-meta">{summary}</span><ChevronDown size={17} className="community-disclosure-chevron" /></summary><div className="community-disclosure-body">{children}</div></details>;
}

function communityDisclosureSummary(result: ApiResult, ui: I18nDictionary["social"]["ui"], summary: string): string {
  if (result.status === "loading") return ui.loadingResults;
  if (result.status === "error") return ui.unableToLoadView;
  if (result.status === "permission") return ui.logInToContinueTitle;
  return summary;
}

function CoupleManagementPanel({ props, collapsible = false }: { props: PageProps; collapsible?: boolean }) {
  const ui = props.dictionary.social.ui;
  const copy = getCommunityMessages(props.locale.code);
  const result = useApi(props.auth.user?.adult_profile_complete !== false ? "/api/couples/mine" : null);
  const [unlinkId, setUnlinkId] = useState<string | null>(null);
  const [invitation, setInvitation] = useState<Record<string, unknown> | null>(null);
  const invitationUrl = invitation?.url ? new URL(String(invitation.url), typeof window === "undefined" ? "https://throuple.social" : window.location.origin).href : "";
  const action = useCommunityAction(result.reload);
  async function save(event: FormEvent<HTMLFormElement>, row?: Record<string, unknown>, renew = false) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const response = await action.run(() => submitJson(row ? `/api/couples/${encodeURIComponent(String(row.id))}${renew ? "/invitations" : ""}` : "/api/couples", row && !renew ? "PATCH" : "POST", {
      displayName: String(form.get("displayName") ?? row?.display_name ?? ""),
      partnerEmail: String(form.get("partnerEmail") ?? ""),
      chatPolicy: String(form.get("chatPolicy") ?? row?.chat_policy ?? "both"),
      bio: String(form.get("bio") ?? row?.bio ?? ""),
      intentions: renew ? row?.looking_for : form.getAll("intentions").map(String),
      sendEmail: true
    }));
    if (isRecord(response)) setInvitation(isRecord(response.invitation) ? response.invitation : renew ? response : null);
  }
  function chatSelect(value = "both") {
    return <label><span>{copy.chatPolicy}</span><select name="chatPolicy" defaultValue={value}><option value="common">{copy.common}</option><option value="individual">{copy.individualChats}</option><option value="both">{copy.both}</option></select></label>;
  }
  const currentCouple = result.rows.find((couple) => couple.status === "active") ?? result.rows[0];
  const summary = communityDisclosureSummary(result, ui, currentCouple ? `${String(currentCouple.display_name)} · ${currentCouple.partner_two ? ui.active : copy.pendingPartner}` : copy.noLinkedCouple);
  return <CommunityDisclosure enabled={collapsible} title={copy.coupleProfile} summary={summary} icon={Users}><section className="community-panel"><h2><Users size={19} />{copy.coupleProfile}</h2><AdultProfilePrompt props={props} />{(result.status !== "ready" || result.rows.length > 0) && <DataPanel result={result} title={copy.coupleProfile} view={props.view} hideTitle compact emptyActions={false} empty={copy.pendingPartner} ui={ui} renderRow={(row) => <div className="community-stack" key={String(row.id)}><div className="community-members">{couplePeople(row).map((person) => <a href={`/profile/${encodeURIComponent(String(person.username ?? person.user_id))}`} onClick={(event) => handleOpenSNRouteClick(event, `/profile/${encodeURIComponent(String(person.username ?? person.user_id))}`)} key={String(person.user_id)}>{String(person.display_name ?? person.username)}</a>)}{!row.partner_two && <span>{copy.pendingPartner}</span>}</div><form className="community-form" onSubmit={(event) => save(event, row)}><label><span>{copy.coupleName}</span><input name="displayName" required minLength={2} maxLength={120} defaultValue={String(row.display_name ?? "")} /></label>{chatSelect(String(row.chat_policy ?? "both"))}<label className="community-wide"><span>{ui.bio}</span><textarea name="bio" maxLength={2000} defaultValue={String(row.bio ?? "")} /></label><IntentionFields values={row.looking_for} /><BaseButton type="submit" variant="primary" loading={action.busy}>{ui.save}</BaseButton></form>{!row.partner_two && <form className="community-form" onSubmit={(event) => save(event, row, true)}><label><span>{copy.partnerEmail}</span><input name="partnerEmail" type="email" required /></label><BaseButton type="submit" variant="outlined" loading={action.busy}>{copy.renewInvitation}</BaseButton></form>}<BaseButton type="button" variant="transparent" disabled={action.busy} onClick={() => setUnlinkId(String(row.id))}>{copy.unlinkCouple}</BaseButton></div>} />}{result.status === "ready" && result.rows.length === 0 && props.auth.user?.adult_profile_complete !== false && <form className="community-form" onSubmit={(event) => save(event)}><label><span>{copy.coupleName}</span><input name="displayName" required minLength={2} maxLength={120} /></label><label><span>{copy.partnerEmail}</span><input name="partnerEmail" type="email" required /></label>{chatSelect()}<label className="community-wide"><span>{ui.bio}</span><textarea name="bio" maxLength={2000} /></label><IntentionFields /><BaseButton type="submit" variant="primary" loading={action.busy}>{copy.createCouple}</BaseButton></form>}{invitation?.url ? <div className="community-notice community-stack"><label><span>{copy.copyInvitation}</span><input value={invitationUrl} readOnly onFocus={(event) => event.currentTarget.select()} /></label><BaseButton type="button" variant="outlined" onClick={() => action.run(() => copyTextToClipboard(invitationUrl, ui.requestFailed), false)}><Link2 size={16} />{copy.copyInvitation}</BaseButton><small>{copy.invitationExpires}: {postAbsoluteTimeLabel(String(invitation.expires_at ?? ""), props.locale.code, ui)}</small><p role="status">{invitation.email_status === "sent" ? copy.invitationSent : invitation.email_status === "failed" ? copy.invitationFailed : copy.invitationUnavailable}</p></div> : null}{!unlinkId && <InlineStatus state={action.state} />}{unlinkId && <DeleteConfirmDialog idBase="couple-unlink" title={copy.unlinkCouple} body={copy.unlinkConfirm} confirmLabel={copy.unlinkCouple} busyLabel={copy.unlinkCouple} cancelLabel={ui.cancel} busy={action.busy} icon={Link2} error={action.state.status === "error" ? action.state.message : undefined} onCancel={() => setUnlinkId(null)} onConfirm={async () => {
    const response = await action.run(() => submitJson(`/api/couples/${encodeURIComponent(unlinkId)}`, "DELETE", {}));
    if (response !== undefined) setUnlinkId(null);
  }} />}</section></CommunityDisclosure>;
}

function CoupleInvitationView({ props }: { props: PageProps }) {
  const copy = getCommunityMessages(props.locale.code);
  const ui = props.dictionary.social.ui;
  const token = props.currentQuery.token ?? "";
  const returnTo = `${props.currentUrl}?${new URLSearchParams({ token }).toString()}`;
  const result = useApi(`/api/couple-invitations/${encodeURIComponent(token)}`);
  const row = apiObject(result);
  const action = useCommunityAction(result.reload);
  const [accepted, setAccepted] = useState(false);
  const pending = row.status === "pending";
  async function accept() {
    const response = await action.run(() => submitJson(`/api/couple-invitations/${encodeURIComponent(token)}/accept`, "POST", {}));
    if (response) setAccepted(true);
  }
  return <section className="community-panel couple-invitation-page"><h1>{copy.couple_invitation}</h1>{result.status === "loading" && <LoadingState label={ui.loadingResults} />}{result.error && <p role="alert">{result.error}</p>}{result.status === "ready" && <><h2>{String(row.couple_name ?? "")}</h2><p>{String(row.inviter_name ?? "")}</p><p>{copy.invitationConsent}</p><p>{copy.invitationExpires}: {postAbsoluteTimeLabel(String(row.expires_at ?? ""), props.locale.code, ui)}</p>{accepted || row.status === "accepted" ? <p role="status">{copy.couple_joined}</p> : !pending ? <p>{copy[`state_${String(row.status)}`] ?? ui.unavailable}</p> : !props.auth.user ? <div className="community-actions"><a className="base-button primary" href={authRouteHrefForRedirect("/login", returnTo)}>{ui.logIn}</a><a className="base-button outlined" href={authRouteHrefForRedirect("/signup", returnTo)}>{ui.createAccount}</a></div> : <><AdultProfilePrompt props={props} returnTo={returnTo} /><BaseButton type="button" variant="primary" disabled={props.auth.user.adult_profile_complete === false} loading={action.busy} onClick={accept}>{copy.acceptInvitation}</BaseButton></>}</>}<InlineStatus state={action.state} /></section>;
}

function RelationshipLinksPanel({ props, userId, own, collapsible = false }: { props: PageProps; userId: string; own: boolean; collapsible?: boolean }) {
  const ui = props.dictionary.social.ui;
  const copy = getCommunityMessages(props.locale.code);
  const links = useApi(userId ? `/api/relationship-links?userId=${encodeURIComponent(userId)}` : null);
  const [search, setSearch] = useState("");
  const people = useApi(own && search.trim().length >= 2 ? `/api/users?search=${encodeURIComponent(search.trim())}` : null);
  const [selected, setSelected] = useState<Array<Record<string, unknown>>>([]);
  const action = useCommunityAction(links.reload);
  const matchingPeople = people.rows.filter((person) => String(person.id ?? person.user_id) !== userId && `${String(person.display_name ?? "")} ${String(person.username ?? "")}`.toLocaleLowerCase().includes(search.toLocaleLowerCase()));
  async function invite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (selected.length !== 2) return;
    const response = await action.run(() => submitJson("/api/relationship-links", "POST", { memberIds: selected.map((person) => String(person.id ?? person.user_id)) }));
    if (response) { setSelected([]); setSearch(""); }
  }
  if (!own && links.status === "ready" && !links.rows.length) return null;
  const activeLinks = links.rows.filter((link) => link.status === "active").length;
  const summary = communityDisclosureSummary(links, ui, links.rows.length ? `${ui.active}: ${activeLinks} · ${copy.pending}: ${links.rows.length - activeLinks}` : copy.noRelationshipLinks);
  return <CommunityDisclosure enabled={collapsible} title={copy.relationshipLinks} summary={summary} icon={Link2}><section className="community-panel"><h2><Link2 size={19} />{copy.relationshipLinks}</h2><DataPanel result={links} title={copy.relationshipLinks} view={props.view} hideTitle compact emptyActions={false} empty={copy.relationshipConsent} ui={ui} renderRow={(row) => { const members = Array.isArray(row.members) ? row.members.filter(isRecord) : []; const me = members.find((member) => String(member.user_id) === props.auth.user?.id); return <div className="community-stack"><div className="community-members">{members.map((member) => <a href={`/profile/${encodeURIComponent(String(member.username ?? member.user_id))}`} key={String(member.user_id)} onClick={(event) => handleOpenSNRouteClick(event, `/profile/${encodeURIComponent(String(member.username ?? member.user_id))}`)}>{String(member.display_name ?? member.username)}</a>)}</div>{row.status !== "active" && <span>{copy.pending}</span>}{own && <div className="community-actions">{me?.accepted === false && <BaseButton type="button" variant="primary" disabled={action.busy} onClick={() => action.run(() => submitJson(`/api/relationship-links/${encodeURIComponent(String(row.id))}/accept`, "POST", {}))}>{copy.accept}</BaseButton>}<BaseButton type="button" variant="transparent" disabled={action.busy} onClick={() => action.run(() => submitJson(`/api/relationship-links/${encodeURIComponent(String(row.id))}`, "DELETE", {}))}>{copy.endRelationship}</BaseButton></div>}</div>; }} />{own && props.auth.user?.adult_profile_complete !== false && <form className="community-stack" onSubmit={invite}><p>{copy.relationshipConsent}</p><label><span>{copy.choosePeople}</span><input type="search" value={search} onChange={(event) => setSearch(event.currentTarget.value)} placeholder={ui.search} autoComplete="off" /></label><div className="community-people-search">{matchingPeople.map((person) => { const id = String(person.id ?? person.user_id); const chosen = selected.some((item) => String(item.id ?? item.user_id) === id); return <button type="button" key={id} aria-pressed={chosen} disabled={!chosen && selected.length >= 2} onClick={() => setSelected((current) => chosen ? current.filter((item) => String(item.id ?? item.user_id) !== id) : [...current, person])}><ProfileAvatar row={person} size="sm" /><span>{String(person.display_name ?? person.username)}</span>{chosen ? <CheckCircle2 size={16} /> : <Plus size={16} />}</button>; })}</div><div className="community-actions">{selected.map((person) => <button type="button" className="community-person-pill" key={String(person.id ?? person.user_id)} onClick={() => setSelected((current) => current.filter((item) => item !== person))}>{String(person.display_name ?? person.username)}<X size={13} /></button>)}</div><BaseButton type="submit" variant="outlined" disabled={selected.length !== 2} loading={action.busy}>{copy.inviteRelationship}</BaseButton></form>}<InlineStatus state={action.state} /></section></CommunityDisclosure>;
}

function ProfileBlockButton({ props, userId }: { props: PageProps; userId: string }) {
  const copy = getCommunityMessages(props.locale.code);
  const blocks = useApi(props.auth.user && props.auth.user.id !== userId ? "/api/blocks" : null);
  const action = useCommunityAction(blocks.reload);
  const block = blocks.rows.find((row) => String(row.blocked_user_id ?? row.user_id ?? row.id) === userId);
  if (!props.auth.user || props.auth.user.id === userId || !userId) return null;
  return <div className="community-block"><BaseButton type="button" variant="transparent" loading={action.busy} disabled={blocks.status !== "ready"} onClick={() => action.run(() => submitJson(block ? `/api/blocks/${encodeURIComponent(String(block.id))}` : "/api/blocks", block ? "DELETE" : "POST", { userId }))}><UserX size={16} />{block ? copy.unblock : copy.block}</BaseButton><InlineStatus state={action.state} /></div>;
}

function AlbumGallery({ props, ownerId, own, ownerType = "user", collapsible = false }: { props: PageProps; ownerId: string; own: boolean; ownerType?: "user" | "couple"; collapsible?: boolean }) {
  const ui = props.dictionary.social.ui;
  const copy = getCommunityMessages(props.locale.code);
  const couples = useApi(own && ownerType === "user" ? "/api/couples/mine" : null);
  const [sharedId, setSharedId] = useState("");
  const [visibility, setVisibility] = useState<"" | "public" | "private">("");
  const activeOwnerType = sharedId ? "couple" : ownerType;
  const activeOwnerId = sharedId || ownerId;
  const albums = useApi(activeOwnerId ? `/api/albums?ownerType=${activeOwnerType}&ownerId=${encodeURIComponent(activeOwnerId)}` : null);
  const action = useCommunityAction(albums.reload);
  const [files, setFiles] = useState<File[]>([]);
  const input = useRef<HTMLInputElement | null>(null);
  async function upload(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!visibility || !files.length) return;
    const response = await action.run(() => uploadFiles(files, { visibility, coupleId: activeOwnerType === "couple" ? activeOwnerId : undefined }));
    if (response) { setFiles([]); setVisibility(""); if (input.current) input.current.value = ""; }
  }
  const publicCount = albums.rows.filter((album) => album.visibility === "public").reduce((count, album) => count + Number(album.photo_count ?? 0), 0);
  const privateCount = albums.rows.filter((album) => album.visibility === "private").reduce((count, album) => count + Number(album.photo_count ?? 0), 0);
  const summary = communityDisclosureSummary(albums, ui, `${copy.publicPhotos}: ${publicCount} · ${copy.privatePhotos}: ${privateCount}`);
  return <CommunityDisclosure enabled={collapsible} title={ownerType === "couple" ? copy.sharedGallery : copy.myGallery} summary={summary} icon={Images}><div className="album-gallery community-stack"><p className="community-help">{copy.photoRules}</p>{own && <><AdultProfilePrompt props={props} />{ownerType === "user" && couples.rows.some((couple) => couple.status === "active") && <label><span>{uiLabel(ui, "photos", "Photos")}</span><select value={sharedId} onChange={(event) => setSharedId(event.currentTarget.value)}><option value="">{copy.myGallery}</option>{couples.rows.filter((couple) => couple.status === "active").map((couple) => <option value={String(couple.id)} key={String(couple.id)}>{copy.sharedGallery}: {String(couple.display_name)}</option>)}</select></label>}{props.auth.user?.adult_profile_complete !== false && <form className="gallery-upload community-form" onSubmit={upload}><label><span>{copy.chooseVisibility}</span><select required value={visibility} onChange={(event) => setVisibility(event.currentTarget.value as "" | "public" | "private")}><option value="" disabled>{copy.chooseVisibility}</option><option value="public">{copy.publicPhotos}</option><option value="private">{copy.privatePhotos}</option></select></label><label><span>{ui.photo}</span><input ref={input} type="file" accept={imageUploadAccept} multiple required disabled={!visibility || action.busy} onChange={(event) => setFiles(Array.from(event.currentTarget.files ?? []).filter(isImageUploadFile))} /></label><label className="community-consent"><input type="checkbox" required /><span>{copy.photoRules}</span></label><BaseButton type="submit" variant="primary" loading={action.busy} disabled={!visibility || !files.length}><Upload size={16} />{copy.uploadPhotos}</BaseButton></form>}</>}<DataPanel result={albums} title={uiLabel(ui, "photos", "Photos")} view={props.view} hideTitle emptyActions={false} empty={uiLabel(ui, "photosEmpty", "Photos will appear here after they are uploaded.")} ui={ui} renderRow={(album) => <AlbumPanel key={`${String(album.id)}-${String(album.photo_count)}`} album={album} props={props} onChanged={albums.reload} />} /><InlineStatus state={action.state} /></div></CommunityDisclosure>;
}

function AlbumPanel({ album, props, onChanged }: { album: Record<string, unknown>; props: PageProps; onChanged: () => void }) {
  const ui = props.dictionary.social.ui;
  const copy = getCommunityMessages(props.locale.code);
  const privateAlbum = album.visibility === "private";
  const allowed = Boolean(album.can_view);
  const manage = Boolean(album.can_manage);
  const endpoint = `/api/albums/${encodeURIComponent(String(album.id))}`;
  const photos = useApi(allowed ? `${endpoint}/photos` : null);
  const [manageOpen, setManageOpen] = useState(false);
  const [activePhotoId, setActivePhotoId] = useState<string | null>(null);
  const requests = useApi(manage && manageOpen && privateAlbum ? `${endpoint}/access-requests` : null);
  const grants = useApi(manage && manageOpen && privateAlbum ? `${endpoint}/access-grants` : null);
  const couples = useApi(props.auth.user && !allowed && privateAlbum ? "/api/couples/mine" : null);
  const [recipient, setRecipient] = useState("");
  const action = useCommunityAction(() => { onChanged(); photos.reload(); requests.reload(); grants.reload(); });
  const pending = album.access_status === "pending";
  const activePhotoIndex = activePhotoId === null ? -1 : photos.rows.findIndex((photo) => String(photo.id) === activePhotoId);
  const viewerItems = photos.rows.map((photo) => ({ id: photo.id, url: photo.url, name: photo.original_name }));
  function names(row: Record<string, unknown>) { return Array.isArray(row.recipient_names) ? row.recipient_names.join(" + ") : String(row.recipient_name ?? row.requester_name ?? ""); }
  return <section className={`community-panel album-panel${privateAlbum ? " is-private" : ""}`}><header className="community-panel-heading"><h2>{privateAlbum ? <Lock size={18} /> : <Images size={18} />}{privateAlbum ? copy.privatePhotos : copy.publicPhotos}<small>{String(album.photo_count ?? 0)} {copy.photosCount}</small></h2>{manage && privateAlbum && <BaseButton type="button" variant="transparent" onClick={() => setManageOpen((open) => !open)} aria-expanded={manageOpen}>{copy.accessRequests}</BaseButton>}</header>{!allowed ? <div className="locked-album"><Lock size={34} /><strong>{copy.lockedAlbum}</strong><span>{String(album.photo_count ?? 0)} {copy.photosCount}</span>{!props.auth.user ? <a className="base-button outlined" href={authRouteHrefForRedirect("/login", props.currentUrl)}>{ui.logIn}</a> : pending ? <span role="status">{copy.accessPending}</span> : <form className="community-stack" onSubmit={(event) => { event.preventDefault(); void action.run(() => submitJson(`${endpoint}/access-requests`, "POST", { recipientType: recipient ? "couple" : "user", recipientId: recipient || props.auth.user?.id })); }}><label><span>{copy.requestAs}</span><select value={recipient} onChange={(event) => setRecipient(event.currentTarget.value)}><option value="">{props.auth.user.display_name}</option>{couples.rows.filter((couple) => couple.status === "active").map((couple) => <option key={String(couple.id)} value={String(couple.id)}>{couplePeople(couple).map((person) => String(person.display_name)).join(" + ")}</option>)}</select></label><BaseButton type="submit" variant="outlined" loading={action.busy}>{copy.requestAccess}</BaseButton></form>}</div> : <DataPanel result={photos} title={privateAlbum ? copy.privatePhotos : copy.publicPhotos} view={props.view} hideTitle emptyActions={false} density="grid" empty={uiLabel(ui, "photosEmpty", "Photos will appear here after they are uploaded.")} ui={ui} renderRow={(photo) => <AlbumPhoto photo={photo} album={album} props={props} onOpen={() => setActivePhotoId(String(photo.id))} onChanged={() => { setActivePhotoId(null); photos.reload(); onChanged(); }} />} />}{manage && manageOpen && privateAlbum && <div className="album-access-management community-stack"><p>{copy.accessScope}</p><h3>{copy.accessRequests}</h3><DataPanel result={requests} title={copy.accessRequests} view={props.view} hideTitle compact emptyActions={false} empty={copy.noRequests} ui={ui} renderRow={(request) => <div className="community-access-row"><span>{names(request)}</span><BaseButton type="button" variant="outlined" disabled={action.busy} onClick={() => action.run(() => submitJson(`${endpoint}/access-grants`, "POST", { requestId: request.id }))}>{copy.approve}</BaseButton></div>} /><h3>{copy.accessGrants}</h3><DataPanel result={grants} title={copy.accessGrants} view={props.view} hideTitle compact emptyActions={false} empty={copy.noGrants} ui={ui} renderRow={(grant) => <div className="community-access-row"><span>{names(grant)}</span><BaseButton type="button" variant="transparent" disabled={action.busy} onClick={() => action.run(() => submitJson(`${endpoint}/access-grants/${encodeURIComponent(String(grant.id))}`, "DELETE", {}))}>{copy.revoke}</BaseButton></div>} /></div>}<InlineStatus state={action.state} />{allowed && photos.status === "ready" && activePhotoIndex >= 0 && <PhotoViewer items={viewerItems} activeIndex={activePhotoIndex} ui={ui} onClose={() => setActivePhotoId(null)} onIndexChange={(index) => setActivePhotoId(String(photos.rows[index].id))} />}</section>;
}

function AlbumPhoto({ photo, album, props, onChanged, onOpen }: { photo: Record<string, unknown>; album: Record<string, unknown>; props: PageProps; onChanged: () => void; onOpen: () => void }) {
  const ui = props.dictionary.social.ui;
  const copy = getCommunityMessages(props.locale.code);
  const action = useCommunityAction(onChanged);
  const [reportOpen, setReportOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmation, setConfirmation] = useState<"delete" | "public" | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const reportInputRef = useRef<HTMLTextAreaElement | null>(null);
  const menuId = `album-${String(album.id)}-photo-${String(photo.id)}-menu`;
  const endpoint = `/api/albums/${encodeURIComponent(String(album.id))}/photos/${encodeURIComponent(String(photo.id))}`;
  const privatePhoto = album.visibility === "private";
  function closeMenu(restoreFocus = false) {
    setMenuOpen(false);
    if (restoreFocus) menuButtonRef.current?.focus({ preventScroll: true });
  }
  useEffect(() => {
    if (!menuOpen) return;
    function dismiss(event: PointerEvent | FocusEvent) {
      if (event.target instanceof Node && menuRef.current?.contains(event.target)) return;
      setMenuOpen(false);
    }
    function escape(event: KeyboardEvent) {
      if (event.key === "Escape") closeMenu(true);
    }
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("focusin", dismiss);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("focusin", dismiss);
      document.removeEventListener("keydown", escape);
    };
  }, [menuOpen]);
  useEffect(() => {
    if (!reportOpen) return;
    const frame = window.requestAnimationFrame(() => reportInputRef.current?.focus({ preventScroll: true }));
    return () => window.cancelAnimationFrame(frame);
  }, [reportOpen]);
  async function confirmPhotoAction() {
    const response = await action.run(() => submitJson(endpoint, confirmation === "delete" ? "DELETE" : "PATCH", confirmation === "delete" ? {} : { visibility: "public" }));
    if (response !== undefined) setConfirmation(null);
  }
  return (
    <article className="album-photo">
      <button className="album-photo-open" type="button" onClick={onOpen} aria-label={String(photo.original_name ?? ui.photo)}>
        <PictureImage src={String(photo.url)} alt={String(photo.original_name ?? "")} />
      </button>
      <div className="album-photo-toolbar">
        <span className="album-photo-visibility">{privatePhoto ? <Lock size={14} /> : <Globe2 size={14} />}{privatePhoto ? copy.privatePhotos : copy.publicPhotos}</span>
        {(photo.can_manage || props.auth.user) && <div className="album-photo-menu post-menu-wrap" ref={menuRef}>
          <button ref={menuButtonRef} className="post-menu-button" type="button" aria-label={ui.more} title={ui.more} aria-haspopup="menu" aria-expanded={menuOpen} aria-controls={menuId} disabled={action.busy} onClick={() => setMenuOpen((open) => !open)} onKeyDown={(event) => {
            if (!["ArrowDown", "ArrowUp"].includes(event.key)) return;
            event.preventDefault();
            setMenuOpen(true);
            focusMenuItem(menuId, event.key === "ArrowUp");
          }}><MoreHorizontal size={18} /></button>
          {menuOpen && <div id={menuId} className="post-menu" role="menu" onKeyDown={(event) => handleMenuPanelKeyDown(event, event.currentTarget)}>
            {photo.can_manage ? <>
              <button type="button" role="menuitem" disabled={action.busy} onClick={() => {
                closeMenu(true);
                if (privatePhoto) setConfirmation("public");
                else void action.run(() => submitJson(endpoint, "PATCH", { visibility: "private" }));
              }}>{privatePhoto ? <Globe2 size={16} /> : <Lock size={16} />}<span>{privatePhoto ? copy.movePublic : copy.movePrivate}</span></button>
              <button className="destructive" type="button" role="menuitem" disabled={action.busy} onClick={() => { closeMenu(true); setConfirmation("delete"); }}><Trash2 size={16} /><span>{ui.deleteItem}</span></button>
            </> : <button type="button" role="menuitem" onClick={() => { closeMenu(); setReportOpen(true); }}><Flag size={16} /><span>{copy.reportPhoto}</span></button>}
          </div>}
        </div>}
      </div>
      {reportOpen && <form className="community-stack" onSubmit={(event) => {
        event.preventDefault();
        const form = new FormData(event.currentTarget);
        void action.run(async () => {
          const response = await submitJson("/api/reports", "POST", { subjectType: "photo", subjectId: photo.id, reason: String(form.get("reason")) });
          setReportOpen(false);
          return response;
        });
      }}><label><span>{copy.reportReason}</span><textarea ref={reportInputRef} name="reason" required maxLength={2000} /></label><div className="community-actions"><BaseButton type="submit" variant="outlined" loading={action.busy}>{copy.reportPhoto}</BaseButton><BaseButton type="button" variant="transparent" disabled={action.busy} onClick={() => { setReportOpen(false); closeMenu(true); }}>{ui.cancel}</BaseButton></div></form>}
      {!confirmation && <InlineStatus state={action.state} />}
      {confirmation && <DeleteConfirmDialog idBase={`${menuId}-confirm`} title={confirmation === "delete" ? ui.deleteItem : copy.movePublic} body={confirmation === "delete" ? copy.deletePhotoConfirm : `${copy.publicMediaConfirm} ${copy.photoRules}`} confirmLabel={confirmation === "delete" ? ui.deleteItem : copy.movePublic} busyLabel={confirmation === "delete" ? ui.deleteItem : copy.movePublic} cancelLabel={ui.cancel} busy={action.busy} icon={confirmation === "delete" ? Trash2 : Globe2} confirmVariant={confirmation === "delete" ? "danger" : "primary"} error={action.state.status === "error" ? action.state.message : undefined} onCancel={() => setConfirmation(null)} onConfirm={confirmPhotoAction} />}
    </article>
  );
}

function PhotoReportsPanel({ props }: { props: PageProps }) {
  const ui = props.dictionary.social.ui;
  const copy = getCommunityMessages(props.locale.code);
  const reports = useApi("/api/ops/photo-reports");
  return <section className="community-panel"><h2>{copy.photoReports}</h2><DataPanel result={reports} title={copy.photoReports} view={props.view} hideTitle emptyActions={false} empty={copy.noRequests} ui={ui} renderRow={(report) => <PhotoReportRow report={report} props={props} onChanged={reports.reload} />} /></section>;
}

function PhotoReportRow({ report, props, onChanged }: { report: Record<string, unknown>; props: PageProps; onChanged: () => void }) {
  const copy = getCommunityMessages(props.locale.code);
  const [mediaUrl, setMediaUrl] = useState("");
  const action = useCommunityAction(onChanged);
  const endpoint = `/api/ops/photo-reports/${encodeURIComponent(String(report.id))}`;
  useEffect(() => () => { if (mediaUrl) URL.revokeObjectURL(mediaUrl); }, [mediaUrl]);
  async function openPhoto() {
    await action.run(async () => {
      const response = await fetch(`${endpoint}/media`, { credentials: "include", headers: apiHeaders() });
      if (!response.ok) { const data = await response.json().catch(() => ({})); throw new Error(readableError(data, response.status)); }
      setMediaUrl(URL.createObjectURL(await response.blob()));
      return true;
    }, false);
  }
  return <article className="community-stack photo-report-row"><strong>{String(report.owner_name ?? report.uploader_name ?? "")}</strong><p>{String(report.reason ?? "")}</p><small>{String(report.reporter_name ?? "")} · {copy[`state_${String(report.status)}`] ?? ""} · {String(report.created_at ?? "")}</small>{mediaUrl ? <PictureImage src={mediaUrl} alt="" /> : <BaseButton type="button" variant="outlined" loading={action.busy} onClick={openPhoto}><Eye size={16} />{copy.reviewPhoto}</BaseButton>}<div className="community-actions"><BaseButton type="button" variant="outlined" disabled={action.busy} onClick={() => action.run(() => submitJson(endpoint, "PATCH", { status: "resolved" }))}>{copy.resolve}</BaseButton><BaseButton type="button" variant="transparent" disabled={action.busy} onClick={() => action.run(() => submitJson(endpoint, "PATCH", { status: "rejected" }))}>{copy.reject}</BaseButton><BaseButton type="button" variant="transparent" disabled={action.busy} onClick={() => action.run(() => submitJson(endpoint, "PATCH", { status: "resolved", remove: true }))}>{copy.removePhoto}</BaseButton></div><InlineStatus state={action.state} /></article>;
}

const communityIntentions = ["throuple", "dating", "exploration", "friendship", "community"];
const communityPreferences: Record<string, string[]> = { exclusivity: ["closed", "open", "flexible"], experience: ["new", "experienced"], pace: ["slow", "flexible", "fast"], cohabitation: ["yes", "no", "open"], children: ["has", "wants", "no", "open"], availability: ["weekdays", "weekends", "flexible"], travel: ["local", "travel", "relocate"] };

function IntentionFields({ values = [] }: { values?: unknown }) {
  const copy = getCommunityMessages(activeLocaleCode);
  const selected = Array.isArray(values) ? values : [];
  return <fieldset className="community-intentions"><legend>{copy.intentions}</legend>{communityIntentions.map((intent) => <label key={intent}><input name="intentions" type="checkbox" value={intent} defaultChecked={selected.includes(intent)} /><span>{copy[intent]}</span></label>)}</fieldset>;
}

function PreferenceFields({ values = {}, filter = false }: { values?: Record<string, unknown>; filter?: boolean }) {
  const copy = getCommunityMessages(activeLocaleCode);
  return <>{Object.entries(communityPreferences).map(([key, options]) => <label key={key}><span>{copy[key]}</span><select name={filter ? key : `preference.${key}`} defaultValue={String(values[key] ?? "")}><option value="">{copy.any}</option>{options.map((value) => <option value={value} key={value}>{copy[value === "travel" ? "travelOption" : value]}</option>)}</select></label>)}</>;
}

function DatingFilters({ props, onChange }: { props: PageProps; onChange: (query: string) => void }) {
  const ui = props.dictionary.social.ui;
  const copy = getCommunityMessages(props.locale.code);
  const form = useRef<HTMLFormElement | null>(null);
  return <details className="community-panel dating-filters"><summary><Filter size={17} />{ui.filters || ui.search}</summary><form ref={form} className="community-form" onSubmit={(event) => { event.preventDefault(); const data = new FormData(event.currentTarget); const query = new URLSearchParams(); data.forEach((value, key) => { if (String(value).trim()) query.set(key, String(value).trim()); }); onChange(query.toString()); }}><label><span>{copy.profileType}</span><select name="profileType"><option value="">{copy.any}</option><option value="user">{copy.individual}</option><option value="couple">{copy.couple}</option></select></label><SignupSelectField label={ui.gender} name="gender" options={genderOptions(ui)} placeholder={copy.any} /><label><span>{copy.ageMin}</span><input name="ageMin" type="number" min={18} max={120} /></label><label><span>{copy.ageMax}</span><input name="ageMax" type="number" min={18} max={120} /></label><GlobalLocationFields ui={ui} filter /><label><span>{copy.intentions}</span><select name="intention"><option value="">{copy.any}</option>{communityIntentions.map((intent) => <option value={intent} key={intent}>{copy[intent]}</option>)}</select></label><label><span>{copy.relationshipStatus}</span><select name="relationshipStatus"><option value="">{copy.any}</option><option value="single">{copy.single}</option><option value="couple">{copy.couple}</option><option value="throuple">{copy.triad}</option></select></label><details className="community-disclosure community-optional-preferences"><summary className="community-disclosure-summary"><span className="community-disclosure-title">{copy.preferences}</span><ChevronDown size={17} className="community-disclosure-chevron" /></summary><div className="community-form community-disclosure-body"><PreferenceFields filter /></div></details><div className="community-actions"><BaseButton type="submit" variant="primary"><Search size={16} />{ui.search}</BaseButton><BaseButton type="button" variant="transparent" onClick={() => { form.current?.reset(); onChange(""); }}>{copy.clearFilters}</BaseButton></div></form></details>;
}

function DatingView(props: PageProps) {
  const ui = props.dictionary.social.ui;
  const isProfile = props.view.name === "Dating Profile";
  const isMatches = props.view.name === "Dating Matches";
  const [filters, setFilters] = useState("");
  const profile = useApi(props.auth.user ? "/api/dating/profile" : null);
  const profileRow = profile.rows[0];
  const canDiscover = profile.status === "ready" && profileRow?.dating_status === "active";
  const endpoint = props.auth.user?.adult_profile_complete !== false && props.auth.user && !isProfile && (isMatches || canDiscover) ? (isMatches ? "/api/dating/matches" : `/api/dating/discover${filters ? `?${filters}` : ""}`) : null;
  const result = useApi(endpoint);
  const tabs = [["/dating", "datingDiscover"], ["/dating/matches", "datingMatches"], ["/dating/profile", "datingProfile"]] as const;
  if (!props.auth.user) return <PermissionPanel title={ui.logInToContinueTitle} body={uiLabel(ui, "datingLoginBody", "Log in to create a dating profile, discover people, and start conversations.")} actionLabel={props.dictionary.public.nav.login} />;
  return (
    <section className="contract-page dating-page">
      <ContractHero props={props} onRefresh={isProfile ? profile.reload : result.reload} />
      <AdultProfilePrompt props={props} />
      <nav className="dating-tabs profile-tabs" aria-label={uiLabel(ui, "dating", "Dating")}>
        {tabs.map(([href, label]) => <a className={props.currentUrl === href ? "active" : ""} href={href} aria-current={props.currentUrl === href ? "page" : undefined} key={href} onClick={(event) => handleOpenSNRouteClick(event, href)}>{uiLabel(ui, label, label)}</a>)}
      </nav>
      {profile.status === "loading" && !profileRow && <LoadingState label={ui.loadingResults} />}
      {profile.status === "error" && <EmptyState icon={LifeBuoy} title={ui.unableToLoadView} body={displayErrorMessage(profile.error, ui.retry)} showBody action={<BaseButton type="button" variant="outlined" onClick={profile.reload}>{ui.retry}</BaseButton>} />}
      {profile.status === "permission" && <PermissionPanel title={ui.logInToContinueTitle} body={ui.logInToContinueBody} compact actionLabel={props.dictionary.public.nav.login} />}
      {isProfile && profile.status === "ready" && props.auth.user.adult_profile_complete !== false && <DatingProfileForm key={String(profileRow?.updated_at ?? "new")} row={profileRow ?? {}} ui={ui} onSaved={profile.reload} />}
      {isProfile && <div className="community-disclosure-list"><CoupleManagementPanel props={props} collapsible /><RelationshipLinksPanel props={props} userId={props.auth.user.id} own collapsible /><AlbumGallery props={props} ownerId={props.currentQuery.couple || props.auth.user.id} own={!props.currentQuery.couple} ownerType={props.currentQuery.couple ? "couple" : "user"} collapsible /></div>}
      {!isProfile && !isMatches && canDiscover && <DatingFilters props={props} onChange={setFilters} />}
      {!isProfile && !isMatches && profile.status === "ready" && !canDiscover && <EmptyState icon={Heart} title={uiLabel(ui, "datingProfileRequired", "Create your dating profile")} body={uiLabel(ui, "datingProfileRequiredBody", "Discovery starts after you define your intent and boundaries.")} showBody action={<a className="base-button primary" href="/dating/profile" onClick={(event) => handleOpenSNRouteClick(event, "/dating/profile")}>{uiLabel(ui, "datingProfile", "Dating profile")}<ArrowRight size={16} /></a>} />}
      {endpoint && <div className="dating-directory"><DataPanel result={result} title={uiLabel(ui, isMatches ? "datingMatches" : "datingDiscover", isMatches ? "Matches" : "Discover")} view={props.view} emptyTitle={uiLabel(ui, isMatches ? "datingNoMatches" : "datingNoProfiles", isMatches ? "No matches yet" : "No dating profiles yet")} empty={uiLabel(ui, isMatches ? "datingNoMatchesBody" : "datingNoProfilesBody", isMatches ? "A match appears when two members like each other." : "New active dating profiles will appear here when members opt in.")} renderRow={(row) => <DatingCard row={row} props={props} onChanged={result.reload} />} density="grid" emptyActions={false} hideTitle ui={ui} /></div>}
    </section>
  );
}

function DatingProfileForm({ row, ui, onSaved }: { row: Record<string, unknown>; ui: I18nDictionary["social"]["ui"]; onSaved: () => void }) {
  const [state, setState] = useState<SubmitState>({ status: "idle", message: "" });
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setState({ status: "saving", message: ui.savingSettings });
    try {
      await submitJson("/api/dating/profile", "PATCH", { ...Object.fromEntries(["datingStatus", "relationshipStructure", "lookingFor", "bio", "boundaries", "locationText", "gender", "sexuality"].map((key) => [key, String(form.get(key) ?? "")])), intentions: form.getAll("intentions").map(String), preferences: Object.fromEntries(Object.keys(communityPreferences).map((key) => [key, String(form.get(`preference.${key}`) ?? "")])) });
      setState({ status: "success", message: ui.settingsSaved });
      onSaved();
    } catch (error) {
      setState({ status: "error", message: localizedErrorMessage(error, ui.settingsRequestFailed) });
    }
  }
  return (
    <form className="settings-form dating-profile-form" onSubmit={submit}>
      <div className="dating-form-heading"><span className="dating-form-icon"><Heart size={24} /></span><div><h2>{uiLabel(ui, "datingProfile", "Dating profile")}</h2><p>{uiLabel(ui, "datingProfileRequiredBody", "Discovery starts after you define your intent and boundaries.")}</p></div></div>
      <label><span>{uiLabel(ui, "datingStatus", "Dating status")}</span><select name="datingStatus" defaultValue={String(row.dating_status ?? "active")}><option value="active">{ui.active}</option><option value="paused">{ui.paused}</option><option value="hidden">{uiLabel(ui, "hidden", "Hidden")}</option></select></label>
      <SignupSelectField label={ui.gender} name="gender" options={genderOptions(ui)} placeholder={getCommunityMessages(activeLocaleCode).any} defaultValue={String(row.gender ?? "")} />
      <label><span>{getCommunityMessages(activeLocaleCode).sexuality}</span><input name="sexuality" maxLength={80} defaultValue={String(row.sexuality ?? "")} /></label>
      <label><span>{getCommunityMessages(activeLocaleCode).relationshipStatus}</span><select name="relationshipStructure" defaultValue={String(row.relationship_structure ?? "single")}><option value="single">{getCommunityMessages(activeLocaleCode).single}</option><option value="couple">{getCommunityMessages(activeLocaleCode).couple}</option><option value="throuple">{getCommunityMessages(activeLocaleCode).triad}</option></select></label>
      <IntentionFields values={row.intentions} />
      <label><span>{ui.bio}</span><textarea name="bio" rows={4} maxLength={2000} defaultValue={String(row.bio ?? "")} placeholder={uiLabel(ui, "datingBioPlaceholder", "Describe your intent, pace, and what transparency means to you.")} /></label>
      <label><span>{uiLabel(ui, "boundaries", "Boundaries")}</span><textarea name="boundaries" rows={4} maxLength={2000} defaultValue={String(row.boundaries ?? "")} placeholder={uiLabel(ui, "boundariesPlaceholder", "Name privacy, consent, communication, or public visibility expectations.")} /></label>
      <label><span>{uiLabel(ui, "location", "Location")}</span><input name="locationText" maxLength={160} defaultValue={String(row.location_text ?? "")} placeholder={uiLabel(ui, "locationPlaceholder", "City, region, or online-first")} /></label>
      <details className="community-disclosure community-optional-preferences"><summary className="community-disclosure-summary"><span className="community-disclosure-title">{getCommunityMessages(activeLocaleCode).preferences}</span><ChevronDown size={17} className="community-disclosure-chevron" /></summary><div className="community-form community-disclosure-body"><PreferenceFields values={isRecord(row.preferences) ? row.preferences : {}} /></div></details>
      <div className="form-actions sticky-actions"><BaseButton type="submit" variant="primary" loading={state.status === "saving"}>{ui.save}</BaseButton></div>
      <InlineStatus state={state} />
    </form>
  );
}

function DatingCard({ row, props, onChanged }: { row: Record<string, unknown>; props: PageProps; onChanged: () => void }) {
  const ui = props.dictionary.social.ui;
  const copy = getCommunityMessages(props.locale.code);
  const [state, setState] = useState<SubmitState>({ status: "idle", message: "" });
  const [matched, setMatched] = useState(booleanValue(row.matched));
  const [dismissed, setDismissed] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const couple = row.profile_type === "couple";
  const title = String(row.display_name ?? row.username ?? "");
  const accountLabel = couple ? copy.couple : copy.individual;
  const chatPolicyLabel = row.chat_policy === "common" ? copy.common : row.chat_policy === "individual" ? copy.individualChats : row.chat_policy === "both" ? copy.both : "";
  const cover = mediaSrcFromRow(row, "cover");
  const explicitLocation = firstNonEmptyText(row.location_text);
  const location = explicitLocation && explicitLocation !== firstNonEmptyText(row.current_city) ? explicitLocation : memberLocationLabel(row);
  const partnerRows = couple ? [
    { display_name: row.partner_one_name, username: row.partner_one_username, gender: row.partner_one_gender },
    { display_name: row.partner_two_name, username: row.partner_two_username, gender: row.partner_two_gender }
  ] : [];
  async function interact(action: "like" | "pass") {
    setState({ status: "saving", message: ui.savingSettings });
    try {
      const response = await submitJson("/api/dating/interactions", "POST", { targetUserId: row.user_id, action });
      const nowMatched = isRecord(response) && booleanValue(response.matched);
      setMatched(nowMatched);
      setState({ status: "success", message: nowMatched ? uiLabel(ui, "datingMatched", "Match created") : ui.requestCompleted });
      if (!nowMatched) setDismissed(true);
      onChanged();
    } catch (error) {
      setState({ status: "error", message: localizedErrorMessage(error, ui.actionFailed) });
    }
  }
  async function startCoupleConversation() {
    setState({ status: "saving", message: ui.startingDirectDiscussion });
    try {
      const response = await submitJson("/api/dating/couple-conversations", "POST", { coupleProfileId: row.couple_profile_id, requestedChatType: "group" });
      const roomId = chatRoomIdFromResponse(response);
      if (!roomId) throw new Error(ui.directDiscussionFailed);
      const href = `/chat?room=${encodeURIComponent(roomId)}`;
      if (!navigateOpenSNRoute(href)) window.location.assign(href);
    } catch (error) {
      setState({ status: "error", message: localizedErrorMessage(error, ui.directDiscussionFailed) });
    }
  }
  if (dismissed) return null;
  return (
    <article className={`dating-card ${couple ? "is-couple" : "is-individual"}`}>
      <div className={`dating-card-cover ${cover ? "has-image" : ""}`}>{cover && <PictureImage src={cover} alt="" />}{accountLabel && <span className="dating-kind-pill">{couple ? <Users size={14} /> : <UserRound size={14} />}{accountLabel}</span>}{matched && <span className="dating-match-pill"><Heart size={14} />{uiLabel(ui, "datingMatch", "Match")}</span>}</div>
      <div className="dating-card-body">
        <div className="dating-avatar-group">{couple ? partnerRows.map((partner, index) => <ProfileAvatarLink row={partner} size="lg" key={index} />) : <ProfileAvatarLink row={row} size="lg" />}</div>
        <h2>{couple ? title : <ProfileNameLink row={row}>{title}</ProfileNameLink>}</h2>
        {couple && <div className="dating-partners">{partnerRows.map((partner, index) => <ProfileNameLink row={partner} key={index}>{String(partner.display_name ?? "")}</ProfileNameLink>)}</div>}
        {location && <p className="dating-location"><MapPin size={14} />{location}</p>}
        {row.age ? <span className="dating-age">{String(row.age)}</span> : null}
        {row.relationship_structure ? <span className="dating-intent">{copy[row.relationship_structure === "throuple" ? "triad" : String(row.relationship_structure)] ?? String(row.relationship_structure)}</span> : null}
        {row.bio ? <p className="dating-bio">{String(row.bio)}</p> : null}
        {Array.isArray(row.intentions) && row.intentions.length > 0 ? <div className="dating-detail"><span>{copy.intentions}</span><p>{row.intentions.map((value) => copy[String(value)] ?? String(value)).join(" · ")}</p></div> : null}
        {row.boundaries ? <div className="dating-detail"><span><ShieldCheck size={14} />{uiLabel(ui, "boundaries", "Boundaries")}</span><p>{String(row.boundaries)}</p></div> : null}
        {chatPolicyLabel && <p className="dating-chat-policy"><Users size={15} />{chatPolicyLabel}</p>}
      </div>
      <div className="dating-card-actions">
        {couple ? <>{row.chat_policy !== "individual" && <BaseButton type="button" variant="primary" loading={state.status === "saving"} onClick={startCoupleConversation}><MessageCircle size={17} />{copy.common}</BaseButton>}{row.chat_policy !== "common" && partnerRows.map((partner, index) => <a className="base-button outlined" key={index} href={chatRecipientHref(String(partner.username), String(partner.display_name))} onClick={(event) => handleOpenSNRouteClick(event, chatRecipientHref(String(partner.username), String(partner.display_name)))}><MessageCircle size={16} />{String(partner.display_name)}</a>)}</> : <><a className="base-button primary" href={chatRecipientHref(String(row.username ?? row.user_id), title)} onClick={(event) => handleOpenSNRouteClick(event, chatRecipientHref(String(row.username ?? row.user_id), title))}><MessageCircle size={17} />{ui.messages}</a>{!matched && <><BaseButton type="button" variant="outlined" disabled={state.status === "saving"} onClick={() => interact("pass")} aria-label={uiLabel(ui, "pass", "Pass")}><X size={17} /></BaseButton><BaseButton type="button" variant="outlined" loading={state.status === "saving"} onClick={() => interact("like")}><Heart size={17} />{ui.like}</BaseButton></>}</>}
      </div>
      {couple && <details className="dating-couple-gallery" onToggle={(event) => setGalleryOpen(event.currentTarget.open)}><summary><Images size={16} />{copy.sharedGallery}</summary>{galleryOpen && <AlbumGallery props={props} ownerId={String(row.couple_profile_id ?? row.id)} ownerType="couple" own={false} />}</details>}
      <InlineStatus state={state} />
    </article>
  );
}

const ChatConversationButton = memo(function ChatConversationButton({
  row,
  active,
  ui,
  onSelect
}: {
  row: Record<string, unknown>;
  active: boolean;
  ui: I18nDictionary["social"]["ui"];
  onSelect: (id: string) => void;
}) {
  const id = String(row.id);
  const meta = chatConversationMeta(row, ui);
  const unreadCount = chatRoomUnreadCount(row);
  const title = chatConversationTitle(row, ui);
  const preview = chatConversationPreview(row, ui);
  const accessibleTitle =
    unreadCount > 0 ? `${title}, ${unreadCount} ${uiLabel(ui, "unreadMessages", "unread messages")}` : title;
  return (
    <button
      className={`chat-list-item${active ? " active" : ""}${unreadCount ? " unread" : ""}`}
      type="button"
      aria-label={accessibleTitle}
      aria-pressed={active}
      title={title}
      onClick={() => onSelect(id)}
    >
      <span className="chat-avatar">
        {row.type === "group" ? (
          <Users size={18} aria-hidden="true" />
        ) : (
          <ProfileAvatar row={chatParticipantProfileRow(row)} size="sm" className="chat-avatar-profile" />
        )}
        {unreadCount > 0 && (
          <span className="chat-unread-badge" aria-hidden="true">
            {notificationBadgeText(unreadCount)}
          </span>
        )}
      </span>
      <span>
        <span className="chat-list-title">
          <strong>{title}</strong>
        </span>
        {meta && <small>{meta}</small>}
        <em>{preview}</em>
      </span>
    </button>
  );
});

function ChatView(props: PageProps) {
  const ui = props.dictionary.social.ui;
  const rooms = useApi("/api/chat/rooms");
  useMemberPolling(rooms.reload, Boolean(props.auth.user));
  const routeRecipient = String(props.currentQuery.recipient ?? "").trim();
  const routeRoomId = String(props.currentQuery.room ?? "").trim();
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(routeRoomId || null);
  const [mobileConversationOpen, setMobileConversationOpen] = useState(Boolean(routeRecipient || routeRoomId));
  const selectedRoom = useMemo(() => rooms.rows.find((row) => String(row.id) === selectedRoomId) ?? null, [rooms.rows, selectedRoomId]);
  const messages = useChatMessages(selectedRoomId);
  const [message, setMessage] = useState("");
  const [draftRecipient, setDraftRecipient] = useState(routeRecipient);
  const [directComposerOpen, setDirectComposerOpen] = useState(false);
  const [directState, setDirectState] = useState<SubmitState>({ status: "idle", message: "" });
  const [messageState, setMessageState] = useState<SubmitState>({ status: "idle", message: "" });
  const [optimisticMessages, setOptimisticMessages] = useState<Record<string, ChatOptimisticMessage[]>>({});
  const messageListRef = useRef<HTMLDivElement | null>(null);
  const messageInputRef = useRef<HTMLTextAreaElement | null>(null);
  const historyScrollRef = useRef<{ height: number; top: number } | null>(null);
  const scrollToBottomRef = useRef(false);
  const visibleMessageRows = useMemo(() => {
    if (!selectedRoomId) return [];
    const localRows = optimisticMessages[selectedRoomId] ?? [];
    const serverIds = new Set(messages.rows.map((row) => String(row.id)));
    return [
      ...messages.rows,
      ...localRows.filter((row) => !serverIds.has(String(row.server_id ?? row.id)))
    ];
  }, [messages.rows, optimisticMessages, selectedRoomId]);
  const canUseChat = Boolean(props.auth.user);
  const canWriteMessage = canUseChat && Boolean(selectedRoomId || draftRecipient);
  const routeRecipientLabel = String(props.currentQuery.recipientName ?? props.currentQuery.recipient_name ?? "").trim();
  const [draftRecipientLabel, setDraftRecipientLabel] = useState(routeRecipientLabel);
  const draftRecipientTitle = draftRecipient ? draftRecipientLabel || chatDraftRecipientTitle(draftRecipient) : "";
  const conversationTitle = selectedRoom ? chatConversationTitleContent(selectedRoom, ui) : draftRecipientTitle || ui.selectConversation;
  const conversationMeta = selectedRoom ? chatConversationMeta(selectedRoom, ui) : draftRecipient ? (canUseChat ? ui.writeMessage : ui.logInToUseMessages) : ui.startConversationHint;
  const messageComposerPlaceholder = canWriteMessage ? ui.writeMessage : (draftRecipient && !canUseChat ? props.dictionary.public.nav.login : ui.selectConversation);

  useEffect(() => {
    setDraftRecipient(routeRecipient);
    setDraftRecipientLabel(routeRecipientLabel);
    if (routeRecipient) setSelectedRoomId(null);
    if (routeRoomId) setSelectedRoomId(routeRoomId);
    if (routeRecipient || routeRoomId) setMobileConversationOpen(true);
  }, [routeRecipient, routeRecipientLabel, routeRoomId]);

  useEffect(() => {
    if (rooms.status !== "ready") return;
    if (draftRecipient) {
      const matchingRoom = rooms.rows.find((row) => chatRoomMatchesRecipient(row, draftRecipient));
      if (matchingRoom) {
        const roomId = String(matchingRoom.id);
        setDraftRecipient("");
        setDraftRecipientLabel("");
        if (selectedRoomId !== roomId) setSelectedRoomId(roomId);
      }
      return;
    }
    if (rooms.rows.length === 0) {
      if (selectedRoomId) setSelectedRoomId(null);
      return;
    }
    const selectedStillExists = selectedRoomId && rooms.rows.some((row) => String(row.id) === selectedRoomId);
    if (!selectedStillExists) setSelectedRoomId(String(rooms.rows[0].id));
  }, [draftRecipient, rooms.rows, rooms.status, selectedRoomId]);

  useEffect(() => {
    if (!selectedRoomId || messages.status !== "ready") return;
    const serverIds = new Set(messages.rows.map((row) => String(row.id)));
    if (serverIds.size === 0) return;
    setOptimisticMessages((current) => {
      const roomRows = current[selectedRoomId] ?? [];
      const nextRoomRows = roomRows.filter((row) => !serverIds.has(String(row.server_id ?? row.id)));
      if (nextRoomRows.length === roomRows.length) return current;
      const next = { ...current };
      if (nextRoomRows.length) next[selectedRoomId] = nextRoomRows;
      else delete next[selectedRoomId];
      return next;
    });
  }, [messages.rows, messages.status, selectedRoomId]);

  useEffect(() => {
    if (!selectedRoomId) return;
    scrollToBottomRef.current = true;
  }, [selectedRoomId]);

  useEffect(() => {
    if (!canWriteMessage) return;
    if (window.matchMedia("(max-width: 959px)").matches && !mobileConversationOpen) return;
    const frame = window.requestAnimationFrame(() => messageInputRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [canWriteMessage, draftRecipient, selectedRoomId, mobileConversationOpen]);

  useEffect(() => {
    resizeChatMessageInput();
  }, [message]);

  useEffect(() => {
    const list = messageListRef.current;
    if (!selectedRoomId || !list || visibleMessageRows.length === 0) return;
    const frame = window.requestAnimationFrame(() => {
      const historyScroll = historyScrollRef.current;
      if (historyScroll) {
        list.scrollTop = list.scrollHeight - historyScroll.height + historyScroll.top;
        historyScrollRef.current = null;
        return;
      }
      if (scrollToBottomRef.current) {
        list.scrollTop = list.scrollHeight;
        scrollToBottomRef.current = false;
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [selectedRoomId, messages.status, visibleMessageRows.length]);

  const loadOlderMessages = useCallback((event: UIEvent<HTMLDivElement>) => {
    const list = event.currentTarget;
    if (list.scrollTop > 36 || !messages.nextCursor || messages.loadingOlder || messages.status !== "ready") return;
    historyScrollRef.current = { height: list.scrollHeight, top: list.scrollTop };
    messages.loadOlder();
  }, [messages]);

  function resizeChatMessageInput(element = messageInputRef.current) {
    if (!element) return;
    element.style.height = "0px";
    element.style.height = `${Math.min(132, Math.max(40, element.scrollHeight))}px`;
  }

  function handleMessageKeyDown(event: ReactKeyboardEvent<HTMLTextAreaElement>) {
    if (event.key !== "Enter" || event.shiftKey || event.altKey || event.ctrlKey || event.metaKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    event.currentTarget.form?.requestSubmit();
  }

  async function createDirectDiscussionFor(recipient: string): Promise<string> {
    const response = await submitJson("/api/chat/direct-discussions", "POST", { recipient });
    const roomId = chatRoomIdFromResponse(response);
    if (!roomId) throw new Error(ui.directDiscussionFailed);
    setSelectedRoomId(roomId);
    setDraftRecipient(recipient);
    rooms.reload();
    return roomId;
  }

  async function startDirectDiscussion(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const recipient = String(form.get("recipient") ?? "").trim();
    if (!recipient) {
      setDirectState({ status: "error", message: ui.directRecipientRequired });
      return;
    }
    setDirectState({ status: "saving", message: ui.startingDirectDiscussion });
    try {
      const response = await submitJson("/api/chat/direct-discussions", "POST", { recipient });
      const roomId = chatRoomIdFromResponse(response);
      if (roomId) setSelectedRoomId(roomId);
      setMobileConversationOpen(true);
      const roomAlreadyVisible = Boolean(roomId && rooms.rows.some((row) => String(row.id) === roomId || chatRoomMatchesRecipient(row, recipient)));
      setDraftRecipient(roomAlreadyVisible ? "" : recipient);
      setDraftRecipientLabel("");
      setDirectState({ status: "success", message: isRecord(response) && response.created === false ? ui.directDiscussionOpened : ui.directDiscussionStarted });
      formElement.reset();
      setDirectComposerOpen(false);
      rooms.reload();
      messages.reload();
    } catch (error) {
      setDirectState({ status: "error", message: localizedErrorMessage(error, ui.directDiscussionFailed) });
    }
  }

  async function sendMessage(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canUseChat) {
      setMessageState({ status: "permission", message: ui.logInToUseMessages });
      return;
    }
    let roomId = selectedRoomId;
    const body = message.trim();
    if (!body) return;
    if (!roomId && draftRecipient) {
      setMessageState({ status: "saving", message: ui.startingDirectDiscussion });
      try {
        roomId = await createDirectDiscussionFor(draftRecipient);
      } catch (error) {
        setMessageState({ status: "error", message: localizedErrorMessage(error, ui.directDiscussionFailed) });
        return;
      }
    }
    if (!roomId) {
      setMessageState({ status: "error", message: ui.selectConversationFirst });
      return;
    }
    const localId = `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const user = props.auth.user;
    const localMessage: ChatOptimisticMessage = {
      id: localId,
      room_id: roomId,
      sender_id: user?.id ?? "me",
      body,
      status: "sending",
      local_status: "sending",
      created_at: new Date().toISOString(),
      sender_name: user?.display_name ?? user?.username ?? ui.you,
      sender_username: user?.username ?? "",
      mine: 1
    };
    setMessage("");
    window.requestAnimationFrame(() => resizeChatMessageInput());
    setMessageState({ status: "idle", message: "" });
    scrollToBottomRef.current = true;
    setOptimisticMessages((current) => ({
      ...current,
      [roomId]: [...(current[roomId] ?? []), localMessage]
    }));
    try {
      const response = await submitJson(`/api/chat/rooms/${encodeURIComponent(roomId)}/messages`, "POST", { body });
      const serverId = isRecord(response) && response.id ? String(response.id) : "";
      rooms.reload();
      if (serverId) {
        setOptimisticMessages((current) => {
          const roomRows = current[roomId] ?? [];
          return {
            ...current,
            [roomId]: roomRows.map((row) => row.id === localId ? { ...row, server_id: serverId, id: serverId, local_status: undefined, status: "sent" } : row)
          };
        });
      }
    } catch (error) {
      setOptimisticMessages((current) => {
        const roomRows = current[roomId] ?? [];
        return {
          ...current,
          [roomId]: roomRows.map((row) => row.id === localId ? { ...row, local_status: "failed", status: "failed" } : row)
        };
      });
      setMessageState({ status: "error", message: localizedErrorMessage(error, ui.messageFailed) });
    }
  }

  return (
    <section className="contract-page chat-page">
      <ContractHero props={props} />
      <div className={`chat-shell ${mobileConversationOpen ? "has-active-conversation" : ""}`}>
        <aside className="chat-inbox data-panel">
          <header>
            <div>
              <h2>{ui.messages}</h2>
            </div>
            {!routeRecipient && (
              <button className="icon-only chat-compose-button" type="button" aria-label={uiLabel(ui, "newMessage", "New message")} title={uiLabel(ui, "newMessage", "New message")} onClick={() => setDirectComposerOpen((open) => !open)}>
                <PencilLine size={18} />
              </button>
            )}
          </header>
          {!routeRecipient && directComposerOpen && (
            <div className="chat-create-stack">
              <form className="chat-create-form" onSubmit={startDirectDiscussion}>
                <FloatingField label={ui.directDiscussionMember}>
                  <input name="recipient" aria-label={ui.directDiscussionMember} placeholder={ui.directRecipientPlaceholder} defaultValue={draftRecipient} />
                </FloatingField>
                <BaseButton type="submit" variant="outlined" loading={directState.status === "saving"}><UserPlus size={16} /> {ui.startChat}</BaseButton>
                <InlineStatus state={directState} />
              </form>
            </div>
          )}
          {rooms.status === "loading" && <LoadingState label={ui.loadingConversations} />}
          {rooms.status === "permission" && <PermissionPanel title={ui.logInToContinueTitle} body={ui.logInToUseMessages} compact actionLabel={props.dictionary.public.nav.login} />}
          {rooms.status === "error" && <EmptyState icon={LifeBuoy} title={ui.unableToLoadMessages} body={rooms.error ?? ui.retryRequest} action={<BaseButton type="button" variant="outlined" onClick={rooms.reload}>{ui.retry}</BaseButton>} />}
          {rooms.status === "ready" && rooms.rows.length === 0 && !draftRecipient && <EmptyState icon={Inbox} title={ui.noConversationsYet} body={ui.startConversationEmpty} />}
          {rooms.status === "ready" && (rooms.rows.length > 0 || draftRecipient) && (
            <div className="chat-list" aria-label={ui.conversations}>
              {draftRecipient && (
                <button className="chat-list-item active" type="button" onClick={() => { setSelectedRoomId(null); setMobileConversationOpen(true); }}>
                  <span className="chat-avatar"><UserRound size={18} /></span>
                  <span>
                    <strong>{draftRecipientTitle}</strong>
                    <small>{ui.directDiscussion}</small>
                    <em>{ui.writeMessage}</em>
                  </span>
                </button>
              )}
              {rooms.rows.map((row) => <ChatConversationButton row={row} active={String(row.id) === selectedRoomId} ui={ui} key={String(row.id)} onSelect={(id) => { setDraftRecipient(""); setSelectedRoomId(id); setMobileConversationOpen(true); }} />)}
            </div>
          )}
        </aside>
        <section className="conversation">
          <header className="conversation-header">
            <button className="icon-only conversation-back" type="button" aria-label={ui.conversations} onClick={() => { setMobileConversationOpen(false); messageInputRef.current?.blur(); }}><ArrowLeft size={20} /></button>
            <MessageCircle size={22} />
            <div>
              <strong>{conversationTitle}</strong>
              <span>{conversationMeta}</span>
            </div>
          </header>
          <div className="message-list" ref={messageListRef} onScroll={loadOlderMessages}>
            {!selectedRoomId && !draftRecipient && <EmptyState icon={MessageCircle} title={ui.noConversationSelected} body={ui.chooseConversationBody} />}
            {!selectedRoomId && draftRecipient && (canUseChat ? <EmptyState icon={MessageCircle} title={ui.noMessagesYet} body={ui.sendFirstMessage} /> : <PermissionPanel title={ui.logInToContinueTitle} body={ui.logInToUseMessages} compact actionLabel={props.dictionary.public.nav.login} />)}
            {selectedRoomId && messages.status === "loading" && visibleMessageRows.length === 0 && <LoadingState label={ui.loadingMessages} />}
            {selectedRoomId && messages.status === "permission" && <PermissionPanel title={ui.logInToContinueTitle} body={ui.logInToLoadMessages} compact actionLabel={props.dictionary.public.nav.login} />}
            {selectedRoomId && messages.status === "error" && <EmptyState icon={LifeBuoy} title={ui.unableToLoadMessages} body={messages.error ?? ui.retryRequest} action={<BaseButton type="button" variant="outlined" onClick={messages.reload}>{ui.retry}</BaseButton>} />}
            {selectedRoomId && messages.status === "ready" && visibleMessageRows.length === 0 && <EmptyState icon={MessageCircle} title={ui.noMessagesYet} body={ui.sendFirstMessage} />}
            {selectedRoomId && messages.loadingOlder && <div className="message-history-loader">{ui.loadingMessages}</div>}
            {selectedRoomId && visibleMessageRows.map((row) => {
              const localStatus = String(row.local_status ?? "");
              return (
              <article className={`message ${booleanValue(row.mine) ? "mine" : ""} ${localStatus ? `message-${localStatus}` : ""}`} key={String(row.id)}>
                <strong>{messageSender(row, ui)}</strong>
                <p>{displayMessageBody(row.body, ui)}</p>
                <time>{localStatus === "failed" ? ui.messageFailed : timeLabel(row.created_at)}</time>
              </article>
              );
            })}
          </div>
          <form className="message-composer" onSubmit={sendMessage}>
            <FloatingField label={messageComposerPlaceholder} className="message-field">
              <textarea ref={messageInputRef} aria-label={ui.messages} value={message} rows={1} onChange={(event) => { setMessage(event.currentTarget.value); resizeChatMessageInput(event.currentTarget); }} onKeyDown={handleMessageKeyDown} placeholder={messageComposerPlaceholder} disabled={!canWriteMessage || messageState.status === "saving"} />
            </FloatingField>
            <button type="submit" title={ui.send} aria-label={ui.send} disabled={!canWriteMessage || !message.trim() || messageState.status === "saving"}><Send size={18} /></button>
          </form>
          <InlineStatus state={messageState} />
        </section>
      </div>
    </section>
  );
}

function chatRoomIdFromResponse(response: unknown): string {
  if (!isRecord(response)) return "";
  const source = isRecord(response.data) ? response.data : response;
  return String(source.id ?? source.room_id ?? source.roomId ?? "").trim();
}

function normalizedChatRecipient(value: unknown): string {
  return String(value ?? "").trim().toLowerCase().replace(/^@/u, "");
}

function chatDraftRecipientTitle(value: unknown): string {
  const recipient = String(value ?? "").trim().replace(/^@/u, "");
  return recipient ? `@${recipient}` : "";
}

function chatRecipientHref(recipient: string, label: string): string {
  const params = new URLSearchParams({ recipient });
  const cleanLabel = label.trim();
  if (cleanLabel && cleanLabel !== recipient) params.set("recipientName", cleanLabel);
  return `/chat?${params.toString()}`;
}

function chatRoomMatchesRecipient(row: Record<string, unknown>, recipient: string): boolean {
  const target = normalizedChatRecipient(recipient);
  if (!target) return false;
  return [
    row.participant_profile_slug,
    row.participantProfileSlug,
    row.participant_public_id,
    row.participantPublicId,
    row.participant_username,
    row.participant_slug,
    row.participant_email,
    row.participant_user_slug,
    row.profile_slug,
    row.profileSlug,
    row.public_id,
    row.publicId,
    row.username,
    row.email
  ].some((value) => normalizedChatRecipient(value) === target);
}

function chatConversationTitle(row: Record<string, unknown>, ui: I18nDictionary["social"]["ui"]): string {
  const type = String(row.type ?? "direct");
  const explicitTitle = String(row.title ?? "").trim();
  const participants = String(row.participant_names ?? "").trim();
  if (type === "group") return explicitTitle || participants || ui.groupRoom;
  return participants || explicitTitle || ui.directDiscussion;
}

function chatConversationTitleContent(row: Record<string, unknown>, ui: I18nDictionary["social"]["ui"]): ReactNode {
  const title = chatConversationTitle(row, ui);
  const type = String(row.type ?? "direct");
  const profileSlug = firstProfileIdentifier(row.participant_profile_slug, row.participantProfileSlug, row.participant_public_id, row.participantPublicId);
  const username = String(row.participant_username ?? "").trim();
  const userId = String(row.participant_user_id ?? "").trim();
  const publicId = String(row.participant_public_id ?? row.participantPublicId ?? "").trim();
  if (type !== "group" && (profileSlug || publicId || username || userId)) {
    return <ProfileNameLink row={{ profile_slug: profileSlug, public_id: publicId, username, user_id: userId, display_name: row.participant_display_name ?? title }}>{title}</ProfileNameLink>;
  }
  return title;
}

function chatConversationMeta(row: Record<string, unknown>, ui: I18nDictionary["social"]["ui"]): string {
  const type = String(row.type ?? "direct");
  const members = numberValue(row.member_count);
  if (type === "group") return `${members || 1} ${ui.members}`;
  return ui.directDiscussion;
}

function chatConversationPreview(row: Record<string, unknown>, ui: I18nDictionary["social"]["ui"]): string {
  return displayMessageBody(row.last_message, ui) || ui.noMessagesYet;
}

function messageSender(row: Record<string, unknown>, ui: I18nDictionary["social"]["ui"]): ReactNode {
  const senderRow = messageSenderProfileRow(row);
  const label = booleanValue(row.mine) ? ui.you : String(senderRow.display_name ?? senderRow.username ?? ui.member);
  return <ProfileNameLink row={senderRow}>{label}</ProfileNameLink>;
}

function displayMessageBody(value: unknown, ui: I18nDictionary["social"]["ui"]): string {
  const text = String(value ?? "").trim();
  if (!text) return ui.messageRemoved;
  try {
    const parsed = JSON.parse(text) as unknown;
    if (isRecord(parsed) && typeof parsed.message === "string" && parsed.message.trim()) return parsed.message.trim();
  } catch {
    return text;
  }
  return text;
}

function displayListValue(value: unknown): string {
  const text = String(value ?? "").trim();
  if (!text) return "";
  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) return parsed.map((item) => String(item ?? "").trim()).filter(Boolean).join(", ");
  } catch {
  }
  return text;
}

function memberLocationLabel(row: Record<string, unknown>): string {
  const city = firstNonEmptyText(row.current_city, row.currentCity);
  const countryCode = firstNonEmptyText(row.current_country_code, row.currentCountryCode);
  const countryName = firstNonEmptyText(row.current_country, row.currentCountry);
  const country = countryCode ? new Intl.DisplayNames([activeLocaleCode], { type: "region" }).of(countryCode) ?? countryName : countryName;
  return [city, country].filter(Boolean).join(", ") || firstNonEmptyText(row.location_text);
}

function uiLabel(ui: I18nDictionary["social"]["ui"], key: string, fallback: string): string {
  return String(ui[key] ?? "").trim() || fallback;
}

function timeLabel(value: unknown): string {
  const date = new Date(String(value ?? ""));
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(activeTimeLocaleCode(), { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }).format(date);
}

function WalletMembershipView(props: PageProps) {
  const isMembership = props.view.module === "membership";
  const isMoneyForm = props.view.name.includes("Deposit") || props.view.name.includes("Withdraw") || props.view.name.includes("Transfer");
  const endpoint = membershipEndpoint(props.view) ?? (props.view.name.includes("Transactions") ? "/api/wallet" : "/api/wallet/balance");
  const transactions = props.view.name.includes("Transactions");
  const result = useApi(transactions ? null : endpoint);
  const paymentId = String(props.currentQuery.paymentId ?? "");
  const selectedPlanId = String(props.currentQuery.planId ?? props.currentQuery.plan ?? "").trim();
  const visibleResult = useMemo(() => {
    if (props.view.name !== "Membership Checkout" || !selectedPlanId || result.rows.length === 0) return result;
    return { ...result, rows: result.rows.filter((row) => String(row.id ?? "") === selectedPlanId) };
  }, [props.view.name, result, selectedPlanId]);
  return (
    <section className="contract-page">
      <ContractHero props={props} onRefresh={result.reload} />
      {paymentId && props.auth.user && <MemberPaymentStatus {...memberViewProps(props)} paymentId={paymentId} onCompleted={result.reload} />}
      <div className={`wallet-grid ${isMembership ? "membership-workspace" : ""}`}>
        {transactions ? <MemberTransactionList {...memberViewProps(props)} /> : <DataPanel result={visibleResult} title={localizedViewTitle(props.view, props.dictionary)} view={props.view} empty={emptyText(props.view, props.dictionary.social.ui)} renderRow={(row) => isMembership ? <MembershipRow row={row} view={props.view} ui={props.dictionary.social.ui} /> : renderGenericRow(row, props.dictionary.social.ui)} density={isMembership && props.view.name !== "Membership Current" ? "grid" : "list"} emptyActions={!isMembership} hideTitle hideStatus={isMembership} ui={props.dictionary.social.ui} />}
        {props.view.name.includes("Withdraw") ? <MemberWithdrawalForm {...memberViewProps(props)} onRequested={result.reload} /> : isMoneyForm ? <EntityFormView {...props} embedded /> : !isMembership ? <ActionPanel view={props.view} currentUrl={props.currentUrl} dictionary={props.dictionary} /> : null}
      </div>
    </section>
  );
}

function membershipEndpoint(view: ViewCatalogItem): string | null {
  if (view.name === "Membership Current") return "/api/membership/current";
  if (view.name === "Membership Plans" || view.name === "Membership Checkout") return "/api/membership/plans";
  return null;
}

function MembershipRow({ row, view, ui }: { row: Record<string, unknown>; view: ViewCatalogItem; ui: I18nDictionary["social"]["ui"] }) {
  if (view.name === "Membership Current") return <MembershipCurrentCard row={row} ui={ui} />;
  return <MembershipPlanCard row={row} checkout={view.name === "Membership Checkout"} ui={ui} />;
}

function MembershipPlanCard({ row, checkout, ui }: { row: Record<string, unknown>; checkout: boolean; ui: I18nDictionary["social"]["ui"] }) {
  const [state, setState] = useState<SubmitState>({ status: "idle", message: "" });
  const [provider, setProvider] = useState("wallet");
  const requestKeyRef = useRef<{ signature: string; key: string } | null>(null);
  const id = String(row.id ?? "").trim();
  const title = localizedGenericTitle(row.name ?? row.title ?? row.slug ?? ui.membership, ui);
  const description = localizedMachineText(row.description, ui);
  const priceCents = numberValue(row.price_cents ?? row.amount_cents);
  const currency = String(row.currency ?? "EUR");
  const interval = priceCents > 0 ? "" : localizedSystemValue(row.billing_interval ?? row.interval ?? row.period, ui);
  const checkoutHref = `/membership/checkout?planId=${encodeURIComponent(id)}`;
  async function startCheckout() {
    if (!id) {
      setState({ status: "error", message: ui.chooseMembershipPlan });
      return;
    }
    setState({ status: "saving", message: ui.startCheckout });
    try {
      const payload = { planId: id, provider };
      const response = await submitJson("/api/membership/checkout", "POST", payload, persistentIdempotency(requestKeyRef, "membership", payload));
      if (isRecord(response) && typeof response.checkoutUrl === "string") {
        window.location.assign(response.checkoutUrl);
        return;
      }
      if (isRecord(response) && response.id && response.status === "pending") {
        setState({ status: "saving", message: memberCopy(activeLocaleCode).payment_pending });
        navigateMemberHref(`/membership?paymentId=${encodeURIComponent(String(response.id))}`);
        return;
      }
      requestKeyRef.current = null;
      setState({ status: "success", message: ui.membershipCheckoutStarted });
      navigateMemberHref("/membership/current");
    } catch (error) {
      setState({ status: "error", message: localizedErrorMessage(error, ui.submitFailed) });
    }
  }
  return (
    <div className="membership-plan-card">
      <header>
        <span><CreditCard size={17} />{ui.membership}</span>
        <strong>{title}</strong>
      </header>
      {description && <p>{description}</p>}
      <div className="membership-price">
        <strong>{formatMoney(priceCents, currency)}</strong>
        {interval && <span>{interval}</span>}
      </div>
      {priceCents > 0 && <p>{memberCopy(activeLocaleCode).paidMembershipTerms}</p>}
      {checkout ? (
        <>
          {priceCents > 0 && <label><span>{ui.paymentProvider}</span><select value={provider} onChange={(event) => setProvider(event.currentTarget.value)}><option value="wallet">{ui.payWithWallet}</option><option value="stripe">{ui.payWithCard}</option></select></label>}
          <BaseButton type="button" variant="primary" loading={state.status === "saving"} onClick={startCheckout}>{ui.startCheckout}</BaseButton>
        </>
      ) : (
        <a className="base-button primary md" href={checkoutHref} onClick={(event) => handleOpenSNRouteClick(event, checkoutHref)}>{ui.selectPlan}</a>
      )}
      <InlineStatus state={state} />
    </div>
  );
}

function MembershipCurrentCard({ row, ui }: { row: Record<string, unknown>; ui: I18nDictionary["social"]["ui"] }) {
  const status = localizedMachineText(row.status, ui) || ui.ready;
  const plan = localizedGenericTitle(row.plan_name ?? row.plan_id ?? row.planId ?? row.id ?? ui.membership, ui);
  const renewal = row.renews_at ?? row.renewsAt ?? row.ends_at ?? row.endsAt ?? row.created_at ?? row.createdAt;
  return (
    <div className="membership-current-card">
      <header>
        <span><BadgeCheck size={17} />{ui.currentMembership}</span>
        <strong>{plan}</strong>
      </header>
      <div className="membership-current-meta">
        <span>{ui.status}: {status}</span>
        {renewal ? <span>{timeLabel(renewal)}</span> : null}
      </div>
    </div>
  );
}

function OpsView(props: PageProps) {
  const ui = props.dictionary.social.ui;
  const currentPath = adminPath(props.currentUrl);
  const activeSection = opsSections.find((section) => section.href === currentPath);
  const sectionGroups = opsGroupedSections(props.dictionary, props.locale.code);
  const activeGroupKey = activeSection?.group;
  const [expandedGroup, setExpandedGroup] = useState<OpsSectionGroupKey | null>(() => activeGroupKey ?? null);
  const isPhotoReports = currentPath === "/admin/photo-reports";
  const isMediaDefaults = Boolean(activeSection && "kind" in activeSection && activeSection.kind === "media-defaults" && activeSection.endpoint);
  const panels = activeSection?.endpoint ? [{ title: opsSectionLabel(activeSection, props.dictionary), endpoint: activeSection.endpoint }] : opsOverviewPanels.map((panel) => ({ title: ui[panel.titleKey], endpoint: panel.endpoint }));
  useEffect(() => {
    if (!activeGroupKey) return;
    setExpandedGroup(activeGroupKey);
  }, [activeGroupKey]);
  function setGroupExpanded(group: OpsSectionGroupKey, expanded: boolean) {
    setExpandedGroup(expanded ? group : null);
  }
  return (
    <section className="contract-page">
      <ContractHero props={props} />
      <div className="ops-layout">
        <aside className="ops-sidebar" aria-label={ui.operationsSections}>
          <nav className="ops-sidebar-nav" aria-label={ui.operationsSections}>
            {sectionGroups.map((group) => {
              const active = group.key === activeGroupKey;
              const expanded = expandedGroup === group.key;
              return (
                <details className={`ops-nav-group${active ? " active" : ""}`} open={expanded} onToggle={(event) => setGroupExpanded(group.key, event.currentTarget.open)} key={group.key}>
                  <summary>
                    <span>{group.label}</span>
                    <small>{group.sections.length}</small>
                    <ChevronDown size={15} aria-hidden="true" />
                  </summary>
                  <div className="ops-nav-links">
                    {group.sections.map(({ section, label }) => (
                      <a className={currentPath === section.href ? "active" : ""} href={section.href} aria-current={currentPath === section.href ? "page" : undefined} onClick={(event) => handleOpenSNRouteClick(event, section.href)} key={section.href}>
                        {label}
                      </a>
                    ))}
                  </div>
                </details>
              );
            })}
          </nav>
        </aside>
        <div className={`ops-grid ${panels.length === 1 ? "single" : ""}`}>
          {isPhotoReports ? <PhotoReportsPanel props={props} /> : isMediaDefaults && activeSection?.endpoint ? (
            <OpsMediaDefaultsPanel title={opsSectionLabel(activeSection, props.dictionary)} endpoint={activeSection.endpoint} props={props} ui={ui} />
          ) : (
            panels.map(({ title, endpoint }) => <OpsPanel key={endpoint} title={title} endpoint={endpoint} props={props} view={props.view} hideTitle={panels.length === 1} ui={ui} />)
          )}
        </div>
      </div>
    </section>
  );
}

function opsGroupedSections(dictionary: I18nDictionary, localeCode: string): Array<{ key: OpsSectionGroupKey; label: string; sections: Array<{ section: (typeof opsSections)[number]; label: string }> }> {
  const collator = new Intl.Collator(localeCode || undefined, { sensitivity: "base" });
  return opsSectionGroupOrder
    .map((group) => ({
      key: group,
      label: opsSectionGroupLabel(group, dictionary),
      sections: opsSections
        .filter((section) => section.group === group)
        .map((section) => ({ section, label: opsSectionLabel(section, dictionary) }))
        .sort((first, second) => collator.compare(first.label, second.label))
    }))
    .filter((group) => group.sections.length > 0)
    .sort((first, second) => collator.compare(first.label, second.label));
}

function opsSectionGroupLabel(group: OpsSectionGroupKey, dictionary: I18nDictionary): string {
  const key = `opsGroup${titleCase(group).replace(/\s/gu, "")}`;
  return opsCapitalizedLabel(dictionary.social.ui[key] ?? titleCase(group));
}

function opsSectionLabel(section: (typeof opsSections)[number], dictionary: I18nDictionary): string {
  if ("labelKey" in section && section.labelKey === "photoReports") return getCommunityMessages(activeLocaleCode).photoReports;
  const label = "moduleKey" in section && section.moduleKey ? dictionary.social.modules[section.moduleKey] ?? section.moduleKey : dictionary.social.ui[section.labelKey] ?? section.labelKey;
  return opsCapitalizedLabel(label);
}

function opsCapitalizedLabel(value: string): string {
  const label = String(value ?? "").trim();
  return label ? `${label.charAt(0).toLocaleUpperCase()}${label.slice(1)}` : label;
}

function adminPath(currentUrl: string): string {
  const path = currentUrl.replace(/\/$/, "") || "/admin";
  if (path === "/ops") return "/admin";
  if (path.startsWith("/ops/")) return `/admin${path.slice("/ops".length)}`;
  return path;
}

function OpsPanel({ title, endpoint, props, view, ui, hideTitle = false }: { title: string; endpoint: string; props: PageProps; view: ViewCatalogItem; ui: I18nDictionary["social"]["ui"]; hideTitle?: boolean }) {
  const hasListControls = opsEndpointHasListControls(endpoint);
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageDraft, setPageDraft] = useState("1");
  const [perPage, setPerPage] = useState(25);
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDirection, setSortDirection] = useState<OpsSortDirection>("desc");
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [settingsReady, setSettingsReady] = useState(false);
  const [drawer, setDrawer] = useState<OpsDrawerState>(null);
  useEffect(() => {
    setSearchDraft("");
    setSearch("");
    setPage(1);
    setPageDraft("1");
    setPerPage(25);
    setSortKey("created_at");
    setSortDirection("desc");
    setColumnWidths({});
    setSettingsReady(!hasListControls);
    setDrawer(null);
  }, [endpoint]);
  useEffect(() => {
    if (!hasListControls) return;
    const timer = window.setTimeout(() => setSearch(searchDraft.trim()), 180);
    return () => window.clearTimeout(timer);
  }, [hasListControls, searchDraft]);
  useEffect(() => {
    setPage(1);
    setPageDraft("1");
  }, [search, perPage]);
  useEffect(() => {
    setPageDraft(String(page));
  }, [page]);
  useEffect(() => {
    let alive = true;
    if (!hasListControls) return;
    setSettingsReady(false);
    fetch(opsTableSettingsEndpoint(endpoint), { credentials: "include", headers: apiHeaders() })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!alive) return;
        if (response.ok) {
          const settings = opsTableSettingsFromPayload(data);
          if (settings.perPage) setPerPage(settings.perPage);
          if (settings.sortKey) setSortKey(settings.sortKey);
          if (settings.sortDirection) setSortDirection(settings.sortDirection);
          if (settings.columnWidths) setColumnWidths(settings.columnWidths);
        }
        setSettingsReady(true);
      })
      .catch(() => {
        if (alive) setSettingsReady(true);
      });
    return () => {
      alive = false;
    };
  }, [endpoint, hasListControls]);
  const settingsKey = JSON.stringify({ columnWidths, sortKey, sortDirection, perPage });
  useEffect(() => {
    if (!hasListControls || !settingsReady) return;
    const timer = window.setTimeout(() => {
      submitJson("/api/ops/table-settings", "PATCH", {
        view: endpoint,
        settings: { columnWidths, sortKey, sortDirection, perPage }
      }).catch(() => undefined);
    }, 360);
    return () => window.clearTimeout(timer);
  }, [columnWidths, endpoint, hasListControls, perPage, settingsKey, settingsReady, sortDirection, sortKey]);
  const listEndpoint = hasListControls ? opsEndpointWithParams(endpoint, { search, page, perPage, sortKey, sortDirection }) : endpoint;
  const result = useApi(listEndpoint, initialApiData(props, listEndpoint));
  const columns = opsTableColumns(endpoint, ui, result.rows).map((column) => ({ ...column, width: columnWidths[column.key] ?? column.width }));
  const pageCount = result.total && result.perPage ? Math.max(1, Math.ceil(result.total / result.perPage)) : null;
  const canPrevious = hasListControls && page > 1 && result.status !== "loading";
  const canNext = hasListControls && result.status !== "loading" && (pageCount ? page < pageCount : Boolean(result.nextCursor));
  function submitPageJump(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const next = Number(pageDraft);
    if (!Number.isFinite(next)) return;
    const max = pageCount ?? 100000;
    setPage(Math.max(1, Math.min(max, Math.floor(next))));
  }
  function toggleSort(column: OpsTableColumn) {
    if (!column.sortable) return;
    setPage(1);
    setSortDirection((current) => column.key === sortKey && current === "asc" ? "desc" : "asc");
    setSortKey(column.key);
  }
  function resizeColumn(event: ReactMouseEvent<HTMLSpanElement>, column: OpsTableColumn) {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = column.width;
    const minWidth = column.minWidth ?? 96;
    const handleMove = (moveEvent: MouseEvent) => {
      const nextWidth = Math.max(minWidth, Math.min(640, startWidth + moveEvent.clientX - startX));
      setColumnWidths((current) => ({ ...current, [column.key]: nextWidth }));
    };
    const handleUp = () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
  }
  if (hasListControls) {
    return (
      <div className="ops-panel-stack">
        <div className="ops-toolbar">
          <label className="ops-search-field">
            <Search size={16} aria-hidden="true" />
            <input value={searchDraft} onChange={(event) => setSearchDraft(event.currentTarget.value)} placeholder={ui.adminSearchPlaceholder} aria-label={ui.search} />
          </label>
          <label className="ops-page-size">
            <span>{ui.itemsPerPage}</span>
            <select value={perPage} onChange={(event) => setPerPage(Number(event.currentTarget.value))}>
              {[10, 25, 50, 100].map((option) => <option value={option} key={option}>{option}</option>)}
            </select>
          </label>
          {opsCanCreate(endpoint) && <BaseButton type="button" variant="primary" size="sm" onClick={() => setDrawer({ mode: "create", row: {} })}><Plus size={15} />{ui.create}</BaseButton>}
          <BaseButton type="button" variant="outlined" size="sm" onClick={result.reload}><RefreshCcw size={15} />{ui.refresh}</BaseButton>
        </div>
        <OpsAdminTable
          columns={columns}
          endpoint={endpoint}
          result={result}
          sortKey={sortKey}
          sortDirection={sortDirection}
          title={title}
          ui={ui}
          onEdit={(row) => setDrawer({ mode: "edit", row })}
          onResizeColumn={resizeColumn}
          onSort={toggleSort}
        />
        <footer className="ops-pagination">
          <span>{opsPageSummary(result, page, perPage, ui)}</span>
          <form onSubmit={submitPageJump}>
            <label>
              <span>{ui.page}</span>
              <input value={pageDraft} inputMode="numeric" onChange={(event) => setPageDraft(event.currentTarget.value.replace(/\D+/gu, ""))} aria-label={ui.page} />
            </label>
            <BaseButton type="button" variant="outlined" size="sm" disabled={!canPrevious} onClick={() => setPage((value) => Math.max(1, value - 1))}>{ui.previousPage}</BaseButton>
            <BaseButton type="submit" variant="outlined" size="sm">{ui.open}</BaseButton>
            <BaseButton type="button" variant="outlined" size="sm" disabled={!canNext} onClick={() => setPage((value) => value + 1)}>{ui.nextPage}</BaseButton>
          </form>
        </footer>
        {drawer && <OpsAdminDrawer drawer={drawer} endpoint={endpoint} columns={columns} ui={ui} onClose={() => setDrawer(null)} onSaved={() => { setDrawer(null); result.reload(); }} />}
      </div>
    );
  }
  return (
    <div className="ops-panel-stack">
      <DataPanel result={result} title={title} view={view} empty={ui.opsEmpty} renderRow={endpoint.endsWith("/queues") ? (row) => renderQueueRow(row, ui) : (row) => renderOpsRow(row, endpoint, ui, result.reload)} compact hideTitle={hideTitle} ui={ui} />
    </div>
  );
}

function opsEndpointHasListControls(endpoint: string): boolean {
  return endpoint.startsWith("/api/ops/") && !endpoint.endsWith("/media-defaults");
}

function opsEndpointWithParams(endpoint: string, options: { search: string; page: number; perPage: number; sortKey: string; sortDirection: OpsSortDirection }): string {
  const params = new URLSearchParams();
  if (options.search) params.set("q", options.search);
  params.set("page", String(options.page));
  params.set("per_page", String(options.perPage));
  if (options.sortKey) {
    params.set("sort", options.sortKey);
    params.set("order", options.sortDirection);
  }
  return `${endpoint}?${params.toString()}`;
}

function opsTableSettingsEndpoint(endpoint: string): string {
  return `/api/ops/table-settings?view=${encodeURIComponent(endpoint)}`;
}

function opsTableSettingsFromPayload(data: unknown): OpsTableSettings {
  const source = opsTableSettingsSource(data);
  const sortDirection = source.sortDirection === "asc" || source.sortDirection === "desc" ? source.sortDirection : undefined;
  const perPage = numericApiValue(source.perPage ?? source.per_page);
  return {
    columnWidths: recordOfNumbers(source.columnWidths ?? source.column_widths),
    sortKey: typeof source.sortKey === "string" ? source.sortKey : typeof source.sort_key === "string" ? source.sort_key : undefined,
    sortDirection,
    perPage: perPage && [10, 25, 50, 100].includes(perPage) ? perPage : undefined
  };
}

function opsTableSettingsSource(data: unknown): Record<string, unknown> {
  const row = isRecord(data) && isRecord(data.data) ? data.data : data;
  if (!isRecord(row)) return {};
  if (isRecord(row.settings)) return row.settings;
  const settingsJson = recordFromJson(row.settings_json ?? row.settingsJson);
  return Object.keys(settingsJson).length ? settingsJson : row;
}

function recordOfNumbers(value: unknown): Record<string, number> | undefined {
  if (!isRecord(value)) return undefined;
  const entries = Object.entries(value)
    .map(([key, item]) => [key, numericApiValue(item)] as const)
    .filter((entry): entry is readonly [string, number] => typeof entry[1] === "number" && entry[1] >= 72);
  return entries.length ? Object.fromEntries(entries) : undefined;
}

function opsCanCreate(endpoint: string): boolean {
  return /\/api\/ops\/(support-tickets|reports|users|follows|posts|comments|replies|likes|shares|hashtags|categories|stories|media|clips|articles|documents|groups|events|pages|ads|notifications|messages|chat-rooms|invites|saved-items|openid-providers|languages|roles)$/u.test(endpoint);
}

function opsTableColumns(endpoint: string, ui: I18nDictionary["social"]["ui"], rows: Array<Record<string, unknown>>): OpsTableColumn[] {
  const statusColumn: OpsTableColumn = { key: "status", label: ui.status, width: 134, sortable: true, editable: true, input: "select", options: opsStatusOptions(endpoint, rows[0] ?? {}) };
  if (endpoint.endsWith("/users")) return [
    { key: "display_name", label: ui.displayName, width: 220, sortable: true, editable: true },
    { key: "username", label: ui.username, width: 160, sortable: true, editable: true },
    { key: "email", label: ui.email, width: 240, sortable: true, editable: true },
    { key: "role", label: ui.roles, width: 150, sortable: true, editable: true, input: "select", options: opsRoleOptions(String(rows[0]?.role ?? "")) },
    statusColumn,
    { key: "created_at", label: uiLabel(ui, "createdAt", "Created"), width: 172, sortable: true, input: "datetime" }
  ];
  if (endpoint.endsWith("/follows")) return [
    { key: "follower_username", label: ui.profile, width: 190, sortable: true },
    { key: "target_username", label: uiLabel(ui, "target", "Target"), width: 190, sortable: true },
    { key: "relationship_type", label: ui.type, width: 150, sortable: true, editable: true, input: "select", options: ["friend", "follow"] },
    statusColumn,
    { key: "created_at", label: uiLabel(ui, "createdAt", "Created"), width: 172, sortable: true, input: "datetime" }
  ];
  if (endpoint.endsWith("/posts")) return [
    { key: "body", label: ui.post, width: 420, sortable: true, editable: true, input: "textarea" },
    { key: "display_name", label: ui.profile, width: 190, sortable: true },
    statusColumn,
    { key: "visibility", label: ui.visibility, width: 130, sortable: true, editable: true, input: "select", options: ["public", "friends", "private"] },
    { key: "comments_count", label: ui.comments, width: 112, sortable: true, input: "number" },
    { key: "likes_count", label: ui.likes, width: 92, sortable: true, input: "number" },
    { key: "reports_count", label: ui.report, width: 104, sortable: true, input: "number" },
    { key: "created_at", label: uiLabel(ui, "createdAt", "Created"), width: 172, sortable: true, input: "datetime" }
  ];
  if (endpoint.endsWith("/comments") || endpoint.endsWith("/replies")) return [
    { key: "body", label: endpoint.endsWith("/replies") ? uiLabel(ui, "reply", "Reply") : ui.comment, width: 420, sortable: true, editable: true, input: "textarea" },
    { key: "display_name", label: ui.profile, width: 190, sortable: true },
    { key: "post_id", label: ui.post, width: 180, sortable: true },
    statusColumn,
    { key: "likes_count", label: ui.likes, width: 92, sortable: true, input: "number" },
    { key: "reports_count", label: ui.report, width: 104, sortable: true, input: "number" },
    { key: "created_at", label: uiLabel(ui, "createdAt", "Created"), width: 172, sortable: true, input: "datetime" }
  ];
  if (endpoint.endsWith("/likes")) return [
    { key: "subject_type", label: uiLabel(ui, "subjectType", "Subject type"), width: 150, sortable: true, editable: true, input: "select", options: ["post", "comment", "reply"] },
    { key: "subject_id", label: uiLabel(ui, "subjectId", "Subject ID"), width: 220, sortable: true, editable: true },
    { key: "reaction", label: ui.like, width: 132, sortable: true, editable: true, input: "select", options: ["like", "love", "haha", "wow", "sad", "angry"] },
    { key: "username", label: ui.profile, width: 170, sortable: true },
    { key: "created_at", label: uiLabel(ui, "createdAt", "Created"), width: 172, sortable: true, input: "datetime" }
  ];
  if (endpoint.endsWith("/shares")) return [
    { key: "subject_type", label: uiLabel(ui, "subjectType", "Subject type"), width: 150, sortable: true, editable: true, input: "select", options: ["post", "article", "event", "group", "page", "document"] },
    { key: "subject_id", label: uiLabel(ui, "subjectId", "Subject ID"), width: 220, sortable: true, editable: true },
    { key: "username", label: ui.profile, width: 170, sortable: true },
    { key: "created_at", label: uiLabel(ui, "createdAt", "Created"), width: 172, sortable: true, input: "datetime" }
  ];
  if (endpoint.endsWith("/hashtags")) return [
    { key: "tag", label: ui.hashtags, width: 220, sortable: true, editable: true },
    { key: "normalized_tag", label: ui.slug, width: 180, sortable: true, editable: true },
    statusColumn,
    { key: "posts_count", label: ui.posts, width: 112, sortable: true, input: "number" },
    { key: "followers_count", label: ui.followers, width: 124, sortable: true, input: "number" },
    { key: "created_at", label: uiLabel(ui, "createdAt", "Created"), width: 172, sortable: true, input: "datetime" }
  ];
  if (endpoint.endsWith("/categories")) return [
    { key: "label", label: ui.name, width: 240, sortable: true, editable: true },
    { key: "slug", label: ui.slug, width: 180, sortable: true, editable: true },
    { key: "type", label: ui.type, width: 150, sortable: true, editable: true, input: "select", options: ["group", "event", "page", "report", "content_warning"] },
    { key: "description", label: ui.description, width: 320, sortable: true, editable: true, input: "textarea" },
    statusColumn,
    { key: "sort_order", label: ui.sortOrder, width: 118, sortable: true, editable: true, input: "number" },
    { key: "created_at", label: uiLabel(ui, "createdAt", "Created"), width: 172, sortable: true, input: "datetime" }
  ];
  if (endpoint.endsWith("/stories")) return [
    { key: "body", label: ui.stories, width: 320, sortable: true, editable: true, input: "textarea" },
    { key: "display_name", label: ui.profile, width: 190, sortable: true },
    { key: "type", label: ui.type, width: 124, sortable: true, editable: true, input: "select", options: ["text", "photo", "video"] },
    statusColumn,
    { key: "expires_at", label: uiLabel(ui, "expiresAt", "Expires"), width: 172, sortable: true, editable: true, input: "datetime" },
    { key: "created_at", label: uiLabel(ui, "createdAt", "Created"), width: 172, sortable: true, input: "datetime" }
  ];
  if (endpoint.endsWith("/media")) return [
    { key: "filename", label: ui.uploadedFileId, width: 240, sortable: true, editable: true },
    { key: "content_type", label: ui.contentType, width: 170, sortable: true, editable: true },
    { key: "owner_username", label: ui.profile, width: 170, sortable: true },
    { key: "asset_url", label: ui.media, width: 340, sortable: true, editable: true },
    statusColumn,
    { key: "created_at", label: uiLabel(ui, "createdAt", "Created"), width: 172, sortable: true, input: "datetime" }
  ];
  if (endpoint.endsWith("/clips")) return [
    { key: "title", label: ui.clips, width: 260, sortable: true, editable: true },
    { key: "caption", label: ui.caption, width: 360, sortable: true, editable: true, input: "textarea" },
    { key: "display_name", label: ui.profile, width: 190, sortable: true },
    statusColumn,
    { key: "created_at", label: uiLabel(ui, "createdAt", "Created"), width: 172, sortable: true, input: "datetime" }
  ];
  if (endpoint.endsWith("/articles") || endpoint.endsWith("/documents")) return [
    { key: "title", label: endpoint.endsWith("/documents") ? ui.documents : ui.articles, width: 280, sortable: true, editable: true },
    { key: "slug", label: ui.slug, width: 180, sortable: true, editable: true },
    { key: "excerpt", label: ui.excerpt, width: 340, sortable: true, editable: true, input: "textarea" },
    { key: "display_name", label: ui.profile, width: 190, sortable: true },
    statusColumn,
    { key: "published_at", label: ui.published, width: 172, sortable: true, input: "datetime" },
    { key: "created_at", label: uiLabel(ui, "createdAt", "Created"), width: 172, sortable: true, input: "datetime" }
  ];
  if (endpoint.endsWith("/groups") || endpoint.endsWith("/pages")) return [
    { key: "name", label: ui.name, width: 240, sortable: true, editable: true },
    { key: "slug", label: ui.slug, width: 180, sortable: true, editable: true },
    { key: "description", label: ui.description, width: 360, sortable: true, editable: true, input: "textarea" },
    { key: "privacy", label: ui.privacy, width: 132, sortable: true, editable: true, input: "select", options: ["public", "private"] },
    statusColumn,
    { key: "members_count", label: ui.members, width: 118, sortable: true, input: "number" },
    { key: "created_at", label: uiLabel(ui, "createdAt", "Created"), width: 172, sortable: true, input: "datetime" }
  ];
  if (endpoint.endsWith("/events")) return [
    { key: "title", label: ui.title, width: 260, sortable: true, editable: true },
    { key: "format", label: ui.type, width: 132, sortable: true, editable: true, input: "select", options: ["online", "in_person"] },
    { key: "starts_at", label: ui.start, width: 172, sortable: true, editable: true, input: "datetime" },
    { key: "visibility", label: ui.visibility, width: 132, sortable: true, editable: true, input: "select", options: ["public", "friends", "private"] },
    statusColumn,
    { key: "going_count", label: uiLabel(ui, "going", "Going"), width: 106, sortable: true, input: "number" },
    { key: "created_at", label: uiLabel(ui, "createdAt", "Created"), width: 172, sortable: true, input: "datetime" }
  ];
  if (endpoint.endsWith("/ads")) return [
    { key: "campaign_name", label: ui.campaignName, width: 260, sortable: true, editable: true },
    { key: "objective", label: ui.objective, width: 140, sortable: true, editable: true, input: "select", options: ["traffic", "reach", "message"] },
    { key: "budget_eur", label: ui.budgetEur, width: 128, sortable: true, editable: true, input: "number" },
    statusColumn,
    { key: "impressions", label: ui.impressions, width: 112, sortable: true, input: "number" },
    { key: "clicks", label: ui.clicks, width: 92, sortable: true, input: "number" },
    { key: "ctr", label: ui.ctr, width: 92, sortable: true },
    { key: "spent_cents", valueKeys: ["reported_spend_cents", "spent_cents"], label: ui.spent, width: 128, sortable: true, input: "number" },
    { key: "created_at", label: uiLabel(ui, "createdAt", "Created"), width: 172, sortable: true, input: "datetime" }
  ];
  if (endpoint.endsWith("/notifications")) return [
    { key: "type", label: ui.type, width: 220, sortable: true, editable: true },
    { key: "title", label: ui.title, width: 260, sortable: true, editable: true },
    { key: "body", label: ui.message, width: 360, sortable: true, editable: true, input: "textarea" },
    { key: "recipient_username", label: ui.recipientId, width: 180, sortable: true },
    { key: "read_at", label: ui.status, width: 172, sortable: true, input: "datetime" },
    { key: "created_at", label: uiLabel(ui, "createdAt", "Created"), width: 172, sortable: true, input: "datetime" }
  ];
  if (endpoint.endsWith("/messages")) return [
    { key: "body", label: ui.message, width: 420, sortable: true, editable: true, input: "textarea" },
    { key: "sender_username", label: ui.profile, width: 170, sortable: true },
    { key: "receiver_username", label: ui.recipientId, width: 170, sortable: true },
    statusColumn,
    { key: "created_at", label: uiLabel(ui, "createdAt", "Created"), width: 172, sortable: true, input: "datetime" }
  ];
  if (endpoint.endsWith("/chat-rooms")) return [
    { key: "title", label: ui.conversations, width: 260, sortable: true, editable: true },
    { key: "type", label: ui.type, width: 132, sortable: true, editable: true, input: "select", options: ["direct", "group"] },
    { key: "participants_count", label: ui.groupRoomMembers, width: 150, sortable: true, input: "number" },
    statusColumn,
    { key: "updated_at", label: uiLabel(ui, "updatedAt", "Updated"), width: 172, sortable: true, input: "datetime" }
  ];
  if (endpoint.endsWith("/invites")) return [
    { key: "email", label: ui.email, width: 240, sortable: true, editable: true },
    { key: "phone", label: ui.phone, width: 160, sortable: true, editable: true },
    { key: "sender_username", label: ui.profile, width: 170, sortable: true },
    statusColumn,
    { key: "created_at", label: uiLabel(ui, "createdAt", "Created"), width: 172, sortable: true, input: "datetime" }
  ];
  if (endpoint.endsWith("/saved-items")) return [
    { key: "subject_type", label: ui.type, width: 150, sortable: true, editable: true },
    { key: "subject_id", label: ui.methodId, width: 220, sortable: true, editable: true },
    { key: "collection", label: ui.savedItems, width: 220, sortable: true, editable: true },
    { key: "username", label: ui.profile, width: 170, sortable: true },
    { key: "created_at", label: uiLabel(ui, "createdAt", "Created"), width: 172, sortable: true, input: "datetime" }
  ];
  if (endpoint.endsWith("/reports")) return [
    { key: "title", label: ui.report, width: 220, sortable: true },
    { key: "subject_type", label: uiLabel(ui, "subjectType", "Subject type"), width: 150, sortable: true, editable: true, input: "select", options: ["post", "comment", "reply", "user", "group", "event", "page", "ad"] },
    { key: "subject_id", label: uiLabel(ui, "subjectId", "Subject ID"), width: 220, sortable: true, editable: true },
    { key: "reason", label: uiLabel(ui, "reason", "Reason"), width: 320, sortable: true, editable: true, input: "textarea" },
    statusColumn,
    { key: "reporter_username", label: ui.profile, width: 170, sortable: true },
    { key: "created_at", label: uiLabel(ui, "createdAt", "Created"), width: 172, sortable: true, input: "datetime" }
  ];
  if (endpoint.endsWith("/support-tickets")) return [
    { key: "subject", label: ui.subject, width: 260, sortable: true, editable: true },
    { key: "category", label: ui.section, width: 170, sortable: true, editable: true },
    { key: "body", label: ui.message, width: 360, sortable: true, editable: true, input: "textarea" },
    statusColumn,
    { key: "username", label: ui.profile, width: 170, sortable: true },
    { key: "created_at", label: uiLabel(ui, "createdAt", "Created"), width: 172, sortable: true, input: "datetime" }
  ];
  return opsGenericColumns(rows, ui);
}

function opsGenericColumns(rows: Array<Record<string, unknown>>, ui: I18nDictionary["social"]["ui"]): OpsTableColumn[] {
  const preferred = ["title", "name", "module", "type", "status", "queue_name", "created_at", "updated_at", "id"];
  const row = rows[0] ?? {};
  const keys = preferred.filter((key) => key in row);
  const fallbackKeys = Object.keys(row).filter((key) => !keys.includes(key)).slice(0, Math.max(0, 6 - keys.length));
  const selected = [...keys, ...fallbackKeys].slice(0, 8);
  return (selected.length ? selected : ["id", "status"]).map((key) => ({
    key,
    label: opsColumnLabel(key, ui),
    width: key.includes("description") || key.includes("body") || key.includes("message") ? 340 : 160,
    sortable: true,
    editable: ["title", "name", "status", "description", "body", "message"].includes(key),
    input: key.includes("description") || key.includes("body") || key.includes("message") ? "textarea" : key === "status" ? "select" : "text",
    options: key === "status" ? ["active", "disabled", "open", "resolved", "published", "draft"] : undefined
  }));
}

function opsColumnLabel(key: string, ui: I18nDictionary["social"]["ui"]): string {
  const normalized = key.replace(/_id$/u, "").replace(/_count$/u, "").replace(/_/gu, "");
  const direct = getCommunityMessages(activeLocaleCode)[`column_${key}`] ?? ui[key] ?? ui[toCamelCase(key)] ?? ui[normalized];
  if (direct) return direct;
  return titleCase(key.replace(/_/gu, " "));
}

function opsCellText(row: Record<string, unknown>, column: OpsTableColumn, ui: I18nDictionary["social"]["ui"]): string {
  if (column.key === "ctr") {
    const impressions = numericApiValue(row.impressions);
    const clicks = numericApiValue(row.clicks);
    if (impressions && impressions > 0 && typeof clicks === "number") return `${((clicks / impressions) * 100).toFixed(1)}%`;
  }
  const value = opsCellValue(row, column);
  if (column.input === "datetime") return value ? postAbsoluteTimeLabel(String(value)) : "";
  if (column.key.endsWith("_cents")) {
    const cents = numericApiValue(value);
    return typeof cents === "number" ? formatMoney(cents, String(row.currency ?? "EUR")) : "";
  }
  if (column.input === "number") return String(numericApiValue(value) ?? value ?? "");
  if (column.input === "select" || ["status", "role", "type", "module", "kind", "enabled"].includes(column.key)) return localizedSystemValue(value, ui);
  return compactText(value, column.input === "textarea" ? 220 : 120);
}

function opsCellValue(row: Record<string, unknown>, column: OpsTableColumn): unknown {
  const baseKeys = [column.key, toCamelCase(column.key)];
  const aliasKeys = column.valueKeys ? [...column.valueKeys, ...column.valueKeys.map(toCamelCase)] : [];
  const keys = Array.from(new Set(column.valueKeys ? [...aliasKeys, ...baseKeys] : baseKeys));
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
}

function toCamelCase(value: string): string {
  return value.replace(/_([a-z])/gu, (_, letter: string) => letter.toUpperCase());
}

function OpsAdminTable({ columns, endpoint, result, sortKey, sortDirection, title, ui, onEdit, onResizeColumn, onSort }: { columns: OpsTableColumn[]; endpoint: string; result: ApiResult; sortKey: string; sortDirection: OpsSortDirection; title: string; ui: I18nDictionary["social"]["ui"]; onEdit: (row: Record<string, unknown>) => void; onResizeColumn: (event: ReactMouseEvent<HTMLSpanElement>, column: OpsTableColumn) => void; onSort: (column: OpsTableColumn) => void }) {
  const hasRows = result.rows.length > 0;
  const editable = opsEndpointEditable(endpoint, columns);
  return (
    <section className="data-panel ops-table-panel">
      <header>
        <h2>{title}</h2>
        {result.status === "loading" && hasRows ? <span className="ops-table-sync">{ui.loadingResults}</span> : null}
      </header>
      {result.status === "loading" && !hasRows && <LoadingState label={ui.loadingResults} />}
      {result.status === "permission" && <PermissionPanel title={ui.logInToContinueTitle} body={ui.logInToContinueBody} compact actionLabel={ui.logIn} />}
      {result.status === "error" && <EmptyState icon={LifeBuoy} title={ui.unableToLoadView} body={displayErrorMessage(result.error, ui.retry)} showBody action={<BaseButton type="button" variant="outlined" onClick={result.reload}>{ui.retry}</BaseButton>} />}
      {result.status === "ready" && !hasRows && <EmptyState icon={Inbox} title={ui.opsEmpty} />}
      {hasRows && (
        <div className="ops-table-scroll">
          <table className="ops-table">
            <colgroup>
              {columns.map((column) => <col style={{ width: column.width }} key={column.key} />)}
              <col style={{ width: editable ? 92 : 0 }} />
            </colgroup>
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.key} style={{ width: column.width }}>
                    <button type="button" className={column.sortable ? "ops-sort-button" : "ops-sort-button disabled"} disabled={!column.sortable} onClick={() => onSort(column)}>
                      <span>{column.label}</span>
                      {sortKey === column.key && <span aria-hidden="true">{sortDirection === "asc" ? "↑" : "↓"}</span>}
                    </button>
                    <span className="ops-column-resize" role="separator" aria-orientation="vertical" onMouseDown={(event) => onResizeColumn(event, column)} />
                  </th>
                ))}
                {editable && <th className="ops-table-actions">{ui.editItem}</th>}
              </tr>
            </thead>
            <tbody>
              {result.rows.map((row, index) => (
                <tr key={String(row.id ?? row.key ?? index)}>
                  {columns.map((column) => <td key={column.key}>{opsCellText(row, column, ui)}</td>)}
                  {editable && (
                    <td className="ops-table-actions">
                      <button className="icon-button" type="button" title={ui.editItem} aria-label={ui.editItem} onClick={() => onEdit(row)}><PencilLine size={16} /></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function opsEndpointEditable(endpoint: string, columns: OpsTableColumn[]): boolean {
  if (/\/api\/ops\/(jobs|dead-letters|queues|crons|outbox|webhooks|locks|media-processing|data-exports|account-deletes)$/u.test(endpoint)) return false;
  return columns.some((column) => column.editable);
}

function OpsAdminDrawer({ drawer, endpoint, columns, ui, onClose, onSaved }: { drawer: NonNullable<OpsDrawerState>; endpoint: string; columns: OpsTableColumn[]; ui: I18nDictionary["social"]["ui"]; onClose: () => void; onSaved: () => void }) {
  const drawerRef = useRef<HTMLElement | null>(null);
  const fields = opsDrawerFields(endpoint, columns, drawer.row, drawer.mode, ui);
  const [values, setValues] = useState<Record<string, string>>(() => opsDrawerInitialValues(fields, drawer.row));
  const [state, setState] = useState<SubmitState>({ status: "idle", message: "" });
  useEffect(() => {
    setValues(opsDrawerInitialValues(fields, drawer.row));
    setState({ status: "idle", message: "" });
  }, [drawer.mode, drawer.row, endpoint]);
  useEffect(() => {
    window.requestAnimationFrame(() => drawerRef.current?.focus());
  }, []);
  function handleKeyDown(event: ReactKeyboardEvent<HTMLElement>) {
    if (event.key === "Escape" && state.status !== "saving") {
      event.preventDefault();
      onClose();
      return;
    }
    trapFocusTab(event, drawerRef.current);
  }
  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const id = String(drawer.row.id ?? drawer.row.key ?? "").trim();
    if (drawer.mode === "edit" && !id) {
      setState({ status: "error", message: ui.actionFailed });
      return;
    }
    setState({ status: "saving", message: ui.save });
    try {
      const payload = Object.fromEntries(fields.map((field) => [field.key, opsDrawerPayloadValue(field, values[field.key])]));
      const target = drawer.mode === "edit" ? `${endpoint}/${encodeURIComponent(id)}` : endpoint;
      await submitJson(target, drawer.mode === "edit" ? "PATCH" : "POST", payload);
      setState({ status: "success", message: ui.saved });
      onSaved();
    } catch (error) {
      setState({ status: "error", message: localizedErrorMessage(error, ui.actionFailed) });
    }
  }
  return (
    <div className="ops-drawer-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && state.status !== "saving") onClose(); }}>
      <aside className="ops-drawer" role="dialog" aria-modal="true" aria-label={drawer.mode === "create" ? ui.create : ui.editItem} tabIndex={-1} ref={drawerRef} onKeyDown={handleKeyDown}>
        <header>
          <div>
            <strong>{drawer.mode === "create" ? ui.create : ui.editItem}</strong>
            <p>{opsDrawerTitle(drawer.row, ui)}</p>
          </div>
          <button className="icon-only" type="button" aria-label={ui.cancel} disabled={state.status === "saving"} onClick={onClose}><X size={18} /></button>
        </header>
        <form onSubmit={save}>
          {fields.map((field) => (
            <label key={field.key}>
              <span>{field.label}</span>
              {field.input === "textarea" ? (
                <textarea value={values[field.key] ?? ""} onChange={(event) => setValues((current) => ({ ...current, [field.key]: event.currentTarget.value }))} />
              ) : field.input === "select" ? (
                <select value={values[field.key] ?? ""} onChange={(event) => setValues((current) => ({ ...current, [field.key]: event.currentTarget.value }))}>
                  {opsDrawerSelectOptions(field, values[field.key]).map((option) => <option value={option} key={option}>{localizedSystemValue(option, ui)}</option>)}
                </select>
              ) : (
                <input type={field.input === "number" ? "number" : "text"} value={values[field.key] ?? ""} onChange={(event) => setValues((current) => ({ ...current, [field.key]: event.currentTarget.value }))} />
              )}
            </label>
          ))}
          <div className="form-actions">
            <BaseButton type="submit" variant="primary" loading={state.status === "saving"}>{ui.save}</BaseButton>
            <BaseButton type="button" variant="outlined" disabled={state.status === "saving"} onClick={onClose}>{ui.cancel}</BaseButton>
          </div>
          <InlineStatus state={state} />
        </form>
      </aside>
    </div>
  );
}

function opsDrawerFields(endpoint: string, columns: OpsTableColumn[], row: Record<string, unknown>, mode: "create" | "edit", ui: I18nDictionary["social"]["ui"]): OpsTableColumn[] {
  if (mode === "create") return opsCreateFields(endpoint, ui);
  const editableColumns = columns.filter((column) => column.editable);
  if (editableColumns.length) return editableColumns;
  const keys = ["title", "name", "status", "description"].filter((key) => key in row);
  return keys.map((key) => ({ key, label: opsColumnLabel(key, ui), width: 180, editable: true, input: key === "description" ? "textarea" : key === "status" ? "select" : "text", options: key === "status" ? ["active", "disabled"] : undefined }));
}

function opsCreateFields(endpoint: string, ui: I18nDictionary["social"]["ui"]): OpsTableColumn[] {
  const status = (options: string[] = ["active", "disabled"]): OpsTableColumn => ({ key: "status", label: ui.status, width: 132, editable: true, input: "select", options });
  const userId = (key: string, label: string): OpsTableColumn => ({ key, label, width: 180, editable: true });
  if (endpoint.endsWith("/users")) return [
    { key: "display_name", label: ui.displayName, width: 220, editable: true },
    { key: "username", label: ui.username, width: 160, editable: true },
    { key: "email", label: ui.email, width: 240, editable: true },
    { key: "role", label: ui.roles, width: 150, editable: true, input: "select", options: opsRoleOptions("") },
    status(["active", "archived"])
  ];
  if (endpoint.endsWith("/follows")) return [
    userId("follower_id", ui.profile),
    userId("target_id", uiLabel(ui, "target", "Target")),
    { key: "relationship_type", label: ui.type, width: 150, editable: true, input: "select", options: ["friend", "follow"] },
    status(["pending", "accepted", "blocked", "deleted"])
  ];
  if (endpoint.endsWith("/posts")) return [
    userId("author_id", ui.profile),
    { key: "body", label: ui.post, width: 420, editable: true, input: "textarea" },
    { key: "visibility", label: ui.visibility, width: 130, editable: true, input: "select", options: ["public", "friends", "private"] },
    status(["draft", "published", "rejected", "deleted"])
  ];
  if (endpoint.endsWith("/comments") || endpoint.endsWith("/replies")) return [
    userId("author_id", ui.profile),
    { key: "post_id", label: ui.post, width: 180, editable: true },
    ...(endpoint.endsWith("/replies") ? [{ key: "parent_id", label: uiLabel(ui, "commentId", "Comment ID"), width: 180, editable: true } satisfies OpsTableColumn] : []),
    { key: "body", label: endpoint.endsWith("/replies") ? uiLabel(ui, "reply", "Reply") : ui.comment, width: 420, editable: true, input: "textarea" },
    status(["draft", "published", "rejected", "deleted"])
  ];
  if (endpoint.endsWith("/likes")) return [
    userId("user_id", ui.profile),
    { key: "subject_type", label: uiLabel(ui, "subjectType", "Subject type"), width: 150, editable: true, input: "select", options: ["post", "comment", "reply"] },
    { key: "subject_id", label: uiLabel(ui, "subjectId", "Subject ID"), width: 220, editable: true },
    { key: "reaction", label: ui.like, width: 132, editable: true, input: "select", options: ["like", "love", "haha", "wow", "sad", "angry"] }
  ];
  if (endpoint.endsWith("/shares")) return [
    userId("user_id", ui.profile),
    { key: "subject_type", label: uiLabel(ui, "subjectType", "Subject type"), width: 150, editable: true, input: "select", options: ["post", "article", "event", "group", "page", "document"] },
    { key: "subject_id", label: uiLabel(ui, "subjectId", "Subject ID"), width: 220, editable: true }
  ];
  if (endpoint.endsWith("/hashtags")) return [
    { key: "tag", label: ui.hashtags, width: 220, editable: true },
    { key: "normalized_tag", label: ui.slug, width: 180, editable: true },
    status()
  ];
  if (endpoint.endsWith("/categories")) return [
    { key: "label", label: ui.name, width: 240, editable: true },
    { key: "slug", label: ui.slug, width: 180, editable: true },
    { key: "type", label: ui.type, width: 150, editable: true, input: "select", options: ["group", "event", "page", "report", "content_warning"] },
    { key: "description", label: ui.description, width: 320, editable: true, input: "textarea" },
    { key: "sort_order", label: ui.sortOrder, width: 118, editable: true, input: "number" },
    status()
  ];
  if (endpoint.endsWith("/stories")) return [
    userId("author_id", ui.profile),
    { key: "body", label: ui.stories, width: 320, editable: true, input: "textarea" },
    { key: "type", label: ui.type, width: 124, editable: true, input: "select", options: ["text", "photo", "video"] },
    { key: "expires_at", label: uiLabel(ui, "expiresAt", "Expires"), width: 172, editable: true },
    status(["active", "expired", "deleted"])
  ];
  if (endpoint.endsWith("/media")) return [
    userId("owner_id", ui.profile),
    { key: "filename", label: ui.uploadedFileId, width: 240, editable: true },
    { key: "asset_url", label: ui.media, width: 340, editable: true },
    { key: "content_type", label: ui.contentType, width: 170, editable: true },
    status(["pending", "ready", "failed", "deleted"])
  ];
  if (endpoint.endsWith("/clips")) return [
    userId("author_id", ui.profile),
    { key: "title", label: ui.clips, width: 260, editable: true },
    { key: "caption", label: ui.caption, width: 360, editable: true, input: "textarea" },
    { key: "media_url", label: ui.media, width: 340, editable: true },
    status(["draft", "published", "rejected", "deleted"])
  ];
  if (endpoint.endsWith("/articles") || endpoint.endsWith("/documents")) return [
    userId("author_id", ui.profile),
    { key: "title", label: endpoint.endsWith("/documents") ? ui.documents : ui.articles, width: 280, editable: true },
    { key: "slug", label: ui.slug, width: 180, editable: true },
    { key: "excerpt", label: ui.excerpt, width: 340, editable: true, input: "textarea" },
    status(["draft", "published", "rejected", "deleted"])
  ];
  if (endpoint.endsWith("/groups") || endpoint.endsWith("/pages")) return [
    userId("owner_id", ui.profile),
    { key: "name", label: ui.name, width: 240, editable: true },
    { key: "slug", label: ui.slug, width: 180, editable: true },
    { key: "description", label: ui.description, width: 360, editable: true, input: "textarea" },
    { key: "privacy", label: ui.privacy, width: 132, editable: true, input: "select", options: ["public", "private"] },
    status()
  ];
  if (endpoint.endsWith("/events")) return [
    { key: "title", label: ui.title, width: 260, editable: true },
    { key: "starts_at", label: ui.start, width: 172, editable: true },
    { key: "format", label: ui.type, width: 132, editable: true, input: "select", options: ["online", "in_person"] },
    { key: "visibility", label: ui.visibility, width: 132, editable: true, input: "select", options: ["public", "friends", "private"] },
    status(["draft", "published", "cancelled", "completed"])
  ];
  if (endpoint.endsWith("/ads")) return [
    { key: "campaign_name", label: ui.campaignName, width: 260, editable: true },
    { key: "objective", label: ui.objective, width: 140, editable: true, input: "select", options: ["traffic", "reach", "message"] },
    { key: "budget_eur", label: ui.budgetEur, width: 128, editable: true, input: "number" },
    status(["draft", "active", "paused", "completed", "cancelled"])
  ];
  if (endpoint.endsWith("/notifications")) return [
    userId("user_id", ui.recipientId),
    { key: "type", label: ui.type, width: 220, editable: true },
    { key: "title", label: ui.title, width: 260, editable: true },
    { key: "body", label: ui.message, width: 360, editable: true, input: "textarea" }
  ];
  if (endpoint.endsWith("/messages")) return [
    userId("sender_id", ui.profile),
    userId("receiver_id", ui.recipientId),
    { key: "body", label: ui.message, width: 420, editable: true, input: "textarea" },
    status(["active", "deleted"])
  ];
  if (endpoint.endsWith("/chat-rooms")) return [
    { key: "title", label: ui.groupRoomName, width: 260, editable: true },
    { key: "type", label: ui.type, width: 132, editable: true, input: "select", options: ["direct", "group"] },
    { key: "member_ids", label: ui.groupRoomMembers, width: 320, editable: true },
    status(["active", "archived", "deleted"])
  ];
  if (endpoint.endsWith("/invites")) return [
    userId("sender_id", ui.profile),
    { key: "email", label: ui.email, width: 240, editable: true },
    { key: "phone", label: ui.phone, width: 160, editable: true },
    status(["pending", "accepted", "revoked", "expired"])
  ];
  if (endpoint.endsWith("/saved-items")) return [
    userId("user_id", ui.profile),
    { key: "subject_type", label: ui.type, width: 150, editable: true },
    { key: "subject_id", label: ui.methodId, width: 220, editable: true },
    { key: "collection", label: ui.savedItems, width: 220, editable: true }
  ];
  if (endpoint.endsWith("/reports")) return [
    userId("reporter_id", ui.profile),
    { key: "subject_type", label: uiLabel(ui, "subjectType", "Subject type"), width: 150, editable: true, input: "select", options: ["post", "comment", "reply", "user", "group", "event", "page", "ad"] },
    { key: "subject_id", label: uiLabel(ui, "subjectId", "Subject ID"), width: 220, editable: true },
    { key: "reason", label: uiLabel(ui, "reason", "Reason"), width: 320, editable: true, input: "textarea" },
    status(["open", "resolved", "rejected", "escalated"])
  ];
  if (endpoint.endsWith("/support-tickets")) return [
    userId("user_id", ui.profile),
    { key: "category", label: ui.section, width: 170, editable: true },
    { key: "subject", label: ui.subject, width: 260, editable: true },
    { key: "body", label: ui.message, width: 360, editable: true, input: "textarea" },
    status(["open", "pending", "resolved", "closed", "escalated"])
  ];
  return [
    { key: "title", label: ui.title, width: 220, editable: true },
    status()
  ];
}

function opsDrawerInitialValues(fields: OpsTableColumn[], row: Record<string, unknown>): Record<string, string> {
  return Object.fromEntries(fields.map((field) => [field.key, String(row[field.key] ?? row[toCamelCase(field.key)] ?? "")]));
}

function opsDrawerPayloadValue(field: OpsTableColumn, value: unknown): unknown {
  if (field.input !== "number") return value;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : value;
}

function opsDrawerSelectOptions(field: OpsTableColumn, current: unknown): string[] {
  const value = String(current ?? "");
  const options = field.options?.length ? field.options : [value].filter(Boolean);
  return value && !options.includes(value) ? [value, ...options] : options;
}

function opsDrawerTitle(row: Record<string, unknown>, ui: I18nDictionary["social"]["ui"]): string {
  return String(row.display_name ?? row.name ?? row.title ?? row.username ?? row.email ?? row.subject ?? row.id ?? ui.directoryItem);
}

function opsPageSummary(result: ApiResult, page: number, perPage: number, ui: I18nDictionary["social"]["ui"]): string {
  const total = result.total ?? result.rows.length;
  const pageCount = total > 0 ? Math.max(1, Math.ceil(total / perPage)) : 1;
  const start = total > 0 ? (page - 1) * perPage + 1 : 0;
  const end = Math.min(page * perPage, total);
  return `${ui.page} ${page}/${pageCount} - ${start}-${end} / ${total}`;
}

function OpsMediaDefaultsPanel({ title, endpoint, props, ui }: { title: string; endpoint: string; props: PageProps; ui: I18nDictionary["social"]["ui"] }) {
  const result = useApi(endpoint, initialApiData(props, endpoint));
  const [values, setValues] = useState<Record<string, string>>({});
  const [state, setState] = useState<SubmitState>({ status: "idle", message: "" });
  const defaultsKey = mediaDefaultsKey(result.data);
  useEffect(() => {
    setValues(mediaDefaultsValues(result.data));
  }, [defaultsKey, result.data]);
  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setState({ status: "saving", message: ui.save });
    try {
      await submitJson(endpoint, "PATCH", values);
      setState({ status: "success", message: ui.saved });
      result.reload();
    } catch (error) {
      setState({ status: "error", message: localizedErrorMessage(error, ui.actionFailed) });
    }
  }
  return (
    <section className="data-panel ops-media-defaults-panel">
      <header>
        <div>
          <h2>{title}</h2>
          <p>{ui.mediaDefaultsHelp}</p>
        </div>
      </header>
      {result.status === "loading" && <LoadingState label={ui.loadingResults} />}
      {result.status === "permission" && <PermissionPanel title={ui.logInToContinueTitle} body={ui.logInToContinueBody} compact actionLabel={ui.logIn} />}
      {result.status === "error" && <EmptyState icon={LifeBuoy} title={ui.unableToLoadView} body={displayErrorMessage(result.error, ui.retry)} showBody action={<BaseButton type="button" variant="outlined" onClick={result.reload}>{ui.retry}</BaseButton>} />}
      {result.status === "ready" && (
        <form className="ops-media-defaults-form" onSubmit={save}>
          {mediaDefaultFields(ui).map((field) => {
            const value = values[field.name] ?? "";
            return (
              <label className="ops-media-default-field" key={field.name}>
                <span>{field.label}</span>
                <div>
                  <span className={`ops-media-preview ${value ? "has-image" : ""}`} aria-hidden="true">
                    {value ? <PictureImage src={value} alt="" /> : <Images size={20} />}
                  </span>
                  <input value={value} onChange={(event) => setValues((current) => ({ ...current, [field.name]: event.currentTarget.value }))} placeholder="/media/defaults/example.avif" />
                </div>
              </label>
            );
          })}
          <div className="form-actions">
            <BaseButton type="submit" variant="primary" loading={state.status === "saving"}>{ui.save}</BaseButton>
            <InlineStatus state={state} />
          </div>
        </form>
      )}
    </section>
  );
}

const mediaDefaultFieldNames = ["default_profile_photo_light", "default_profile_photo_dark", "default_cover_photo_light", "default_cover_photo_dark"] as const;

function mediaDefaultFields(ui: I18nDictionary["social"]["ui"]): Array<{ name: (typeof mediaDefaultFieldNames)[number]; label: string }> {
  return mediaDefaultFieldNames.map((name) => ({
    name,
    label: {
      default_profile_photo_light: ui.defaultProfilePhotoLight,
      default_profile_photo_dark: ui.defaultProfilePhotoDark,
      default_cover_photo_light: ui.defaultCoverPhotoLight,
      default_cover_photo_dark: ui.defaultCoverPhotoDark
    }[name]
  }));
}

function mediaDefaultsValues(data: unknown): Record<string, string> {
  const source = isRecord(data) && isRecord(data.settings) ? data.settings : {};
  const values: Record<string, string> = {};
  for (const name of mediaDefaultFieldNames) {
    values[name] = String(source[name] ?? "");
  }
  return values;
}

function mediaDefaultsKey(data: unknown): string {
  return JSON.stringify(mediaDefaultsValues(data));
}

function ModalContractView(props: PageProps) {
  return (
    <section className="contract-page">
      <ContractHero props={props} />
      <div className="modal-contract-card">
        <ActionPanel view={props.view} currentUrl={props.currentUrl} dictionary={props.dictionary} />
      </div>
    </section>
  );
}

function ContractHero({ props, rightSlot, onRefresh, onSearch }: { props: PageProps; rightSlot?: ReactNode; onRefresh?: () => void; onSearch?: () => void }) {
  const Icon = iconByModule[props.view.module] ?? Compass;
  const hasActions = Boolean(onSearch || onRefresh || rightSlot);
  return (
    <header className={`module-hero layout-${props.view.layoutTemplate}`}>
      <div className="hero-icon"><Icon size={22} /></div>
      <div className="hero-copy">
        <h1>{localizedViewTitle(props.view, props.dictionary)}</h1>
      </div>
      {hasActions && (
        <div className="hero-actions">
          {onSearch && <button className="icon-button" type="button" title={props.dictionary.social.ui.search} aria-label={props.dictionary.social.ui.search} onClick={onSearch}><Search size={18} /></button>}
          {onRefresh && <button className="icon-button" type="button" title={props.dictionary.social.ui.refresh} aria-label={props.dictionary.social.ui.refresh} onClick={onRefresh}><RefreshCcw size={18} /></button>}
          {rightSlot}
        </div>
      )}
    </header>
  );
}

function socialNavLabel(dict: I18nDictionary, key: string): string {
  if (key === "home") return dict.public.nav.home;
  if (key === "profile") return dict.social.ui.profile;
  if (key === "network") return dict.social.modules.network;
  if (key === "chat") return dict.social.modules.chat;
  if (key && Object.prototype.hasOwnProperty.call(dict.social.modules, key)) {
    return dict.social.modules[key as keyof I18nDictionary["social"]["modules"]] ?? key;
  }
  if (key && Object.prototype.hasOwnProperty.call(dict.social.ui, key)) {
    return dict.social.ui[key as keyof I18nDictionary["social"]["ui"]] ?? key;
  }
  return key;
}

type VirtualDataListProps = ComponentPropsWithoutRef<"div"> & { context?: unknown };

const VirtualDataList = forwardRef<HTMLDivElement, VirtualDataListProps>(function VirtualDataList(
  { context: _context, className, role = "list", ...props },
  ref
) {
  return <div {...props} ref={ref} role={role} className={`data-list ${className ?? ""}`.trim()} />;
});

const VirtualDataItem = forwardRef<HTMLDivElement, VirtualDataListProps>(function VirtualDataItem(
  { context: _context, role = "listitem", ...props },
  ref
) {
  return <div {...props} ref={ref} role={role} />;
});

const virtualDataComponents: VirtuosoComponents<Record<string, unknown>, unknown> = {
  List: VirtualDataList,
  Item: VirtualDataItem
};

const virtualDataGridComponents: VirtuosoGridComponents<unknown> = {
  List: forwardRef<HTMLDivElement, VirtualDataListProps>(function VirtualDataGridList(
    { context: _context, role = "list", ...props },
    ref
  ) {
    return <div {...props} ref={ref} role={role} />;
  }),
  Item: forwardRef<HTMLDivElement, VirtualDataListProps>(function VirtualDataGridItem(
    { context: _context, role = "listitem", ...props },
    ref
  ) {
    return <div {...props} ref={ref} role={role} />;
  })
};

function apiRowIdentity(row: Record<string, unknown>): string {
  return String(row.id ?? row.key ?? row.user_id ?? row.couple_profile_id ?? "").trim();
}

function DataPanel({
  result,
  title,
  view,
  empty,
  emptyTitle: customEmptyTitle,
  emptyBody = "",
  emptyAction,
  renderRow,
  ui,
  density = "list",
  compact = false,
  emptyActions = true,
  hideTitle = false,
  hideStatus = false,
  autoLoad = false,
  disableAutoLoad = false,
  autoLoadKey = "",
  entityDetail = false,
  headingLevel = 2
}: {
  result: ApiResult;
  title: string;
  view: ViewCatalogItem;
  empty: string;
  emptyTitle?: string;
  emptyBody?: string;
  emptyAction?: ReactNode;
  renderRow: (row: Record<string, unknown>) => ReactNode;
  ui: I18nDictionary["social"]["ui"];
  density?: "list" | "grid";
  compact?: boolean;
  emptyActions?: boolean;
  hideTitle?: boolean;
  hideStatus?: boolean;
  autoLoad?: boolean;
  disableAutoLoad?: boolean;
  autoLoadKey?: string;
  entityDetail?: boolean;
  headingLevel?: 1 | 2;
}) {
  const isFeed = view.name === "Home Feed";
  const shouldAutoLoad = !disableAutoLoad && (isFeed || autoLoad || isAutoLoadListView(view));
  const panelRef = useRef<HTMLElement | null>(null);
  const autoLoadRef = useRef<HTMLDivElement | null>(null);
  const hasFeedScrollIntentRef = useRef(false);
  const [workspaceScrollParent, setWorkspaceScrollParent] = useState<HTMLElement | null | undefined>(undefined);
  const EmptyIcon = emptyStateIcon(view);
  const hasRows = result.rows.length > 0;
  const isLoadingMore = result.loadingNext ?? (result.status === "loading" && hasRows);
  const isLoadingPrevious = Boolean(result.loadingPrevious);
  const hasNextPage = Boolean(result.nextCursor);
  const hasPreviousPage = Boolean(result.hasPrevious && result.loadPrevious);
  const loadMoreFailed = Boolean(result.nextError) || (result.status === "error" && hasNextPage);
  const loadPreviousFailed = Boolean(result.previousError);
  const isEmpty = result.status === "ready" && !hasRows && !hasNextPage;
  const isSkippingFilteredPage = result.status === "ready" && !hasRows && hasNextPage;
  const showPanelTitle = !isFeed && !hideTitle && !isEmpty;
  const showHeader = !isFeed && showPanelTitle;
  const showPagination = !shouldAutoLoad && hasNextPage;
  const isCommunityDirectory = ["groups", "pages"].includes(view.module);
  const moduleClass = isCommunityDirectory ? "module-community-directory" : `module-${cssClassToken(view.module)}`;
  const directoryGridRowClass =
    `data-row directory-data-row ${isCommunityDirectory ? "community-directory-row" : ""} ${moduleClass}`.trim();
  const panelEmptyCopy = emptyStateCopy(view, empty, ui);
  const emptyTitle = customEmptyTitle || panelEmptyCopy.title || title;
  const resolvedEmptyBody = emptyBody || panelEmptyCopy.body;
  const emptyActionPath = typeof window === "undefined" ? "" : window.location.pathname;
  const hasEmptyActions =
    emptyActions && view.actions.some((action) => isRenderableAction(action, view, emptyActionPath));
  const resolvedEmptyAction = emptyAction ?? (hasEmptyActions ? <ActionLinks view={view} compact ui={ui} /> : undefined);
  const hideEmptyTitle = normalizeHeadingText(emptyTitle) === normalizeHeadingText(title) && showPanelTitle;
  const Heading = headingLevel === 1 ? "h1" : "h2";
  const viewCursorKey = `${view.module}:${view.slug}:${view.endpoint}:${autoLoadKey}`;
  const shouldVirtualize = shouldAutoLoad && typeof ResizeObserver !== "undefined";
  useEffect(() => {
    setWorkspaceScrollParent(shouldVirtualize ? (panelRef.current?.closest<HTMLElement>(".workspace") ?? null) : null);
  }, [shouldVirtualize, viewCursorKey]);
  useEffect(() => {
    hasFeedScrollIntentRef.current = false;
  }, [viewCursorKey]);
  useEffect(() => {
    if (!shouldAutoLoad || (result.status === "loading" && !hasRows)) {
      hasFeedScrollIntentRef.current = false;
    }
    if (!shouldAutoLoad || result.status !== "ready" || result.loadingNext || result.nextError) return;
    const cursor = result.nextCursor;
    if (!cursor) return;
    const sentinel = autoLoadRef.current;
    if (!sentinel) return;
    const scrollContainer = sentinel.closest<HTMLElement>(".workspace");
    const loadWhenNearViewport = () => {
      const rect = sentinel.getBoundingClientRect();
      const viewportBottom = scrollContainer
        ? scrollContainer.getBoundingClientRect().bottom
        : window.innerHeight || document.documentElement.clientHeight;
      if (rect.top <= viewportBottom + 560) {
        result.loadMore();
      }
    };
    const handleScrollIntent = () => {
      hasFeedScrollIntentRef.current = true;
      loadWhenNearViewport();
    };
    const handleResize = () => {
      if (hasFeedScrollIntentRef.current) loadWhenNearViewport();
    };
    const observer =
      "IntersectionObserver" in window
        ? new IntersectionObserver(
            (entries) => {
              if (hasFeedScrollIntentRef.current && entries.some((entry) => entry.isIntersecting))
                loadWhenNearViewport();
            },
            { root: scrollContainer, rootMargin: "560px 0px 560px 0px" }
          )
        : null;
    observer?.observe(sentinel);
    const initialFrame = window.requestAnimationFrame(loadWhenNearViewport);
    if (scrollContainer) scrollContainer.addEventListener("scroll", handleScrollIntent, { passive: true });
    else window.addEventListener("scroll", handleScrollIntent, { passive: true });
    window.addEventListener("resize", handleResize);
    return () => {
      window.cancelAnimationFrame(initialFrame);
      observer?.disconnect();
      if (scrollContainer) scrollContainer.removeEventListener("scroll", handleScrollIntent);
      else window.removeEventListener("scroll", handleScrollIntent);
      window.removeEventListener("resize", handleResize);
    };
  }, [
    hasRows,
    shouldAutoLoad,
    result.nextCursor,
    result.nextError,
    result.loadingNext,
    result.status,
    result.loadMore
  ]);
  return (
    <section
      ref={panelRef}
      className={`data-panel ${moduleClass} ${compact ? "compact" : ""} ${isFeed ? "feed-panel" : ""}`}
    >
      {showHeader && <header>{showPanelTitle && <Heading>{title}</Heading>}</header>}
      {result.status === "loading" &&
        !hasRows &&
        (isFeed ? (
          <FeedLoadingSkeleton label={ui.loadingResults} />
        ) : density === "grid" ? (
          <DirectoryLoadingSkeleton label={ui.loadingResults} />
        ) : (
          <LoadingState label={ui.loadingResults} />
        ))}
      {result.status === "permission" && result.statusCode === 401 && (
        <PermissionPanel title={ui.logInToContinueTitle} body={ui.logInToContinueBody} compact actionLabel={ui.logIn} />
      )}
      {result.status === "permission" && result.statusCode !== 401 && (
        <EmptyState
          icon={Lock}
          title={uiLabel(ui, "privateContent", "Private content")}
          body={uiLabel(ui, "privateContentBody", "This content is not available because of its privacy settings.")}
          showBody
        />
      )}
      {result.status === "error" && !hasRows && !loadMoreFailed && (
        <EmptyState
          icon={LifeBuoy}
          title={ui.unableToLoadView}
          body={dataPanelErrorMessage(result, view, ui)}
          showBody
          action={
            <BaseButton type="button" variant="outlined" onClick={result.reload}>
              {ui.retry}
            </BaseButton>
          }
        />
      )}
      {result.status === "ready" &&
        !hasRows &&
        (isEmpty ? (
          <EmptyState
            icon={EmptyIcon}
            title={emptyTitle}
            body={resolvedEmptyBody}
            showBody={Boolean(resolvedEmptyBody)}
            hideTitle={hideEmptyTitle}
            action={resolvedEmptyAction}
            headingLevel={headingLevel}
          />
        ) : isSkippingFilteredPage ? (
          <LoadingState label={ui.loadingResults} />
        ) : null)}
      {hasRows && hasPreviousPage && (isLoadingPrevious || loadPreviousFailed) && (
        <div
          className={`feed-scroll-sentinel ${isLoadingPrevious ? "loading" : ""} ${loadPreviousFailed ? "error" : ""}`}
          aria-busy={isLoadingPrevious}
        >
          {isLoadingPrevious ? (
            <>
              <span className="feed-scroll-loader" aria-hidden="true" />
              <span className="visually-hidden" role="status" aria-live="polite">
                {ui.loadingResults}
              </span>
            </>
          ) : null}
          {loadPreviousFailed ? (
            <div className="feed-scroll-error" role="alert">
              <p>{displayErrorMessage(result.previousError, ui.retry)}</p>
              <BaseButton type="button" variant="outlined" size="sm" onClick={result.loadPrevious}>
                {ui.retry}
              </BaseButton>
            </div>
          ) : null}
        </div>
      )}
      {hasRows && hasPreviousPage && !isLoadingPrevious && !loadPreviousFailed && (
        <div className="panel-footer">
          <BaseButton type="button" variant="outlined" size="sm" onClick={result.loadPrevious}>
            {uiLabel(ui, "loadPrevious", "Load previous")}
          </BaseButton>
        </div>
      )}
      {hasRows &&
        (shouldVirtualize && workspaceScrollParent ? (
          density === "grid" ? (
            <VirtuosoGrid
              className="virtual-data-grid"
              listClassName={`directory-grid ${moduleClass}`}
              itemClassName="virtual-directory-item"
              components={virtualDataGridComponents}
              customScrollParent={workspaceScrollParent}
              data={result.rows}
              computeItemKey={(_index, row) => apiRowIdentity(row)}
              startReached={
                hasPreviousPage && !isLoadingPrevious && !loadPreviousFailed ? result.loadPrevious : undefined
              }
              increaseViewportBy={{ top: 700, bottom: 900 }}
              overscan={{ main: 480, reverse: 320 }}
              initialItemCount={Math.min(12, result.rows.length)}
              itemContent={(_index, row) => <article className={directoryGridRowClass}>{renderRow(row)}</article>}
            />
          ) : (
            <Virtuoso
              className="virtual-data-list"
              customScrollParent={workspaceScrollParent}
              data={result.rows}
              firstItemIndex={result.firstItemIndex}
              startReached={
                hasPreviousPage && !isLoadingPrevious && !loadPreviousFailed ? result.loadPrevious : undefined
              }
              computeItemKey={(_index, row) => apiRowIdentity(row)}
              components={virtualDataComponents}
              context={undefined}
              defaultItemHeight={isFeed ? 420 : 128}
              increaseViewportBy={{ top: 900, bottom: 1100 }}
              minOverscanItemCount={{ top: 2, bottom: 3 }}
              initialItemCount={Math.min(12, result.rows.length)}
              itemContent={(_index, row) => <article className={`data-row ${moduleClass}`}>{renderRow(row)}</article>}
            />
          )
        ) : (
          <div className={density === "grid" ? `directory-grid ${moduleClass}` : "data-list"} role="list">
            {(shouldVirtualize && workspaceScrollParent === undefined ? result.rows.slice(0, 12) : result.rows).map(
              (row, index) => (
                <article
                  className={density === "grid" ? directoryGridRowClass : `data-row ${moduleClass}`}
                  role="listitem"
                  key={apiRowIdentity(row) || `row-${index}`}
                >
                  {renderRow(row)}
                </article>
              )
            )}
          </div>
        ))}
      {shouldAutoLoad && (hasRows || hasNextPage) ? (
        <div
          className={`feed-scroll-sentinel ${isLoadingMore ? "loading" : ""} ${loadMoreFailed ? "error" : ""}`}
          ref={autoLoadRef}
          aria-busy={isLoadingMore}
          data-next-cursor={result.nextCursor ?? ""}
          data-status={result.status}
        >
          {isLoadingMore ? (
            <span className="visually-hidden" role="status" aria-live="polite">
              {ui.loadingResults}
            </span>
          ) : null}
          {hasNextPage && !loadMoreFailed ? (
            <BaseButton
              type="button"
              variant="outlined"
              size="sm"
              loading={isLoadingMore}
              disabled={isLoadingMore}
              onClick={result.loadMore}
            >
              {ui.loadMore}
            </BaseButton>
          ) : null}
          {loadMoreFailed ? (
            <div className="feed-scroll-error" role="alert">
              <p>{displayErrorMessage(result.nextError ?? result.error, ui.retry)}</p>
              <BaseButton type="button" variant="outlined" size="sm" onClick={result.loadMore}>
                {ui.retry}
              </BaseButton>
            </div>
          ) : null}
        </div>
      ) : showPagination ? (
        <footer className="panel-footer">
          <BaseButton type="button" variant="outlined" disabled={isLoadingMore} onClick={result.loadMore}>
            {ui.loadMore}
          </BaseButton>
        </footer>
      ) : null}
    </section>
  );
}

function isAutoLoadListView(view: ViewCatalogItem): boolean {
  return ["articles", "clips", "documents", "events", "groups", "network", "pages", "photos", "saved", "search"].includes(view.module);
}

function dataPanelErrorMessage(result: ApiResult, view: ViewCatalogItem, ui: I18nDictionary["social"]["ui"]): string {
  if (view.module === "events" && result.statusCode === 404) return ui.eventsUnavailable;
  return displayErrorMessage(result.error, ui.retry);
}

function eventsEndpointUnavailable(result: ApiResult, view: ViewCatalogItem): boolean {
  return view.module === "events" && result.status === "error" && result.statusCode === 404;
}

function ProfileView(props: PageProps) {
  const ui = props.dictionary.social.ui;
  const copy = getCommunityMessages(props.locale.code);
  const [pendingMedia, setPendingMedia] = useState<{ kind: "avatar" | "cover"; file: File } | null>(null);
  const identifier = currentProfileIdentifier(props);
  const profileEndpoint = `/api/profile${identifier ? `?username=${encodeURIComponent(identifier)}` : ""}`;
  const profile = useApi(profileEndpoint, initialApiData(props, profileEndpoint));
  const isPublicProfile = isPublicProfileRoute(props.currentUrl);
  const [mediaState, setMediaState] = useState<SubmitState>({ status: "idle", message: "" });
  const avatarInputRef = useRef<HTMLInputElement | null>(null);
  const coverInputRef = useRef<HTMLInputElement | null>(null);
  const row = profile.rows[0] ?? {};
  const minimalProfile = !ownsProfile(row, props.auth.user) && row.public_profile_mode === "minimal";
  const tab = minimalProfile ? "photos" : currentProfileTab(props.currentUrl);
  const coverSrc = mediaSrcFromRow(row, "cover", true);
  const username = profileIdentifierFromRow(row) || identifier;
  const profileRowId = String(row.id ?? row.user_id ?? row.userId ?? "").trim();
  const profileKey = username || (profileRowId ? "me" : "");
  const hasProfileRow = profile.rows.length > 0;
  const profileAvailable = hasProfileRow && Boolean(profileKey || profileRowId);
  const tabEndpoint = profile.status === "ready" && profileAvailable && tab !== "photos" ? profileTabEndpoint(tab, profileKey) : null;
  const tabResult = useApi(tabEndpoint, initialApiData(props, tabEndpoint));
  const title = String(row.display_name ?? row.username ?? username ?? ui.profile).trim() || ui.profile;
  const summary = nonRoleProfileDetail(row.bio ?? row.description ?? row.headline);
  const socialMeta = profileSocialMeta(row, ui);
  const location = memberLocationLabel(row);
  const tabs = profileTabs(ui, profileKey).filter((item) => !minimalProfile || item.id === "photos");
  const articlesView: ViewCatalogItem = { ...props.view, name: "Profile Articles", slug: "profile-articles", module: "articles", layoutTemplate: "grid-directory", endpoint: "/api/profile/articles" };
  const timelineView: ViewCatalogItem = { ...props.view, name: "Profile Timeline", slug: "profile-timeline", module: "feed", layoutTemplate: "feed", endpoint: "/api/profile/posts" };
  const canEditMedia = ownsProfile(row, props.auth.user);
  const showProfileComposer = tab === "timeline" && canEditMedia;
  function chooseProfileMedia(kind: "avatar" | "cover", files: FileList | null) {
    const file = Array.from(files ?? []).find(isImageUploadFile) ?? null;
    const input = kind === "avatar" ? avatarInputRef.current : coverInputRef.current;
    if (input) input.value = "";
    if (!file) {
      setMediaState({ status: "error", message: kind === "avatar" ? ui.chooseImageFile : ui.chooseCoverImageFile });
      return;
    }
    setMediaState({ status: "idle", message: "" });
    setPendingMedia({ kind, file });
  }
  async function uploadProfileMedia() {
    if (!pendingMedia || mediaState.status === "saving") return;
    const { kind, file } = pendingMedia;
    setMediaState({ status: "saving", message: ui.uploadProfileMedia });
    try {
      const uploaded = await uploadFiles([file], { visibility: "public" });
      const fileId = uploaded[0]?.id ? String(uploaded[0].id) : "";
      if (!fileId) throw new Error(ui.settingsRequestFailed);
      await submitJson("/api/settings/profile", "PATCH", profileMediaPayload(row, kind, fileId));
      setMediaState({ status: "success", message: ui.settingsSaved });
      setPendingMedia(null);
      profile.reload();
    } catch (error) {
      setMediaState({ status: "error", message: localizedErrorMessage(error, ui.settingsRequestFailed) });
    }
  }
  return (
    <section className="profile-page">
      {pendingMedia && <DeleteConfirmDialog idBase="profile-public-media" title={pendingMedia.kind === "avatar" ? ui.uploadAvatar : ui.uploadCover} body={copy.publicMediaConfirm} confirmLabel={ui.uploadProfileMedia} busyLabel={ui.uploadProfileMedia} cancelLabel={ui.cancel} busy={mediaState.status === "saving"} icon={Globe2} confirmVariant="primary" error={mediaState.status === "error" ? mediaState.message : undefined} onCancel={() => setPendingMedia(null)} onConfirm={uploadProfileMedia} />}
      {profile.status === "loading" && profile.rows.length === 0 && <ProfileLoadingSkeleton label={ui.loadingResults} />}
      {profile.status === "permission" && (isPublicProfile ? <EmptyState icon={CircleUserRound} title={ui.unableToLoadView} body={displayErrorMessage(profile.error, ui.retry)} showBody action={<BaseButton type="button" variant="outlined" onClick={profile.reload}>{ui.retry}</BaseButton>} /> : <PermissionPanel title={ui.logInToContinueTitle} body={ui.logInToContinueBody} compact actionLabel={ui.logIn} />)}
      {profile.status === "error" && <EmptyState icon={LifeBuoy} title={ui.unableToLoadView} body={displayErrorMessage(profile.error, ui.retry)} showBody action={<BaseButton type="button" variant="outlined" onClick={profile.reload}>{ui.retry}</BaseButton>} />}
      {profile.status === "ready" && !profileAvailable && <EmptyState icon={CircleUserRound} title={uiLabel(ui, "profileNotFound", "Profile unavailable")} body={uiLabel(ui, "profileNotFoundBody", "This profile does not exist or is not available.")} showBody />}
      {profileAvailable && (
        <>
          <section className="profile-hero">
            <div className={`profile-cover ${coverSrc ? "has-image" : ""}`}>
              {coverSrc ? <PictureImage src={coverSrc} alt="" /> : null}
              {canEditMedia && (
                <>
                  <input ref={coverInputRef} hidden type="file" accept={imageUploadAccept} onChange={(event) => chooseProfileMedia("cover", event.currentTarget.files)} />
                  <button className="profile-media-edit profile-cover-edit" type="button" onClick={() => coverInputRef.current?.click()} disabled={mediaState.status === "saving"} aria-label={ui.uploadCover}>
                    <Camera size={17} />
                    <span>{ui.uploadCover}</span>
                  </button>
                </>
              )}
            </div>
            <div className="profile-identity">
              <div className="profile-avatar-editor">
                <ProfileAvatar row={row} size="xl" className="profile-main-avatar" />
                {canEditMedia && (
                  <>
                    <input ref={avatarInputRef} hidden type="file" accept={imageUploadAccept} onChange={(event) => chooseProfileMedia("avatar", event.currentTarget.files)} />
                    <button className="profile-media-edit profile-avatar-edit" type="button" onClick={() => avatarInputRef.current?.click()} disabled={mediaState.status === "saving"} aria-label={ui.uploadAvatar}>
                      <Camera size={16} />
                      <span>{ui.uploadAvatar}</span>
                    </button>
                  </>
                )}
              </div>
              <div className="profile-identity-copy">
                <h1>{title}</h1>
                {username && !minimalProfile && <p>@{username}</p>}
                {socialMeta && <span className="profile-social-meta">{socialMeta}</span>}
                {summary && <span>{summary}</span>}
                {canEditMedia && !pendingMedia && <InlineStatus state={mediaState} />}
              </div>
              <div className="profile-identity-side">
                {location && <span className="profile-location"><MapPin size={15} />{location}</span>}
                <ProfileHeroActions row={row} ui={ui} currentUrl={props.currentUrl} authUser={props.auth.user} ownsCurrentProfile={canEditMedia} />
                <ProfileBlockButton props={props} userId={profileRowId} />
              </div>
            </div>
          </section>
          {canEditMedia && <AdultProfilePrompt props={props} />}
          {!minimalProfile && <RelationshipLinksPanel props={props} userId={profileRowId} own={canEditMedia} collapsible={canEditMedia} />}
          {canEditMedia && tab === "about" && <CoupleManagementPanel props={props} collapsible />}
          <nav className="profile-tabs" aria-label={ui.profile}>
            {tabs.map((item) => <a className={item.id === tab ? "active" : ""} href={item.href} aria-current={item.id === tab ? "page" : undefined} onClick={(event) => handleOpenSNRouteClick(event, item.href)} key={item.id}>{item.label}</a>)}
          </nav>
          {tab === "about" ? (
            <ProfileAboutPanel row={row} ui={ui} />
          ) : tab === "photos" ? (
            <AlbumGallery props={props} ownerId={profileRowId} own={canEditMedia} />
          ) : tab === "articles" ? (
            <DataPanel result={tabResult} title={ui.articles} view={articlesView} empty={ui.articlesEmpty} renderRow={(item) => <ProfileArticleRow row={item} ui={ui} />} emptyActions={false} hideTitle autoLoad ui={ui} />
          ) : (
            <>
              {showProfileComposer && <Composer authReady={Boolean(props.auth.user)} authUser={props.auth.user} onPublished={tabResult.reload} dictionary={props.dictionary} localeCode={props.locale.code} />}
              <DataPanel result={tabResult} title={uiLabel(ui, "timeline", "Posts")} view={timelineView} empty={uiLabel(ui, "profileTimelineEmpty", "Posts will appear here after they are published.")} renderRow={(item) => renderPostRow(item, ui, props.auth.user, props.locale.code)} emptyActions={false} hideTitle autoLoad ui={ui} />
            </>
          )}
        </>
      )}
    </section>
  );
}

function profileSocialMeta(row: Record<string, unknown>, ui: I18nDictionary["social"]["ui"]): string {
  const friendsCount = positiveCount(row.friends_count ?? row.friendsCount ?? row.friend_count ?? row.friendCount ?? row.connections_count ?? row.connectionsCount);
  const mutualCount = positiveCount(row.mutual_friends_count ?? row.mutualFriendsCount ?? row.mutual_count ?? row.mutualCount);
  const parts = [
    friendsCount ? `${formatCount(friendsCount)} ${profileCountLabel(friendsCount, ui, "friendCountOne", "friendCountMany", "friend", "friends")}` : "",
    mutualCount ? `${formatCount(mutualCount)} ${profileCountLabel(mutualCount, ui, "mutualFriendCountOne", "mutualFriendCountMany", "mutual friend", "mutual friends")}` : ""
  ];
  return parts.filter(Boolean).join(" - ");
}

function positiveCount(value: unknown): number {
  const count = Math.floor(numberValue(value));
  return count > 0 ? count : 0;
}

function formatCount(value: number): string {
  return new Intl.NumberFormat(activeTimeLocaleCode()).format(value);
}

function profileCountLabel(count: number, ui: I18nDictionary["social"]["ui"], oneKey: string, manyKey: string, oneFallback: string, manyFallback: string): string {
  return uiLabel(ui, count === 1 ? oneKey : manyKey, count === 1 ? oneFallback : manyFallback);
}

function ProfileLoadingSkeleton({ label }: { label: string }) {
  return (
    <div className="profile-loading-skeleton" role="status" aria-label={label}>
      <section className="profile-hero">
        <div className="profile-skeleton-cover" />
        <div className="profile-skeleton-identity">
          <span className="profile-skeleton-avatar" />
          <span className="profile-skeleton-copy">
            <span />
            <span />
          </span>
          <span className="profile-skeleton-actions">
            <span />
            <span />
          </span>
        </div>
      </section>
      <div className="profile-skeleton-tabs" aria-hidden="true">
        <span />
        <span />
        <span />
      </div>
      <div className="profile-skeleton-feed" aria-hidden="true">
        <span />
        <span />
        <span />
      </div>
    </div>
  );
}

function ProfileHeroActions({ row, ui, currentUrl, authUser, ownsCurrentProfile }: { row: Record<string, unknown>; ui: I18nDictionary["social"]["ui"]; currentUrl: string; authUser: PageProps["auth"]["user"]; ownsCurrentProfile: boolean }) {
  const username = String(row.username ?? "").trim();
  const profileIdentifier = profileIdentifierFromRow(row) || username;
  const profileId = String(row.id ?? row.user_id ?? row.userId ?? "").trim();
  const profilePath = profileIdentifier ? profileHrefFromIdentifier(profileIdentifier) : currentUrl || "/timeline";
  const [state, setState] = useState<SubmitState>({ status: "idle", message: "" });
  const [followed, setFollowed] = useState(rowBoolean(row.followed_by_me ?? row.followedByMe));
  const [busyAction, setBusyAction] = useState<"follow" | "share" | null>(null);
  const relationship = defaultMemberRelationshipType();
  const relationshipTitleChoices = relationshipTitleOptions(relationship);
  const relationshipTitle = defaultRelationshipTitle(relationship, relationshipTitleChoices);
  const followLabel = relationship ? localizeOpenSNText(relationship.actionLabel ?? relationship.label, activeLocaleCode, ui.follow) : ui.follow;
  const followedLabel = relationship ? localizeOpenSNText(relationship.successLabel ?? relationship.label, activeLocaleCode, ui.following) : ui.following;
  const loginHref = authRouteHrefForRedirect("/login", profilePath);
  const messageRecipient = profileIdentifier || username;
  const messageRecipientLabel = String(row.display_name ?? row.displayName ?? row.name ?? username ?? messageRecipient).trim();
  const messageHref = authUser && messageRecipient ? chatRecipientHref(messageRecipient, messageRecipientLabel || messageRecipient) : loginHref;
  const missingRequiredRelationshipTitle = Boolean(authUser && relationship?.titleMode === "required" && !relationshipTitle);
  const followDisabled = !profileId || Boolean(busyAction) || (!followed && missingRequiredRelationshipTitle);

  useEffect(() => {
    setFollowed(rowBoolean(row.followed_by_me ?? row.followedByMe));
  }, [row.followed_by_me, row.followedByMe, row.id, row.user_id]);

  async function followProfile() {
    if (!authUser) {
      window.location.assign(loginHref);
      return;
    }
    if (followDisabled) return;
    setBusyAction("follow");
    setState({ status: "saving", message: followLabel });
    try {
      await submitJson("/api/follows", "POST", relationshipActionPayload({ userId: profileId }, relationship, relationshipTitle));
      setFollowed(true);
      setState({ status: "success", message: followedLabel });
    } catch (error) {
      setState({ status: "error", message: localizedErrorMessage(error, ui.actionFailed) });
    } finally {
      setBusyAction(null);
    }
  }

  async function cancelFriendship() {
    if (!authUser) {
      window.location.assign(loginHref);
      return;
    }
    if (!profileId || busyAction) return;
    setBusyAction("follow");
    setState({ status: "saving", message: ui.cancel });
    try {
      await submitJson("/api/follows", "DELETE", relationshipActionPayload({ userId: profileId }, relationship, relationshipTitle));
      setFollowed(false);
      setState({ status: "success", message: ui.requestCompleted });
    } catch (error) {
      setState({ status: "error", message: localizedErrorMessage(error, ui.actionFailed) });
    } finally {
      setBusyAction(null);
    }
  }

  async function submitProfileRelationshipAction() {
    if (followed) {
      await cancelFriendship();
      return;
    }
    await followProfile();
  }

  async function shareProfile() {
    const href = new URL(profilePath || currentUrl, window.location.origin).toString();
    setBusyAction("share");
    setState({ status: "saving", message: ui.copyingLink });
    try {
      await copyTextToClipboard(href, ui.clipboardUnavailable);
      setState({ status: "success", message: ui.linkCopied });
    } catch (error) {
      setState({ status: "permission", message: `${ui.linkReady}: ${href}` });
    } finally {
      setBusyAction(null);
    }
  }

  return (
    <div className="profile-hero-actions">
      <div className="profile-action-row">
        {ownsCurrentProfile ? (
          <a className="base-button primary sm" href="/settings/profile" onClick={(event) => handleOpenSNRouteClick(event, "/settings/profile")}><Settings size={16} /><span>{ui.editProfile}</span></a>
        ) : (
          <>
            <a className="base-button primary sm" href={messageHref} onClick={(event) => handleOpenSNRouteClick(event, messageHref)}><MessageCircle size={16} /><span>{ui.message ?? ui.messages}</span></a>
            {authUser ? (
              <BaseButton type="button" variant={followed ? "outlined" : "primary"} size="sm" loading={busyAction === "follow"} disabled={followDisabled} onClick={submitProfileRelationshipAction}>{followed ? <UserX size={16} /> : <UserPlus size={16} />}<span>{followed ? ui.cancel : followLabel}</span></BaseButton>
            ) : (
              <a className="base-button outlined sm" href={loginHref} onClick={(event) => handleOpenSNRouteClick(event, loginHref)}><UserPlus size={16} /><span>{followLabel}</span></a>
            )}
          </>
        )}
        <BaseButton type="button" variant="secondary" size="sm" loading={busyAction === "share"} disabled={Boolean(busyAction)} onClick={shareProfile}><Share2 size={16} /><span>{ui.share}</span></BaseButton>
      </div>
      <InlineStatus state={state} />
    </div>
  );
}

function ownsProfile(row: Record<string, unknown>, user: PageProps["auth"]["user"]): boolean {
  if (!user) return false;
  const profileId = String(row.id ?? row.user_id ?? row.userId ?? "").trim();
  const userId = String(user.id ?? "").trim();
  const profileIdentifier = profileIdentifierFromRow(row).toLowerCase();
  const userIdentifier = authUserProfileIdentifier(user).toLowerCase();
  return Boolean((profileId && userId && profileId === userId) || (profileIdentifier && userIdentifier && profileIdentifier === userIdentifier));
}

function ownsPost(row: Record<string, unknown>, user?: PageProps["auth"]["user"]): boolean {
  if (!user) return false;
  const authorId = String(row.author_id ?? row.user_id ?? row.owner_id ?? "").trim();
  const authorUsername = String(row.username ?? row.author_username ?? "").trim().toLowerCase();
  const username = user.username.toLowerCase();
  return Boolean((authorId && authorId === user.id) || (authorUsername && authorUsername === username));
}

function ownsComment(row: Record<string, unknown>, user?: PageProps["auth"]["user"]): boolean {
  if (!user) return false;
  const authorId = String(row.author_id ?? row.user_id ?? "").trim();
  const authorUsername = String(row.username ?? "").trim().toLowerCase();
  const username = user.username.toLowerCase();
  return Boolean((authorId && authorId === user.id) || (authorUsername && authorUsername === username));
}

function profileMediaPayload(row: Record<string, unknown>, kind: "avatar" | "cover", fileId: string): Record<string, unknown> {
  return {
    displayName: String(row.display_name ?? row.name ?? ""),
    username: String(row.username ?? ""),
    gender: String(row.gender ?? ""),
    bio: String(row.bio ?? row.description ?? row.headline ?? ""),
    currentCity: String(row.current_city ?? ""),
    workSector: String(row.work_sector ?? ""),
    avatarFileId: kind === "avatar" ? fileId : undefined,
    coverFileId: kind === "cover" ? fileId : undefined,
    removeAvatar: false,
    removeCover: false
  };
}

function currentProfileIdentifier(props: PageProps): string {
  const currentPath = normalizeAppPath(props.currentUrl);
  const authIdentifier = authUserProfileIdentifier(props.auth.user);
  if (currentPath.startsWith("/@")) {
    const segment = decodeProfileHandle(currentPath.split("/")[1] ?? "");
    return segment === "me" ? authIdentifier : segment;
  }
  if (currentPath.startsWith("/profile/")) {
    const segment = decodeURIComponent(currentPath.split("/")[2] ?? "");
    return segment === "me" ? authIdentifier : segment;
  }
  return authIdentifier;
}

function authUserProfileIdentifier(user: PageProps["auth"]["user"]): string {
  return firstProfileIdentifier(user?.profile_slug, user?.profileSlug, user?.public_id, user?.publicId, user?.username);
}

type ProfileTabId = "timeline" | "about" | "photos" | "articles";

function currentProfileTab(currentUrl: string): ProfileTabId {
  const currentPath = normalizeAppPath(currentUrl);
  if (currentPath.endsWith("/about")) return "about";
  if (currentPath.endsWith("/photos")) return "photos";
  if (currentPath.endsWith("/articles")) return "articles";
  return "timeline";
}

function profileTabEndpoint(tab: ProfileTabId, identifier: string): string | null {
  const query = identifier ? `?username=${encodeURIComponent(identifier)}` : "";
  if (tab === "about") return null;
  if (tab === "photos") return `/api/profile/media${query}`;
  if (tab === "articles") return `/api/profile/articles${query}`;
  return `/api/profile/posts${query}`;
}

function profileTabs(ui: I18nDictionary["social"]["ui"], identifier: string): Array<{ id: ProfileTabId; href: string; label: string }> {
  const base = profileHrefFromIdentifier(identifier || "me");
  return [
    { id: "timeline", href: base, label: uiLabel(ui, "timeline", "Posts") },
    { id: "about", href: `${base}/about`, label: uiLabel(ui, "about", "About") },
    { id: "photos", href: `${base}/photos`, label: uiLabel(ui, "photos", "Photos") },
    { id: "articles", href: `${base}/articles`, label: ui.articles }
  ];
}

function ProfileAboutPanel({ row, ui }: { row: Record<string, unknown>; ui: I18nDictionary["social"]["ui"] }) {
  const headline = nonRoleProfileDetail(row.headline);
  const bio = nonRoleProfileDetail(firstNonEmptyText(row.bio, row.description));
  const city = nonRoleProfileDetail(memberLocationLabel(row));
  const workSector = nonRoleProfileDetail(firstNonEmptyText(row.work_sector, row.workSector));
  const details = [
    bio ? { label: ui.bio, value: bio } : null,
    headline && headline !== bio ? { label: ui.headline, value: headline } : null,
    city ? { label: ui.currentCity, value: city } : null,
    row.relationship_structure ? { label: uiLabel(ui, "relationshipStructure", "Relationship structure"), value: String(row.relationship_structure) } : null,
    row.looking_for ? { label: uiLabel(ui, "lookingFor", "Looking for"), value: String(row.looking_for) } : null,
    row.boundaries ? { label: uiLabel(ui, "boundaries", "Boundaries"), value: String(row.boundaries) } : null,
    workSector ? { label: ui.workSector, value: workSector } : null
  ].filter((item): item is { label: string; value: string } => Boolean(item));
  return (
    <section className="profile-about-panel" aria-label={uiLabel(ui, "about", "About")}>
      {details.length ? (
        details.map((item) => (
          <div className="profile-about-row" key={item.label}>
            <span>{item.label}</span>
            <p>{item.value}</p>
          </div>
        ))
      ) : (
        <EmptyState icon={CircleUserRound} title={uiLabel(ui, "profileAboutEmpty", "No profile details")} />
      )}
    </section>
  );
}

function ProfileMediaRow({ row, ui }: { row: Record<string, unknown>; ui: I18nDictionary["social"]["ui"] }) {
  const src = mediaFileSrc(row) || mediaSrcFromRow(row, "avatar");
  const label = String(row.alt ?? row.title ?? row.filename ?? ui.photo);
  const href = profileMediaHref(row);
  const content = (
    <>
      {src ? <PictureImage src={src} alt={label} /> : <Images size={32} />}
      <span>{label}</span>
    </>
  );
  return (
    href ? <a className="profile-media-tile" href={href} onClick={(event) => handleOpenSNRouteClick(event, href)}>{content}</a> : <div className="profile-media-tile">{content}</div>
  );
}

function profileMediaHref(row: Record<string, unknown>): string {
  const explicitHref = String(row.href ?? row.permalink_url ?? row.permalinkUrl ?? row.post_href ?? row.postHref ?? row.post_url ?? row.postUrl ?? "").trim();
  if (explicitHref && !isMediaAssetHref(explicitHref)) return canonicalProfileRouteHref(explicitHref);
  const postIdentifier = String(row.post_slug ?? row.postSlug ?? row.post_id ?? row.postId ?? row.source_post_slug ?? row.sourcePostSlug ?? row.source_post_id ?? row.sourcePostId ?? "").trim();
  return postIdentifier ? `/post/${encodeURIComponent(postIdentifier)}` : "";
}

function isMediaAssetHref(value: string): boolean {
  const path = value.split("?")[0].split("#")[0].toLowerCase();
  return path.startsWith("/media/") || /\.(avif|gif|jpe?g|png|webp)$/u.test(path);
}

function ProfileArticleRow({ row, ui }: { row: Record<string, unknown>; ui: I18nDictionary["social"]["ui"] }) {
  const id = String(row.slug ?? row.id ?? "").trim();
  const href = id ? `/articles/${encodeURIComponent(id)}` : "/articles";
  const title = localizedGenericTitle(row.title ?? row.name ?? ui.articles, ui);
  const detail = localizedMachineText(row.excerpt ?? row.body ?? row.status ?? row.published_at ?? ui.ready, ui) || ui.ready;
  return (
    <>
      <strong><a className="profile-inline-link" href={href} onClick={(event) => handleOpenSNRouteClick(event, href)}>{title}</a></strong>
      <p>{detail}</p>
    </>
  );
}

function ActionPanel({ view, actions: providedActions, onRefresh, onSearch, currentUrl, dictionary }: { view: ViewCatalogItem; actions?: string[]; onRefresh?: () => void; onSearch?: () => void; currentUrl: string; dictionary: I18nDictionary }) {
  const ui = dictionary.social.ui;
  const actions = Array.from(new Set(providedActions ?? actionsForCurrentView(view, currentUrl))).slice(0, 10);
  if (actions.length === 0) return null;
  return (
    <section className="action-panel">
      <header>
        <ListChecks size={21} />
        <div>
          <h2>{ui.quickActions}</h2>
        </div>
      </header>
      <div className="action-list">
        {actions.map((action, index) => (
          <ActionButton action={action} view={view} key={`${action}-${index}`} currentUrl={currentUrl} onRefresh={onRefresh} onSearch={onSearch} dictionary={dictionary} />
        ))}
      </div>
    </section>
  );
}

function actionsForCurrentView(view: ViewCatalogItem, currentUrl: string): string[] {
  const currentPath = normalizeAppPath(currentUrl || (typeof window === "undefined" ? "" : window.location.pathname));
  if (view.name === "Profile Overview" && (currentPath === "/profile/me" || currentPath.startsWith("/@"))) {
    return ["profile.edit", "profile.avatar", "profile.cover", "share"];
  }
  return view.actions.filter((action) => isRenderableAction(action, view, currentPath) && !duplicatesDirectoryCardAction(action, view, currentPath));
}

function duplicatesDirectoryCardAction(action: string, view: ViewCatalogItem, currentPath: string): boolean {
  const value = action.toLowerCase();
  if (view.module === "pages" && currentPath === "/pages" && value.includes("follow")) return true;
  if (view.module === "groups" && currentPath === "/groups" && (value.includes("join") || value.includes("leave"))) return true;
  return false;
}

function ActionButton({ action, view, currentUrl, onRefresh, onSearch, dictionary }: { action: string; view: ViewCatalogItem; currentUrl: string; onRefresh?: () => void; onSearch?: () => void; dictionary: I18nDictionary }) {
  const ui = dictionary.social.ui;
  const href = hrefForAction(action, view, currentUrl);
  const actionableHref = href && !isSameAppPath(href, currentUrl || (typeof window === "undefined" ? "" : window.location.pathname)) ? href : undefined;
  const normalized = action.toLowerCase();
  const label = displayActionLabel(action, view, ui);
  const [state, setState] = useState<SubmitState>({ status: "idle", message: "" });
  function navigateActionHref(href: string) {
    if (!navigateOpenSNRoute(href)) window.location.assign(href);
  }
  async function submitAction() {
    const request = actionRequestFor(action, view);
    if (!request) return;
    setState({ status: "saving", message: label });
    try {
      if (request.method === "DELETE") {
        const response = await fetch(request.endpoint, { method: "DELETE", credentials: "include", headers: apiHeaders("application/json"), body: JSON.stringify(request.payload) });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(readableError(payload, response.status));
      } else {
        await submitJson(request.endpoint, request.method, request.payload);
      }
      setState({ status: "success", message: ui.done });
      if (request.endpoint === "/api/notifications/read-all") window.dispatchEvent(new Event(notificationsChangedEvent));
      onRefresh?.();
    } catch (error) {
      setState({ status: "error", message: localizedErrorMessage(error, ui.actionFailed) });
    }
  }
  async function runClientAction() {
    const currentHref = new URL(currentUrl || window.location.pathname, window.location.origin).toString();
    if (normalized.includes("copy") || normalized.includes("share")) {
      setState({ status: "saving", message: ui.copyingLink });
      try {
        await copyTextToClipboard(currentHref, ui.clipboardUnavailable);
        setState({ status: "success", message: ui.linkCopied });
      } catch (error) {
        setState({ status: "permission", message: `${ui.linkReady}: ${currentHref}` });
      }
      return;
    }
    if (normalized === "close" || normalized === "cancel" || normalized.includes("back")) {
      navigateActionHref(parentUrl(currentUrl || window.location.pathname));
      return;
    }
    setState({ status: "permission", message: clientActionMessage(action, view, label, ui) });
  }
  if (normalized.includes("refresh") || normalized.includes("retry")) {
    return <BaseButton type="button" variant="outlined" onClick={onRefresh ?? (() => window.location.reload())}><RefreshCcw size={16} />{label}</BaseButton>;
  }
  if (normalized === "clear" || normalized.includes("reset")) {
    return <BaseButton type="button" variant="outlined" onClick={() => navigateActionHref(currentUrl || "/search")}><X size={16} />{label}</BaseButton>;
  }
  if (normalized.includes("search") || normalized.includes("filter")) {
    return <BaseButton type="button" variant="outlined" onClick={onSearch ?? (() => navigateActionHref("/search"))}><Filter size={16} />{label}</BaseButton>;
  }
  if (actionableHref) {
    return <a className={`base-button ${buttonVariant(action)}`} href={actionableHref} onClick={(event) => handleOpenSNRouteClick(event, actionableHref)}>{iconForAction(action)}<span>{label}</span></a>;
  }
  if (actionRequestFor(action, view)) {
    return <span className="action-stack"><BaseButton type="button" variant={buttonVariant(action) as "primary" | "outlined" | "secondary" | "transparent" | "danger"} loading={state.status === "saving"} onClick={submitAction}>{iconForAction(action)}<span>{label}</span></BaseButton><InlineStatus state={state} /></span>;
  }
  return <span className="action-stack"><BaseButton type="button" variant={buttonVariant(action) as "primary" | "outlined" | "secondary" | "transparent" | "danger"} loading={state.status === "saving"} onClick={runClientAction}>{iconForAction(action)}<span>{label}</span></BaseButton><InlineStatus state={state} /></span>;
}

function ActionLinks({ view, compact = false, ui }: { view: ViewCatalogItem; compact?: boolean; ui: I18nDictionary["social"]["ui"] }) {
  const currentUrl = typeof window === "undefined" ? "" : window.location.pathname;
  const actions = view.actions.filter((action) => isRenderableAction(action, view, currentUrl)).slice(0, compact ? 3 : 6);
  if (actions.length === 0) return null;
  return (
    <div className="inline-actions">
      {actions.map((action, index) => {
        const href = hrefForAction(action, view, currentUrl);
        const linkHref = href ?? currentUrl;
        return <a className="base-button outlined sm" href={linkHref} onClick={href ? (event) => handleOpenSNRouteClick(event, href) : undefined} key={`${action}-${index}`}>{displayActionLabel(action, view, ui)}</a>;
      })}
    </div>
  );
}

const composerTypeOptions = [
  { type: "post", icon: MessageCircle },
  { type: "article", icon: FileText },
  { type: "photo", icon: Images },
  { type: "movie", icon: Clapperboard }
] as const;

const composerVisibilityOptions = ["public", "friends", "private"] as const;

type ComposerType = (typeof composerTypeOptions)[number]["type"];
type ComposerVisibility = (typeof composerVisibilityOptions)[number];
type ComposerMediaPreview = { file: File; url: string; kind: "image" | "video" };

function composerTypeLabel(value: ComposerType, ui: I18nDictionary["social"]["ui"]): string {
  if (value === "article") return ui.article;
  if (value === "photo") return ui.photo;
  if (value === "movie") return ui.movie;
  return ui.post;
}

function composerPlaceholder(value: ComposerType, authReady: boolean, ui: I18nDictionary["social"]["ui"]): string {
  if (!authReady) return ui.signInToPublish;
  if (value === "article") return ui.articlePlaceholder;
  if (value === "photo") return ui.photoPlaceholder;
  if (value === "movie") return ui.moviePlaceholder;
  return ui.postPlaceholder;
}

function postVisibilityMeta(value: unknown, ui: I18nDictionary["social"]["ui"]): { label: string; Icon: LucideIcon } {
  const normalized = String(value ?? "public").trim().toLowerCase();
  if (normalized === "private" || normalized === "only_me") return { label: ui.onlyMe, Icon: Lock };
  if (normalized === "network" || normalized === "friends") return { label: uiLabel(ui, "friendsOnly", ui.friends), Icon: Users };
  if (normalized === "group") return { label: uiLabel(ui, "group", "Group"), Icon: Users };
  if (normalized === "page") return { label: uiLabel(ui, "page", "Page"), Icon: Flag };
  return { label: ui.public, Icon: Globe2 };
}

function postTimestampValue(row: Record<string, unknown>): string {
  return String(row.published_at ?? row.created_at ?? row.updated_at ?? "").trim();
}

function postRelativeTimeLabel(value: string, localeCode?: string, ui?: I18nDictionary["social"]["ui"]): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const resolvedLocaleCode = activeTimeLocaleCode(localeCode, ui);
  const seconds = Math.round((date.getTime() - Date.now()) / 1000);
  const absoluteSeconds = Math.abs(seconds);
  const formatter = new Intl.RelativeTimeFormat(resolvedLocaleCode, { numeric: "auto", style: "short" });
  if (absoluteSeconds < 60) return formatter.format(0, "second");
  if (absoluteSeconds < 3600) return formatter.format(Math.round(seconds / 60), "minute");
  if (absoluteSeconds < 86400) return formatter.format(Math.round(seconds / 3600), "hour");
  if (absoluteSeconds < 604800) return formatter.format(Math.round(seconds / 86400), "day");
  if (absoluteSeconds < 2419200) return formatter.format(Math.round(seconds / 604800), "week");
  return new Intl.DateTimeFormat(resolvedLocaleCode, { month: "short", day: "numeric", year: date.getFullYear() === new Date().getFullYear() ? undefined : "numeric" }).format(date);
}

function postAbsoluteTimeLabel(value: string, localeCode?: string, ui?: I18nDictionary["social"]["ui"]): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(activeTimeLocaleCode(localeCode, ui), { dateStyle: "medium", timeStyle: "short" }).format(date);
}

function activeTimeLocaleCode(localeValue: unknown = activeLocaleCode, ui?: I18nDictionary["social"]["ui"]): string {
  const localeCode = supportedTimeLocaleCode(localeValue);
  if (localeCode && localeLanguageCode(localeCode) !== "en") return localeCode;
  let documentLocale = "";
  if (typeof document !== "undefined") {
    documentLocale = supportedTimeLocaleCode(document.documentElement.lang);
    if (documentLocale && localeLanguageCode(documentLocale) !== "en") return documentLocale;
  }
  const dictionaryLocale = supportedTimeLocaleCode(localeCodeFromUi(ui) || localeCodeFromDictionary(activeDictionary));
  if (dictionaryLocale) return dictionaryLocale;
  if (documentLocale) return documentLocale;
  return localeCode || "en";
}

function supportedTimeLocaleCode(value: unknown): string {
  const localeCode = normalizeLocaleCode(value);
  if (!localeCode) return "";
  try {
    return Intl.DateTimeFormat.supportedLocalesOf([localeCode])[0] ?? "";
  } catch {
    return "";
  }
}

function normalizeLocaleCode(value: unknown): string {
  return String(value ?? "").trim().replace(/_/g, "-");
}

function localeLanguageCode(localeCode: string): string {
  return localeCode.split("-")[0]?.toLowerCase() ?? "";
}

function localeCodeFromDictionary(dictionary: I18nDictionary | null): string {
  if (!dictionary) return "";
  const explicitLocale = normalizeLocaleCode(dictionary.localeCode ?? dictionary.locale ?? dictionary.lang ?? dictionary.language);
  return localeCodeFromUi(dictionary.social?.ui) || localeCodeFromLabels(dictionary.social?.modules) || explicitLocale;
}

function localeCodeFromUi(ui: I18nDictionary["social"]["ui"] | null | undefined): string {
  return localeCodeFromLabels(ui);
}

function localeCodeFromLabels(labels: Record<string, string> | null | undefined): string {
  if (!labels) return "";
  if (Object.values(labels).some((value) => romanianLocaleMarkers.has(normalizeLocaleMarker(value)))) return "ro-RO";
  return "";
}

function normalizeLocaleMarker(value: unknown): string {
  return String(value ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase();
}

function Composer({ authReady, authUser, onPublished, dictionary, localeCode }: { authReady: boolean; authUser: PageProps["auth"]["user"]; onPublished?: () => void; dictionary: I18nDictionary; localeCode: string }) {
  const ui = dictionary.social.ui;
  const [composerType, setComposerType] = useState<ComposerType>("post");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [body, setBody] = useState("");
  const [visibility, setVisibility] = useState<ComposerVisibility>("public");
  const [visibilityOpen, setVisibilityOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedMediaPreviews, setSelectedMediaPreviews] = useState<ComposerMediaPreview[]>([]);
  const [pendingFilePicker, setPendingFilePicker] = useState<ComposerType | null>(null);
  const [status, setStatus] = useState<SubmitState>({ status: "idle", message: "" });
  const launcherButtonRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useRef<HTMLElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const bodyInputRef = useRef<HTMLTextAreaElement | null>(null);
  const trimmedBody = body.trim();
  const mediaMode = composerType === "photo" || composerType === "movie";
  const composerBusy = status.status === "saving";
  const canPublish = composerType === "article" || composerType === "post" ? Boolean(trimmedBody) : selectedFiles.length > 0;
  const currentTypeLabel = composerTypeLabel(composerType, ui);
  const visibilityMeta = postVisibilityMeta(visibility, ui);
  const VisibilityIcon = visibilityMeta.Icon;
  const authorName = authUser?.display_name || authUser?.username || ui.guestMember;
  const loginHref = authRouteHrefForCurrentRoute("/login");
  const visibilityMenuId = "composer-visibility-menu";
  const visibilityControlRef = useRef<HTMLDivElement | null>(null);
  const visibilityButtonRef = useRef<HTMLButtonElement | null>(null);
  const visibilityMenuRef = useRef<HTMLDivElement | null>(null);
  useModalLifecycle(dialogOpen, dialogRef, bodyInputRef, true, launcherButtonRef);
  const selectedMediaItems = selectedMediaPreviews.length ? selectedMediaPreviews : selectedFiles.map((file) => ({ file, url: "", kind: file.type.startsWith("video/") ? "video" as const : "image" as const }));
  function resizeComposerBody(element = bodyInputRef.current) {
    if (!element) return;
    const minHeight = composerType === "article" ? 132 : 52;
    element.style.height = "auto";
    element.style.height = `${Math.min(Math.max(element.scrollHeight, minHeight), 180)}px`;
  }
  useEffect(() => {
    resizeComposerBody();
  }, [body, composerType]);
  useEffect(() => {
    if (!dialogOpen) return;
    window.requestAnimationFrame(() => {
      resizeComposerBody();
      bodyInputRef.current?.focus();
    });
  }, [dialogOpen, composerType]);
  useEffect(() => {
    if (!pendingFilePicker || pendingFilePicker !== composerType) return;
    setPendingFilePicker(null);
    window.requestAnimationFrame(() => fileInputRef.current?.click());
  }, [pendingFilePicker, composerType]);
  useEffect(() => {
    if (!selectedFiles.length || typeof URL === "undefined" || typeof URL.createObjectURL !== "function") {
      setSelectedMediaPreviews([]);
      return;
    }
    const previews = selectedFiles.map((file) => ({ file, url: URL.createObjectURL(file), kind: file.type.startsWith("video/") ? "video" as const : "image" as const }));
    setSelectedMediaPreviews(previews);
    return () => {
      previews.forEach((preview) => URL.revokeObjectURL(preview.url));
    };
  }, [selectedFiles]);
  useEffect(() => {
    if (!visibilityOpen) return;
    const closeOnOutsidePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Node && visibilityControlRef.current?.contains(target)) return;
      closeVisibilityMenu();
    };
    document.addEventListener("pointerdown", closeOnOutsidePointerDown);
    return () => document.removeEventListener("pointerdown", closeOnOutsidePointerDown);
  }, [visibilityOpen]);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (composerBusy) return;
    if (!authReady) {
      setStatus({ status: "permission", message: ui.loginToPublish });
      return;
    }
    if (composerType === "article") {
      if (!trimmedBody) {
        setStatus({ status: "error", message: ui.writeArticleBeforePublishing });
        return;
      }
      const article = articlePayloadFromComposer(trimmedBody);
      if (article.title.length < 2) {
        setStatus({ status: "error", message: ui.articleTitleMin });
        return;
      }
      if (article.slug.length < 2) {
        setStatus({ status: "error", message: ui.articleSlugRequired });
        return;
      }
      setStatus({ status: "saving", message: ui.publishingArticle });
      try {
        await submitJson("/api/articles", "POST", { ...article, visibility, language: localeCode, status: "published" });
        setStatus({ status: "idle", message: "" });
        setBody("");
        setSelectedFiles([]);
        if (fileInputRef.current) fileInputRef.current.value = "";
        closeComposer();
        onPublished?.();
      } catch (error) {
        setStatus({ status: "error", message: localizedErrorMessage(error, ui.articleFailed) });
      }
      return;
    }
    if (composerType === "movie") {
      if (selectedFiles.length === 0) {
        setStatus({ status: "error", message: ui.chooseVideoFile });
        return;
      }
      setStatus({ status: "saving", message: ui.publishingMovie });
      try {
        const [uploaded] = await uploadFiles(selectedFiles.slice(0, 1));
        const fileId = String(uploaded?.id ?? "");
        if (!fileId) throw new Error(ui.chooseVideoFile);
        await submitJson("/api/clips", "POST", { fileId, body: trimmedBody, visibility, language: localeCode });
        setStatus({ status: "success", message: ui.moviePublished });
        setBody("");
        setSelectedFiles([]);
        if (fileInputRef.current) fileInputRef.current.value = "";
        closeComposer();
        onPublished?.();
      } catch (error) {
        setStatus({ status: "error", message: localizedErrorMessage(error, ui.movieFailed) });
      }
      return;
    }
    if (composerType === "post" && !trimmedBody) {
      setStatus({ status: "error", message: ui.writeSomethingOrAttachPhoto });
      return;
    }
    if (composerType === "photo" && selectedFiles.length === 0) {
      setStatus({ status: "error", message: ui.chooseImageFiles });
      return;
    }
    setStatus({ status: "saving", message: composerType === "photo" ? ui.uploadingMedia : ui.publishing });
    try {
      const uploaded = composerType === "photo" ? await uploadFiles(selectedFiles, { visibility: visibility === "public" ? "public" : "private" }) : [];
      const items = uploaded.map((file) => ({
        type: "image",
        fileId: String(file.id),
        metadata: {
          fileName: file.name ?? null,
          mime: file.mime_type ?? file.type ?? null,
          size: file.size ?? null
        }
      }));
      await submitJson("/api/posts", "POST", { body: trimmedBody, visibility, language: localeCode, items });
      setStatus({ status: "idle", message: "" });
      setBody("");
      setSelectedFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      closeComposer();
      onPublished?.();
    } catch (error) {
      setStatus({ status: "error", message: localizedErrorMessage(error, ui.postFailed) });
    }
  }
  function chooseFiles(files: FileList | null) {
    const expectsVideo = composerType === "movie";
    const nextFiles = Array.from(files ?? []).filter((file) => expectsVideo ? file.type.startsWith("video/") : isImageUploadFile(file));
    setSelectedFiles(nextFiles.slice(0, expectsVideo ? 1 : 8));
    setStatus(nextFiles.length ? { status: "idle", message: "" } : { status: "error", message: expectsVideo ? ui.chooseVideoFile : ui.chooseImageFiles });
  }
  function removeSelectedFile(index: number) {
    setSelectedFiles((files) => files.filter((_, fileIndex) => fileIndex !== index));
    if (fileInputRef.current) fileInputRef.current.value = "";
  }
  function chooseComposerType(nextType: ComposerType, openFilePicker = false) {
    setComposerType(nextType);
    setVisibilityOpen(false);
    setStatus({ status: "idle", message: "" });
    setSelectedFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (openFilePicker && (nextType === "photo" || nextType === "movie")) setPendingFilePicker(nextType);
  }
  function openComposer(nextType: ComposerType, openFilePicker = false) {
    setDialogOpen(true);
    chooseComposerType(nextType, openFilePicker);
  }
  function focusComposerLauncher() {
    window.requestAnimationFrame(() => launcherButtonRef.current?.focus({ preventScroll: true }));
  }
  function closeComposer() {
    if (composerBusy) return;
    setDialogOpen(false);
    setVisibilityOpen(false);
    setStatus({ status: "idle", message: "" });
    focusComposerLauncher();
  }
  function closeVisibilityMenu(restoreFocus = false) {
    setVisibilityOpen(false);
    if (restoreFocus) window.requestAnimationFrame(() => visibilityButtonRef.current?.focus({ preventScroll: true }));
  }
  function focusVisibilityOption(last = false) {
    window.requestAnimationFrame(() => {
      const items = focusableElements(visibilityMenuRef.current);
      const target = last ? items[items.length - 1] : items[0];
      target?.focus({ preventScroll: true });
    });
  }
  function handleVisibilityButtonKeyDown(event: ReactKeyboardEvent<HTMLButtonElement>) {
    if (!["Enter", " ", "ArrowDown", "ArrowUp"].includes(event.key)) return;
    event.preventDefault();
    if (visibilityOpen && (event.key === "Enter" || event.key === " ")) {
      closeVisibilityMenu();
      return;
    }
    if (!visibilityOpen) setVisibilityOpen(true);
    focusVisibilityOption(event.key === "ArrowUp");
  }
  function handleVisibilityMenuKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      closeVisibilityMenu(true);
      return;
    }
    trapFocusTab(event, event.currentTarget);
  }
  function selectVisibility(option: ComposerVisibility) {
    setVisibility(option);
    closeVisibilityMenu(true);
  }
  function handleComposerDialogKeyDown(event: ReactKeyboardEvent<HTMLElement>) {
    if (event.key === "Escape") closeComposer();
    trapFocusTab(event, dialogRef.current);
  }
  return (
    <form className={`composer ${dialogOpen ? "is-expanded" : ""}`} id="composer" onSubmit={submit} aria-busy={composerBusy}>
      <input ref={fileInputRef} className="composer-file-input" type="file" accept={composerType === "movie" ? "video/*" : imageUploadAccept} multiple={composerType === "photo"} onChange={(event) => chooseFiles(event.currentTarget.files)} disabled={composerBusy} />
      <div className="composer-launcher">
        {authUser ? <ProfileAvatarLink row={authUser} size="md" className="composer-avatar" /> : <CircleUserRound size={40} />}
        <button ref={launcherButtonRef} className="composer-launcher-button" type="button" onClick={() => openComposer("post")} disabled={composerBusy}>
          {composerPlaceholder("post", authReady, ui)}
        </button>
      </div>
      <div className="composer-quick-actions" role="group" aria-label={ui.contentType}>
        <button className="composer-quick-action-photo" type="button" onClick={() => openComposer("photo", true)} disabled={composerBusy}>
          <Images size={18} />
          <span>{composerTypeLabel("photo", ui)}</span>
        </button>
        <button className="composer-quick-action-movie" type="button" onClick={() => openComposer("movie", true)} disabled={composerBusy}>
          <Clapperboard size={18} />
          <span>{composerTypeLabel("movie", ui)}</span>
        </button>
        <button className="composer-quick-action-article" type="button" onClick={() => openComposer("article")} disabled={composerBusy}>
          <FileText size={18} />
          <span>{composerTypeLabel("article", ui)}</span>
        </button>
      </div>
      {dialogOpen && (
        <div className="composer-dialog-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) closeComposer(); }}>
          <section ref={dialogRef} className="composer-expanded-panel composer-dialog" role="dialog" aria-modal="true" aria-label={uiLabel(ui, "createPost", "Create post")} aria-busy={composerBusy} tabIndex={-1} onKeyDown={handleComposerDialogKeyDown}>
            <header className="composer-dialog-header">
              <span aria-hidden="true" />
              <strong>{uiLabel(ui, "createPost", "Create post")}</strong>
              <button className="icon-only" type="button" aria-label={ui.cancel} onClick={closeComposer} disabled={composerBusy}><X size={18} /></button>
            </header>
            <div className="composer-dialog-body">
              <div className="composer-entry">
                {authUser ? <ProfileAvatarLink row={authUser} size="md" className="composer-avatar" /> : <CircleUserRound size={40} />}
                <div className="composer-entry-main">
                  <div className="composer-author-block">
                    {authUser ? <ProfileNameLink row={authUser} className="composer-author-name">{authorName}</ProfileNameLink> : <span className="composer-author-name">{authorName}</span>}
                    <div className="visibility-control composer-audience-control" ref={visibilityControlRef}>
                      <button ref={visibilityButtonRef} type="button" title={ui.visibility} aria-label={ui.visibility} aria-haspopup="menu" aria-expanded={visibilityOpen} aria-controls={visibilityMenuId} onKeyDown={handleVisibilityButtonKeyDown} onClick={() => setVisibilityOpen((value) => !value)} disabled={composerBusy}>
                        <VisibilityIcon size={14} />
                        <span>{visibilityMeta.label}</span>
                        <ChevronDown size={13} />
                      </button>
                      {visibilityOpen && (
                        <div id={visibilityMenuId} className="visibility-menu" role="menu" ref={visibilityMenuRef} onKeyDown={handleVisibilityMenuKeyDown}>
                          {composerVisibilityOptions.map((option) => {
                            const optionMeta = postVisibilityMeta(option, ui);
                            const OptionIcon = optionMeta.Icon;
                            return (
                              <button type="button" role="menuitemradio" aria-checked={visibility === option} className={visibility === option ? "active" : ""} key={option} onClick={() => selectVisibility(option)} disabled={composerBusy}>
                                <OptionIcon size={15} />
                                <span>{optionMeta.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="composer-body-field">
                    <textarea ref={bodyInputRef} value={body} onChange={(event) => { setBody(event.currentTarget.value); resizeComposerBody(event.currentTarget); }} aria-label={currentTypeLabel} placeholder={composerPlaceholder(composerType, authReady, ui)} disabled={composerBusy} />
                  </div>
                </div>
              </div>
              <div className="composer-type-switch" role="group" aria-label={ui.contentType}>
                {composerTypeOptions.map(({ type, icon: Icon }) => (
                  <button type="button" key={type} className={composerType === type ? "active" : ""} aria-pressed={composerType === type} onClick={() => chooseComposerType(type, type === "photo" || type === "movie")} disabled={composerBusy}>
                    <Icon size={16} />
                    <span>{composerTypeLabel(type, ui)}</span>
                  </button>
                ))}
              </div>
              {mediaMode && selectedFiles.length > 0 && (
                <div className={`composer-selected-media ${composerType === "movie" || selectedFiles.length === 1 ? "single" : ""}`} aria-label={composerType === "movie" ? ui.selectedMovie : ui.selectedPhotos}>
                  {selectedMediaItems.map(({ file, url, kind }, index) => (
                    <span className="composer-selected-file" key={`${file.name}-${file.size}-${file.lastModified}-${index}`}>
                      <span className={`composer-selected-preview ${url ? "has-preview" : ""}`}>
                        {url ? kind === "video" ? <video src={url} muted playsInline preload="metadata" /> : <img src={url} alt="" /> : kind === "video" ? <Clapperboard size={26} /> : <Images size={26} />}
                      </span>
                      <span className="composer-selected-file-name">{file.name}</span>
                      <button type="button" aria-label={`${ui.clear}: ${file.name}`} title={ui.clear} onClick={() => removeSelectedFile(index)} disabled={composerBusy}><X size={13} /></button>
                    </span>
                  ))}
                  <button className="composer-clear-media" type="button" onClick={() => { setSelectedFiles([]); if (fileInputRef.current) fileInputRef.current.value = ""; }} disabled={composerBusy}>{ui.clear}</button>
                </div>
              )}
              {composerType === "photo" && <p className="community-help">{getCommunityMessages(localeCode)[visibility === "public" ? "publicPhotos" : "privatePhotos"]}. {getCommunityMessages(localeCode).photoRules}</p>}
            </div>
            <div className="composer-actions">
              {authReady ? (
                <BaseButton type="submit" variant="primary" disabled={!canPublish || composerBusy} loading={composerBusy}><Send size={18} /> {ui.publish}</BaseButton>
              ) : (
                <a className="base-button primary md" href={loginHref} onClick={(event) => handleOpenSNRouteClick(event, loginHref)}><Send size={18} /> <span>{ui.logIn}</span></a>
              )}
            </div>
            <InlineStatus state={status} />
          </section>
        </div>
      )}
    </form>
  );
}

const POST_REACTIONS = [
  { value: "like", symbol: "👍", labelKey: "like", fallback: "Like" },
  { value: "love", symbol: "❤️", labelKey: "reactionLove", fallback: "Love" },
  { value: "haha", symbol: "😆", labelKey: "reactionHaha", fallback: "Haha" },
  { value: "wow", symbol: "😮", labelKey: "reactionWow", fallback: "Wow" },
  { value: "sad", symbol: "😢", labelKey: "reactionSad", fallback: "Sad" },
  { value: "angry", symbol: "😡", labelKey: "reactionAngry", fallback: "Angry" }
] as const;

type PostReaction = (typeof POST_REACTIONS)[number]["value"];
type PostReactionCounts = Partial<Record<PostReaction, number>>;

function normalizePostReaction(value: unknown): PostReaction | null {
  const normalized = String(value ?? "").trim().toLowerCase();
  return POST_REACTIONS.some((option) => option.value === normalized) ? normalized as PostReaction : null;
}

function postReactionOption(value: PostReaction, ui: I18nDictionary["social"]["ui"]): { value: PostReaction; symbol: string; label: string } {
  const option = POST_REACTIONS.find((item) => item.value === value) ?? POST_REACTIONS[0];
  return { value: option.value, symbol: option.symbol, label: uiLabel(ui, option.labelKey, option.fallback) };
}

function postReactionCountsFromRow(row: Record<string, unknown>): PostReactionCounts {
  const source = recordFromJson(row.reaction_counts ?? row.reactionCounts ?? row.reaction_counts_json ?? row.reactions_json ?? row.reactions);
  return Object.fromEntries(POST_REACTIONS.flatMap((option) => {
    const count = numberValue(source[option.value] ?? source[`${option.value}_count`] ?? row[`${option.value}_count`] ?? row[`reaction_${option.value}_count`]);
    return count > 0 ? [[option.value, count]] : [];
  })) as PostReactionCounts;
}

function adjustedPostReactionCounts(current: PostReactionCounts, previousReaction: PostReaction | null, nextReaction: PostReaction | null): PostReactionCounts {
  const counts: PostReactionCounts = { ...current };
  if (previousReaction) {
    const nextCount = Math.max((counts[previousReaction] ?? 0) - 1, 0);
    if (nextCount > 0) counts[previousReaction] = nextCount;
    else delete counts[previousReaction];
  }
  if (nextReaction) counts[nextReaction] = (counts[nextReaction] ?? 0) + 1;
  return counts;
}

function postReactionSummary(reactionCounts: PostReactionCounts, selectedReaction: PostReaction | null, likesCount: number, ui: I18nDictionary["social"]["ui"]): Array<{ value: PostReaction; symbol: string; label: string }> {
  const ranked = POST_REACTIONS
    .map((option, index) => ({ ...postReactionOption(option.value, ui), count: reactionCounts[option.value] ?? 0, index }))
    .filter((item) => item.count > 0)
    .sort((first, second) => second.count - first.count || first.index - second.index)
    .slice(0, 3);
  if (ranked.length) return ranked;
  const fallback = selectedReaction ?? (likesCount > 0 ? "like" : null);
  return fallback ? [postReactionOption(fallback, ui)] : [];
}

function ReadMoreText({ text, ui, limit, className = "" }: { text: string; ui: I18nDictionary["social"]["ui"]; limit: number; className?: string }) {
  const [expanded, setExpanded] = useState(false);
  const value = text.trim();
  if (!value) return null;
  const shouldClamp = value.length > limit;
  const visibleText = shouldClamp && !expanded ? `${value.slice(0, limit).trimEnd()}...` : value;
  return (
    <p className={`read-more-text ${className}`.trim()}>
      {visibleText}
      {shouldClamp && (
        <>
          {" "}
          <button type="button" className="read-more-toggle" aria-expanded={expanded} onClick={() => setExpanded((open) => !open)}>
            {expanded ? uiLabel(ui, "seeLess", "See less") : uiLabel(ui, "seeMore", "See more")}
          </button>
        </>
      )}
    </p>
  );
}

function CommentRow({
  comment,
  ui,
  authUser,
  onUpdated,
  onDeleted,
  kind = "comment"
}: {
  comment: Record<string, unknown>;
  ui: I18nDictionary["social"]["ui"];
  authUser?: PageProps["auth"]["user"];
  onUpdated: (id: string, body: string) => void;
  onDeleted: (id: string) => void;
  kind?: "comment" | "reply";
}) {
  const commentId = String(comment.id ?? "");
  const initialBody = String(comment.body ?? "").trim();
  const initialRepliesCount = numberValue(comment.replies_count);
  const initialLiked = Boolean(normalizePostReaction(comment.viewer_reaction ?? comment.my_reaction ?? comment.reaction_by_me) || booleanValue(comment.liked_by_me));
  const commentDomId = commentId ? commentId.replace(/[^A-Za-z0-9_-]/g, "-") : "";
  const deleteDialogId = commentDomId ? `${kind}-delete-${commentDomId}` : `${kind}-delete-confirm`;
  const menuId = commentDomId ? `${kind}-menu-${commentDomId}` : undefined;
  const initialLikesCount = numberValue(comment.likes_count);
  const [body, setBody] = useState(initialBody);
  const [editBody, setEditBody] = useState(initialBody);
  const [editing, setEditing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [likeSubmitting, setLikeSubmitting] = useState(false);
  const [status, setStatus] = useState("");
  const [likedByViewer, setLikedByViewer] = useState(initialLiked);
  const [likesCount, setLikesCount] = useState(initialLikesCount);
  const [repliesCount, setRepliesCount] = useState(initialRepliesCount);
  const [repliesOpen, setRepliesOpen] = useState(false);
  const [replies, setReplies] = useState<Array<Record<string, unknown>>>([]);
  const [repliesStatus, setRepliesStatus] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [replying, setReplying] = useState(false);
  const [replyBody, setReplyBody] = useState("");
  const [replySubmitting, setReplySubmitting] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const menuPanelRef = useRef<HTMLDivElement | null>(null);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const editInputRef = useRef<HTMLTextAreaElement | null>(null);
  const replyInputRef = useRef<HTMLInputElement | null>(null);
  const repliesRef = useRef<Array<Record<string, unknown>>>([]);
  const canManage = ownsComment(comment, authUser);
  const canLike = Boolean(authUser && commentId);
  const canReply = kind === "comment" && Boolean(authUser && commentId);
  const canSave = Boolean(commentId && editBody.trim() && !submitting);
  const updateEndpoint = kind === "reply" ? `/api/replies/${encodeURIComponent(commentId)}` : `/api/comments/${encodeURIComponent(commentId)}`;
  const likeSubjectType = kind === "reply" ? "reply" : "comment";
  const editLabel = kind === "reply" ? uiLabel(ui, "editReply", "Edit reply") : uiLabel(ui, "editComment", "Edit comment");
  const deleteLabel = kind === "reply" ? uiLabel(ui, "deleteReply", "Delete reply") : uiLabel(ui, "deleteComment", "Delete comment");
  repliesRef.current = replies;

  function resizeEditInput(element = editInputRef.current) {
    if (!element) return;
    element.style.height = "auto";
    element.style.height = `${Math.min(element.scrollHeight, 160)}px`;
  }

  useEffect(() => {
    setBody(initialBody);
    setEditBody(initialBody);
    setEditing(false);
    setStatus("");
    setDeleteConfirmOpen(false);
    setLikedByViewer(initialLiked);
    setLikesCount(initialLikesCount);
    setLikeSubmitting(false);
    setRepliesCount(initialRepliesCount);
    setRepliesOpen(false);
    setReplies([]);
    setRepliesStatus("idle");
    setReplying(false);
    setReplyBody("");
  }, [commentId, initialBody, initialLiked, initialLikesCount, initialRepliesCount]);

  useEffect(() => {
    if (!menuOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeCommentMenu(true);
    };
    const closeOnOutsidePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Node && menuRef.current?.contains(target)) return;
      setMenuOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    document.addEventListener("pointerdown", closeOnOutsidePointerDown);
    return () => {
      document.removeEventListener("keydown", closeOnEscape);
      document.removeEventListener("pointerdown", closeOnOutsidePointerDown);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!editing) return;
    window.requestAnimationFrame(() => {
      const input = editInputRef.current;
      input?.focus();
      input?.setSelectionRange(input.value.length, input.value.length);
      resizeEditInput(input);
    });
  }, [editing]);

  useEffect(() => {
    if (editing) resizeEditInput();
  }, [editBody, editing]);

  function startEdit() {
    if (!canManage || !commentId) return;
    setMenuOpen(false);
    setEditBody(body);
    setEditing(true);
    setStatus("");
  }

  function cancelEdit() {
    if (submitting) return;
    setEditBody(body);
    setEditing(false);
    focusCommentMenuButton();
  }

  async function submitEdit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextBody = editBody.trim();
    if (!canManage || !commentId || !nextBody || submitting) return;
    let saved = false;
    setSubmitting(true);
    setStatus(uiLabel(ui, "saving", "Saving"));
    try {
      await submitJson(updateEndpoint, "PATCH", { body: nextBody });
      setBody(nextBody);
      setEditBody(nextBody);
      setEditing(false);
      onUpdated(commentId, nextBody);
      saved = true;
      setStatus(kind === "reply" ? uiLabel(ui, "replyUpdated", "Reply updated") : uiLabel(ui, "commentUpdated", "Comment updated"));
    } catch (error) {
      setStatus(localizedErrorMessage(error, kind === "reply" ? uiLabel(ui, "replyUpdateFailed", "Could not update reply") : uiLabel(ui, "commentUpdateFailed", "Could not update comment")));
    } finally {
      setSubmitting(false);
      if (saved) focusCommentMenuButton();
    }
  }

  async function deleteComment() {
    if (!canManage || !commentId || submitting) return;
    setDeleteConfirmOpen(false);
    setSubmitting(true);
    setStatus(kind === "reply" ? uiLabel(ui, "deletingReply", "Deleting reply") : uiLabel(ui, "deletingComment", "Deleting comment"));
    try {
      await submitJson(updateEndpoint, "DELETE", {});
      onDeleted(commentId);
    } catch (error) {
      setStatus(localizedErrorMessage(error, kind === "reply" ? uiLabel(ui, "replyDeleteFailed", "Could not delete reply") : uiLabel(ui, "commentDeleteFailed", "Could not delete comment")));
      setSubmitting(false);
    }
  }

  function requestDeleteComment() {
    if (!canManage || !commentId || submitting) return;
    setMenuOpen(false);
    setDeleteConfirmOpen(true);
  }

  function toggleCommentMenu() {
    setMenuOpen((open) => !open);
  }

  function handleCommentMenuButtonKeyDown(event: ReactKeyboardEvent<HTMLButtonElement>) {
    if (!["Enter", " ", "ArrowDown", "ArrowUp"].includes(event.key)) return;
    event.preventDefault();
    if (menuOpen && (event.key === "Enter" || event.key === " ")) {
      setMenuOpen(false);
      return;
    }
    if (!menuOpen) setMenuOpen(true);
    focusCommentMenuItem(event.key === "ArrowUp");
  }

  function focusCommentMenuItem(last = false) {
    window.requestAnimationFrame(() => {
      const items = focusableElements(menuPanelRef.current);
      const target = last ? items[items.length - 1] : items[0];
      target?.focus({ preventScroll: true });
    });
  }

  function closeCommentMenu(restoreFocus = false) {
    setMenuOpen(false);
    if (restoreFocus) focusCommentMenuButton();
  }

  function focusCommentMenuButton() {
    window.requestAnimationFrame(() => menuButtonRef.current?.focus({ preventScroll: true }));
  }

  async function loadReplies() {
    if (!commentId || kind === "reply" || repliesStatus === "loading") return;
    setRepliesStatus("loading");
    try {
      const response = await fetch(`/api/comments/${encodeURIComponent(commentId)}/replies`, { credentials: "include", headers: apiHeaders() });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(readableError(payload, response.status));
      const rows = extractRows(payload);
      const mergedRows = mergeServerRowsWithLocalRows(rows, repliesRef.current);
      setReplies(mergedRows);
      setRepliesCount(mergedRows.length);
      setRepliesStatus("ready");
    } catch (error) {
      setRepliesStatus((current) => current === "ready" ? "ready" : "error");
    }
  }

  async function toggleCommentLike() {
    if (!canLike || likeSubmitting) return;
    const wasLiked = likedByViewer;
    const previousLikesCount = likesCount;
    const nextLiked = !wasLiked;
    const nextLikesCount = nextLiked ? previousLikesCount + 1 : Math.max(previousLikesCount - 1, 0);
    setLikeSubmitting(true);
    setLikedByViewer(nextLiked);
    setLikesCount(nextLikesCount);
    setStatus("");
    try {
      if (nextLiked) {
        await submitJson("/api/likes", "POST", { subjectType: likeSubjectType, subjectId: commentId, reaction: "like" });
      } else {
        await submitJson("/api/likes", "DELETE", { subjectType: likeSubjectType, subjectId: commentId });
      }
    } catch (error) {
      setLikedByViewer(wasLiked);
      setLikesCount(previousLikesCount);
      setStatus(localizedErrorMessage(error, ui.likeFailed));
    } finally {
      setLikeSubmitting(false);
    }
  }

  function toggleReplies() {
    if (!commentId || kind === "reply") return;
    const nextOpen = !repliesOpen;
    setRepliesOpen(nextOpen);
    if (nextOpen && repliesStatus === "idle") void loadReplies();
  }

  function startReply() {
    if (!canReply) return;
    setReplying(true);
    setRepliesOpen(true);
    if (repliesStatus === "idle") void loadReplies();
    window.requestAnimationFrame(() => replyInputRef.current?.focus());
  }

  function cancelReply() {
    if (replySubmitting) return;
    setReplyBody("");
    setReplying(false);
  }

  function updateReply(replyId: string, body: string) {
    setReplies((current) => current.map((reply) => String(reply.id ?? "") === replyId ? { ...reply, body } : reply));
  }

  function deleteReplyFromList(replyId: string) {
    setReplies((current) => current.filter((reply) => String(reply.id ?? "") !== replyId));
    setRepliesCount((count) => Math.max(count - 1, 0));
  }

  async function submitReply(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextBody = replyBody.trim();
    if (!canReply || !nextBody || replySubmitting) return;
    setReplySubmitting(true);
    setStatus(uiLabel(ui, "publishingReply", "Publishing reply"));
    try {
      const response = await submitJson(`/api/comments/${encodeURIComponent(commentId)}/replies`, "POST", { body: nextBody });
      const id = isRecord(response) && response.id ? String(response.id) : `local-reply-${commentId}-${Date.now()}`;
      const reply = { ...authUser, id, author_id: authUser?.id, body: nextBody, local_status: "created" };
      setReplies((current) => mergeServerRowsWithLocalRows(current, [reply]));
      setRepliesCount((count) => count + 1);
      setRepliesStatus("ready");
      setReplyBody("");
      setReplying(false);
      setStatus("");
    } catch (error) {
      setStatus(localizedErrorMessage(error, uiLabel(ui, "replyFailed", "Reply failed")));
    } finally {
      setReplySubmitting(false);
    }
  }

  return (
    <article className={`comment-item${kind === "reply" ? " reply" : ""}`}>
      <ProfileAvatarLink row={comment} size="sm" className="comment-avatar" />
      <div className="comment-body">
        <div className="comment-bubble-row">
          <div className="comment-bubble">
            <ProfileNameLink row={comment}>{String(comment.display_name ?? comment.username ?? ui.new2Member)}</ProfileNameLink>
            {editing ? (
              <form className="comment-edit-form" onSubmit={submitEdit} aria-busy={submitting}>
                <textarea
                  ref={editInputRef}
                  aria-label={editLabel}
                  value={editBody}
                  onChange={(event) => {
                    setEditBody(event.currentTarget.value);
                    resizeEditInput(event.currentTarget);
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== "Escape") return;
                    event.preventDefault();
                    cancelEdit();
                  }}
                  disabled={submitting}
                  maxLength={2000}
                />
                <span className="comment-edit-actions">
                  <button type="button" onClick={cancelEdit} disabled={submitting}>{ui.cancel}</button>
                  <button type="submit" disabled={!canSave}>{uiLabel(ui, "save", "Save")}</button>
                </span>
              </form>
            ) : (
              <ReadMoreText text={body} ui={ui} limit={kind === "reply" ? 180 : 220} />
            )}
          </div>
          {canManage && (
            <div className="comment-menu-wrap" ref={menuRef}>
              <button ref={menuButtonRef} className="comment-menu-button" type="button" title={ui.more} aria-label={uiLabel(ui, "moreCommentActions", "More comment actions")} aria-haspopup="menu" aria-expanded={menuOpen} aria-controls={menuId} onKeyDown={handleCommentMenuButtonKeyDown} onClick={toggleCommentMenu} disabled={submitting}><MoreHorizontal size={16} /></button>
              {menuOpen && (
                <div id={menuId} className="comment-menu" role="menu" ref={menuPanelRef} onKeyDown={(event) => trapFocusTab(event, event.currentTarget)}>
                  <button type="button" role="menuitem" onClick={startEdit} disabled={submitting}><PencilLine size={16} /><span>{editLabel}</span></button>
                  <button className="destructive" type="button" role="menuitem" onClick={requestDeleteComment} disabled={submitting}><Trash2 size={16} /><span>{deleteLabel}</span></button>
                </div>
              )}
            </div>
          )}
        </div>
        {(canLike || kind === "comment") && (
          <div className="comment-actions-row">
            {canLike && (
              <button className={`comment-like-action${likedByViewer ? " active" : ""}`} type="button" aria-pressed={likedByViewer} onClick={toggleCommentLike} disabled={likeSubmitting}>
                <ThumbsUp size={13} strokeWidth={2.4} aria-hidden="true" />
                <span>{likedByViewer ? uiLabel(ui, "liked", "Liked") : ui.like}{likesCount > 0 ? ` - ${likesCount}` : ""}</span>
              </button>
            )}
            {canReply && <button className="comment-reply-action" type="button" onClick={startReply}><Reply size={13} strokeWidth={2.4} aria-hidden="true" /><span>{uiLabel(ui, "reply", "Reply")}</span></button>}
            {kind === "comment" && repliesCount > 0 && <button type="button" onClick={toggleReplies}>{repliesOpen ? uiLabel(ui, "hideReplies", "Hide replies") : `${repliesCount} ${repliesCount === 1 ? uiLabel(ui, "reply", "reply") : uiLabel(ui, "replies", "replies")}`}</button>}
          </div>
        )}
        {status && <span className="comment-status" role="status" aria-live="polite">{status}</span>}
        {repliesOpen && kind === "comment" && (
          <div className="comment-replies">
            {repliesStatus === "loading" && <CommentLoadingSkeleton label={ui.loadingComments} compact />}
            {repliesStatus === "error" && <p>{ui.commentsUnavailable}</p>}
            {repliesStatus === "ready" && replies.map((reply) => <CommentRow comment={reply} ui={ui} authUser={authUser} onUpdated={updateReply} onDeleted={deleteReplyFromList} kind="reply" key={String(reply.id)} />)}
            {replying && authUser && (
              <form className="comment-reply-form" onSubmit={submitReply} aria-busy={replySubmitting}>
                <ProfileAvatarLink row={authUser} size="sm" className="comment-avatar" />
                <div className="comment-input-shell">
                  <input ref={replyInputRef} aria-label={uiLabel(ui, "writeReply", "Write a reply")} value={replyBody} onChange={(event) => setReplyBody(event.currentTarget.value)} onKeyDown={(event) => { if (event.key === "Escape") cancelReply(); }} placeholder={uiLabel(ui, "writeReply", "Write a reply")} disabled={replySubmitting} />
                  <button type="submit" aria-label={uiLabel(ui, "postReply", "Post reply")} title={uiLabel(ui, "postReply", "Post reply")} disabled={!replyBody.trim() || replySubmitting}><Send size={16} /></button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
      {deleteConfirmOpen && (
        <DeleteConfirmDialog
          idBase={deleteDialogId}
          title={deleteLabel}
          body={kind === "reply" ? uiLabel(ui, "deleteReplyConfirm", "Delete this reply?") : uiLabel(ui, "deleteCommentConfirm", "Delete this comment?")}
          confirmLabel={deleteLabel}
          busyLabel={kind === "reply" ? uiLabel(ui, "deletingReply", "Deleting reply") : uiLabel(ui, "deletingComment", "Deleting comment")}
          cancelLabel={ui.cancel}
          busy={submitting}
          onCancel={() => setDeleteConfirmOpen(false)}
          onConfirm={deleteComment}
        />
      )}
    </article>
  );
}

function mergeServerRowsWithLocalRows(primaryRows: Array<Record<string, unknown>>, localRows: Array<Record<string, unknown>>): Array<Record<string, unknown>> {
  const primaryIds = new Set(primaryRows.map((row) => String(row.id ?? "")).filter(Boolean));
  const uniqueLocalRows = localRows.filter((row) => {
    const id = String(row.id ?? "");
    return id && String(row.local_status ?? "") === "created" && !primaryIds.has(id);
  });
  return [...primaryRows, ...uniqueLocalRows];
}

function PostRow({ row, ui, authUser, localeCode, detail = false }: { row: Record<string, unknown>; ui: I18nDictionary["social"]["ui"]; authUser?: PageProps["auth"]["user"]; localeCode: string; detail?: boolean }) {
  const instanceId = useId().replace(/[^A-Za-z0-9_-]/gu, "");
  const postId = String(row.id ?? "");
  const postSlug = String(row.slug ?? (postId || "post"));
  const postHref = `/post/${encodeURIComponent(postSlug)}`;
  const postDomId = postId ? `${postId.replace(/[^A-Za-z0-9_-]/g, "-")}-${instanceId}` : "";
  const commentsPanelId = postDomId ? `post-comments-${postDomId}` : undefined;
  const postMenuId = postDomId ? `post-menu-${postDomId}` : undefined;
  const reactionMenuId = postDomId ? `post-reactions-${postDomId}` : undefined;
  const shareMenuId = postDomId ? `post-share-${postDomId}` : undefined;
  const initialReaction = normalizePostReaction(row.my_reaction ?? row.reaction_by_me ?? (booleanValue(row.liked_by_me) ? "like" : ""));
  const initialPostBody = String(row.body ?? row.title ?? "").trim();
  const [reaction, setReaction] = useState<PostReaction | null>(initialReaction);
  const [bookmarked, setBookmarked] = useState(booleanValue(row.bookmarked_by_me));
  const [reported, setReported] = useState(booleanValue(row.reported_by_me));
  const [likesCount, setLikesCount] = useState(numberValue(row.likes_count));
  const [reactionCounts, setReactionCounts] = useState<PostReactionCounts>(() => postReactionCountsFromRow(row));
  const [commentsCount, setCommentsCount] = useState(numberValue(row.comments_count));
  const [sharesCount, setSharesCount] = useState(numberValue(row.shares_count ?? row.share_count));
  const [moreOpen, setMoreOpen] = useState(false);
  const [reactionPickerOpen, setReactionPickerOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(detail);
  const [likesOpen, setLikesOpen] = useState(false);
  const [comments, setComments] = useState<Array<Record<string, unknown>>>([]);
  const [commentsStatus, setCommentsStatus] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [commentBody, setCommentBody] = useState("");
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [shareSubmitting, setShareSubmitting] = useState(false);
  const [actionStatus, setActionStatus] = useState("");
  const [currentPostBody, setCurrentPostBody] = useState(initialPostBody);
  const [editBody, setEditBody] = useState(initialPostBody);
  const [editingPost, setEditingPost] = useState(false);
  const [postMutating, setPostMutating] = useState(false);
  const [postDeleted, setPostDeleted] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const postMenuRef = useRef<HTMLDivElement | null>(null);
  const postMenuActionRef = useRef<HTMLButtonElement | null>(null);
  const shareMenuRef = useRef<HTMLDivElement | null>(null);
  const shareActionRef = useRef<HTMLButtonElement | null>(null);
  const reactionPickerRef = useRef<HTMLDivElement | null>(null);
  const reactionActionRef = useRef<HTMLButtonElement | null>(null);
  const likesCounterRef = useRef<HTMLButtonElement | null>(null);
  const reactionButtonRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const likesDialogRef = useRef<HTMLElement | null>(null);
  useModalLifecycle(likesOpen, likesDialogRef, undefined, true, likesCounterRef);
  const commentInputRef = useRef<HTMLInputElement | null>(null);
  const commentActionRef = useRef<HTMLButtonElement | null>(null);
  const editPostInputRef = useRef<HTMLTextAreaElement | null>(null);
  const reactionPressTimerRef = useRef<number | null>(null);
  const reactionCloseTimerRef = useRef<number | null>(null);
  const reactionLongPressRef = useRef(false);
  const hasLikes = likesCount > 0;
  const hasComments = commentsCount > 0;
  const hasShares = sharesCount > 0;
  const hasEngagement = hasLikes || hasComments || hasShares;
  const likersResult = useApi(likesOpen && hasLikes && postId ? `/api/likes?subjectType=post&subjectId=${encodeURIComponent(postId)}` : null);
  const authorRow = authorProfileRow(row);
  const postBody = currentPostBody;
  const mediaItems = postMediaItems(row);
  const canManagePost = ownsPost(row, authUser);
  const canSavePostEdit = Boolean(postId && !postMutating && (editBody.trim() || mediaItems.length > 0));
  const selectedReaction = reaction ? postReactionOption(reaction, ui) : null;
  const reactionSummary = postReactionSummary(reactionCounts, reaction, likesCount, ui);
  const openPostLabel = uiLabel(ui, "openPost", "Open post");
  const logInToLikeLabel = uiLabel(ui, "logInToLike", "Log in to like");
  const postLoginHref = authRouteHrefForRedirect("/login", postHref);
  const commentLoginHref = postLoginHref;
  const commentSignupHref = authRouteHrefForRedirect("/signup", postHref);
  const shareLoginHref = postLoginHref;
  const postTimeValue = postTimestampValue(row);
  const postTimeLabel = postRelativeTimeLabel(postTimeValue, localeCode, ui);
  const postTimeTitle = postAbsoluteTimeLabel(postTimeValue, localeCode, ui);
  const visibilityMeta = postVisibilityMeta(row.visibility, ui);
  const VisibilityIcon = visibilityMeta.Icon;

  function resizePostEditInput(element = editPostInputRef.current) {
    if (!element) return;
    element.style.height = "auto";
    element.style.height = `${Math.min(element.scrollHeight, 220)}px`;
  }

  useEffect(() => {
    setReaction(initialReaction);
    setLikesCount(numberValue(row.likes_count));
    setReactionCounts(postReactionCountsFromRow(row));
    setSharesCount(numberValue(row.shares_count ?? row.share_count));
    setCurrentPostBody(initialPostBody);
    setEditBody(initialPostBody);
    setEditingPost(false);
    setPostDeleted(false);
    setDeleteConfirmOpen(false);
    setComments([]);
    setCommentsStatus("idle");
    setCommentBody("");
    setCommentSubmitting(false);
    setCommentsOpen(detail);
  }, [postId, initialPostBody, detail]);

  useEffect(() => {
    if (!editingPost) return;
    window.requestAnimationFrame(() => {
      const input = editPostInputRef.current;
      input?.focus();
      input?.setSelectionRange(input.value.length, input.value.length);
      resizePostEditInput(input);
    });
  }, [editingPost]);

  useEffect(() => {
    if (editingPost) resizePostEditInput();
  }, [editBody, editingPost]);

  useEffect(() => {
    if (!commentsOpen || !postId) return;
    let alive = true;
    setCommentsStatus("loading");
    fetch(`/api/comments?postId=${encodeURIComponent(postId)}`, { credentials: "include", headers: apiHeaders() })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!alive) return;
        if (!response.ok) {
          setCommentsStatus((current) => current === "ready" ? "ready" : "error");
          return;
        }
        const rows = extractRows(payload);
        setComments((current) => mergeServerRowsWithLocalRows(rows, current));
        setCommentsStatus("ready");
      })
      .catch(() => {
        if (alive) setCommentsStatus((current) => current === "ready" ? "ready" : "error");
      });
    return () => {
      alive = false;
    };
  }, [commentsOpen, postId, commentsCount]);

  useEffect(() => {
    if (!likesOpen) return;
    likesDialogRef.current?.focus();
  }, [likesOpen]);

  useEffect(() => () => {
    clearReactionPressTimer();
    clearReactionCloseTimer();
  }, []);

  useEffect(() => {
    if (!reactionPickerOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeReactionPicker(true);
    };
    const closeOnOutsidePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Node && reactionPickerRef.current?.contains(target)) return;
      setReactionPickerOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    document.addEventListener("pointerdown", closeOnOutsidePointerDown);
    return () => {
      document.removeEventListener("keydown", closeOnEscape);
      document.removeEventListener("pointerdown", closeOnOutsidePointerDown);
    };
  }, [reactionPickerOpen]);

  useEffect(() => {
    if (!moreOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") closePostMenu(true);
    };
    const closeOnOutsidePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Node && postMenuRef.current?.contains(target)) return;
      setMoreOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    document.addEventListener("pointerdown", closeOnOutsidePointerDown);
    return () => {
      document.removeEventListener("keydown", closeOnEscape);
      document.removeEventListener("pointerdown", closeOnOutsidePointerDown);
    };
  }, [moreOpen]);

  useEffect(() => {
    if (!shareOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeShareMenu(true);
    };
    const closeOnOutsidePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Node && shareMenuRef.current?.contains(target)) return;
      setShareOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    document.addEventListener("pointerdown", closeOnOutsidePointerDown);
    return () => {
      document.removeEventListener("keydown", closeOnEscape);
      document.removeEventListener("pointerdown", closeOnOutsidePointerDown);
    };
  }, [shareOpen]);

  function requireAuthenticatedPostAction(message = ui.logInToContinueTitle): boolean {
    if (authUser) return true;
    setReactionPickerOpen(false);
    setMoreOpen(false);
    setActionStatus(message);
    return false;
  }

  async function setPostReaction(nextReaction: PostReaction | null, restoreFocus = false) {
    if (!postId) return;
    if (!requireAuthenticatedPostAction(logInToLikeLabel)) return;
    if (nextReaction === reaction) {
      closeReactionPicker(restoreFocus);
      return;
    }
    const previousReaction = reaction;
    const previousLikesCount = likesCount;
    const previousReactionCounts = reactionCounts;
    const nextLikesCount = !previousReaction && nextReaction ? previousLikesCount + 1 : previousReaction && !nextReaction ? Math.max(previousLikesCount - 1, 0) : previousLikesCount;
    const nextReactionCounts = adjustedPostReactionCounts(reactionCounts, previousReaction, nextReaction);
    setActionStatus(nextReaction ? ui.addingLike : ui.removingLike);
    setReaction(nextReaction);
    setLikesCount(nextLikesCount);
    setReactionCounts(nextReactionCounts);
    closeReactionPicker(restoreFocus);
    try {
      if (nextReaction) {
        await submitJson("/api/likes", "POST", { subjectType: "post", subjectId: postId, reaction: nextReaction });
        if (likesOpen) likersResult.reload();
        setActionStatus(nextReaction === "like" ? ui.liked : postReactionOption(nextReaction, ui).label);
      } else {
        await submitJson("/api/likes", "DELETE", { subjectType: "post", subjectId: postId });
        if (likesOpen && nextLikesCount > 0) likersResult.reload();
        if (nextLikesCount === 0) setLikesOpen(false);
        setActionStatus(ui.likeRemoved);
      }
    } catch (error) {
      setReaction(previousReaction);
      setLikesCount(previousLikesCount);
      setReactionCounts(previousReactionCounts);
      setActionStatus(localizedErrorMessage(error, ui.likeFailed));
    }
  }

  async function toggleLike() {
    await setPostReaction(reaction ? null : "like");
  }

  function clearReactionPressTimer() {
    if (reactionPressTimerRef.current === null) return;
    window.clearTimeout(reactionPressTimerRef.current);
    reactionPressTimerRef.current = null;
  }

  function clearReactionCloseTimer() {
    if (reactionCloseTimerRef.current === null) return;
    window.clearTimeout(reactionCloseTimerRef.current);
    reactionCloseTimerRef.current = null;
  }

  function selectedReactionIndex() {
    const selectedIndex = reaction ? POST_REACTIONS.findIndex((option) => option.value === reaction) : 0;
    return selectedIndex >= 0 ? selectedIndex : 0;
  }

  function focusReactionOption(index = selectedReactionIndex()) {
    const lastIndex = POST_REACTIONS.length - 1;
    const nextIndex = Math.min(Math.max(index, 0), lastIndex);
    window.requestAnimationFrame(() => reactionButtonRefs.current[nextIndex]?.focus({ preventScroll: true }));
  }

  function openReactionPicker(focusOption = false) {
    if (!postId) return;
    clearReactionCloseTimer();
    if (!authUser) {
      setReactionPickerOpen(false);
      return;
    }
    setMoreOpen(false);
    setShareOpen(false);
    setReactionPickerOpen(true);
    if (focusOption) focusReactionOption();
  }

  function closeReactionPicker(focusAction = false) {
    clearReactionCloseTimer();
    setReactionPickerOpen(false);
    if (focusAction) window.requestAnimationFrame(() => reactionActionRef.current?.focus({ preventScroll: true }));
  }

  function scheduleReactionPickerClose() {
    clearReactionCloseTimer();
    reactionCloseTimerRef.current = window.setTimeout(() => {
      reactionCloseTimerRef.current = null;
      setReactionPickerOpen(false);
    }, 180);
  }

  function focusRelativeReactionOption(delta: number) {
    const currentIndex = reactionButtonRefs.current.findIndex((button) => button === document.activeElement);
    const baseIndex = currentIndex >= 0 ? currentIndex : selectedReactionIndex();
    focusReactionOption((baseIndex + delta + POST_REACTIONS.length) % POST_REACTIONS.length);
  }

  function handleReactionActionKeyDown(event: ReactKeyboardEvent<HTMLButtonElement>) {
    if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
    event.preventDefault();
    openReactionPicker(true);
  }

  function handleReactionOptionKeyDown(event: ReactKeyboardEvent<HTMLButtonElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      closeReactionPicker(true);
      return;
    }
    if (event.key === "Home") {
      event.preventDefault();
      focusReactionOption(0);
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      focusReactionOption(POST_REACTIONS.length - 1);
      return;
    }
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      event.preventDefault();
      focusRelativeReactionOption(1);
      return;
    }
    if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      event.preventDefault();
      focusRelativeReactionOption(-1);
      return;
    }
  }

  function startReactionPress() {
    if (!postId) return;
    reactionLongPressRef.current = false;
    clearReactionPressTimer();
    reactionPressTimerRef.current = window.setTimeout(() => {
      reactionLongPressRef.current = true;
      openReactionPicker(true);
    }, 420);
  }

  function finishReactionPress() {
    clearReactionPressTimer();
  }

  function handleReactionClick() {
    if (reactionLongPressRef.current) {
      reactionLongPressRef.current = false;
      return;
    }
    void toggleLike();
  }

  async function toggleBookmark() {
    if (!postId) return;
    if (!requireAuthenticatedPostAction()) return;
    setActionStatus(bookmarked ? ui.removingBookmark : ui.savingBookmark);
    try {
      if (bookmarked) {
        await submitJson("/api/bookmarks", "DELETE", { subjectType: "post", subjectId: postId });
        setBookmarked(false);
        setActionStatus(ui.bookmarkRemoved);
      } else {
        await submitJson("/api/bookmarks", "POST", { subjectType: "post", subjectId: postId, collection: "saved_posts" });
        setBookmarked(true);
        setActionStatus(ui.bookmarked);
      }
    } catch (error) {
      setActionStatus(localizedErrorMessage(error, ui.bookmarkFailed));
    }
  }

  async function reportPost() {
    if (!postId || reported) return;
    if (!requireAuthenticatedPostAction()) return;
    setActionStatus(ui.sendingReport);
    try {
      await submitJson("/api/reports", "POST", { subjectType: "post", subjectId: postId, reason: ui.report, metadata: { source: "home_feed" } });
      setReported(true);
      setActionStatus(ui.reportedForReview);
    } catch (error) {
      setActionStatus(localizedErrorMessage(error, ui.reportFailed));
    }
  }

  async function copyPostLink(restoreShareFocus = false) {
    const url = new URL(postHref, window.location.origin).toString();
    setActionStatus(ui.copyingLink);
    try {
      await copyTextToClipboard(url, ui.clipboardUnavailable);
      closeShareMenu(restoreShareFocus);
      setActionStatus(ui.linkCopied);
    } catch (error) {
      closeShareMenu(restoreShareFocus);
      setActionStatus(`${ui.linkReady}: ${url}`);
    }
  }

  async function sharePost() {
    if (!postId || shareSubmitting) return;
    if (!authUser) {
      closeShareMenu(true);
      setActionStatus(ui.logInToContinueTitle);
      return;
    }
    closeShareMenu(true);
    setShareSubmitting(true);
    setActionStatus(ui.sharingPost);
    try {
      await submitJson("/api/shares", "POST", { subjectType: "post", subjectId: postId });
      setSharesCount((count) => count + 1);
      setActionStatus(ui.postShared);
    } catch (error) {
      setActionStatus(localizedErrorMessage(error, ui.shareFailed));
    } finally {
      setShareSubmitting(false);
    }
  }

  function startPostEdit() {
    if (!canManagePost || !postId) return;
    setMoreOpen(false);
    setEditBody(currentPostBody);
    setEditingPost(true);
    setActionStatus("");
  }

  function cancelPostEdit() {
    if (postMutating) return;
    setEditBody(currentPostBody);
    setEditingPost(false);
    focusPostMenuButton();
  }

  async function submitPostEdit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextBody = editBody.trim();
    if (!canManagePost || !postId || postMutating || (!nextBody && mediaItems.length === 0)) return;
    let saved = false;
    setPostMutating(true);
    setActionStatus(uiLabel(ui, "saving", "Saving"));
    try {
      await submitJson(`/api/posts/${encodeURIComponent(postId)}`, "PATCH", { body: nextBody });
      setCurrentPostBody(nextBody);
      setEditBody(nextBody);
      setEditingPost(false);
      saved = true;
      setActionStatus(uiLabel(ui, "postUpdated", "Post updated"));
    } catch (error) {
      setActionStatus(localizedErrorMessage(error, uiLabel(ui, "postUpdateFailed", "Could not update post")));
    } finally {
      setPostMutating(false);
      if (saved) focusPostMenuButton();
    }
  }

  async function deletePost() {
    if (!canManagePost || !postId || postMutating) return;
    setDeleteConfirmOpen(false);
    setPostMutating(true);
    setActionStatus(uiLabel(ui, "deletingPost", "Deleting post"));
    try {
      await submitJson(`/api/posts/${encodeURIComponent(postId)}`, "DELETE", {});
      setPostDeleted(true);
    } catch (error) {
      setActionStatus(localizedErrorMessage(error, uiLabel(ui, "postDeleteFailed", "Could not delete post")));
    } finally {
      setPostMutating(false);
    }
  }

  function requestDeletePost() {
    if (!canManagePost || !postId || postMutating) return;
    setMoreOpen(false);
    setDeleteConfirmOpen(true);
  }

  function togglePostMenu() {
    const nextOpen = !moreOpen;
    setMoreOpen(nextOpen);
    if (!nextOpen) return;
    setReactionPickerOpen(false);
    setShareOpen(false);
  }

  function handlePostMenuButtonKeyDown(event: ReactKeyboardEvent<HTMLButtonElement>) {
    if (!["Enter", " ", "ArrowDown", "ArrowUp"].includes(event.key)) return;
    event.preventDefault();
    if (moreOpen && (event.key === "Enter" || event.key === " ")) {
      closePostMenu();
      return;
    }
    if (!moreOpen) {
      setMoreOpen(true);
      setReactionPickerOpen(false);
      setShareOpen(false);
    }
    focusMenuItem(postMenuId, event.key === "ArrowUp");
  }

  function closePostMenu(restoreFocus = false) {
    setMoreOpen(false);
    if (restoreFocus) focusPostMenuButton();
  }

  function focusPostMenuButton() {
    window.requestAnimationFrame(() => postMenuActionRef.current?.focus({ preventScroll: true }));
  }

  function closeShareMenu(restoreFocus = false) {
    setShareOpen(false);
    if (restoreFocus) window.requestAnimationFrame(() => shareActionRef.current?.focus({ preventScroll: true }));
  }

  function toggleShareMenu() {
    if (!postId) return;
    const nextOpen = !shareOpen;
    setShareOpen(nextOpen);
    if (!nextOpen) return;
    setReactionPickerOpen(false);
    setMoreOpen(false);
  }

  function handleShareMenuButtonKeyDown(event: ReactKeyboardEvent<HTMLButtonElement>) {
    if (!["Enter", " ", "ArrowDown", "ArrowUp"].includes(event.key)) return;
    event.preventDefault();
    if (shareOpen && (event.key === "Enter" || event.key === " ")) {
      closeShareMenu();
      return;
    }
    if (!shareOpen) {
      setShareOpen(true);
      setReactionPickerOpen(false);
      setMoreOpen(false);
    }
    focusMenuItem(shareMenuId, event.key === "ArrowUp");
  }

  function openComments(focusInput = false) {
    setCommentsOpen(true);
    if (focusInput && authUser) window.requestAnimationFrame(() => commentInputRef.current?.focus());
  }

  function toggleCommentComposer() {
    if (!postId) return;
    if (commentsOpen) {
      if (authUser) window.requestAnimationFrame(() => commentInputRef.current?.focus());
      return;
    }
    openComments(Boolean(authUser));
  }

  function openLikesList() {
    if (!postId || !hasLikes) return;
    setLikesOpen(true);
  }

  function closeLikesList() {
    setLikesOpen(false);
    window.requestAnimationFrame(() => likesCounterRef.current?.focus({ preventScroll: true }));
  }

  function handleLikesDialogKeyDown(event: ReactKeyboardEvent<HTMLElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      closeLikesList();
      return;
    }
    trapFocusTab(event, likesDialogRef.current);
  }

  function toggleCommentsList() {
    if (!postId || !hasComments) return;
    setCommentsOpen((open) => !open);
  }

  function closeCommentsFromInput() {
    setCommentsOpen(false);
    window.requestAnimationFrame(() => commentActionRef.current?.focus());
  }

  function handleCommentInputKeyDown(event: ReactKeyboardEvent<HTMLInputElement>) {
    if (event.key !== "Escape" || commentSubmitting) return;
    event.preventDefault();
    if (commentBody.trim()) {
      event.currentTarget.blur();
      return;
    }
    closeCommentsFromInput();
  }

  async function submitComment(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const body = commentBody.trim();
    if (!postId || !body || commentSubmitting) return;
    if (!requireAuthenticatedPostAction()) return;
    setCommentSubmitting(true);
    setActionStatus(ui.publishingComment);
    try {
      const response = await submitJson(`/api/posts/${encodeURIComponent(postId)}/comments`, "POST", { body });
      setCommentBody("");
      setCommentsOpen(true);
      setCommentsCount((count) => count + 1);
      if (authUser) {
        const id = isRecord(response) && response.id ? String(response.id) : `local-${postId}-${Date.now()}`;
        setComments((current) => mergeServerRowsWithLocalRows(current, [{ ...authUser, id, author_id: authUser.id, body, local_status: "created" }]));
        setCommentsStatus("ready");
      }
      setActionStatus("");
      window.requestAnimationFrame(() => commentInputRef.current?.focus());
    } catch (error) {
      setActionStatus(localizedErrorMessage(error, ui.commentFailed));
    } finally {
      setCommentSubmitting(false);
    }
  }

  function updateComment(commentId: string, body: string) {
    setComments((current) => current.map((comment) => String(comment.id ?? "") === commentId ? { ...comment, body } : comment));
  }

  function deleteCommentFromList(commentId: string) {
    setComments((current) => current.filter((comment) => String(comment.id ?? "") !== commentId));
    setCommentsCount((count) => Math.max(count - 1, 0));
  }

  if (postDeleted) return null;

  return (
    <div className="post-content">
      <header className="feed-item-header">
        <ProfileAvatarLink row={authorRow} size="sm" className="feed-avatar" />
        <div className="feed-author-meta">
          <ProfileNameLink row={authorRow}>{String(row.display_name ?? row.username ?? row.author_id ?? "New2.EU")}</ProfileNameLink>
          <span className="feed-post-meta">
            {postTimeLabel && postId ? (
              <a className="feed-post-link" href={postHref} aria-label={`${openPostLabel}: ${postTimeTitle || postTimeLabel}`} title={postTimeTitle} onClick={(event) => handleOpenSNRouteClick(event, postHref)}>
                <time dateTime={postTimeValue}>{postTimeLabel}</time>
              </a>
            ) : (
              postTimeLabel && <time dateTime={postTimeValue} title={postTimeTitle}>{postTimeLabel}</time>
            )}
            {postTimeLabel && <span aria-hidden="true">&middot;</span>}
            <span className="post-visibility-meta" aria-label={`${ui.visibility}: ${visibilityMeta.label}`} title={`${ui.visibility}: ${visibilityMeta.label}`}>
              <VisibilityIcon size={12} strokeWidth={2.4} />
            </span>
          </span>
        </div>
        <div className="post-menu-wrap" ref={postMenuRef}>
          <button ref={postMenuActionRef} className="post-menu-button" type="button" title={ui.more} aria-label={ui.morePostActions} aria-haspopup="menu" aria-expanded={moreOpen} aria-controls={postMenuId} onKeyDown={handlePostMenuButtonKeyDown} onClick={togglePostMenu}><MoreHorizontal size={18} /></button>
          {moreOpen && (
            <div id={postMenuId} className="post-menu" role="menu" onKeyDown={(event) => handleMenuPanelKeyDown(event, event.currentTarget)}>
              {canManagePost && <button type="button" role="menuitem" disabled={!postId || postMutating} onClick={startPostEdit}><PencilLine size={16} /><span>{uiLabel(ui, "editPost", "Edit post")}</span></button>}
              {canManagePost && <button className="destructive" type="button" role="menuitem" disabled={!postId || postMutating} onClick={requestDeletePost}><Trash2 size={16} /><span>{uiLabel(ui, "deletePost", "Delete post")}</span></button>}
              {authUser ? (
                <button className={bookmarked ? "active" : ""} type="button" role="menuitem" disabled={!postId} onClick={() => { closePostMenu(true); toggleBookmark(); }}><Bookmark size={16} /><span>{bookmarked ? ui.bookmarked : ui.bookmark}</span></button>
              ) : (
                <a href={postLoginHref} role="menuitem" onClick={(event) => handleOpenSNRouteClick(event, postLoginHref, () => setMoreOpen(false))}><Bookmark size={16} /><span>{uiLabel(ui, "logInToSave", "Log in to save")}</span></a>
              )}
              <button type="button" role="menuitem" onClick={() => { closePostMenu(true); copyPostLink(); }}><Link2 size={16} /><span>{ui.copyLink}</span></button>
              {authUser ? (
                <button type="button" role="menuitem" disabled={!postId || reported} onClick={() => { closePostMenu(true); reportPost(); }}><Flag size={16} /><span>{reported ? ui.reported : ui.report}</span></button>
              ) : (
                <a href={postLoginHref} role="menuitem" onClick={(event) => handleOpenSNRouteClick(event, postLoginHref, () => setMoreOpen(false))}><Flag size={16} /><span>{uiLabel(ui, "logInToReport", "Log in to report")}</span></a>
              )}
            </div>
          )}
        </div>
      </header>
      {editingPost ? (
        <form className="post-edit-form" onSubmit={submitPostEdit} aria-busy={postMutating}>
          <textarea
            ref={editPostInputRef}
            aria-label={uiLabel(ui, "editPost", "Edit post")}
            value={editBody}
            onChange={(event) => {
              setEditBody(event.currentTarget.value);
              resizePostEditInput(event.currentTarget);
            }}
            onKeyDown={(event) => {
              if (event.key !== "Escape") return;
              event.preventDefault();
              cancelPostEdit();
            }}
            disabled={postMutating}
            maxLength={5000}
          />
          <div className="post-edit-actions">
            <button type="button" onClick={cancelPostEdit} disabled={postMutating}>{ui.cancel}</button>
            <button type="submit" disabled={!canSavePostEdit}>{uiLabel(ui, "save", "Save")}</button>
          </div>
        </form>
      ) : (
        <ReadMoreText text={postBody} ui={ui} limit={420} className="post-body-text" />
      )}
      {row.shared_post_id ? <div className="member-shared-post">{isRecord(row.original_post) ? renderPostRow(row.original_post, ui, authUser, localeCode) : <p>{memberCopy(localeCode).content_not_found}</p>}</div> : null}
      <PostMediaGrid items={mediaItems} ui={ui} />
      {hasEngagement && (
        <div className="post-counters">
          {hasLikes && (
            <div className="post-counter-reactions">
              {postId ? (
                <button ref={likesCounterRef} className="post-counter-button post-reaction-counter" type="button" onClick={openLikesList}>
                  {reactionSummary.length > 0 && (
                    <span className="post-reaction-summary" aria-hidden="true">
                      {reactionSummary.map((item) => <span key={item.value}>{item.symbol}</span>)}
                    </span>
                  )}
                  <span>{likesCount} {ui.likes}</span>
                </button>
              ) : (
                <span className="post-counter-static">{likesCount} {ui.likes}</span>
              )}
            </div>
          )}
          {(hasComments || hasShares) && (
            <div className="post-counter-meta">
              {hasComments && (
                postId ? (
                  <button className="post-counter-button" type="button" aria-expanded={commentsOpen} aria-controls={commentsPanelId} onClick={toggleCommentsList}>{commentsCount} {ui.comments}</button>
                ) : (
                  <span className="post-counter-static">{commentsCount} {ui.comments}</span>
                )
              )}
              {hasShares && <span className="post-counter-static">{sharesCount} {ui.shares}</span>}
            </div>
          )}
        </div>
      )}
      <footer className="post-actions">
        <div
          ref={reactionPickerRef}
          className={`post-reaction-wrap${reactionPickerOpen ? " open" : ""}`}
          onMouseEnter={() => openReactionPicker()}
          onMouseLeave={scheduleReactionPickerClose}
          onBlur={(event) => {
            const nextFocus = event.relatedTarget;
            if (!(nextFocus instanceof Node) || !event.currentTarget.contains(nextFocus)) closeReactionPicker();
          }}
        >
          <button
            ref={reactionActionRef}
            className={`post-action post-reaction-action${reaction ? ` active reaction-${reaction}` : ""}`}
            type="button"
            title={selectedReaction?.label ?? ui.like}
            aria-haspopup="menu"
            aria-expanded={reactionPickerOpen}
            aria-controls={reactionMenuId}
            onPointerDown={startReactionPress}
            onPointerUp={finishReactionPress}
            onPointerCancel={finishReactionPress}
            onPointerLeave={finishReactionPress}
            onKeyDown={handleReactionActionKeyDown}
            onClick={handleReactionClick}
            disabled={!postId}
          >
            {selectedReaction ? <span className="post-reaction-symbol" aria-hidden="true">{selectedReaction.symbol}</span> : <ThumbsUp size={18} />}
            <span>{selectedReaction?.label ?? ui.like}</span>
          </button>
          {reactionPickerOpen && postId && (
            <div id={reactionMenuId} className="post-reaction-picker" role="menu" aria-label={uiLabel(ui, "chooseReaction", "Choose reaction")} onKeyDown={(event) => trapFocusTab(event, event.currentTarget)}>
              {POST_REACTIONS.map((option, index) => {
                const item = postReactionOption(option.value, ui);
                return (
                  <button
                    ref={(element) => { reactionButtonRefs.current[index] = element; }}
                    className={reaction === option.value ? "active" : ""}
                    type="button"
                    role="menuitemradio"
                    aria-checked={reaction === option.value}
                    aria-label={item.label}
                    title={item.label}
                    key={option.value}
                    onKeyDown={handleReactionOptionKeyDown}
                    onClick={() => setPostReaction(option.value, true)}
                  >
                    <span aria-hidden="true">{item.symbol}</span>
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <button ref={commentActionRef} className={`post-action${commentsOpen ? " active" : ""}`} type="button" title={ui.comment} aria-expanded={commentsOpen} aria-controls={commentsPanelId} onClick={toggleCommentComposer} disabled={!postId}><MessageCircle size={18} /> <span>{ui.comment}</span></button>
        <div
          ref={shareMenuRef}
          className={`post-share-wrap${shareOpen ? " open" : ""}`}
          onBlur={(event) => {
            const nextFocus = event.relatedTarget;
            if (!(nextFocus instanceof Node) || !event.currentTarget.contains(nextFocus)) setShareOpen(false);
          }}
        >
          <button ref={shareActionRef} className="post-action" type="button" title={ui.share} aria-haspopup="menu" aria-expanded={shareOpen} aria-controls={shareMenuId} onKeyDown={handleShareMenuButtonKeyDown} onClick={toggleShareMenu} disabled={!postId}><Share2 size={18} /> <span>{ui.share}</span></button>
          {shareOpen && postId && (
            <div id={shareMenuId} className="post-share-menu" role="menu" onKeyDown={(event) => handleMenuPanelKeyDown(event, event.currentTarget)}>
              {authUser ? (
                <button type="button" role="menuitem" onClick={sharePost} disabled={shareSubmitting}><Share2 size={16} /><span>{shareSubmitting ? ui.sharingPost : ui.sharePost}</span></button>
              ) : (
                <a role="menuitem" href={shareLoginHref} onClick={(event) => handleOpenSNRouteClick(event, shareLoginHref, () => setShareOpen(false))}><Share2 size={16} /><span>{uiLabel(ui, "logInToShare", "Log in to share")}</span></a>
              )}
              <button type="button" role="menuitem" onClick={() => copyPostLink(true)}><Link2 size={16} /><span>{ui.copyLink}</span></button>
              <a role="menuitem" href={postHref} onClick={(event) => handleOpenSNRouteClick(event, postHref, () => setShareOpen(false))}><MousePointerClick size={16} /><span>{openPostLabel}</span></a>
            </div>
          )}
        </div>
      </footer>
      {actionStatus && <span className="post-status" role="status" aria-live="polite">{actionStatus}{!authUser && (actionStatus === ui.logInToContinueTitle || actionStatus === logInToLikeLabel) && <a href={postLoginHref} onClick={(event) => handleOpenSNRouteClick(event, postLoginHref)}>{ui.logIn}</a>}</span>}
      {commentsOpen && (
        <section id={commentsPanelId} className="comments-panel">
          {commentsStatus === "loading" && <CommentLoadingSkeleton label={ui.loadingComments} />}
          {commentsStatus === "error" && <p>{ui.commentsUnavailable}</p>}
          {commentsStatus === "ready" && comments.length === 0 && <p>{ui.noCommentsYetBody}</p>}
          {commentsStatus === "ready" && comments.length > 0 && <div className="comment-list">{comments.map((comment) => <CommentRow comment={comment} ui={ui} authUser={authUser} onUpdated={updateComment} onDeleted={deleteCommentFromList} key={String(comment.id)} />)}</div>}
          {authUser ? (
            <form className="comment-form" onSubmit={submitComment} aria-busy={commentSubmitting}>
              <ProfileAvatarLink row={authUser} size="sm" className="comment-avatar" />
              <div className="comment-input-shell">
                <input ref={commentInputRef} aria-label={ui.writeComment} value={commentBody} onChange={(event) => setCommentBody(event.currentTarget.value)} onKeyDown={handleCommentInputKeyDown} placeholder={ui.writeComment} disabled={commentSubmitting} />
                <button type="submit" aria-label={ui.postComment} title={ui.postComment} disabled={!commentBody.trim() || commentSubmitting}><Send size={16} /></button>
              </div>
            </form>
          ) : (
            <div className="comment-form comment-auth-prompt">
              <span className="comment-avatar guest"><CircleUserRound size={18} /></span>
              <div className="comment-auth-actions">
                <a href={commentLoginHref} onClick={(event) => handleOpenSNRouteClick(event, commentLoginHref)}>{uiLabel(ui, "logInToComment", "Log in to comment")}</a>
                <a href={commentSignupHref} onClick={(event) => handleOpenSNRouteClick(event, commentSignupHref)}>{ui.createAccount}</a>
              </div>
            </div>
          )}
        </section>
      )}
      {likesOpen && hasLikes && (
        <div className="post-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closeLikesList(); }}>
          <section className="post-likers-modal" role="dialog" aria-modal="true" aria-label={`${likesCount} ${ui.likes}`} tabIndex={-1} ref={likesDialogRef} onKeyDown={handleLikesDialogKeyDown}>
            <header>
              <div>
                <strong>{likesCount} {ui.likes}</strong>
              </div>
              <button className="icon-only" type="button" aria-label={ui.cancel} onClick={closeLikesList}><X size={18} /></button>
            </header>
            <div className="likers-list">
              {likersResult.status === "loading" && likersResult.rows.length === 0 && <LikersLoadingSkeleton label={ui.loadingResults} />}
              {likersResult.status === "error" && <p className="likers-state">{displayErrorMessage(likersResult.error, ui.unableToLoadView)}</p>}
              {likersResult.status === "ready" && likersResult.rows.length === 0 && <p className="likers-state">0 {ui.likes}</p>}
              {likersResult.rows.map((liker) => {
                const profileHref = profileHrefFromRow(liker);
                const profileName = String(liker.display_name ?? liker.username ?? ui.new2Member);
                const content = (
                  <>
                    <ProfileAvatar row={liker} size="md" className="liker-avatar" />
                    <span className="liker-copy">
                      <span className="profile-name-link">{profileName}</span>
                      {String(liker.username ?? "").trim() && <span>@{String(liker.username)}</span>}
                    </span>
                  </>
                );
                const rowKey = String(liker.id ?? liker.user_id ?? liker.username ?? liker.display_name);
                return profileHref ? <a className="liker-row" href={profileHref} onClick={(event) => handleOpenSNRouteClick(event, profileHref, closeLikesList)} key={rowKey}>{content}</a> : <div className="liker-row" key={rowKey}>{content}</div>;
              })}
            </div>
          </section>
        </div>
      )}
      {deleteConfirmOpen && (
        <DeleteConfirmDialog
          idBase={postDomId ? `post-delete-${postDomId}` : "post-delete-confirm"}
          title={uiLabel(ui, "deletePost", "Delete post")}
          body={uiLabel(ui, "deletePostConfirm", "Delete this post?")}
          confirmLabel={uiLabel(ui, "deletePost", "Delete post")}
          busyLabel={uiLabel(ui, "deletingPost", "Deleting post")}
          cancelLabel={ui.cancel}
          busy={postMutating}
          onCancel={() => setDeleteConfirmOpen(false)}
          onConfirm={deletePost}
        />
      )}
    </div>
  );
}

function DeleteConfirmDialog({ idBase, title, body, confirmLabel, busyLabel, cancelLabel, busy, onCancel, onConfirm, icon: Icon = Trash2, confirmVariant = "danger", error }: { idBase: string; title: string; body: string; confirmLabel: string; busyLabel: string; cancelLabel: string; busy: boolean; onCancel: () => void; onConfirm: () => void; icon?: LucideIcon; confirmVariant?: "primary" | "danger"; error?: string }) {
  const dialogRef = useRef<HTMLElement | null>(null);
  useModalLifecycle(true, dialogRef);
  const titleId = `${idBase}-title`;
  const bodyId = `${idBase}-body`;
  useEffect(() => {
    window.requestAnimationFrame(() => dialogRef.current?.focus());
  }, []);
  function handleDialogKeyDown(event: ReactKeyboardEvent<HTMLElement>) {
    if (event.key === "Escape" && !busy) {
      event.preventDefault();
      onCancel();
      return;
    }
    trapFocusTab(event, dialogRef.current);
  }
  return (
    <div className="post-modal-backdrop delete-confirm-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onCancel(); }}>
      <section className="delete-confirm-dialog" role="alertdialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={bodyId} tabIndex={-1} ref={dialogRef} onKeyDown={handleDialogKeyDown}>
        <header>
          <span className={`delete-confirm-icon ${confirmVariant}`} aria-hidden="true"><Icon size={20} /></span>
          <div>
            <strong id={titleId}>{title}</strong>
            <p id={bodyId}>{body}</p>
          </div>
        </header>
        {error && <InlineStatus state={{ status: "error", message: error }} />}
        <div className="delete-confirm-actions">
          <BaseButton type="button" variant="transparent" onClick={onCancel} disabled={busy}>{cancelLabel}</BaseButton>
          <BaseButton type="button" variant={confirmVariant} loading={busy} disabled={busy} onClick={onConfirm}>{busy ? busyLabel : confirmLabel}</BaseButton>
        </div>
      </section>
    </div>
  );
}

function postMediaItemKind(item: Record<string, unknown>): "image" | "video" | null {
  const contentType = String(item.content_type ?? item.contentType ?? "")
    .trim()
    .toLowerCase();
  const itemType = String(item.type ?? "")
    .trim()
    .toLowerCase();
  if (["video", "movie", "clip"].includes(itemType) || contentType.startsWith("video/")) return "video";
  if (contentType && !contentType.startsWith("image/")) return null;
  return "image";
}

function PostMediaGrid({ items, ui }: { items: Array<Record<string, unknown>>; ui: I18nDictionary["social"]["ui"] }) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const imageItems = items.filter((item) => postMediaItemKind(item) === "image");
  const visibleItems = items.slice(0, 4);
  const overflowCount = Math.max(items.length - visibleItems.length, 0);
  const photosLabel = uiLabel(ui, "photos", "Photos");
  if (items.length === 0) return null;
  return (
    <>
      <div className={`post-media-grid count-${visibleItems.length}`}>
        {visibleItems.map((item, index) => {
          const src = mediaFileSrc(item);
          const metadata = recordFromJson(item.metadata ?? item.metadata_json);
          const kind = postMediaItemKind(item);
          const label =
            displayListValue(metadata.fileName ?? item.file_name ?? item.name) ||
            (kind === "video" ? ui.movie : ui.photo);
          const hasOverflowOverlay = overflowCount > 0 && index === visibleItems.length - 1;
          const mediaLabel = hasOverflowOverlay
            ? `${label}, ${index + 1} / ${items.length}, +${overflowCount} ${photosLabel}`
            : `${label}, ${index + 1} / ${items.length}`;
          if (kind === "video") {
            return (
              <div
                className="post-media-item post-video-item"
                key={String(item.id ?? item.fileId ?? item.file_id ?? src)}
              >
                <video src={src} controls playsInline preload="metadata" aria-label={label} />
                {hasOverflowOverlay && (
                  <span className="post-media-more" aria-hidden="true">
                    +{overflowCount}
                  </span>
                )}
              </div>
            );
          }
          const imageIndex = imageItems.indexOf(item);
          return (
            <button
              className="post-media-item"
              type="button"
              aria-label={mediaLabel}
              onClick={() => setActiveIndex(imageIndex)}
              key={String(item.id ?? item.fileId ?? item.file_id ?? src)}
            >
              <PictureImage src={src} alt={label} />
              {hasOverflowOverlay && (
                <span className="post-media-more" aria-hidden="true">
                  +{overflowCount}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {activeIndex !== null && imageItems[activeIndex] && <PhotoViewer items={imageItems} activeIndex={activeIndex} ui={ui} onClose={() => setActiveIndex(null)} onIndexChange={setActiveIndex} />}
    </>
  );
}

function PhotoViewer({ items, activeIndex, ui, onClose, onIndexChange }: { items: Array<Record<string, unknown>>; activeIndex: number; ui: I18nDictionary["social"]["ui"]; onClose: () => void; onIndexChange: (index: number) => void }) {
  const viewerRef = useRef<HTMLElement | null>(null);
  useModalLifecycle(true, viewerRef);
  const activeItem = items[activeIndex];
  const activeMediaLabel = activeItem
    ? displayListValue(recordFromJson(activeItem.metadata ?? activeItem.metadata_json).fileName ?? activeItem.file_name ?? activeItem.name) || ui.photo
    : ui.photo;
  const previousLabel = uiLabel(ui, "previous", "Previous");
  const nextLabel = uiLabel(ui, "next", "Next");
  const showPrevious = () => onIndexChange((activeIndex + items.length - 1) % items.length);
  const showNext = () => onIndexChange((activeIndex + 1) % items.length);
  function handleViewerKeyDown(event: ReactKeyboardEvent<HTMLElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key === "ArrowLeft" && items.length > 1) {
      event.preventDefault();
      showPrevious();
      return;
    }
    if (event.key === "ArrowRight" && items.length > 1) {
      event.preventDefault();
      showNext();
      return;
    }
    trapFocusTab(event, viewerRef.current);
  }
  if (!activeItem) return null;
  return (
    <div
      className="post-photo-viewer-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="post-photo-viewer"
        role="dialog"
        aria-modal="true"
        aria-label={ui.photo}
        tabIndex={-1}
        ref={viewerRef}
        onKeyDown={handleViewerKeyDown}
      >
        <button
          className="post-photo-close"
          type="button"
          aria-label={ui.cancel}
          aria-keyshortcuts="Escape"
          onClick={onClose}
        >
          <X size={22} />
        </button>
        {items.length > 1 && (
          <button
            className="post-photo-nav previous"
            type="button"
            aria-label={previousLabel}
            aria-keyshortcuts="ArrowLeft"
            onClick={showPrevious}
          >
            <ArrowLeft size={24} />
          </button>
        )}
        <figure>
          <PictureImage src={mediaFileSrc(activeItem)} alt={activeMediaLabel} />
          {items.length > 1 && (
            <figcaption
              aria-live="polite"
              aria-atomic="true"
              aria-label={`${activeMediaLabel}, ${activeIndex + 1} / ${items.length}`}
            >
              {activeIndex + 1} / {items.length}
            </figcaption>
          )}
        </figure>
        {items.length > 1 && (
          <button
            className="post-photo-nav next"
            type="button"
            aria-label={nextLabel}
            aria-keyshortcuts="ArrowRight"
            onClick={showNext}
          >
            <ArrowRight size={24} />
          </button>
        )}
      </section>
    </div>
  );
}

function BaseButton({
  children,
  variant = "secondary",
  size = "md",
  fluid = false,
  loading = false,
  disabled = false,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "outlined" | "secondary" | "transparent" | "danger";
  size?: "sm" | "md";
  fluid?: boolean;
  loading?: boolean;
}) {
  return (
    <button
      {...props}
      className={`base-button ${variant} ${size} ${fluid ? "fluid" : ""} ${props.className ?? ""}`}
      disabled={disabled || loading}
      aria-busy={loading ? true : props["aria-busy"]}
    >
      {loading ? <span className="mini-spinner" aria-hidden="true" /> : null}
      {children}
    </button>
  );
}

function EmptyState({
  icon: Icon,
  title,
  body = "",
  action,
  hideTitle = false,
  showBody = false,
  headingLevel = 2
}: {
  icon: LucideIcon;
  title: string;
  body?: string;
  action?: ReactNode;
  hideTitle?: boolean;
  showBody?: boolean;
  headingLevel?: 1 | 2;
}) {
  const Heading = headingLevel === 1 ? "h1" : "h2";
  return (
    <div className="empty-state">
      <Icon size={28} />
      {!hideTitle && <Heading>{title}</Heading>}
      {showBody && body ? <p>{body}</p> : null}
      {action}
    </div>
  );
}

function PermissionPanel({ title, body = "", actionLabel, compact = false }: { title: string; body?: string; actionLabel: string; compact?: boolean }) {
  const loginHref = authRouteHrefForCurrentRoute("/login");
  return (
    <section className={`permission-panel ${compact ? "compact" : ""}`}>
      <Lock size={24} />
      <h2>{title}</h2>
      {body && <p>{body}</p>}
      <a className="base-button primary" href={loginHref} onClick={(event) => handleOpenSNRouteClick(event, loginHref)}>{actionLabel}</a>
    </section>
  );
}

function LoadingState({ label }: { label: string }) {
  return (
    <div className="loading-state" role="status" aria-live="polite" aria-atomic="true" aria-label={label}>
      <span />
    </div>
  );
}

function FeedLoadingSkeleton({ label }: { label: string }) {
  return (
    <div className="feed-loading-skeleton" role="status" aria-label={label}>
      {[0, 1, 2].map((index) => (
        <article className="feed-skeleton-card" aria-hidden="true" key={index}>
          <header>
            <span className="feed-skeleton-avatar" />
            <span className="feed-skeleton-meta">
              <span />
              <span />
            </span>
          </header>
          <span className="feed-skeleton-line wide" />
          <span className="feed-skeleton-line" />
          <footer>
            <span />
            <span />
            <span />
          </footer>
        </article>
      ))}
    </div>
  );
}

function DirectoryLoadingSkeleton({ label }: { label: string }) {
  return (
    <div className="directory-grid directory-loading-skeleton" role="status" aria-label={label}>
      {[0, 1, 2, 3].map((index) => (
        <article className="directory-skeleton-card" aria-hidden="true" key={index}>
          <span className="directory-skeleton-cover" />
          <span className="directory-skeleton-body">
            <span className="directory-skeleton-avatar" />
            <span className="directory-skeleton-copy">
              <span />
              <span />
            </span>
          </span>
        </article>
      ))}
    </div>
  );
}

function CommentLoadingSkeleton({ label, compact = false }: { label: string; compact?: boolean }) {
  return (
    <div className={`comment-loading-skeleton ${compact ? "compact" : ""}`} role="status" aria-label={label}>
      {[0, 1].map((index) => (
        <div className="comment-skeleton-row" aria-hidden="true" key={index}>
          <span className="comment-skeleton-avatar" />
          <span className="comment-skeleton-bubble">
            <span />
            <span />
          </span>
        </div>
      ))}
    </div>
  );
}

function LikersLoadingSkeleton({ label }: { label: string }) {
  return (
    <div className="likers-loading-skeleton" role="status" aria-label={label}>
      {[0, 1, 2].map((index) => (
        <div className="liker-skeleton-row" aria-hidden="true" key={index}>
          <span className="liker-skeleton-avatar" />
          <span className="liker-skeleton-copy">
            <span />
            <span />
          </span>
        </div>
      ))}
    </div>
  );
}

function InlineStatus({ state, id }: { state: SubmitState; id?: string }) {
  if (state.status === "idle" || !state.message) return null;
  const urgent = state.status === "error" || state.status === "permission";
  return (
    <span
      id={id}
      className={`form-status ${state.status}`}
      role={urgent ? "alert" : "status"}
      aria-live={urgent ? "assertive" : "polite"}
      aria-atomic="true"
    >
      {state.message}
    </span>
  );
}

function FloatingField({ label, children, className = "" }: { label: string; children: ReactNode; className?: string }) {
  return <label className={`floating-field ${className}`.trim()}><span>{label}</span>{children}</label>;
}

function ToggleRow({ name, label, defaultChecked = false }: { name: string; label: string; defaultChecked?: boolean }) {
  return <label className="toggle-row"><span>{label}</span><input name={name} type="checkbox" defaultChecked={defaultChecked} /></label>;
}

function PasswordInput({ name, show, setShow, minLength, placeholder, ui }: { name: string; show: boolean; setShow: (value: boolean) => void; minLength?: number; placeholder: string; ui: I18nDictionary["social"]["ui"] }) {
  return (
    <div className="input-action">
      <input name={name} type={show ? "text" : "password"} minLength={minLength} required placeholder={placeholder} />
      <button className="icon-only" type="button" onClick={() => setShow(!show)} aria-label={show ? ui.hidePassword : ui.showPassword}>{show ? <EyeOff size={18} /> : <Eye size={18} />}</button>
    </div>
  );
}

function apiHeaders(contentType?: string): HeadersInit {
  const headers: Record<string, string> = { accept: "application/json", "x-new2-locale": activeLocaleCode };
  if (contentType) headers["content-type"] = contentType;
  return headers;
}

function useApi(endpoint: string | null, initialData?: unknown): ApiResult {
  const [reloadKey, setReloadKey] = useState(0);
  const [cursor, setCursor] = useState<string | null>(null);
  const [result, setResult] = useState<Omit<ApiResult, "reload" | "loadMore">>(() => apiReadyResult(initialData) ?? { status: "idle", rows: [] });
  const endpointRef = useRef(endpoint);
  const requestRef = useRef<AbortController | null>(null);
  const refreshQueuedRef = useRef(false);
  useEffect(() => {
    let alive = true;
    const endpointChanged = endpointRef.current !== endpoint;
    if (endpointRef.current !== endpoint) {
      endpointRef.current = endpoint;
      if (cursor) {
        setCursor(null);
        return () => {
          alive = false;
        };
      }
    }
    if (!endpoint) {
      setResult({ status: "ready", rows: [] });
      return () => {
        alive = false;
      };
    }
    const initialResult = cursor ? null : apiReadyResult(initialData);
    if (reloadKey === 0 && initialResult) {
      setResult(initialResult);
      return () => {
        alive = false;
      };
    }
    const url = cursor ? `${endpoint}${endpoint.includes("?") ? "&" : "?"}cursor=${encodeURIComponent(cursor)}` : endpoint;
    const controller = new AbortController();
    requestRef.current = controller;
    setResult((current) => ({ ...current, status: "loading", rows: cursor || (!endpointChanged && reloadKey > 0) ? current.rows : [] }));
    fetch(url, { credentials: "include", headers: apiHeaders(), signal: controller.signal })
      .then(async (response) => {
        const isJson = (response.headers.get("content-type") ?? "").toLowerCase().includes("application/json");
        const data = isJson ? await response.json().catch(() => ({})) : {};
        if (!alive) return;
        if (!response.ok) {
          setResult({ status: response.status === 401 || response.status === 403 ? "permission" : "error", rows: [], statusCode: response.status, error: readableError(data, response.status) });
          return;
        }
        if (!isJson && response.status !== 204) {
          setResult({ status: "error", rows: [], statusCode: response.status, error: readableError(data, response.status) });
          return;
        }
        const rows = extractRows(data);
        const meta = apiListMeta(data);
        setResult((current) => ({ status: "ready", data, rows: cursor ? [...current.rows, ...rows] : rows, nextCursor: meta.nextCursor, total: meta.total, page: meta.page, perPage: meta.perPage }));
      })
      .catch((error: Error) => {
        if (alive) setResult({ status: "error", rows: [], error: error.message });
      }).finally(() => {
        if (requestRef.current === controller) {
          requestRef.current = null;
          if (alive && refreshQueuedRef.current) {
            refreshQueuedRef.current = false;
            setCursor(null);
            setReloadKey((value) => value + 1);
          }
        }
      });
    return () => {
      alive = false;
      controller.abort();
    };
  }, [endpoint, initialData, reloadKey, cursor]);
  return {
    ...result,
    reload: () => {
      if (requestRef.current) { refreshQueuedRef.current = true; return; }
      setCursor(null);
      setReloadKey((value) => value + 1);
    },
    loadMore: () => { if (!requestRef.current) setCursor(result.nextCursor ?? null); }
  };
}

function apiReadyResult(data: unknown): Omit<ApiResult, "reload" | "loadMore"> | null {
  if (data === undefined) return null;
  const meta = apiListMeta(data);
  return { status: "ready", data, rows: extractRows(data), nextCursor: meta.nextCursor, total: meta.total, page: meta.page, perPage: meta.perPage };
}

function apiListMeta(data: unknown): Pick<ApiResult, "nextCursor" | "total" | "page" | "perPage"> {
  if (!isRecord(data)) return { nextCursor: null };
  const source = isRecord(data.data) ? data.data : data;
  const meta = isRecord(source.meta) ? source.meta : isRecord(data.meta) ? data.meta : {};
  return {
    nextCursor: String(data.next_cursor ?? data.nextCursor ?? source.next_cursor ?? source.nextCursor ?? "") || null,
    total: numericApiValue(data.total ?? data.total_count ?? data.totalCount ?? source.total ?? source.total_count ?? source.totalCount ?? meta.total ?? meta.total_count ?? meta.totalCount),
    page: numericApiValue(data.page ?? source.page ?? meta.page),
    perPage: numericApiValue(data.per_page ?? data.perPage ?? source.per_page ?? source.perPage ?? meta.per_page ?? meta.perPage)
  };
}

function numericApiValue(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return Math.max(0, Math.floor(value));
  const text = String(value ?? "").trim();
  if (!/^\d+$/u.test(text)) return null;
  return Number(text);
}

function initialApiData(props: PageProps, endpoint: string | null): unknown {
  if (!endpoint || !props.initialData) return undefined;
  return openSNInitialDataPayload(props.initialData, endpoint);
}

function useNotificationDelivery(userId: string | null, stream: OpenSNNotificationStreamConfig | undefined, onDelivery: () => void) {
  const onDeliveryRef = useRef(onDelivery);
  useEffect(() => {
    onDeliveryRef.current = onDelivery;
  }, [onDelivery]);
  useEffect(() => {
    const endpoint = String(stream?.endpoint ?? "").trim();
    if (!userId || !endpoint) return;
    if (stream?.transport === "websocket") {
      const socket = new WebSocket(webSocketEndpoint(endpoint));
      const handleMessage = (event: MessageEvent) => {
        if (isNotificationDeliveryPayload(event.data)) onDeliveryRef.current();
      };
      socket.addEventListener("message", handleMessage);
      return () => {
        socket.removeEventListener("message", handleMessage);
        if (socket.readyState === WebSocket.CONNECTING || socket.readyState === WebSocket.OPEN) socket.close(1000, "opensn-notifications");
      };
    }
    const source = new EventSource(endpoint, { withCredentials: true });
    const handleMessage = (event: Event) => {
      const data = event instanceof MessageEvent ? event.data : null;
      if (isNotificationDeliveryPayload(data)) onDeliveryRef.current();
    };
    source.addEventListener("message", handleMessage);
    source.addEventListener("notification", handleMessage);
    source.addEventListener("notifications.changed", handleMessage);
    return () => {
      source.removeEventListener("message", handleMessage);
      source.removeEventListener("notification", handleMessage);
      source.removeEventListener("notifications.changed", handleMessage);
      source.close();
    };
  }, [stream?.endpoint, stream?.transport, userId]);
}

function webSocketEndpoint(endpoint: string): string {
  const url = new URL(endpoint, window.location.href);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString();
}

function isNotificationDeliveryPayload(value: unknown): boolean {
  const data = notificationDeliveryRecord(value);
  if (!data) return false;
  const type = String(data.type ?? data.event ?? data.kind ?? "").trim().toLowerCase();
  if (type === "ping" || type === "heartbeat") return false;
  if (type.startsWith("notification.") || type.startsWith("notifications.")) return true;
  if (type.startsWith("friend.") || type.startsWith("follow.") || type.startsWith("comment.") || type.startsWith("reply.")) return true;
  if (type.startsWith("message.") || type.startsWith("chat.") || type.startsWith("event.") || type.startsWith("group.") || type.startsWith("page.")) return true;
  return Boolean(data.notificationId ?? data.notification_id ?? data.queueEventId ?? data.queue_event_id ?? (isRecord(data.notification) || isRecord(data.row)));
}

function notificationDeliveryRecord(value: unknown): Record<string, unknown> | null {
  if (isRecord(value)) return value;
  if (typeof value !== "string") return null;
  const text = value.trim();
  if (!text) return null;
  try {
    const parsed = JSON.parse(text) as unknown;
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

type ChatMessagesResult = Omit<ApiResult, "loadMore"> & {
  loadingOlder: boolean;
  loadOlder: () => void;
};

function chatMessagesEndpoint(roomId: string, cursor?: string | null, limit = 10): string {
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) params.set("cursor", cursor);
  return `/api/chat/rooms/${encodeURIComponent(roomId)}/messages?${params.toString()}`;
}

function useChatMessages(roomId: string | null): ChatMessagesResult {
  const roomRef = useRef(roomId);
  const requestRef = useRef<AbortController | null>(null);
  const [result, setResult] = useState<Omit<ChatMessagesResult, "reload" | "loadOlder">>({ status: "idle", rows: [], loadingOlder: false });
  const resultRef = useRef(result);
  resultRef.current = result;
  roomRef.current = roomId;
  useEffect(() => {
    let alive = true;
    if (!roomId) {
      setResult({ status: "ready", rows: [], nextCursor: null, loadingOlder: false });
      return () => {
        alive = false;
      };
    }
    const controller = new AbortController();
    requestRef.current = controller;
    setResult({ status: "loading", rows: [], nextCursor: null, loadingOlder: false });
    fetch(chatMessagesEndpoint(roomId), { credentials: "include", headers: apiHeaders(), signal: controller.signal })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!alive || roomRef.current !== roomId) return;
        if (!response.ok) {
          setResult({ status: response.status === 401 || response.status === 403 ? "permission" : "error", rows: [], error: readableError(data, response.status), loadingOlder: false });
          return;
        }
        setResult({ status: "ready", data, rows: extractRows(data), nextCursor: isRecord(data) ? String(data.next_cursor ?? data.nextCursor ?? "") || null : null, loadingOlder: false });
      })
      .catch((error: Error) => {
        if (alive && roomRef.current === roomId) setResult({ status: "error", rows: [], error: error.message, loadingOlder: false });
      }).finally(() => {
        if (requestRef.current === controller) requestRef.current = null;
      });
    return () => {
      alive = false;
      controller.abort();
      requestRef.current?.abort();
    };
  }, [roomId]);

  const reload = useCallback(() => {
    if (!roomId || requestRef.current) return;
    const controller = new AbortController();
    requestRef.current = controller;
    const knownIds = new Set(resultRef.current.rows.map((row) => String(row.id)));
    void (async () => {
      const incoming: Record<string, unknown>[] = [];
      let cursor: string | null = null;
      let firstCursor: string | null = null;
      do {
        const response: Response = await fetch(chatMessagesEndpoint(roomId, cursor, 50), { credentials: "include", headers: apiHeaders(), signal: controller.signal });
        const data: unknown = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(readableError(data, response.status));
        const page = extractRows(data);
        const nextCursor: string | null = isRecord(data) ? String(data.next_cursor ?? data.nextCursor ?? "") || null : null;
        if (incoming.length === 0) firstCursor = nextCursor;
        incoming.push(...page);
        const overlaps = page.some((row) => knownIds.has(String(row.id)));
        cursor = knownIds.size > 0 && !overlaps && nextCursor !== cursor ? nextCursor : null;
      } while (cursor && !controller.signal.aborted);
      if (controller.signal.aborted || roomRef.current !== roomId) return;
      setResult((current) => {
        const merged = new Map(current.rows.map((row) => [String(row.id), row]));
        incoming.forEach((row) => merged.set(String(row.id), row));
        const rows = [...merged.values()].sort((left, right) => String(left.created_at).localeCompare(String(right.created_at)) || String(left.id).localeCompare(String(right.id)));
        return { ...current, status: "ready", rows, error: undefined, nextCursor: current.rows.length ? current.nextCursor : firstCursor };
      });
    })().catch((error: Error) => {
      if (!controller.signal.aborted && roomRef.current === roomId) setResult((current) => ({ ...current, status: current.rows.length ? "ready" : "error", error: error.message }));
    }).finally(() => {
      if (requestRef.current === controller) requestRef.current = null;
    });
  }, [roomId]);
  useMemberPolling(reload, Boolean(roomId));

  const loadOlder = useCallback(() => {
    const cursor = result.nextCursor;
    if (!roomId || !cursor || result.loadingOlder || result.status !== "ready" || requestRef.current) return;
    const requestedRoomId = roomId;
    const controller = new AbortController();
    requestRef.current = controller;
    setResult((current) => ({ ...current, loadingOlder: true }));
    fetch(chatMessagesEndpoint(requestedRoomId, cursor), { credentials: "include", headers: apiHeaders(), signal: controller.signal })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (roomRef.current !== requestedRoomId || controller.signal.aborted) return;
        if (!response.ok) {
          setResult((current) => ({ ...current, loadingOlder: false, error: readableError(data, response.status) }));
          return;
        }
        const olderRows = extractRows(data);
        setResult((current) => {
          const existingIds = new Set(current.rows.map((row) => String(row.id)));
          const uniqueOlderRows = olderRows.filter((row) => !existingIds.has(String(row.id)));
          return {
            status: "ready",
            data,
            rows: [...uniqueOlderRows, ...current.rows],
            nextCursor: isRecord(data) ? String(data.next_cursor ?? data.nextCursor ?? "") || null : null,
            loadingOlder: false
          };
        });
      })
      .catch((error: Error) => {
        if (!controller.signal.aborted && roomRef.current === requestedRoomId) setResult((current) => ({ ...current, loadingOlder: false, error: error.message }));
      }).finally(() => {
        if (requestRef.current === controller) requestRef.current = null;
      });
  }, [roomId, result.loadingOlder, result.nextCursor, result.status]);

  return {
    ...result,
    reload,
    loadOlder
  };
}

async function submitJson(endpoint: string, method: string, payload: Record<string, unknown>, idempotencyKey?: string): Promise<unknown> {
  const headers = apiHeaders("application/json") as Record<string, string>;
  if (idempotencyKey) headers["idempotency-key"] = idempotencyKey;
  const response = await fetch(endpoint, { method, credentials: "include", headers, body: JSON.stringify(payload) });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new ApiRequestError(readableError(data, response.status), response.status, data);
  return data;
}

const memberRequest: MemberRequest = async (endpoint, method, payload, idempotencyKey) => {
  if (method !== "GET") return submitJson(endpoint, method, payload ?? {}, idempotencyKey);
  const response = await fetch(endpoint, { credentials: "include", headers: apiHeaders() });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new ApiRequestError(readableError(data, response.status), response.status, data);
  return data;
};

async function currentAuthSessionReady(): Promise<boolean> {
  try {
    const response = await fetch("/api/auth/me", { credentials: "include", headers: apiHeaders() });
    if (!response.ok) return false;
    const data = await response.json().catch(() => ({}));
    const user = isRecord(data) ? data.user ?? (isRecord(data.auth) ? data.auth.user : null) : null;
    return isRecord(user);
  } catch {
    return false;
  }
}

class ApiRequestError extends Error {
  constructor(message: string, readonly status: number, readonly data: unknown) {
    super(message);
    this.name = "ApiRequestError";
  }
}

function isApiStatus(error: unknown, status: number): boolean {
  return error instanceof ApiRequestError && error.status === status;
}

function invalidCredentialsMessage(dictionary: I18nDictionary): string {
  return dictionary.errorMessages.invalid_credentials || dictionary.social.ui.invalidCredentials || dictionary.errors.unauthorized || dictionary.social.ui.requestFailed;
}

async function uploadFiles(files: File[], options?: { visibility?: "public" | "private"; coupleId?: string; purpose?: "verification" }): Promise<Array<Record<string, unknown>>> {
  const uploaded: Array<Record<string, unknown>> = [];
  for (const file of files) {
    const headers = new Headers(apiHeaders(file.type || "application/octet-stream"));
    headers.set("x-file-name", encodeURIComponent(file.name));
    if (options?.visibility) headers.set("x-photo-visibility", options.visibility);
    if (options?.purpose) headers.set("x-upload-purpose", options.purpose);
    if (options?.coupleId) headers.set("x-couple-id", options.coupleId);
    const response = await fetch("/api/uploads", { method: "POST", credentials: "include", headers, body: file });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(readableError(payload, response.status));
    const contentType = isRecord(payload) ? String(payload.mime_type ?? file.type) : file.type;
    const name = contentType === "image/avif" ? `${file.name.replace(/\.[^.]+$/, "")}.avif` : file.name;
    uploaded.push(isRecord(payload) ? { ...payload, name: payload.original_name ?? name, size: payload.size ?? file.size, type: contentType } : { name, size: file.size, type: contentType });
  }
  return uploaded;
}

async function copyTextToClipboard(value: string, failureMessage: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "true");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  textarea.remove();
  if (!copied) throw new Error(failureMessage);
}

type AdsCopy = I18nDictionary["social"]["ui"];
type AdCampaignStats = { active: number; spentCents: number; budgetCents: number; currency: string };

function adObjectiveOptions(copy: AdsCopy): Array<{ value: string; label: string }> {
  return [
    { value: "awareness", label: copy.objectiveAwareness },
    { value: "traffic", label: copy.objectiveTraffic },
    { value: "engagement", label: copy.objectiveEngagement },
    { value: "leads", label: copy.objectiveLeads },
    { value: "messages", label: copy.objectiveMessages }
  ];
}

function optionalFormString(form: FormData, key: string): string | undefined {
  const value = String(form.get(key) ?? "").trim();
  return value || undefined;
}

function adIdFromPath(path: string): string {
  const match = path.match(/^\/ads\/([^/]+)(?:\/report)?\/?$/) ?? path.match(/^\/advertisings\/detail\/([^/]+)(?:\/report)?\/?$/);
  if (!match || ["create", "boost-post"].includes(match[1])) return "";
  return decodeURIComponent(match[1]);
}

function adCampaignStats(rows: Array<Record<string, unknown>>): AdCampaignStats {
  return rows.reduce<AdCampaignStats>((stats, row) => {
    const currency = String(row.currency ?? stats.currency);
    return {
      active: stats.active + (String(row.status ?? "") === "active" ? 1 : 0),
      spentCents: stats.spentCents + numberValue(row.reported_spend_cents ?? row.spent_cents),
      budgetCents: stats.budgetCents + numberValue(row.budget_cents),
      currency
    };
  }, { active: 0, spentCents: 0, budgetCents: 0, currency: "EUR" });
}

function formatMoney(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(activeTimeLocaleCode(), { style: "currency", currency }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

function formatCompactNumber(value: number): string {
  return new Intl.NumberFormat(activeTimeLocaleCode(), { notation: "compact", maximumFractionDigits: 1 }).format(value);
}

function adTargetingSummary(row: Record<string, unknown>, copy: AdsCopy): string {
  const targeting = recordFromJson(row.targeting_json);
  const audience = String(targeting.audience ?? "").trim();
  const placement = String(targeting.placement ?? "").trim();
  const creative = String(targeting.creative ?? "").trim();
  return [audience, placement, creative].filter(Boolean).join(" / ") || copy.noAudience;
}

function recordFromJson(value: unknown): Record<string, unknown> {
  if (isRecord(value)) return value;
  if (typeof value !== "string" || !value.trim()) return {};
  try {
    const parsed = JSON.parse(value) as unknown;
    return isRecord(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function adStatusLabel(status: string, copy: AdsCopy): string {
  if (status === "active") return copy.active;
  if (status === "paused") return copy.paused;
  if (status === "cancelled") return copy.cancelled;
  if (status === "completed") return copy.completed;
  return copy.draft;
}

function endpointForCurrentView(props: PageProps, filter: string): string {
  const endpoint = props.view.endpoint;
  const currentQuery = currentQueryFromRoute(props.currentUrl, props.currentQuery);
  if (props.view.name === "Post Detail") return postDetailEndpoint(props.currentUrl);
  if (props.view.name === "Home Feed") return feedEndpoint(currentQuery);
  if (props.view.name === "Search" && filter.trim()) return `/api/search?q=${encodeURIComponent(filter.trim())}`;
  if (props.view.name === "Search") {
    const hashtagQuery = hashtagQueryFromPath(props.currentUrl);
    if (hashtagQuery) return `/api/search?q=${encodeURIComponent(hashtagQuery)}`;
  }
  if (props.view.module === "saved") return directoryEndpoint("/api/bookmarks", filter, currentQuery);
  if (props.view.name === "Network") return directoryEndpoint(currentQuery.connections === "1" ? "/api/follows" : "/api/users", filter, currentQuery);
  if (["Invites Overview", "Your Referrals"].includes(props.view.name)) return directoryEndpoint("/api/invites", filter, currentQuery);
  if (props.view.name === "List Followers") return "/api/follows";
  if (props.view.name === "List Following") return "/api/follows";
  if (props.view.name === "Profile Overview") {
    const currentPath = normalizeAppPath(props.currentUrl);
    const username = currentPath.startsWith("/@") ? decodeProfileHandle(currentPath.split("/")[1] ?? "") : currentPath.startsWith("/profile/") ? decodeURIComponent(currentPath.split("/")[2] ?? "") : "";
    return `/api/profile${username ? `?username=${encodeURIComponent(username)}` : ""}`;
  }
  if (props.view.name.includes("Article")) return directoryEntityEndpoint("/articles", "/api/articles", props.currentUrl) ?? directoryEndpoint("/api/articles", filter, currentQuery);
  if (props.view.name.includes("Document")) return directoryEntityEndpoint("/documents", "/api/documents", props.currentUrl) ?? directoryEndpoint("/api/documents", filter, currentQuery);
  if (props.view.name.includes("Groups")) return directoryEntityEndpoint("/groups", "/api/groups", props.currentUrl) ?? directoryEndpoint("/api/groups", filter, currentQuery);
  if (props.view.name.includes("Events")) return directoryEntityEndpoint("/events", "/api/events", props.currentUrl) ?? directoryEndpoint("/api/events", filter, currentQuery);
  if (props.view.name.includes("Pages")) return directoryEntityEndpoint("/pages", "/api/pages", props.currentUrl) ?? directoryEndpoint("/api/pages", filter, currentQuery);
  return endpoint;
}

type FeedScope = "public" | "friends";

const POST_FEED_SCOPES = new Set(["friends", "groups", "pages"]);

function postFeedScopeFromQuery(currentQuery: Record<string, string>): string {
  const scope = String(currentQuery.scope ?? "").trim().toLowerCase();
  return POST_FEED_SCOPES.has(scope) ? scope : "";
}

function feedScopeFromQuery(currentQuery: Record<string, string>): FeedScope {
  return postFeedScopeFromQuery(currentQuery) === "friends" ? "friends" : "public";
}

function feedEndpoint(currentQuery: Record<string, string>): string {
  const scope = postFeedScopeFromQuery(currentQuery);
  return scope ? `/api/posts?scope=${encodeURIComponent(scope)}` : "/api/posts";
}

function feedScopeHref(scope: FeedScope): string {
  return scope === "friends" ? "/timeline?scope=friends" : "/timeline";
}

function postDetailEndpoint(currentUrl: string): string {
  const currentPath = normalizeAppPath(currentUrl);
  const identifier = currentPath.startsWith("/post/") ? decodeURIComponent(currentPath.split("/")[2] ?? "").trim() : "";
  return identifier ? `/api/posts/${encodeURIComponent(identifier)}` : "/api/posts";
}

function hashtagQueryFromPath(currentUrl: string): string {
  const currentPath = normalizeAppPath(currentUrl);
  if (!currentPath.startsWith("/hashtags/")) return "";
  const tag = decodeURIComponent(currentPath.split("/")[2] ?? "").replace(/^#+/, "").trim();
  return tag ? `#${tag}` : "";
}

function searchQueryFromRoute(currentUrl: string, currentQuery: Record<string, string>): string {
  return String(currentQueryFromRoute(currentUrl, currentQuery).q ?? "").trim() || hashtagQueryFromPath(currentUrl);
}

function withQuery(endpoint: string, params: Record<string, string>): string {
  const [path, query = ""] = endpoint.split("?");
  const searchParams = new URLSearchParams(query);
  Object.entries(params).forEach(([key, value]) => {
    if (value) searchParams.set(key, value);
  });
  const serialized = searchParams.toString();
  return serialized ? `${path}?${serialized}` : path;
}

function directoryEndpoint(basePath: string, filter: string, currentQuery: Record<string, string>): string {
  const params = new URLSearchParams();
  const query = filter.trim() || String(currentQuery.q ?? "").trim();
  const category = String(currentQuery.category ?? "").trim();
  if (query) params.set("q", query);
  if (category) params.set("category", category);
  const suffix = params.toString();
  return `${basePath}${suffix ? `?${suffix}` : ""}`;
}

function directoryEntityEndpoint(routeBasePath: string, apiBasePath: string, currentUrl: string): string | null {
  const path = normalizeAppPath(currentUrl);
  if (path === routeBasePath || !path.startsWith(`${routeBasePath}/`)) return null;
  const segment = path.slice(routeBasePath.length + 1).split("/")[0] ?? "";
  const identifier = decodeURIComponent(segment).trim();
  if (!identifier || ["admin", "create", "editor", "feature", "managed", "manage_users", "notify_settings", "reports"].includes(identifier)) return null;
  return `${apiBasePath}/${encodeURIComponent(identifier)}`;
}

function directoryEntityEndpointForView(view: ViewCatalogItem, currentUrl: string): string | null {
  if (view.name.includes("Article")) return directoryEntityEndpoint("/articles", "/api/articles", currentUrl);
  if (view.name.includes("Document")) return directoryEntityEndpoint("/documents", "/api/documents", currentUrl);
  if (view.name.includes("Groups")) return directoryEntityEndpoint("/groups", "/api/groups", currentUrl);
  if (view.name.includes("Events")) return directoryEntityEndpoint("/events", "/api/events", currentUrl);
  if (view.name.includes("Pages")) return directoryEntityEndpoint("/pages", "/api/pages", currentUrl);
  return null;
}

function localeHref(currentUrl: string, currentQuery: Record<string, string>, localeCode: string): string {
  const routeQuery = currentQueryFromRoute(currentUrl, currentQuery);
  const params = new URLSearchParams();
  Object.entries(routeQuery).forEach(([key, value]) => {
    if (key !== "lang") params.set(key, value);
  });
  params.set("lang", localeCode);
  const query = params.toString();
  return `${normalizeAppPath(currentUrl) || "/"}${query ? `?${query}` : ""}`;
}

function currentRouteHref(currentUrl: string, currentQuery: Record<string, string>): string {
  const query = new URLSearchParams(currentQueryFromRoute(currentUrl, currentQuery)).toString();
  const href = `${normalizeAppPath(currentUrl) || "/timeline"}${query ? `?${query}` : ""}`;
  return canonicalProfileRouteHref(href) || href;
}

function safeAuthRedirect(value: unknown, fallback = "/timeline"): string {
  const text = String(value ?? "").trim();
  const defaultHref = fallback ? localeHref(fallback, {}, activeLocaleCode) : "";
  if (!text || !text.startsWith("/") || text.startsWith("//")) return defaultHref;
  try {
    const url = new URL(text, "https://opensn.local");
    const path = normalizeAppPath(url.pathname);
    if (!path || isAuthRoutePath(path)) return defaultHref;
    if (!url.searchParams.has("lang")) url.searchParams.set("lang", activeLocaleCode);
    const href = `${path}${url.search}${url.hash}`;
    return canonicalProfileRouteHref(href) || href;
  } catch {
    return defaultHref;
  }
}

function authRouteHref(path: string, currentQuery: Record<string, string>): string {
  return authRouteHrefForRedirect(path, currentQuery.redirect);
}

function authRouteHrefForRedirect(path: string, redirectValue: unknown): string {
  const redirect = safeAuthRedirect(redirectValue, "");
  const params = new URLSearchParams({ lang: activeLocaleCode });
  if (redirect) params.set("redirect", redirect);
  return `${path}?${params.toString()}`;
}

function authRouteHrefForCurrentRoute(path: string): string {
  return authRouteHrefForRedirect(path, currentBrowserRouteHref());
}

function currentBrowserRouteHref(): string {
  if (typeof window === "undefined") return "";
  const path = currentBrowserPath();
  return path ? currentRouteHref(path, currentBrowserQuery()) : "";
}

function firstLoginHref(redirect: unknown): string {
  const next = safeAuthRedirect(redirect);
  const params = new URLSearchParams({ lang: activeLocaleCode });
  if (next !== `/timeline?${new URLSearchParams({ lang: activeLocaleCode }).toString()}`) params.set("redirect", next);
  return `/first-login?${params.toString()}`;
}

function isAuthRoutePath(path: string): boolean {
  return path === "/login" || path === "/signup" || path === "/recover" || path === "/first-login" || path.startsWith("/login/") || path.startsWith("/signup/") || path.startsWith("/recover/") || path.startsWith("/first-login/");
}

function settingsEndpoint(name: string): string {
  if (name === "Settings/Profile") return "/api/settings/profile";
  if (name === "Settings/Privacy") return "/api/settings/privacy";
  if (name === "Settings/Notifications") return "/api/settings/notifications";
  if (name === "Settings/Display") return "/api/settings/display";
  if (name === "Settings/Email") return "/api/settings/email";
  if (name === "Settings/Password") return "/api/settings/password";
  if (name === "Settings/Add Email Password") return "/api/settings/email";
  if (name === "Settings/Data Download") return "/api/settings/data-export";
  if (name === "Settings/Account") return "/api/settings/account-delete";
  if (name === "Settings/Two Factor") return "/api/auth/two-factor/setup";
  return "/api/me/settings";
}

function settingsMethod(name: string): string {
  return ["Settings/Email", "Settings/Data Download", "Settings/Account", "Settings/Two Factor"].includes(name) ? "POST" : "PATCH";
}

function settingsPayload(name: string, form: FormData, media: { avatarFileId?: string; coverFileId?: string; removeAvatar?: boolean; removeCover?: boolean } = {}): Record<string, unknown> {
  if (name === "Settings/Profile") {
    return {
      displayName: String(form.get("displayName")),
      username: String(form.get("username")),
      gender: String(form.get("gender") ?? ""),
      bio: String(form.get("bio")),
      ...locationFormPayload(form),
      birthDate: String(form.get("birthDate") ?? ""),
      publicProfileMode: String(form.get("publicProfileMode") ?? "minimal"),
      adultConfirmed: Boolean(form.get("adultConfirmed")),
      termsAccepted: Boolean(form.get("termsAccepted")),
      workSector: String(form.get("workSector")),
      avatarFileId: media.avatarFileId,
      coverFileId: media.coverFileId,
      removeAvatar: Boolean(media.removeAvatar),
      removeCover: Boolean(media.removeCover)
    };
  }
  if (name === "Settings/Privacy") return {
    profileVisibility: privacyAudienceSetting(form.get("profileVisibility"), DEFAULT_USER_SETTINGS.privacy.profileVisibility),
    messagesFrom: privacyAudienceSetting(form.get("messagesFrom"), DEFAULT_USER_SETTINGS.privacy.messagesFrom),
    friendListVisibility: privacyAudienceSetting(form.get("friendListVisibility"), DEFAULT_USER_SETTINGS.privacy.friendListVisibility),
    friendActivityVisibility: privacyAudienceSetting(form.get("friendActivityVisibility"), DEFAULT_USER_SETTINGS.privacy.friendActivityVisibility),
    commentsOnPosts: Boolean(form.get("commentsOnPosts")),
    searchable: Boolean(form.get("searchable")),
    showOnline: Boolean(form.get("showOnline")),
    blockedUserId: optionalFormString(form, "blockedUserId")
  };
  if (name === "Settings/Notifications") return {
    followsEnabled: Boolean(form.get("followsEnabled")),
    commentsEnabled: Boolean(form.get("commentsEnabled")),
    messagesEnabled: Boolean(form.get("messagesEnabled")),
    safetyAlertsEnabled: Boolean(form.get("safetyAlertsEnabled"))
  };
  if (name === "Settings/Email") return { email: String(form.get("email")) };
  if (name === "Settings/Password") return { currentPassword: String(form.get("currentPassword")), password: String(form.get("password")) };
  if (name === "Settings/Data Download") return { scope: String(form.get("scope") ?? "all") };
  if (name === "Settings/Account") return { confirm: String(form.get("confirm") ?? ""), reason: String(form.get("reason") ?? "") };
  if (name === "Settings/Two Factor") return { method: String(form.get("method") ?? "email") };
  return { preferredLocale: String(form.get("locale") ?? ""), themePreference: normalizeThemePreference(form.get("themePreference")), videoAutoplay: Boolean(form.get("autoplay")) };
}

function entityPayload(name: string, form: FormData, locale: string): Record<string, unknown> {
  if (name.includes("Wallet")) {
    const amountCents = Math.round(Number(form.get("amount") ?? 0) * 100);
    if (name.includes("Transfer")) return { recipientId: String(form.get("recipientId")), amountCents, currency: "EUR" };
    if (name.includes("Withdraw")) return { amountCents, currency: "EUR", destination: { provider: String(form.get("provider") ?? "wallet") } };
    return { provider: String(form.get("provider") ?? "stripe"), purpose: "wallet_deposit", amountCents, currency: "EUR", metadata: {} };
  }
  if (name.includes("Ads")) {
    return {
      name: String(form.get("name") ?? "").trim(),
      objective: String(form.get("objective")),
      budgetCents: Math.round(Number(form.get("budget") ?? 0) * 100),
      currency: "EUR",
      status: "draft",
      targeting: {
        audience: String(form.get("audience") ?? "").trim(),
        creative: String(form.get("description") ?? "").trim()
      }
    };
  }
  if (name.includes("Clips")) return { fileId: String(form.get("fileId")), body: String(form.get("body") ?? ""), language: locale };
  if (name.includes("Article")) return { title: String(form.get("title")), slug: String(form.get("slug")), excerpt: String(form.get("excerpt") ?? ""), body: String(form.get("body")), visibility: String(form.get("visibility") ?? "public"), language: locale, status: String(form.get("status") ?? "draft") };
  if (name.includes("Document")) return { title: String(form.get("title")), fileId: String(form.get("fileId")), documentType: String(form.get("documentType") ?? "identity"), description: String(form.get("description") ?? ""), visibility: String(form.get("visibility") ?? "private") };
  if (name.includes("Events")) {
    const hosts = splitCsv(form.get("hosts"));
    const invites = splitCsv(form.get("invites"));
    const startsAt = String(form.get("startsAt") ?? form.get("startDate") ?? "");
    const endsAt = String(form.get("endsAt") ?? form.get("endDate") ?? "");
    const categoryId = String(form.get("categoryId") ?? "").trim();
    return {
      title: String(form.get("title") ?? "").trim(),
      slug: String(form.get("slug") ?? "").trim(),
      description: String(form.get("description") ?? "").trim(),
      format: String(form.get("format") ?? "in_person"),
      startsAt,
      endsAt,
      startDate: startsAt,
      endDate: endsAt,
      location: String(form.get("location") ?? "").trim(),
      onlineUrl: String(form.get("onlineUrl") ?? "").trim(),
      capacity: Number(form.get("capacity") || 0) || null,
      visibility: String(form.get("visibility") ?? "public"),
      categoryId,
      hosts,
      invites,
      status: "published",
      language: locale
    };
  }
  return { name: String(form.get("name")), slug: String(form.get("slug") ?? ""), description: String(form.get("description") ?? ""), privacy: String(form.get("privacy") ?? "public"), categoryId: String(form.get("categoryId") ?? "").trim(), status: "active" };
}

function categoryEndpointForView(view: ViewCatalogItem): string | null {
  if (view.module === "groups") return "/api/groups/categories";
  if (view.module === "events") return "/api/events/categories";
  if (view.module === "pages") return "/api/pages/categories";
  return null;
}

function submitEndpoint(view: ViewCatalogItem): string {
  if (view.name.includes("Groups")) return "/api/groups";
  if (view.name.includes("Events")) return "/api/events";
  if (view.name.includes("Pages")) return "/api/pages";
  if (view.name.includes("Ads")) return view.name === "Ads Boost Post" ? "/api/ads/boost-post" : "/api/ads";
  if (view.name.includes("Clips")) return "/api/clips";
  if (view.name.includes("Article")) return "/api/articles";
  if (view.name.includes("Document")) return "/api/documents";
  if (view.name.includes("Wallet Deposit")) return "/api/wallet/deposit";
  if (view.name.includes("Wallet Withdraw")) return "/api/wallet/withdraw";
  if (view.name.includes("Wallet Transfer")) return "/api/wallet/transfer";
  return view.endpoint;
}

function maybeIdempotency(name: string): string | undefined {
  return /(deposit|payment|checkout|wallet|ads|membership|events)/i.test(name) ? crypto.randomUUID() : undefined;
}

function persistentIdempotency(reference: RefObject<{ signature: string; key: string } | null>, name: string, payload: Record<string, unknown>): string | undefined {
  if (!/(deposit|payment|checkout|wallet|ads|membership)/i.test(name)) return undefined;
  const signature = `${name}:${JSON.stringify(payload)}`;
  const existing = reference.current;
  if (existing?.signature === signature) return existing.key;
  const key = crypto.randomUUID();
  reference.current = { signature, key };
  return key;
}

function requiresAuth(view: ViewCatalogItem): boolean {
  if (isPublicProfileView(view)) return false;
  return !["Contact Support", "Support/Product Feedback"].includes(view.name);
}

function isPublicProfileView(view: ViewCatalogItem): boolean {
  return view.name === "Profile Overview" || view.module === "profile";
}

function guestAuthOverlayAppliesToView(view: ViewCatalogItem, currentUrl = ""): boolean {
  if (isPublicProfileRoute(currentUrl)) return false;
  if (view.name === "Couple Invitation") return false;
  return !["auth", "public", "system", "support"].includes(view.module);
}

function isPublicProfileRoute(currentUrl: string): boolean {
  const identifier = profileRouteIdentifier(currentUrl);
  return Boolean(identifier && identifier !== "me");
}

function profileRouteIdentifier(currentUrl: string): string {
  const path = normalizeAppPath(currentUrl);
  if (path.startsWith("/@")) return decodeProfileHandle(path.split("/")[1] ?? "");
  if (path.startsWith("/profile/")) return decodeProfileHandle(path.split("/")[2] ?? "");
  return "";
}

function supportCategoryFor(name: string): string {
  if (name.includes("Abuse")) return "abuse";
  if (name.includes("Account")) return "account_access";
  if (name.includes("Privacy")) return "privacy_gdpr";
  if (name.includes("Payments")) return "payments_billing";
  if (name.includes("Security")) return "security_disclosure";
  if (name.includes("Legal")) return "legal_rights";
  if (name.includes("Feedback")) return "product_feedback";
  if (name.includes("Sales")) return "sales_partnership";
  return "general_support";
}

function localizedViewTitle(view: ViewCatalogItem, dictionary: I18nDictionary): string {
  const ui = dictionary.social.ui;
  if (view.name === "Affiliates") return affiliateCopy(activeLocaleCode).title;
  if (view.name === "Affiliate Operations") return affiliateCopy(activeLocaleCode).opsTitle;
  if (view.name === "Onboarding/First Login") return ui.initialSetupTitle;
  if (view.name === "Auth/Login") return dictionary.public.nav.login;
  if (view.name === "Auth/Signup") return dictionary.public.nav.join;
  if (view.name === "Auth/Recover Password") return ui.forgotPassword;
  if (view.name === "Auth/Social Consent") return ui.continueWithOpenId;
  if (view.name === "Public/Unsubscribe") return dictionary.public.nav.support;
  if (view.name === "Support/Report Abuse") return ui.reportAbuse;
  if (view.name === "Post Detail") return ui.post;
  if (view.name === "Saved") return dictionary.social.modules.saved;
  if (view.name === "Groups Detail") return dictionary.social.modules.groups;
  if (view.name === "Pages Detail") return dictionary.social.modules.pages;
  if (view.name === "Articles Detail") return ui.articles;
  if (view.name === "Clips Create") return memberCopy(activeLocaleCode).uploadClip;
  if (view.name === "Clips Detail") return ui.clips;
  if (view.name === "Documents Detail") return ui.documents;
  if (view.name.startsWith("Settings/")) return settingsTitle(view.name, ui);
  if (view.name === "Network") return dictionary.social.modules.network;
  if (view.name === "Dating Profile") return uiLabel(ui, "datingProfile", "Dating profile");
  if (view.name === "Dating Matches") return uiLabel(ui, "datingMatches", "Matches");
  if (view.module === "dating") return uiLabel(ui, "datingDiscover", "Discover");
  if (["Invites Overview", "Your Referrals", "Invite"].includes(view.name)) return ui.invitePeople;
  if (view.name.includes("Ads Report")) return ui.report;
  if (view.name.includes("Ads")) return ui.ads;
  if (view.name.includes("Membership")) return ui.membership;
  if (view.name.includes("Invite")) return ui.invites;
  if (view.name.includes("Article")) return ui.articles;
  if (view.name.includes("Document")) return ui.documents;
  if (view.name.includes("Clip")) return ui.clips;
  if (["Followers", "Following", "List Followers", "List Following"].includes(view.name)) return ui.friends;
  if (view.name.includes("List")) return ui.lists;
  if (view.module === "profile") return ui.profile;
  if (view.module === "creator") return ui.settingsCreatorDashboard;
  if (view.module === "ai") return dictionary.social.modules.chat;
  if (view.module === "public" || view.module === "system") return ui.needsAttention;
  if (view.module === "feed") return dictionary.social.modules.feed;
  if (view.module === "network") return dictionary.social.modules.network;
  if (view.module === "chat") return ui.messages;
  if (view.module === "groups") return dictionary.social.modules.groups;
  if (view.module === "events") return dictionary.social.modules.events;
  if (view.module === "pages") return dictionary.social.modules.pages;
  if (view.module === "wallet") return dictionary.social.modules.wallet;
  if (view.module === "ops") return dictionary.social.modules.ops;
  if (view.module === "settings") return dictionary.social.modules.settings;
  if (view.module === "notifications") return dictionary.social.modules.notifications;
  if (view.module === "search") return dictionary.social.modules.search;
  if (view.module === "saved") return dictionary.social.modules.saved;
  if (view.module === "support") return dictionary.public.nav.support;
  return dictionary.public.hero.primary;
}

function settingsTitle(name: string, ui: I18nDictionary["social"]["ui"]): string {
  if (name === "Settings/Profile") return ui.settingsProfile;
  if (name === "Settings/Privacy") return ui.settingsPrivacy;
  if (name === "Settings/Notifications") return ui.settingsNotifications;
  if (name === "Settings/Email") return ui.settingsEmail;
  if (name === "Settings/Password" || name === "Settings/Add Email Password") return ui.settingsPassword;
  if (name === "Settings/Two Factor") return ui.settingsTwoFactor;
  if (name === "Settings/Display") return ui.settingsDisplay;
  if (name === "Settings/Data Download") return ui.settingsDataDownload;
  if (name === "Settings/Account") return ui.settingsAccount;
  if (name === "Settings/Subscriptions") return ui.settingsSubscriptions;
  if (name === "Settings/Creator Dashboard") return ui.settingsCreatorDashboard;
  return ui.settingsSections;
}

function supportSections(ui: I18nDictionary["social"]["ui"]) {
  return [
    { value: "general_support", label: ui.generalSupport },
    { value: "abuse", label: ui.reportAbuse },
    { value: "account_access", label: ui.accountAccess },
    { value: "privacy_gdpr", label: ui.privacyGdpr },
    { value: "payments_billing", label: ui.paymentsBilling },
    { value: "security_disclosure", label: ui.securityDisclosure },
    { value: "legal_rights", label: ui.legalRights },
    { value: "product_feedback", label: ui.productFeedback },
    { value: "sales_partnership", label: ui.salesPartnership }
  ];
}

function normalizeAppPath(value: string): string {
  const raw = value.trim();
  if (!raw) return "";
  const path = raw.startsWith("http://") || raw.startsWith("https://") ? new URL(raw).pathname : raw.split("?")[0].split("#")[0];
  const clean = path.replace(/\/+$/, "");
  return clean || "/";
}

function currentBrowserPath(): string {
  return typeof window === "undefined" ? "" : normalizeAppPath(window.location.pathname);
}

function queryRecordFromSearch(search: string): Record<string, string> {
  const query: Record<string, string> = {};
  new URLSearchParams(search).forEach((value, key) => {
    query[key] = value;
  });
  return query;
}

function currentBrowserQuery(): Record<string, string> {
  return typeof window === "undefined" ? {} : queryRecordFromSearch(window.location.search);
}

function queryRecordFromCurrentUrl(currentUrl: string): Record<string, string> {
  const queryIndex = currentUrl.indexOf("?");
  if (queryIndex === -1) return {};
  return queryRecordFromSearch(currentUrl.slice(queryIndex).split("#")[0] ?? "");
}

function currentQueryFromRoute(currentUrl: string, currentQuery: Record<string, string>): Record<string, string> {
  return { ...queryRecordFromCurrentUrl(currentUrl), ...currentQuery };
}

function queryRecordKey(query: Record<string, string>): string {
  return Object.entries(query).sort(([left], [right]) => left.localeCompare(right)).map(([key, value]) => `${key}=${value}`).join("&");
}

function routeFromHref(href: string, fallbackQuery: Record<string, string>): ClientRoute {
  if (typeof window === "undefined") return { currentUrl: normalizeAppPath(href), currentQuery: fallbackQuery };
  const url = new URL(href, window.location.origin);
  return { currentUrl: normalizeAppPath(url.pathname), currentQuery: queryRecordFromSearch(url.search) };
}

function currentOpenSNRoute(propsPath: string, propsQuery: Record<string, string>): ClientRoute {
  const browserPath = currentBrowserPath();
  if (browserPath && coreRouteViewForPath(browserPath)) return { currentUrl: browserPath, currentQuery: currentBrowserQuery() };
  return { currentUrl: propsPath || browserPath, currentQuery: propsQuery };
}

function handleOpenSNRouteClick(event: ReactMouseEvent<HTMLAnchorElement>, href: string, onNavigated?: () => void) {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  if (!navigateOpenSNRoute(href)) return;
  event.preventDefault();
  onNavigated?.();
}

function navigateOpenSNRoute(href: string): boolean {
  if (!socialAppMounted || typeof window === "undefined") return false;
  const routedHref = canonicalProfileRouteHref(href);
  const path = normalizeAppPath(routedHref);
  if (!path || !coreRouteViewForPath(path)) return false;
  const url = new URL(routedHref, window.location.origin);
  if (!url.searchParams.has("lang")) url.searchParams.set("lang", activeLocaleCode);
  const nextUrl = `${url.pathname}${url.search}${url.hash}`;
  const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (nextUrl !== currentUrl) window.history.pushState({}, "", nextUrl);
  window.dispatchEvent(new CustomEvent(routeNavigationEvent, { detail: { href: nextUrl } }));
  window.scrollTo({ top: 0 });
  return true;
}

function isSameAppPath(nextPath: string, currentPath: string): boolean {
  const next = normalizeAppPath(nextPath);
  const current = normalizeAppPath(currentPath);
  return Boolean(next && current && next === current);
}

function isRenderableAction(action: string, view: ViewCatalogItem, currentUrl: string): boolean {
  const href = hrefForAction(action, view, currentUrl);
  if (href && !isSameAppPath(href, currentUrl || (typeof window === "undefined" ? "" : window.location.pathname))) return true;
  if (actionRequestFor(action, view)) return true;
  return isSupportedClientAction(action);
}

function isSupportedClientAction(action: string): boolean {
  const normalized = action.toLowerCase();
  return (
    normalized.includes("copy") ||
    normalized.includes("share") ||
    normalized.includes("refresh") ||
    normalized.includes("retry") ||
    normalized.includes("search") ||
    normalized.includes("filter") ||
    normalized.includes("clear") ||
    normalized.includes("reset") ||
    normalized === "close" ||
    normalized === "cancel" ||
    normalized.includes("back")
  );
}

function clientActionMessage(action: string, view: ViewCatalogItem, label: string, ui: I18nDictionary["social"]["ui"]): string {
  const normalized = action.toLowerCase();
  if (normalized.includes("open")) return ui.chooseItemFromView;
  if (normalized.includes("follow") || normalized.includes("unfollow")) return ui.chooseMemberPageHashtag;
  if (normalized.includes("cancel request")) return ui.chooseGroupRequest;
  if (normalized.includes("cancel campaign")) return ui.chooseAdCampaign;
  if (normalized.includes("join") || normalized.includes("leave") || normalized.includes("request")) return ui.chooseGroupFirst;
  if (normalized.includes("select plan")) return ui.chooseMembershipPlan;
  if (normalized.includes("delete") || normalized.includes("remove")) return ui.chooseItemBeforeAction.replace("{action}", label.toLowerCase());
  if (normalized.includes("mute") || normalized.includes("block") || normalized.includes("pin") || normalized.includes("turn")) return ui.chooseItemForSetting;
  return ui.actionNeedsSelectedItem;
}

function hrefForAction(action: string, view: ViewCatalogItem, currentUrl: string): string | undefined {
  const normalized = action.toLowerCase();
  if (normalized.includes("sign up")) return "/signup";
  if (normalized.includes("forgot") || normalized.includes("back to login")) return normalized.includes("forgot") ? "/recover" : "/login";
  if (normalized.includes("log in")) return "/login";
  if (normalized.includes("go home") || normalized.includes("go to home")) return "/timeline";
  if (normalized.includes("saved") || normalized.includes("bookmark")) return "/saved";
  if (normalized.includes("invite")) return "/invites/invite";
  if (normalized.includes("manage users") && view.module === "pages") return "/pages/admin";
  if (normalized.includes("notify settings") && view.module === "pages") return "/pages/admin";
  if (normalized.includes("reports") && view.module === "pages") return "/pages/admin";
  if (normalized.includes("feature page") && view.module === "pages") return "/pages/admin";
  if (normalized.includes("settings")) return "/settings/notifications";
  if (view.name === "Profile Overview" && (normalized.startsWith("profile.") || normalized.includes("edit profile") || normalized.includes("upload avatar") || normalized.includes("upload cover"))) return "/settings/profile";
  if (normalized.includes("message") || normalized.includes("chat")) return "/chat";
  if (normalized.includes("create group")) return "/groups/create";
  if (normalized.includes("create event")) return "/events/create";
  if (normalized.includes("create page")) return "/pages/create";
  if (normalized.includes("create ad")) return "/ads/create";
  if (normalized.includes("create article")) return "/articles/editor";
  if (normalized.includes("create document") || normalized.includes("upload document")) return "/documents/create";
  if (normalized.includes("create") && view.module === "clips") return "/clips/create";
  if (normalized.includes("select plan")) return "/membership/checkout";
  if (normalized.includes("current membership") || normalized.includes("subscription")) return "/membership/current";
  if (normalized.includes("membership")) return "/membership";
  if (normalized.includes("transfer")) return "/wallet/transfer";
  if (normalized.includes("withdraw")) return "/wallet/withdraw";
  if (normalized.includes("wallet")) return "/wallet";
  if (normalized.includes("add funds") || normalized.includes("deposit")) return "/wallet/deposit";
  if (normalized.includes("open transactions")) return "/wallet/transactions";
  if (normalized.includes("contact support")) return "/support";
  if (normalized.includes("back")) return parentUrl(currentUrl);
  if (normalized.includes("open profile")) return profileHrefFromIdentifier(activeProfileUsername());
  if (normalized.includes("open group")) return "/groups";
  if (normalized.includes("open event")) return "/events";
  if (normalized.includes("open page")) return "/pages";
  if (normalized.includes("open article")) return "/articles";
  if (normalized.includes("open media") && view.module === "profile") return `${profileHrefFromIdentifier(activeProfileUsername())}/photos`;
  if (normalized.includes("open media") && view.module === "clips") return "/clips";
  if (normalized.includes("open document")) return "/documents";
  if (normalized.includes("open clip")) return "/clips";
  if (normalized.includes("open hashtag")) return "/hashtags";
  if (normalized.includes("open notification")) return "/notifications";
  if (normalized.includes("open room")) return "/chat";
  if (normalized.includes("open post")) return "/timeline";
  if (normalized.includes("open detail")) return `/${view.module}`;
  if (normalized.includes("manage") && view.module === "groups") return "/groups/managed";
  if (normalized.includes("manage") && view.module === "events") return "/events/managed";
  if (normalized.includes("manage") && view.module === "pages") return "/pages/admin";
  if (normalized.includes("report") && view.module !== "support") return "/support/report-abuse";
  if (normalized.includes("follow")) return "/network";
  if (normalized.includes("join")) return "/groups";
  return undefined;
}

function actionRequestFor(action: string, view: ViewCatalogItem): { endpoint: string; method: "POST" | "PATCH" | "DELETE"; payload: Record<string, unknown> } | undefined {
  const normalized = action.toLowerCase();
  if (view.name === "Notifications" && normalized.includes("mark all read")) {
    return { endpoint: "/api/notifications/read-all", method: "POST", payload: {} };
  }
  if (view.name === "Search" && normalized.includes("clear history")) {
    return { endpoint: "/api/search/history", method: "DELETE", payload: {} };
  }
  if (view.name === "Search" && normalized.includes("clear all history")) {
    return { endpoint: "/api/search/history", method: "DELETE", payload: {} };
  }
  return undefined;
}

function parentUrl(currentUrl: string): string {
  const parts = currentUrl.split("/").filter(Boolean);
  if (parts.length <= 1) return "/timeline";
  return `/${parts.slice(0, -1).join("/")}`;
}

function primaryActionLabel(view: ViewCatalogItem, ui: I18nDictionary["social"]["ui"]): string {
  if (view.name === "Onboarding/First Login") return ui.finishOnboarding;
  if (view.name === "Creator Onboarding Steps") return ui.finishOnboarding;
  if (view.name === "Ads Create") return ui.createCampaign;
  const raw = view.primaryAction ?? view.actions[0];
  return raw ? displayActionLabel(raw, view, ui) : ui.open;
}

function buttonVariant(action: string): string {
  const value = action.toLowerCase();
  if (value.includes("delete") || value.includes("remove") || value.includes("cancel campaign") || value.includes("unfollow") || value.includes("leave")) return "danger";
  if (value.includes("save") || value.includes("create") || value.includes("submit") || value.includes("send") || value.includes("publish") || value.includes("log in") || value.includes("continue") || value.includes("pay") || value.includes("add funds") || value.includes("transfer")) return "primary";
  if (value.startsWith("profile.")) return "outlined";
  if (value.includes("search") || value.includes("filter") || value.includes("open") || value.includes("upload")) return "outlined";
  return "secondary";
}

function iconForAction(action: string): ReactNode {
  const value = action.toLowerCase();
  if (value.includes("search") || value.includes("filter")) return <Search size={16} />;
  if (value === "profile.edit") return <Settings size={16} />;
  if (value === "profile.avatar" || value === "profile.cover") return <Upload size={16} />;
  if (value.includes("upload") || value.includes("attach")) return <Upload size={16} />;
  if (value.includes("download") || value.includes("export")) return <Download size={16} />;
  if (value.includes("delete") || value.includes("remove")) return <Trash2 size={16} />;
  if (value.includes("mark")) return <CheckCircle2 size={16} />;
  if (value.includes("message") || value.includes("send")) return <Send size={16} />;
  if (value.includes("event")) return <CalendarDays size={16} />;
  if (value.includes("follow") || value.includes("join")) return <UserPlus size={16} />;
  if (value.includes("share")) return <Share2 size={16} />;
  if (value.includes("open")) return <Link2 size={16} />;
  if (value.includes("report") || value.includes("flag")) return <Flag size={16} />;
  if (value.includes("back") || value.includes("cancel")) return <ArrowLeft size={16} />;
  return <ArrowRight size={16} />;
}

function displayActionLabel(action: string, view: ViewCatalogItem, ui: I18nDictionary["social"]["ui"]): string {
  const clean = action.replace(/`/g, "").trim();
  const value = clean.toLowerCase();
  const exact: Record<string, string> = {
    "profile.edit": ui.editProfile,
    "profile.avatar": ui.uploadAvatar,
    "profile.cover": ui.uploadCover,
    "accept and continue": ui.done,
    "log in": ui.logIn,
    "continue with openid": ui.continueWithOpenId,
    "forgot password": ui.forgotPassword,
    "sign up": ui.signUp,
    "create account": ui.signUp,
    "back to login": ui.backToLogin,
    "finish onboarding": ui.finishOnboarding,
    "skip optional step": ui.skipOptionalSetup,
    "edit profile": ui.editProfile,
    "upload avatar": ui.uploadAvatar,
    "upload cover": ui.uploadCover,
    "remove avatar": ui.removeAvatar,
    "copy invite link": ui.copyLink,
    "copy registration link": ui.copyRegistrationLink,
    "copy profile link": ui.copyLink,
    "copy ticket id": ui.ticketId,
    "send invite": ui.sendInvite,
    "clear all history": ui.clearHistory,
    "mark all read": ui.markAllRead ?? ui.done,
    "delete history item": ui.deleteItem,
    "create campaign": ui.createCampaign,
    "create event": ui.createEvent,
    "event details": ui.eventDetails,
    "view event details": ui.eventDetails,
    "interested": ui.eventInterested,
    "going": ui.eventGoing,
    "can't go": ui.eventCantGo,
    "launch campaign": ui.launchCampaign,
    "save draft": ui.saveDraft,
    "cancel campaign": ui.cancelCampaign,
    "create post": ui.addNewPost,
    "post comment": ui.postComment,
    "post reply": ui.postComment,
    "submit report": ui.report,
    "submit ticket": ui.createSupportTicket,
    "submit request": ui.createSupportTicket,
    "submit privacy request": ui.createSupportTicket,
    "submit billing ticket": ui.createSupportTicket,
    "submit disclosure": ui.createSupportTicket,
    "submit legal request": ui.createSupportTicket,
    "submit partnership request": ui.createSupportTicket,
    "submit code": ui.verify,
    "send tip": ui.sendMoney,
    "send funds": ui.sendMoney,
    "transfer": ui.sendMoney,
    "add funds": ui.addFunds,
    "select package": ui.addFunds,
    "pay": ui.payNow,
    "submit withdraw": ui.withdrawFunds,
    "withdraw": ui.withdrawFunds,
    "open wallet": ui.open,
    "view referrals": ui.invites,
    "new message": ui.send,
    "room settings": ui.settingsSections,
    "request export": ui.exports,
    "log out other devices": ui.logOutOtherDevices,
    "who can comment": ui.comment,
    "content warning": ui.needsAttention,
    "paid content": ui.payNow,
    "clips for you": ui.clips,
    "for you/following": ui.filterResults,
    "trust this device": ui.done,
    "use another method": ui.method,
    "recovery path": ui.forgotPassword,
    "regenerate recovery codes": ui.resetToken,
    "view edit history": ui.open,
    "view more transactions": ui.open,
    "go home": ui.goHome,
    "go to home": ui.goHome,
    "contact support": ui.contactSupport,
    "keep subscription": ui.keepSubscription,
    "feature page": ui.featurePage,
    "boost post": ui.boostPost,
    "run now": ui.runNow,
    "disconnect": ui.disconnect,
    "install": ui.installApp,
    "edit": ui.editItem,
    "edit post": ui.editItem,
    "edit description": ui.editItem,
    "start trial": ui.startCheckout,
    "start": ui.start,
    "continue": ui.open,
    "next": ui.open,
    "previous": ui.open
  };
  if (exact[value]) return exact[value];
  if (value === view.title.trim().toLowerCase()) return ui.open;
  if (value.includes("open") || value.includes("view")) return ui.open;
  if (value.includes("search") || value.includes("filter") || value.includes("sort")) return ui.search;
  if (value.includes("change range")) return ui.filterResults;
  if (value.includes("follow") || value.includes("unfollow")) return ui.follow;
  if (value.includes("join") || value.includes("leave")) return ui.join;
  if (value.includes("message") || value.includes("chat")) return ui.message ?? ui.messages ?? ui.send;
  if (value.includes("send") || value.includes("record")) return ui.send;
  if (value.includes("create") || value.includes("add") || value.includes("duplicate")) return ui.create;
  if (value.includes("edit")) return ui.editItem;
  if (value.includes("attach") || value.includes("upload") || value.includes("import")) return ui.attachFiles;
  if (value.includes("copy") || value.includes("share")) return value.includes("share") ? ui.share : ui.copyLink;
  if (value.includes("download") || value.includes("export")) return ui.exports;
  if (value.includes("publish")) return ui.publish;
  if (value.includes("save")) return ui.save;
  if (value.includes("submit")) return ui.createSupportTicket;
  if (value.includes("apply") || value.includes("accept") || value.includes("approve") || value.includes("complete") || value.includes("confirm") || value.includes("mark")) return ui.done;
  if (value.includes("cancel") || value.includes("close") || value.includes("back") || value.includes("reject") || value.includes("dismiss") || value.includes("not now")) return ui.cancel;
  if (value.includes("delete") || value.includes("remove") || value.includes("discard") || value.includes("unsend")) return ui.deleteItem;
  if (value.includes("clear") || value.includes("reset")) return ui.clear;
  if (value.includes("report") || value.includes("warn") || value.includes("suspend") || value.includes("escalate")) return ui.report;
  if (value.includes("like")) return ui.like;
  if (value.includes("comment") || value.includes("reply") || value.includes("review")) return ui.comment;
  if (value.includes("bookmark") || value === "book") return ui.bookmark;
  if (value.includes("saved")) return ui.savedItems;
  if (value.includes("play")) return ui.play;
  if (value.includes("pause")) return ui.pause;
  if (value.includes("retry") || value.includes("refresh") || value.includes("reconnect") || value.includes("replay")) return ui.retry;
  if (value.includes("run now")) return ui.runNow;
  if (value.includes("install")) return ui.installApp;
  if (value.includes("subscribe") || value.includes("unlock") || value.includes("package") || value.includes("plan") || value.includes("trial")) return ui.membership;
  if (value.includes("verify")) return ui.verify;
  if (value.includes("add funds") || value.includes("deposit")) return ui.addFunds;
  if (value.includes("transfer") || value.includes("send funds") || value.includes("tip")) return ui.sendMoney;
  if (value.includes("withdraw")) return ui.withdrawFunds;
  if (value.includes("pay") || value.includes("buy")) return ui.payNow;
  if (value.includes("refund")) return ui.createSupportTicket;
  if (value.includes("wallet")) return ui.open;
  if (value.includes("settings") || value.includes("manage") || value.includes("notify") || value.includes("enable") || value.includes("disable") || value.includes("mute") || value.includes("pin") || value.includes("turn") || value.includes("block")) return ui.settingsSections;
  if (value.includes("role") || value.includes("admin") || value.includes("moderator") || value.includes("owner") || value.includes("assign") || value.includes("promote")) return ui.roles;
  if (value.includes("resolve") || value.includes("restore") || value.includes("resume") || value.includes("renew")) return ui.done;
  if (value.includes("email")) return ui.email;
  if (value.includes("phone")) return ui.phone;
  if (value.includes("password")) return ui.password;
  if (value.includes("category")) return ui.category;
  if (value.includes("audience")) return ui.audience;
  if (value.includes("budget") || value.includes("price")) return ui.budget;
  if (value.includes("emoji") || value.includes("gif") || value.includes("image") || value.includes("photo") || value.includes("video") || value.includes("media") || value.includes("crop")) return ui.media;
  if (value.includes("song") || value.includes("audio")) return ui.clips;
  return ui.open;
}

function AuthLinks({ view, ui, currentQuery }: { view: ViewCatalogItem; ui: I18nDictionary["social"]["ui"]; currentQuery: PageProps["currentQuery"] }) {
  const link = (href: string, label: ReactNode) => <a href={href} onClick={(event) => handleOpenSNRouteClick(event, href)}>{label}</a>;
  if (view.name === "Auth/Login") return <div className="auth-links">{link(authRouteHref("/recover", currentQuery), ui.forgotPassword)}{link(authRouteHref("/signup", currentQuery), ui.signUp)}</div>;
  if (view.name === "Auth/Signup") return <div className="auth-links">{link(authRouteHref("/login", currentQuery), ui.alreadyHaveAccount)}</div>;
  if (view.name === "Onboarding/First Login") return <div className="auth-links">{link(safeAuthRedirect(currentQuery.redirect), ui.skipOptionalSetup)}{link("/settings/profile", ui.profileSettings)}</div>;
  return <div className="auth-links">{link(authRouteHref("/login", currentQuery), ui.backToLogin)}{link("/support", ui.supportRequestFailed)}</div>;
}

function emptyText(view: ViewCatalogItem, ui: I18nDictionary["social"]["ui"]): string {
  if (view.name === "Home Feed") return ui.feedEmpty;
  if (view.name === "Network") return ui.invitedPeopleEmpty;
  if (view.name === "Search") return ui.searchEmpty;
  if (view.module === "notifications") return ui.notificationsEmpty;
  if (view.module === "saved") return ui.savedEmpty;
  if (view.module === "groups") return ui.groupsEmpty;
  if (view.module === "events") return ui.eventsEmpty;
  if (view.module === "pages") return ui.pagesEmpty;
  if (view.module === "documents") return ui.documentsEmpty;
  if (view.module === "clips") return ui.clipsEmpty;
  if (view.module === "photos") return uiLabel(ui, "photosEmpty", "Photos will appear here after they are uploaded.");
  if (view.module === "articles") return ui.articlesEmpty;
  if (view.module === "ads") return ui.adsEmpty;
  if (view.module === "ops") return ui.opsEmpty;
  if (view.module === "profile") return ui.profileEmpty;
  if (view.name.includes("List")) return ui.listsEmpty;
  if (view.name === "Membership Current") return ui.membershipCurrentEmpty;
  if (view.name === "Membership Checkout") return ui.membershipCheckoutEmpty;
  if (view.module === "membership") return ui.membershipPlansEmpty;
  if (view.module === "wallet") return ui.walletEmpty;
  return ui.genericEmpty;
}

function emptyStateCopy(view: ViewCatalogItem, body: string, ui: I18nDictionary["social"]["ui"]): { title: string; body: string } {
  if (view.name === "Home Feed") return { title: ui.noPostsYet, body };
  if (view.slug === "profile-timeline") return { title: ui.noPostsYet, body };
  if (view.name === "Search") return { title: ui.startWithQuery, body };
  if (view.module === "chat") return { title: ui.noConversationsYet, body };
  if (view.module === "notifications") return { title: ui.allClear, body };
  if (view.module === "saved") return { title: ui.savedItems, body };
  if (view.module === "membership") return { title: ui.membership, body };
  if (view.module === "wallet") return { title: ui.noActivityYet, body };
  if (view.module === "ops") return { title: ui.operationsSections, body };
  if (view.module === "network") return { title: uiLabel(ui, "noFriendsYet", "No friends yet"), body };
  if (view.module === "groups") return { title: uiLabel(ui, "noGroupsYet", "No groups yet"), body };
  if (view.module === "events") return { title: uiLabel(ui, "noEventsYet", "No events yet"), body };
  if (view.module === "pages") return { title: uiLabel(ui, "noPagesYet", "No pages yet"), body };
  if (view.module === "documents") return { title: uiLabel(ui, "noDocumentsYet", "No documents yet"), body };
  if (view.module === "clips") return { title: ui.clips, body };
  if (view.module === "photos") return { title: uiLabel(ui, "noPhotosYet", "No photos yet"), body };
  if (view.module === "articles") return { title: uiLabel(ui, "noArticlesYet", "No articles yet"), body };
  if (view.module === "ads") return { title: ui.ads, body };
  if (view.module === "profile") return { title: ui.profile, body };
  if (view.name.includes("List")) return { title: ui.lists, body };
  return { title: ui.nothingHereYet, body };
}

function emptyStateIcon(view: ViewCatalogItem): LucideIcon {
  if (view.slug === "profile-timeline" || view.name === "Home Feed") return Inbox;
  if (view.endpoint === "/api/profile/friends" || view.module === "network") return Users;
  if (view.module === "photos") return Images;
  if (view.module === "articles") return FileText;
  if (view.module === "profile") return CircleUserRound;
  return Inbox;
}

function renderPostRow(row: Record<string, unknown>, ui: I18nDictionary["social"]["ui"], authUser: PageProps["auth"]["user"] | undefined, localeCode: string, detail = false) {
  return <PostRow row={row} ui={ui} authUser={authUser} localeCode={localeCode} detail={detail} />;
}

function notificationUnreadCount(result: ApiResult): number {
  const data = isRecord(result.data) ? result.data : null;
  const meta = data && isRecord(data.meta) ? data.meta : null;
  const candidates = [data?.unread_count, data?.unreadCount, meta?.unread_count, meta?.unreadCount];
  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null || String(candidate).trim() === "") continue;
    return Math.max(0, Math.floor(numberValue(candidate)));
  }
  return result.rows.filter(isUnreadNotificationRow).length;
}

function notificationBadgeText(count: number): string {
  return count > 99 ? "99+" : String(count);
}

function isUnreadNotificationRow(row: Record<string, unknown>): boolean {
  return !row.read_at && !row.readAt;
}

function NotificationRow({ row, ui }: { row: Record<string, unknown>; ui: I18nDictionary["social"]["ui"] }) {
  const unread = isUnreadNotificationRow(row);
  const title = notificationTitle(row, ui);
  const body = notificationBody(row, ui);
  const when = timeLabel(row.created_at ?? row.createdAt);
  const Icon = notificationIcon(row);
  const href = notificationHref(row);
  return (
    <a className={`notification-item ${unread ? "unread" : ""}`} href={href} onClick={(event) => handleOpenSNRouteClick(event, href)}>
      <span className="notification-dot" aria-hidden="true"><Icon size={20} /></span>
      <span>
        <header>
          <strong>{title}</strong>
          {when && <time>{when}</time>}
        </header>
        <p>{body}</p>
      </span>
      {unread && <span className="unread-dot" aria-label={ui.notificationUnread} />}
    </a>
  );
}

function notificationType(row: Record<string, unknown>): string {
  return String(row.type ?? row.event_type ?? row.eventType ?? "").trim().toLowerCase();
}

function notificationTitle(row: Record<string, unknown>, ui: I18nDictionary["social"]["ui"]): string {
  const type = notificationType(row);
  const community = getCommunityMessages(activeLocaleCode);
  if (community[`notification_${type}`]) return community[`notification_${type}`];
  if (community[type]) return community[type];
  if (type === "friend.request_received") return ui.friendRequestReceivedNotification;
  if (type === "friend.request_accepted") return ui.friendRequestAcceptedNotification;
  if (type === "friend.added_friend") return ui.friendAddedFriendNotification;
  if (type.startsWith("follow.")) return ui.followNotification;
  if (type.startsWith("comment.") || type.startsWith("reply.")) return ui.commentNotifications;
  if (type.startsWith("message.") || type.startsWith("chat.")) return ui.messageNotifications;
  if (type.startsWith("event.")) return ui.events;
  if (type.startsWith("group.")) return ui.groups;
  if (type.startsWith("page.")) return ui.pages;
  return localizedMachineText(row.title, ui) || ui.notifications;
}

function notificationBody(row: Record<string, unknown>, ui: I18nDictionary["social"]["ui"]): string {
  const explicitBody = String(row.body ?? row.message ?? "");
  if (explicitBody) return explicitBody;
  const type = notificationType(row);
  const actor = notificationName(row, "actor");
  const target = notificationName(row, "target");
  if (type === "friend.added_friend" && actor && target) return `${actor} ${ui.friendAddedFriendBody} ${target}`;
  if (type === "friend.request_received" && actor) return actor;
  if (type === "friend.request_accepted" && actor) return actor;
  if (type.startsWith("follow.") && actor) return actor;
  return localizedMachineText(row.subject_type ?? row.subjectType ?? row.status ?? "", ui) || ui.ready;
}

function notificationName(row: Record<string, unknown>, prefix: "actor" | "target"): string {
  return String(
    row[`${prefix}_display_name`] ??
    row[`${prefix}DisplayName`] ??
    row[`${prefix}_name`] ??
    row[`${prefix}Name`] ??
    row[`${prefix}_username`] ??
    row[`${prefix}Username`] ??
    ""
  ).trim();
}

function notificationIcon(row: Record<string, unknown>): LucideIcon {
  const type = notificationType(row);
  if (type === "friend.request_accepted") return UserCheck;
  if (type.startsWith("friend.") || type.startsWith("follow.")) return UserPlus;
  if (type.startsWith("message.") || type.startsWith("chat.") || type.startsWith("comment.") || type.startsWith("reply.")) return MessageCircle;
  if (type.startsWith("event.")) return CalendarDays;
  if (type.startsWith("group.")) return Users;
  if (type.startsWith("page.")) return BriefcaseBusiness;
  return Bell;
}

function notificationHref(row: Record<string, unknown>): string {
  const explicitHref = String(row.href ?? row.url ?? row.target_url ?? row.targetUrl ?? "").trim();
  if (explicitHref) return canonicalProfileRouteHref(explicitHref);
  const type = notificationType(row);
  const subjectType = String(row.subject_type ?? row.subjectType ?? "").trim().toLowerCase();
  const subjectId = String(row.subject_id ?? row.subjectId ?? "").trim();
  const slug = String(row.subject_slug ?? row.subjectSlug ?? row.slug ?? "").trim();
  if ((type.startsWith("event.") || subjectType === "event") && subjectId) return directoryEntityHref("/events", subjectId, slug);
  if ((type.startsWith("group.") || subjectType === "group") && subjectId) return directoryEntityHref("/groups", subjectId, slug);
  if ((type.startsWith("page.") || subjectType === "page") && subjectId) return directoryEntityHref("/pages", subjectId, slug);
  if (type.startsWith("message.") || type.startsWith("chat.") || subjectType === "chat") return "/chat";
  if (type.startsWith("comment.") || type.startsWith("reply.") || subjectType === "post" || subjectType === "comment" || subjectType === "reply") return notificationPostHref(row, subjectType, subjectId, slug) || "/notifications";
  if (type.startsWith("friend.") || type.startsWith("follow.") || subjectType === "user") return notificationProfileHref(row) || "/network";
  return "/notifications";
}

function notificationPostHref(row: Record<string, unknown>, subjectType: string, subjectId: string, subjectSlug: string): string {
  const postIdentifier = String(
    row.post_slug ??
    row.postSlug ??
    row.post_id ??
    row.postId ??
    row.target_post_slug ??
    row.targetPostSlug ??
    row.target_post_id ??
    row.targetPostId ??
    ""
  ).trim();
  if (postIdentifier) return `/post/${encodeURIComponent(postIdentifier)}`;
  if (subjectType === "post" && (subjectSlug || subjectId)) return `/post/${encodeURIComponent(subjectSlug || subjectId)}`;
  return "";
}

function notificationProfileHref(row: Record<string, unknown>): string {
  const identifier = firstProfileIdentifier(
    row.subject_profile_slug,
    row.subjectProfileSlug,
    row.subject_public_id,
    row.subjectPublicId,
    row.subject_slug,
    row.subjectSlug,
    row.subject_username ??
    row.subjectUsername,
    row.actor_profile_slug,
    row.actorProfileSlug,
    row.actor_public_id,
    row.actorPublicId,
    row.actor_slug,
    row.actorSlug,
    row.actor_username,
    row.actorUsername,
    row.target_profile_slug,
    row.targetProfileSlug,
    row.target_public_id,
    row.targetPublicId,
    row.target_slug,
    row.targetSlug,
    row.target_username,
    row.targetUsername,
    row.profile_slug,
    row.profileSlug,
    row.public_id,
    row.publicId,
    row.username
  );
  return profileHrefFromIdentifier(identifier);
}

function directoryEntityHref(basePath: string, id: string, slug: string): string {
  const identifier = slug || id;
  return identifier ? `${basePath}/${encodeURIComponent(identifier)}` : basePath;
}

function defaultMemberRelationshipType(): OpenSNRelationshipType | null {
  const definition = activeSocialDefinition;
  if (!definition) return DEFAULT_MEMBER_RELATIONSHIP;
  return definition.relationships.types.find((item) => item.id === definition.relationships.defaultType) ?? definition.relationships.types[0] ?? null;
}

function relationshipTitleOptions(relationship: OpenSNRelationshipType | null): SelectOption[] {
  return localizedFieldOptions(relationship?.titleOptions, activeLocaleCode);
}

function defaultRelationshipTitle(relationship: OpenSNRelationshipType | null, options: SelectOption[]): string {
  if (!relationship || relationship.titleMode === "none") return "";
  return localizeOpenSNText(relationship.defaultTitle, activeLocaleCode, options[0]?.value ?? "");
}

function relationshipActionPayload(payload: Record<string, unknown>, relationship: OpenSNRelationshipType | null, title: string): Record<string, unknown> {
  if (!relationship) return payload;
  const nextPayload: Record<string, unknown> = { ...payload, relationshipType: relationship.id };
  if (relationship.titleMode !== "none") nextPayload.relationshipTitle = title.trim();
  return nextPayload;
}

function DirectoryRow({ row, view, ui, categoryLabels, authUser, detail = false }: { row: Record<string, unknown>; view: ViewCatalogItem; ui: I18nDictionary["social"]["ui"]; categoryLabels: I18nDictionary["categoryLabels"]; authUser?: PageProps["auth"]["user"]; detail?: boolean }) {
  if (view.module === "events") return <EventDirectoryRow row={row} view={view} ui={ui} categoryLabels={categoryLabels} authUser={authUser} />;
  const isFriendList = isFriendDirectoryView(view);
  const isProfileDirectoryItem = view.module === "network" || Boolean(row.username || row.user_id || row.userId);
  const location = isProfileDirectoryItem ? memberLocationLabel(row) : "";
  const title = String(row.name ?? row.title ?? row.display_name ?? row.username ?? row.id ?? ui.directoryItem);
  const moduleClass = `directory-card-${cssClassToken(view.module)}`;
  const coverSrc = isFriendList ? "" : mediaSrcFromRow(row, "cover") || (detail ? directoryDetailCoverSrc(row) : "");
  const categoryLabel = isFriendList ? "" : directoryCategoryLabel(row, categoryLabels);
  const longFormDetail = detail && (view.module === "articles" || view.module === "documents");
  const detailText = longFormDetail ? directoryLongFormSummary(row, ui) : directoryRowDetail(row, ui, isFriendList, detail);
  const bodyText = longFormDetail ? directoryLongFormBody(row, detailText, ui) : "";
  const [state, setState] = useState<SubmitState>({ status: "idle", message: "" });
  const [followed, setFollowed] = useState(rowBoolean(row.followed_by_me ?? row.followedByMe));
  const [joined, setJoined] = useState(rowBoolean(row.joined_by_me ?? row.joinedByMe));
  const action = directoryAction(row, view);
  const relationship = action?.endpoint === "/api/follows" ? defaultMemberRelationshipType() : null;
  const relationshipTitleChoices = relationshipTitleOptions(relationship);
  const [relationshipTitle, setRelationshipTitle] = useState(() => defaultRelationshipTitle(relationship, relationshipTitleChoices));
  const alreadyFollowed = (action?.success === "following" || action?.success === "hashtagFollowed") && followed;
  const alreadyJoined = action?.success === "joined" && joined;
  const showFriendListAction = Boolean(isFriendList && action && authUser && !ownsProfile(row, authUser));
  const showDirectoryAction = Boolean(action && !showFriendListAction && !(isFriendList && (alreadyFollowed || alreadyJoined)));
  const titleRequired = relationship?.titleMode === "required";
  const groupOwnedByViewer = view.module === "groups" && String(row.owner_user_id ?? "") === authUser?.id;
  const actionDisabled = alreadyJoined && groupOwnedByViewer || Boolean(!alreadyFollowed && titleRequired && !relationshipTitle.trim());
  const actionLabel = action && relationship ? localizeOpenSNText(relationship.actionLabel ?? relationship.label, activeLocaleCode, ui[action.labelKey]) : action ? ui[action.labelKey] : "";
  const successLabel = action && relationship ? localizeOpenSNText(relationship.successLabel ?? relationship.label, activeLocaleCode, ui[action.success]) : action ? ui[action.success] : "";
  const titleLabel = relationship ? localizeOpenSNText(relationship.titleLabel, activeLocaleCode, ui.profile) : "";
  const href = directoryHref(row, view);
  const loginHref = authRouteHrefForCurrentRoute("/login");
  const loginActionLabel = action ? directoryLoginActionLabel(action, relationship, ui) : "";
  useEffect(() => {
    setFollowed(rowBoolean(row.followed_by_me ?? row.followedByMe));
    setJoined(rowBoolean(row.joined_by_me ?? row.joinedByMe));
  }, [row.followed_by_me, row.followedByMe, row.joined_by_me, row.joinedByMe]);
  const titleLink = view.module === "network"
    ? <ProfileNameLink row={row}>{title}</ProfileNameLink>
    : detail ? <h3 className="directory-detail-title">{title}</h3> : <a className="profile-name-link" href={href} onClick={(event) => handleOpenSNRouteClick(event, href)}>{title}</a>;
  async function submitAction() {
    if (!action || actionDisabled) return;
    setState({ status: "saving", message: actionLabel });
    try {
      await submitJson(action.endpoint, action.method, relationshipActionPayload(action.payload, relationship, relationshipTitle));
      if (action.success === "following" || action.success === "hashtagFollowed") setFollowed(true);
      if (action.success === "joined") setJoined(true);
      setState({ status: "success", message: successLabel });
    } catch (error) {
      setState({ status: "error", message: localizedErrorMessage(error, ui.actionFailed) });
    }
  }
  async function cancelFriendship() {
    if (!action || state.status === "saving") return;
    setState({ status: "saving", message: ui.cancel });
    try {
      await submitJson(action.endpoint, "DELETE", action.payload);
      setFollowed(false);
      setJoined(false);
      setState({ status: "success", message: ui.requestCompleted });
    } catch (error) {
      setState({ status: "error", message: localizedErrorMessage(error, ui.actionFailed) });
    }
  }
  async function submitFriendListAction() {
    if (alreadyFollowed) {
      await cancelFriendship();
      return;
    }
    await submitAction();
  }
  const friendListActionDisabled = state.status === "saving" || (!alreadyFollowed && actionDisabled);
  const friendListActionLabel = alreadyFollowed ? ui.cancel : actionLabel;
  return (
    <div className={`directory-card ${moduleClass} ${isFriendList ? "friend-directory-card" : ""} ${detail ? "directory-detail-card" : ""}`.trim()}>
      {!isFriendList && (
        <div className={`directory-cover ${coverSrc ? "has-image" : ""}`} aria-hidden="true">
          {coverSrc ? <PictureImage src={coverSrc} alt="" /> : null}
        </div>
      )}
      <header>
        <ProfileAvatar row={row} size="lg" className="directory-avatar" />
        <div className="directory-card-main">
          <div className={isFriendList ? "friend-directory-title-row" : ""}>
            {titleLink}
            {showFriendListAction && action ? (
              <BaseButton type="button" variant={alreadyFollowed ? "outlined" : "primary"} size="sm" className="friend-directory-action" loading={state.status === "saving"} disabled={friendListActionDisabled} onClick={submitFriendListAction}>
                {friendListActionLabel}
              </BaseButton>
            ) : null}
          </div>
          {detailText && <p>{detailText}</p>}
          {location && <p>{location}</p>}
          {categoryLabel && <span className="directory-category-pill">{categoryLabel}</span>}
        </div>
      </header>
      {bodyText && <div className="directory-detail-body"><p>{bodyText}</p></div>}
      {!isFriendList && <div className="inline-actions">
        {!detail && !isProfileDirectoryItem && <a className="base-button outlined" href={href} onClick={(event) => handleOpenSNRouteClick(event, href)}>{ui.open}</a>}
        {showDirectoryAction && relationship && authUser && relationship.titleMode !== "none" && !alreadyFollowed && (
          <label className="relationship-title-field">
            <span>{titleLabel}</span>
            {relationshipTitleChoices.length > 0 ? (
              <select value={relationshipTitle} onChange={(event) => setRelationshipTitle(event.currentTarget.value)} required={titleRequired}>
                {relationshipTitleChoices.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}
              </select>
            ) : (
              <input value={relationshipTitle} onChange={(event) => setRelationshipTitle(event.currentTarget.value)} required={titleRequired} />
            )}
          </label>
        )}
        {showDirectoryAction && action && authUser ? <BaseButton type="button" variant={alreadyJoined || alreadyFollowed ? "outlined" : "primary"} loading={state.status === "saving"} disabled={actionDisabled} onClick={alreadyJoined || alreadyFollowed ? cancelFriendship : submitAction}>{alreadyJoined ? groupOwnedByViewer ? ui.joined : memberCopy(activeLocaleCode).leaveGroup : alreadyFollowed ? memberCopy(activeLocaleCode).unfollow : actionLabel}</BaseButton> : null}
        {action && !authUser && !isFriendList ? <a className="base-button primary" href={loginHref} onClick={(event) => handleOpenSNRouteClick(event, loginHref)}>{loginActionLabel}</a> : null}
      </div>}
      <InlineStatus state={state} />
    </div>
  );
}

function isFriendDirectoryView(view: ViewCatalogItem): boolean {
  return view.endpoint.startsWith("/api/follows") || view.endpoint.startsWith("/api/profile/friends") || view.slug === "profile-friends";
}

function directoryRowDetail(row: Record<string, unknown>, ui: I18nDictionary["social"]["ui"], isFriendList: boolean, detail: boolean): string {
  if (isFriendList) return friendDirectoryDetail(row, ui);
  const value = detail
    ? row.description ?? row.excerpt ?? row.summary ?? row.body ?? ""
    : row.description ?? row.excerpt ?? row.status ?? row.privacy ?? ui.ready;
  return localizedMachineText(value, ui);
}

function directoryLongFormSummary(row: Record<string, unknown>, ui: I18nDictionary["social"]["ui"]): string {
  return localizedMachineText(row.excerpt ?? row.summary ?? row.description ?? row.document_type ?? row.documentType ?? row.status ?? "", ui);
}

function directoryLongFormBody(row: Record<string, unknown>, summary: string, ui: I18nDictionary["social"]["ui"]): string {
  const body = localizedMachineText(row.body ?? row.content ?? row.text ?? "", ui);
  if (!body || body === summary) return "";
  return body;
}

function directoryDetailCoverSrc(row: Record<string, unknown>): string {
  return mediaFileSrc({
    object_key: row.cover_media_object_key ?? row.coverMediaObjectKey ?? row.cover_object_key ?? row.coverObjectKey ?? row.image_object_key ?? row.imageObjectKey ?? row.media_object_key ?? row.mediaObjectKey ?? row.thumbnail_object_key ?? row.thumbnailObjectKey,
    public_url: row.cover_image_url ?? row.coverImageUrl ?? row.cover_url ?? row.coverUrl ?? row.image_url ?? row.imageUrl ?? row.media_url ?? row.mediaUrl ?? row.thumbnail_url ?? row.thumbnailUrl ?? row.public_url ?? row.publicUrl
  });
}

function friendDirectoryDetail(row: Record<string, unknown>, ui: I18nDictionary["social"]["ui"]): string {
  const mutualCount = positiveCount(row.mutual_friends_count ?? row.mutualFriendsCount ?? row.mutual_count ?? row.mutualCount);
  if (mutualCount) return `${formatCount(mutualCount)} ${profileCountLabel(mutualCount, ui, "mutualFriendCountOne", "mutualFriendCountMany", "mutual friend", "mutual friends")}`;
  return nonRoleProfileDetail(row.bio ?? row.headline ?? row.description ?? row.status ?? "");
}

function nonRoleProfileDetail(value: unknown): string {
  const detail = String(value ?? "").trim();
  if (!detail) return "";
  const normalized = detail.toLocaleLowerCase().replace(/[_-]+/gu, " ").trim();
  if (["participant", "facilitator", "trainer", "admin", "administrator", "owner"].includes(normalized)) return "";
  return detail;
}

type EventRsvpValue = "interested" | "going" | "declined";

const eventRsvpOptions: Array<{ value: EventRsvpValue; labelKey: string; Icon: LucideIcon }> = [
  { value: "interested", labelKey: "eventInterested", Icon: Heart },
  { value: "going", labelKey: "eventGoing", Icon: UserCheck },
  { value: "declined", labelKey: "eventCantGo", Icon: UserX }
];

function EventDirectoryRow({ row, view, ui, categoryLabels, authUser, detail = false }: { row: Record<string, unknown>; view: ViewCatalogItem; ui: I18nDictionary["social"]["ui"]; categoryLabels: I18nDictionary["categoryLabels"]; authUser?: PageProps["auth"]["user"]; detail?: boolean }) {
  const id = String(row.id ?? row.event_id ?? row.eventId ?? row.slug ?? row.event_slug ?? row.eventSlug ?? "").trim();
  const title = String(row.title ?? row.name ?? row.id ?? ui.events).trim();
  const description = String(row.description ?? row.body ?? "").trim();
  const coverSrc = mediaSrcFromRow(row, "cover") || mediaFileSrc({
    object_key: row.cover_media_object_key ?? row.coverMediaObjectKey ?? row.image_object_key ?? row.imageObjectKey,
    public_url: row.cover_image_url ?? row.coverImageUrl ?? row.image_url ?? row.imageUrl
  });
  const categoryLabel = directoryCategoryLabel(row, categoryLabels);
  const startsAt = eventDateValue(row, ["starts_at", "startsAt", "start_date", "startDate", "start_time", "startTime"]);
  const endsAt = eventDateValue(row, ["ends_at", "endsAt", "end_date", "endDate", "end_time", "endTime"]);
  const dateLabel = formatEventDateRange(startsAt, endsAt, ui);
  const dateParts = eventDateParts(startsAt, ui);
  const format = eventFormat(row);
  const onlineUrl = String(row.online_url ?? row.onlineUrl ?? row.event_url ?? row.eventUrl ?? "").trim();
  const location = String(row.location ?? row.location_name ?? row.venue_name ?? row.venue ?? row.address ?? "").trim();
  const locationLabel = format === "online" ? (onlineUrl || location || ui.onlineEvent) : (location || onlineUrl || ui.eventLocationTbd);
  const hostLabel = eventHostLabel(row, ui);
  const privacyMeta = postVisibilityMeta(row.visibility ?? row.privacy, ui);
  const PrivacyIcon = privacyMeta.Icon;
  const privacyLabel = privacyMeta.label;
  const goingCount = numberValue(row.going_count ?? row.goingCount ?? row.attending_count ?? row.attendingCount);
  const interestedCount = numberValue(row.interested_count ?? row.interestedCount);
  const initialRsvp = normalizeEventRsvp(row.viewer_rsvp ?? row.viewerRsvp ?? row.my_rsvp ?? row.myRsvp ?? row.rsvp_status ?? row.rsvpStatus);
  const href = directoryHref(row, view);
  const loginHref = authRouteHrefForCurrentRoute("/login");
  const [rsvp, setRsvp] = useState<EventRsvpValue | null>(initialRsvp);
  const [state, setState] = useState<SubmitState>({ status: "idle", message: "" });

  async function submitRsvp(nextRsvp: EventRsvpValue) {
    if (!id || state.status === "saving") return;
    setState({ status: "saving", message: ui.eventRsvpSaving });
    try {
      await submitJson(`/api/events/${encodeURIComponent(id)}/rsvp`, "POST", { response: nextRsvp, status: nextRsvp });
      setRsvp(nextRsvp);
      setState({ status: "success", message: ui.eventRsvpSaved });
    } catch (error) {
      setState({ status: "error", message: localizedErrorMessage(error, ui.eventRsvpFailed) });
    }
  }

  return (
    <div className={`directory-card event-card ${detail ? "event-detail-card" : ""}`}>
      <div className={`directory-cover event-cover ${coverSrc ? "has-image" : ""}`} aria-hidden="true">
        {coverSrc ? <PictureImage src={coverSrc} alt="" /> : <CalendarDays size={34} />}
      </div>
      <header>
        <span className="event-date-badge" aria-hidden="true">
          <span>{dateParts.month}</span>
          <strong>{dateParts.day}</strong>
        </span>
        <div>
          {detail ? <h3 className="event-title">{title}</h3> : <a className="profile-name-link event-title" href={href} onClick={(event) => handleOpenSNRouteClick(event, href)}>{title}</a>}
          <p>{description || hostLabel || privacyLabel}</p>
          <span className="event-format-pill">{format === "online" ? ui.onlineEvent : ui.inPersonEvent}</span>
          {categoryLabel && <span className="directory-category-pill">{categoryLabel}</span>}
        </div>
      </header>
      <div className="event-meta-list">
        <span><CalendarDays size={15} />{dateLabel}</span>
        <span><MapPin size={15} />{locationLabel}</span>
        {hostLabel && <span><Users size={15} />{hostLabel}</span>}
        <span><PrivacyIcon size={15} />{privacyLabel}</span>
      </div>
      {(goingCount > 0 || interestedCount > 0) && (
        <div className="event-social-proof">
          {goingCount > 0 && <span>{formatCompactNumber(goingCount)} {ui.eventGoing}</span>}
          {interestedCount > 0 && <span>{formatCompactNumber(interestedCount)} {ui.eventInterested}</span>}
        </div>
      )}
      <div className={`event-rsvp-actions ${detail ? "detail" : ""} ${authUser ? "" : "guest"}`.trim()} role="group" aria-label={ui.eventRsvp}>
        {authUser ? eventRsvpOptions.map(({ value, labelKey, Icon }) => {
          const active = rsvp === value;
          return (
            <BaseButton type="button" size="sm" variant={active ? "primary" : "outlined"} disabled={!id || state.status === "saving"} loading={state.status === "saving" && active} onClick={() => submitRsvp(value)} key={value}>
              <Icon size={15} />
              <span>{ui[labelKey]}</span>
            </BaseButton>
          );
        }) : <a className="base-button primary sm" href={loginHref} onClick={(event) => handleOpenSNRouteClick(event, loginHref)}>{uiLabel(ui, "logInToEventRsvp", "Log in to RSVP")}</a>}
        {!detail && <a className="base-button secondary sm" href={href} onClick={(event) => handleOpenSNRouteClick(event, href)}>{ui.eventDetails}</a>}
      </div>
      <InlineStatus state={state} />
    </div>
  );
}

function eventDateValue(row: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const value = String(row[key] ?? "").trim();
    if (value) return value;
  }
  return "";
}

function formatEventDateRange(startValue: string, endValue: string, ui: I18nDictionary["social"]["ui"]): string {
  const start = dateFromValue(startValue);
  if (!start) return ui.eventDateTbd;
  const end = dateFromValue(endValue);
  const localeCode = activeTimeLocaleCode();
  const dateText = new Intl.DateTimeFormat(localeCode, { month: "short", day: "numeric", year: start.getFullYear() === new Date().getFullYear() ? undefined : "numeric" }).format(start);
  const startTime = new Intl.DateTimeFormat(localeCode, { hour: "numeric", minute: "2-digit" }).format(start);
  if (!end) return `${dateText} - ${startTime}`;
  const sameDay = start.toDateString() === end.toDateString();
  const endDateText = sameDay ? "" : new Intl.DateTimeFormat(localeCode, { month: "short", day: "numeric" }).format(end);
  const endTime = new Intl.DateTimeFormat(localeCode, { hour: "numeric", minute: "2-digit" }).format(end);
  return sameDay ? `${dateText} - ${startTime} - ${endTime}` : `${dateText} - ${startTime} - ${endDateText} - ${endTime}`;
}

function eventDateParts(value: string, ui: I18nDictionary["social"]["ui"]): { month: string; day: string } {
  const date = dateFromValue(value);
  if (!date) return { month: ui.eventDateTbd, day: "" };
  const localeCode = activeTimeLocaleCode();
  return {
    month: new Intl.DateTimeFormat(localeCode, { month: "short" }).format(date),
    day: new Intl.DateTimeFormat(localeCode, { day: "2-digit" }).format(date)
  };
}

function dateFromValue(value: string): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function eventFormat(row: Record<string, unknown>): "online" | "in_person" {
  const raw = String(row.format ?? row.event_format ?? row.eventFormat ?? row.type ?? "").trim().toLowerCase();
  if (raw.includes("online") || raw.includes("virtual")) return "online";
  return "in_person";
}

function eventHostLabel(row: Record<string, unknown>, ui: I18nDictionary["social"]["ui"]): string {
  const hostRows = arrayFromJson(row.hosts_json ?? row.hostsJson ?? row.hosts).filter(isRecord);
  const hostNames = hostRows.map((host) => displayListValue(host.display_name ?? host.name ?? host.username)).filter(Boolean);
  const directHost = displayListValue(row.host_name ?? row.hostName ?? row.organizer_name ?? row.organizerName ?? row.owner_display_name ?? row.ownerDisplayName);
  const names = hostNames.length ? hostNames : directHost ? [directHost] : [];
  if (!names.length) return "";
  return `${ui.eventHostedBy} ${names.slice(0, 2).join(", ")}${names.length > 2 ? ` +${names.length - 2}` : ""}`;
}

function normalizeEventRsvp(value: unknown): EventRsvpValue | null {
  const raw = String(value ?? "").trim().toLowerCase();
  if (raw === "interested" || raw === "maybe") return "interested";
  if (raw === "going" || raw === "attending") return "going";
  if (raw === "declined" || raw === "not_going" || raw === "cant_go" || raw === "can't go") return "declined";
  return null;
}

function rowBoolean(value: unknown): boolean {
  return value === true || value === 1 || value === "1" || String(value).toLocaleLowerCase() === "true";
}

function directoryCategoryLabel(row: Record<string, unknown>, categoryLabels: I18nDictionary["categoryLabels"]): string {
  const slug = String(row.category_slug ?? row.categorySlug ?? "").trim();
  const label = String(row.category_label ?? row.categoryLabel ?? "").trim();
  if (!slug && !label) return "";
  return localizedCategoryLabel({ slug, label }, categoryLabels);
}

function directoryHref(row: Record<string, unknown>, view: ViewCatalogItem): string {
  const id = String(row.id ?? row.event_id ?? row.eventId ?? row.user_id ?? row.userId ?? row.hashtag_id ?? row.hashtagId ?? "").trim();
  const slug = String(row.slug ?? row.event_slug ?? row.eventSlug ?? row.normalized_tag ?? row.username ?? id).trim();
  if (view.module === "groups") return directoryEntityHref("/groups", id, slug);
  if (view.module === "events") return directoryEntityHref("/events", id, slug);
  if (view.module === "pages") return directoryEntityHref("/pages", id, slug);
  if (view.module === "search" && (row.normalized_tag || String(row.tag ?? "").startsWith("#"))) return `/hashtags/${encodeURIComponent(String(row.normalized_tag ?? row.tag).replace(/^#/, ""))}`;
  if (view.module === "network" || row.username || row.user_id) return profileHrefFromRow(row) || "/network";
  return `/${view.module}/${encodeURIComponent(id || slug || "item")}`;
}

function directoryAction(row: Record<string, unknown>, view: ViewCatalogItem): { labelKey: keyof I18nDictionary["social"]["ui"]; endpoint: string; method: "POST"; payload: Record<string, unknown>; success: keyof I18nDictionary["social"]["ui"] } | null {
  const id = String(row.id ?? row.user_id ?? "").trim();
  if (view.module === "groups" && id) return { labelKey: "join", endpoint: `/api/groups/${encodeURIComponent(id)}/join`, method: "POST", payload: {}, success: "joined" };
  if (view.module === "pages" && id) return { labelKey: "follow", endpoint: `/api/pages/${encodeURIComponent(id)}/follow`, method: "POST", payload: {}, success: "following" };
  if ((view.module === "network" || row.user_id || row.username) && id) return { labelKey: "follow", endpoint: "/api/follows", method: "POST", payload: { userId: id }, success: "following" };
  if (view.module === "search" || view.module === "feed") {
    const tag = String(row.normalized_tag ?? row.tag ?? "").replace(/^#/, "").trim();
    if (tag) return { labelKey: "follow", endpoint: `/api/hashtags/${encodeURIComponent(tag)}/follow`, method: "POST", payload: {}, success: "hashtagFollowed" };
  }
  return null;
}

function directoryLoginActionLabel(action: NonNullable<ReturnType<typeof directoryAction>>, relationship: OpenSNRelationshipType | null, ui: I18nDictionary["social"]["ui"]): string {
  if (relationship) return uiLabel(ui, "logInToAddFriend", "Log in to add friend");
  if (action.labelKey === "join") return uiLabel(ui, "logInToJoin", "Log in to join");
  return uiLabel(ui, "logInToFollow", "Log in to follow");
}

function mediaSrcFromRow(row: Record<string, unknown>, kind: "avatar" | "cover", useProfileDefault = false): string {
  const objectKey = String(row[`${kind}_object_key`] ?? row[`${kind}ObjectKey`] ?? "").trim();
  const publicUrl = String(row[`${kind}_url`] ?? row[`${kind}Url`] ?? "").trim();
  if (objectKey) return mediaObjectSrc(objectKey);
  return publicUrl || (useProfileDefault ? defaultProfileMediaSrc(kind) : "");
}

function defaultProfileMediaSrc(kind: "avatar" | "cover"): string {
  const resolvedTheme = currentResolvedOpenSNTheme();
  if (kind === "avatar") {
    return String((resolvedTheme === "dark" ? activeMediaDefaults.profilePhotoDark ?? activeMediaDefaults.default_profile_photo_dark : activeMediaDefaults.profilePhotoLight ?? activeMediaDefaults.default_profile_photo_light) ?? "").trim();
  }
  return String((resolvedTheme === "dark" ? activeMediaDefaults.coverPhotoDark ?? activeMediaDefaults.default_cover_photo_dark : activeMediaDefaults.coverPhotoLight ?? activeMediaDefaults.default_cover_photo_light) ?? "").trim();
}

function currentResolvedOpenSNTheme(): "light" | "dark" {
  if (typeof document !== "undefined") {
    const resolved = document.documentElement.dataset.opensnResolvedTheme;
    if (resolved === "dark" || resolved === "light") return resolved;
  }
  if (typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches) return "dark";
  return "light";
}

function mediaFileSrc(row: Record<string, unknown>): string {
  const objectKey = String(row.object_key ?? row.objectKey ?? row.bucket_key ?? row.bucketKey ?? row.file_key ?? row.fileKey ?? "").trim();
  const publicUrl = String(row.public_url ?? row.publicUrl ?? row.url ?? row.src ?? "").trim();
  if (objectKey) return mediaObjectSrc(objectKey);
  return publicUrl;
}

function mediaObjectSrc(objectKey: string): string {
  return `/media/${objectKey.split("/").map((part) => encodeURIComponent(part)).join("/")}`;
}

function storyHref(row: Record<string, unknown>): string {
  const explicit = String(row.href ?? row.permalink_url ?? row.story_url ?? row.path ?? "").trim();
  if (explicit) return canonicalProfileRouteHref(explicit);
  const id = String(row.id ?? row.story_id ?? "").trim();
  return id ? `/stories/${encodeURIComponent(id)}` : "";
}

function storyMediaSrc(row: Record<string, unknown>): string {
  return mediaFileSrc({
    object_key: row.media_object_key ?? row.mediaObjectKey ?? row.object_key ?? row.objectKey,
    public_url: row.media_url ?? row.mediaUrl ?? row.image_url ?? row.imageUrl ?? row.public_url ?? row.publicUrl ?? row.src
  }) || mediaSrcFromRow(row, "cover") || mediaSrcFromRow(row, "avatar");
}

function postMediaItems(row: Record<string, unknown>): Array<Record<string, unknown>> {
  const rawItems = [
    row.media_items_json,
    row.mediaItemsJson,
    row.post_media_json,
    row.postMediaJson,
    row.items_json,
    row.itemsJson,
    row.media_json,
    row.mediaJson,
    row.items,
    row.media
  ]
    .flatMap(arrayFromJson)
    .filter(isRecord);
  const seen = new Set<string>();
  return rawItems.flatMap((item) => {
    const normalized = normalizePostMediaItem(item);
    const src = mediaFileSrc(normalized);
    if (!src || !postMediaItemKind(normalized)) return [];
    const key = String(normalized.id ?? normalized.file_id ?? normalized.fileId ?? src);
    if (seen.has(key)) return [];
    seen.add(key);
    return [normalized];
  });
}

function normalizePostMediaItem(item: Record<string, unknown>): Record<string, unknown> {
  const metadata = recordFromJson(item.metadata ?? item.metadata_json);
  return {
    ...item,
    id: item.id ?? item.fileId ?? item.file_id,
    object_key:
      item.object_key ??
      item.objectKey ??
      item.bucket_key ??
      item.bucketKey ??
      item.file_key ??
      item.fileKey ??
      metadata.object_key ??
      metadata.objectKey ??
      metadata.file_key ??
      metadata.fileKey,
    public_url:
      item.public_url ??
      item.publicUrl ??
      item.file_url ??
      item.fileUrl ??
      item.url ??
      item.src ??
      metadata.public_url ??
      metadata.publicUrl ??
      metadata.file_url ??
      metadata.fileUrl ??
      metadata.url,
    content_type:
      item.content_type ??
      item.contentType ??
      item.mime_type ??
      item.mimeType ??
      metadata.content_type ??
      metadata.contentType ??
      metadata.mime,
    file_name: item.file_name ?? item.fileName ?? item.name ?? metadata.fileName ?? metadata.file_name
  };
}

function arrayFromJson(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string" || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value) as unknown;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function authorProfileRow(row: Record<string, unknown>): Record<string, unknown> {
  return {
    ...row,
    id: row.author_id ?? row.user_id ?? row.id,
    user_id: row.author_id ?? row.user_id,
    profile_slug: firstProfileIdentifier(row.author_profile_slug, row.authorProfileSlug, row.profile_slug, row.profileSlug),
    public_id: row.author_public_id ?? row.authorPublicId ?? row.public_id ?? row.publicId,
    username: row.author_username ?? row.username,
    display_name: row.author_display_name ?? row.display_name,
    gender: row.author_gender ?? row.gender,
    avatar_object_key: row.author_avatar_object_key ?? row.avatar_object_key,
    avatar_url: row.author_avatar_url ?? row.avatar_url
  };
}

function messageSenderProfileRow(row: Record<string, unknown>): Record<string, unknown> {
  return {
    ...row,
    id: row.sender_id ?? row.user_id ?? row.id,
    user_id: row.sender_id ?? row.user_id,
    profile_slug: firstProfileIdentifier(row.sender_profile_slug, row.senderProfileSlug, row.profile_slug, row.profileSlug),
    public_id: row.sender_public_id ?? row.senderPublicId ?? row.public_id ?? row.publicId,
    username: row.sender_username ?? row.username,
    display_name: row.sender_name ?? row.display_name,
    gender: row.sender_gender ?? row.gender,
    avatar_object_key: row.sender_avatar_object_key ?? row.avatar_object_key,
    avatar_url: row.sender_avatar_url ?? row.avatar_url
  };
}

function decodeProfileHandle(segment: string): string {
  return decodeURIComponent(segment).replace(/^@+/, "").trim();
}

function profileHrefFromIdentifier(identifier: unknown): string {
  const handle = String(identifier ?? "").trim().replace(/^@+/, "");
  return handle ? `/profile/${encodeURIComponent(handle)}` : "";
}

function canonicalProfileRouteHref(value: unknown): string {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const isHttpUrl = raw.startsWith("http://") || raw.startsWith("https://");
  let path = "";
  let suffix = "";
  if (isHttpUrl) {
    if (typeof window === "undefined") return raw;
    try {
      const url = new URL(raw);
      if (url.origin !== window.location.origin) return raw;
      path = url.pathname;
      suffix = `${url.search}${url.hash}`;
    } catch {
      return raw;
    }
  } else {
    const match = raw.match(/^([^?#]*)([?#].*)?$/);
    path = match?.[1] ?? raw;
    suffix = match?.[2] ?? "";
  }
  const profileMatch = path.replace(/\/+$/, "").match(/^\/(?:profile\/|@)([^/]+)(\/(?:about|friends|photos|articles))?$/);
  if (!profileMatch) {
    const normalized = path.replace(/\/+$/, "") || "/";
    if (normalized === "/friends" || normalized === "/users") return `/network${suffix}`;
    return raw;
  }
  const identifier = decodeProfileHandle(profileMatch[1] ?? "");
  if (!identifier) return raw;
  return `${profileHrefFromIdentifier(identifier)}${profileMatch[2] ?? ""}${suffix}`;
}

function activeProfileUsername(): string {
  if (typeof window === "undefined") return "me";
  const currentPath = normalizeAppPath(window.location.pathname);
  return profileRouteIdentifier(currentPath) || "me";
}

function profileIdentifierFromRow(row: Record<string, unknown>): string {
  return firstProfileIdentifier(
    row.profile_slug,
    row.profileSlug,
    row.author_profile_slug,
    row.authorProfileSlug,
    row.sender_profile_slug,
    row.senderProfileSlug,
    row.owner_profile_slug,
    row.ownerProfileSlug,
    row.reporter_profile_slug,
    row.reporterProfileSlug,
    row.participant_profile_slug,
    row.participantProfileSlug,
    row.actor_profile_slug,
    row.actorProfileSlug,
    row.target_profile_slug,
    row.targetProfileSlug,
    row.public_id,
    row.publicId,
    row.author_public_id,
    row.authorPublicId,
    row.sender_public_id,
    row.senderPublicId,
    row.owner_public_id,
    row.ownerPublicId,
    row.reporter_public_id,
    row.reporterPublicId,
    row.participant_public_id,
    row.participantPublicId,
    row.actor_public_id,
    row.actorPublicId,
    row.target_public_id,
    row.targetPublicId,
    row.username,
    row.author_username,
    row.authorUsername,
    row.sender_username,
    row.senderUsername,
    row.owner_username,
    row.ownerUsername,
    row.reporter_username,
    row.reporterUsername
  );
}

function firstProfileIdentifier(...values: unknown[]): string {
  for (const value of values) {
    const identifier = String(value ?? "").trim();
    if (!identifier) continue;
    if (identifier.startsWith("/@")) return identifier.slice(2);
    return identifier.replace(/^@+/, "");
  }
  return "";
}

function profileHrefFromRow(row: Record<string, unknown>): string {
  const identifier = profileIdentifierFromRow(row);
  return profileHrefFromIdentifier(identifier);
}

function genderToken(row: Record<string, unknown>): string {
  const raw = String(row.gender ?? row.profile_gender ?? row.author_gender ?? "").trim().toLowerCase();
  if (raw.includes("woman") || raw === "female" || raw === "f") return "woman";
  if (raw.includes("man") || raw === "male" || raw === "m") return "man";
  if (raw.includes("non") || raw.includes("trans") || raw.includes("self")) return "neutral";
  return "unknown";
}

function firstNonEmptyText(...values: unknown[]): string {
  for (const value of values) {
    const text = String(value ?? "").trim();
    if (text) return text;
  }
  return "";
}

function profileDisplayNameFromRow(row: Record<string, unknown>, fallback: string): string {
  return firstNonEmptyText(
    row.display_name,
    row.displayName,
    row.friend_display_name,
    row.friendDisplayName,
    row.friend_name,
    row.friendName,
    row.participant_display_name,
    row.participantDisplayName,
    row.actor_display_name,
    row.actorDisplayName,
    row.target_display_name,
    row.targetDisplayName,
    row.participant_name,
    row.participantName,
    row.actor_name,
    row.actorName,
    row.target_name,
    row.targetName,
    row.name,
    row.friend_username,
    row.friendUsername,
    row.participant_username,
    row.participantUsername,
    row.actor_username,
    row.actorUsername,
    row.target_username,
    row.targetUsername,
    row.username,
    fallback
  );
}

function chatParticipantProfileRow(row: Record<string, unknown>): Record<string, unknown> {
  return {
    ...row,
    id: row.participant_user_id ?? row.participantUserId ?? row.user_id ?? row.userId ?? row.id,
    user_id: row.participant_user_id ?? row.participantUserId ?? row.user_id ?? row.userId,
    profile_slug: firstProfileIdentifier(
      row.participant_profile_slug,
      row.participantProfileSlug,
      row.profile_slug,
      row.profileSlug
    ),
    public_id: row.participant_public_id ?? row.participantPublicId ?? row.public_id ?? row.publicId,
    username: row.participant_username ?? row.participantUsername ?? row.username,
    display_name:
      row.participant_display_name ??
      row.participantDisplayName ??
      row.participant_names ??
      row.participantNames ??
      row.display_name ??
      row.displayName,
    gender: row.participant_gender ?? row.participantGender ?? row.gender,
    avatar_object_key:
      row.participant_avatar_object_key ??
      row.participantAvatarObjectKey ??
      row.avatar_object_key ??
      row.avatarObjectKey,
    avatar_url: row.participant_avatar_url ?? row.participantAvatarUrl ?? row.avatar_url ?? row.avatarUrl
  };
}

function chatRoomUnreadCount(row: Record<string, unknown> | null): number {
  return row ? Math.max(0, Math.floor(numberValue(row.unread_count ?? row.unreadCount))) : 0;
}

function profileInitials(row: Record<string, unknown>): string {
  const name = profileDisplayNameFromRow(row, String(row.email ?? "")).trim();
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
  return initials || "U";
}

function ProfileAvatar({
  row,
  size = "md",
  className = "",
  useDefault = true
}: {
  row: Record<string, unknown>;
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
  useDefault?: boolean;
}) {
  const src = mediaSrcFromRow(row, "avatar", useDefault);
  const classes = `profile-avatar ${className} size-${size} gender-${genderToken(row)}`.trim();
  if (src)
    return (
      <span className={`${classes} has-image`} aria-hidden="true">
        <PictureImage src={src} alt="" />
      </span>
    );
  return (
    <span className={classes} aria-hidden="true">
      <span>{profileInitials(row)}</span>
    </span>
  );
}

function ProfileAvatarLink({
  row,
  size = "md",
  className = ""
}: {
  row: Record<string, unknown>;
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
}) {
  const href = profileHrefFromRow(row);
  const label =
    profileDisplayNameFromRow(row, "").trim() ||
    firstNonEmptyText(row.username, row.slug, row.profile_slug, row.public_id, row.id) ||
    href.split("/").filter(Boolean).at(-1) ||
    "Profile";
  if (!href) return <ProfileAvatar row={row} size={size} className={className} />;
  return (
    <a
      className="profile-avatar-link"
      href={href}
      aria-label={label}
      onClick={(event) => handleOpenSNRouteClick(event, href)}
    >
      <ProfileAvatar row={row} size={size} className={className} />
    </a>
  );
}

function ProfileNameLink({ row, children, className = "" }: { row: Record<string, unknown>; children: ReactNode; className?: string }) {
  const href = profileHrefFromRow(row);
  if (!href) return <span className={className}>{children}</span>;
  return <a className={`profile-name-link ${className}`.trim()} href={href} onClick={(event) => handleOpenSNRouteClick(event, href)}>{children}</a>;
}

function cssClassToken(value: string): string {
  return value.toLocaleLowerCase().replace(/[^a-z0-9_-]+/g, "-");
}

function localizedSystemValue(value: unknown, ui: I18nDictionary["social"]["ui"]): string {
  const text = String(value ?? "").trim();
  const copy = getCommunityMessages(activeLocaleCode);
  const translated = copy[`system_${text}`] ?? copy[`notification_${text}`] ?? copy[text];
  if (translated) return translated;
  const keys: Record<string, string> = {
    friend: "friends", friends: "friends", follow: "follow", post: "post", comment: "comment", reply: "reply", user: "profile", group: "groups", event: "events", page: "pages", ad: "ads", article: "articles", document: "documents", message: "message", traffic: "traffic", reach: "reach", photo: "photo", online: "onlineEvent", like: "like", love: "reactionLove", haha: "reactionHaha", wow: "reactionWow", sad: "reactionSad", angry: "reactionAngry"
  };
  return ui[keys[text]] ?? activeDictionary?.social.modules[text] ?? localizedMachineText(text, ui);
}

function localizedMachineText(value: unknown, ui: I18nDictionary["social"]["ui"]): string {
  const text = String(value ?? "").trim();
  if (!text) return "";
  const normalized = text.toLocaleLowerCase();
  const exact: Record<string, string> = {
    public: ui.public,
    private: ui.private,
    network: ui.network,
    direct: ui.directDiscussion,
    group: ui.groupRoom,
    ready: ui.ready,
    ok: ui.ready,
    active: ui.active,
    archived: ui.archived,
    inactive: ui.needsAttention,
    disabled: ui.needsAttention,
    pending: ui.pending,
    accepted: ui.accepted,
    blocked: ui.blocked,
    waiting: ui.waiting,
    queued: ui.waiting,
    retry: ui.retry,
    retrying: ui.retry,
    processing: ui.loading,
    indexed: ui.ready,
    processed: ui.ready,
    received: ui.ready,
    acked: ui.ready,
    sent: ui.messageSent,
    draft: ui.draft,
    published: ui.published,
    failed: ui.failed,
    expired: ui.expired,
    revoked: ui.revoked,
    error: ui.needsAttention,
    dead_letter: ui.needsAttention,
    provider_config_required: ui.settingsRequestFailed,
    not_configured: ui.settingsRequestFailed,
    paused: ui.paused,
    cancelled: ui.cancelled,
    canceled: ui.cancelled,
    completed: ui.completed,
    succeeded: ui.requestCompleted,
    deleted: ui.deleteItem,
    open: ui.open,
    resolved: ui.resolved,
    escalated: ui.escalated,
    closed: ui.completed
  };
  if (exact[normalized]) return exact[normalized];
  if (normalized.startsWith("queue.validation.")) return `${ui.queues}: ${ui.submitFailed}`;
  if (normalized.startsWith("queue.")) return `${ui.queues}: ${ui.needsAttention}`;
  if (normalized.startsWith("upload.")) return `${ui.uploadedFileId}: ${ui.needsAttention}`;
  return text;
}

function localizedGenericTitle(value: unknown, ui: I18nDictionary["social"]["ui"]): string {
  const raw = String(value ?? "").trim();
  const localized = localizedMachineText(raw, ui);
  if (localized && localized !== raw) return localized;
  return titleCase((localized || raw || ui.directoryItem).replaceAll(".", " "));
}

function renderGenericRow(row: Record<string, unknown>, ui: I18nDictionary["social"]["ui"]) {
  const label = row.display_name ?? row.name ?? row.title ?? row.username ?? row.email ?? row.subject ?? row.type ?? row.id ?? row.key ?? ui.directoryItem;
  const detail = row.username ? `@${String(row.username)}` : row.body ?? row.description ?? row.error_message ?? row.error_code ?? row.message ?? row.status ?? row.created_at ?? row.createdAt ?? ui.ready;
  return (
    <>
      <strong><ProfileNameLink row={row}>{localizedGenericTitle(label, ui)}</ProfileNameLink></strong>
      <p>{localizedMachineText(detail, ui) || ui.ready}</p>
    </>
  );
}

function SavedItemRow({ row, ui }: { row: Record<string, unknown>; ui: I18nDictionary["social"]["ui"] }) {
  const subject = recordFromJson(row.subject ?? row.subject_json);
  const title = row.title ?? row.subject_title ?? row.subjectTitle ?? row.post_title ?? subject.title ?? subject.name ?? subject.body ?? row.body ?? ui.savedItem;
  const detail = row.collection ?? row.subject_type ?? row.subjectType ?? row.created_at ?? row.createdAt ?? ui.savedItems;
  const href = savedItemHref(row, subject);
  return (
    <>
      <header>
        <Bookmark size={17} aria-hidden="true" />
        <strong>{href ? <a className="profile-name-link" href={href} onClick={(event) => handleOpenSNRouteClick(event, href)}>{localizedGenericTitle(title, ui)}</a> : localizedGenericTitle(title, ui)}</strong>
      </header>
      <p>{localizedMachineText(detail, ui) || ui.savedItems}</p>
      {href && <footer><a className="base-button outlined sm" href={href} onClick={(event) => handleOpenSNRouteClick(event, href)}>{ui.open}</a></footer>}
    </>
  );
}

function savedItemHref(row: Record<string, unknown>, subject: Record<string, unknown>): string {
  const subjectType = String(row.subject_type ?? row.subjectType ?? subject.subject_type ?? subject.subjectType ?? "").trim().toLowerCase();
  if (subjectType === "post") {
    const postIdentifier = String(
      row.post_slug ??
      row.postSlug ??
      subject.post_slug ??
      subject.postSlug ??
      row.subject_slug ??
      row.subjectSlug ??
      subject.slug ??
      row.subject_id ??
      row.subjectId ??
      subject.id ??
      subject.subject_id ??
      subject.subjectId ??
      ""
    ).trim();
    if (postIdentifier) return `/post/${encodeURIComponent(postIdentifier)}`;
  }
  return canonicalProfileRouteHref(row.href ?? row.url ?? row.subject_href ?? row.subjectHref ?? subject.href ?? subject.url ?? subject.permalink_url ?? "");
}

function renderOpsRow(row: Record<string, unknown>, endpoint: string, ui: I18nDictionary["social"]["ui"], onSaved: () => void) {
  if (/\/api\/ops\/(reports|users|posts|groups|pages)$/.test(endpoint)) return <OpsEditableRow row={row} endpoint={endpoint} ui={ui} onSaved={onSaved} />;
  return renderGenericRow(row, ui);
}

function OpsEditableRow({ row, endpoint, ui, onSaved }: { row: Record<string, unknown>; endpoint: string; ui: I18nDictionary["social"]["ui"]; onSaved: () => void }) {
  const [status, setStatus] = useState(String(row.status ?? "active"));
  const [role, setRole] = useState(String(row.role ?? "user"));
  const [state, setState] = useState<SubmitState>({ status: "idle", message: "" });
  const id = String(row.id ?? "");
  const target = `${endpoint}/${encodeURIComponent(id)}`;
  const options = opsStatusOptions(endpoint, row);
  const showRole = endpoint.endsWith("/users");
  const label = opsRowTitle(row, endpoint, ui);
  const detail = opsRowDetail(row, endpoint, ui);
  const profileRow = {
    ...row,
    username: row.username,
    display_name: row.display_name
  };
  async function save() {
    setState({ status: "saving", message: ui.save });
    try {
      await submitJson(target, "PATCH", showRole ? { status, role } : { status });
      setState({ status: "success", message: ui.saved });
      onSaved();
    } catch (error) {
      setState({ status: "error", message: localizedErrorMessage(error, ui.actionFailed) });
    }
  }
  return (
    <>
      <div>
        <strong><ProfileNameLink row={profileRow}>{endpoint.endsWith("/posts") ? label : localizedGenericTitle(label, ui)}</ProfileNameLink></strong>
        <p>{renderOpsDetail(row, detail, ui)}</p>
      </div>
      <div className="inline-actions">
        <label>
          <span>{ui.status}</span>
          <select value={status} onChange={(event) => setStatus(event.currentTarget.value)}>
            {options.map((option) => <option value={option} key={option}>{localizedSystemValue(option, ui)}</option>)}
          </select>
        </label>
        {showRole && (
          <label>
            <span>{ui.roles}</span>
            <select value={role} onChange={(event) => setRole(event.currentTarget.value)}>
              {opsRoleOptions(role).map((option) => <option value={option} key={option}>{localizedSystemValue(option, ui)}</option>)}
            </select>
          </label>
        )}
        <BaseButton type="button" variant="outlined" loading={state.status === "saving"} onClick={save}>{ui.save}</BaseButton>
      </div>
      <InlineStatus state={state} />
    </>
  );
}

function opsRoleOptions(current: string): string[] {
  const humanOsRoles = ["participant", "facilitator", "trainer", "admin"];
  const genericRoles = ["user", "moderator", "ops", "admin"];
  const options = humanOsRoles.includes(current) ? humanOsRoles : genericRoles;
  return options.includes(current) || !current ? options : [current, ...options];
}

function opsRowTitle(row: Record<string, unknown>, endpoint: string, ui: I18nDictionary["social"]["ui"]): string {
  if (endpoint.endsWith("/posts")) return compactText(row.body ?? row.title ?? row.description ?? row.id ?? ui.post, 180);
  return String(row.display_name ?? row.name ?? row.title ?? row.username ?? row.email ?? row.subject_type ?? row.provider ?? row.id ?? ui.directoryItem);
}

function opsRowDetail(row: Record<string, unknown>, endpoint: string, ui: I18nDictionary["social"]["ui"]): string {
  if (endpoint.endsWith("/posts")) {
    const author = String(row.display_name ?? row.author_display_name ?? row.username ?? "").trim();
    const status = localizedMachineText(row.status, ui) || String(row.status ?? "").trim();
    const created = String(row.created_at ?? row.createdAt ?? "").trim();
    return [author ? `@${String(row.username ?? author).trim()}` : "", status, created ? postAbsoluteTimeLabel(created) : ""].filter(Boolean).join(" - ");
  }
  return String(row.email ?? row.username ?? row.body ?? row.reason ?? row.owner_username ?? row.reporter_username ?? row.created_at ?? row.updated_at ?? "");
}

function compactText(value: unknown, limit: number): string {
  const text = String(value ?? "").replace(/\s+/gu, " ").trim();
  if (text.length <= limit) return text;
  return `${text.slice(0, limit).trimEnd()}...`;
}

function renderOpsDetail(row: Record<string, unknown>, detail: unknown, ui: I18nDictionary["social"]["ui"]): ReactNode {
  const detailText = String(detail ?? "").trim();
  const username = String(row.username ?? row.owner_username ?? row.reporter_username ?? "").trim();
  const isProfileDetail = username && [row.username, row.owner_username, row.reporter_username].some((value) => String(value ?? "").trim() === detailText);
  if (isProfileDetail) {
    const displayName = String(row.display_name ?? row.owner_display_name ?? row.reporter_display_name ?? username).trim();
    return <ProfileNameLink row={{ username, display_name: displayName }}>{localizedMachineText(detailText, ui) || username}</ProfileNameLink>;
  }
  return localizedMachineText(detailText, ui) || localizedMachineText(row.status, ui) || ui.ready;
}

function opsStatusOptions(endpoint: string, row: Record<string, unknown>): string[] {
  if (endpoint.endsWith("/reports")) return ["open", "resolved", "rejected", "escalated"];
  if (endpoint.endsWith("/users")) {
    const current = String(row.status ?? "").trim();
    return current === "archived" || current === "active" ? ["active", "archived"] : ["active", "inactive", "suspended"];
  }
  if (endpoint.endsWith("/support-tickets")) return ["open", "pending", "resolved", "closed", "escalated"];
  if (endpoint.endsWith("/follows")) return ["pending", "accepted", "blocked", "deleted"];
  if (/\/api\/ops\/(posts|comments|replies|clips|articles|documents)$/u.test(endpoint)) return ["draft", "published", "rejected", "deleted"];
  if (endpoint.endsWith("/stories")) return ["active", "expired", "deleted"];
  if (endpoint.endsWith("/media")) return ["pending", "ready", "failed", "deleted"];
  if (endpoint.endsWith("/messages")) return ["active", "deleted"];
  if (endpoint.endsWith("/chat-rooms")) return ["active", "archived", "deleted"];
  if (endpoint.endsWith("/invites")) return ["pending", "accepted", "revoked", "expired"];
  if (endpoint.endsWith("/events")) return ["draft", "published", "cancelled", "completed"];
  if (endpoint.endsWith("/ads")) return ["draft", "active", "paused", "completed", "cancelled"];
  return ["active", "disabled"];
}

function renderQueueRow(row: Record<string, unknown>, ui: I18nDictionary["social"]["ui"]) {
  return (
    <>
      <strong>{String(row.module ?? row.queue_name ?? row.id ?? ui.queues)}</strong>
      <p>{row.critical ? ui.criticalWorkflow : ui.backgroundWorkflow}</p>
    </>
  );
}

function extractRows(data: unknown): Array<Record<string, unknown>> {
  if (Array.isArray(data)) return data.filter(isRecord);
  if (!isRecord(data)) return [];
  if (Array.isArray(data.data)) return data.data.filter(isRecord);
  if (isRecord(data.data)) return extractRecordRows(data.data) ?? extractSingleRow(data.data) ?? [data.data];
  const rows = extractRecordRows(data);
  if (rows) return rows;
  const singleRow = extractSingleRow(data);
  if (singleRow) return singleRow;
  if ("ok" in data || "id" in data || "user" in data) return [data];
  return [];
}

function extractRecordRows(data: Record<string, unknown>): Array<Record<string, unknown>> | undefined {
  const keys = ["rows", "items", "results", "records", "posts", "friends", "follows", "users", "members", "profiles", "media", "photos", "articles", "events", "groups", "pages", "documents", "clips", "videos", "notifications", "bookmarks", "invites", "likes", "reactions", "shares", "hashtags", "tags", "categories", "messages", "rooms", "conversations", "tickets"];
  for (const key of keys) {
    const value = data[key];
    if (Array.isArray(value)) return value.filter(isRecord);
  }
  const entries = Object.entries(data).filter(([, value]) => Array.isArray(value));
  return entries.length ? entries.flatMap(([, value]) => (value as unknown[]).filter(isRecord)) : undefined;
}

function extractSingleRow(data: Record<string, unknown>): Array<Record<string, unknown>> | undefined {
  const keys = ["post", "article", "event", "group", "page", "document", "profile", "user", "member", "item", "follow", "like", "reaction", "share", "hashtag", "tag", "category", "message", "room", "conversation", "ticket"];
  for (const key of keys) {
    if (isRecord(data[key])) return [data[key]];
  }
  return undefined;
}

function readableError(data: unknown, status: number): string {
  const dict = activeDictionary;
  if (isRecord(data)) {
    const code = String(data.code ?? "").trim();
    const community = getCommunityMessages(activeLocaleCode);
    if (code && community[code]) return community[code];
    if (dict && code) {
      if (Object.prototype.hasOwnProperty.call(dict.errorMessages, code)) {
        return dict.errorMessages[code as keyof I18nDictionary["errorMessages"]];
      }
      return localizedStatusError(status, dict);
    }
    if (dict) return localizedStatusError(status, dict);
    if (data.message) return String(data.message);
    if (code) return `${code} (${status})`;
  }
  if (dict) return localizedStatusError(status, dict);
  return `HTTP ${status}`;
}

function localizedStatusError(status: number, dict: I18nDictionary): string {
  if (status === 400 || status === 413 || status === 422) return dict.errors.validation;
  if (status === 401) return dict.errors.unauthorized;
  if (status === 403) return dict.errors.forbidden;
  if (status === 404) return dict.errors.notFound;
  if (status === 409) return dict.errors.conflict;
  return dict.errors.server;
}

function localizedErrorMessage(error: unknown, fallback: string): string {
  return displayErrorMessage(error instanceof Error ? error.message : "", fallback);
}

function displayErrorMessage(message: string | undefined, fallback: string): string {
  const text = String(message ?? "").trim();
  if (!text || isTechnicalErrorMessage(text)) return fallback;
  return text;
}

function isTechnicalErrorMessage(message: string): boolean {
  return (
    /^HTTP\s+\d{3}$/i.test(message) ||
    /^[a-z0-9_]+ \(\d{3}\)$/i.test(message) ||
    /^Failed to fetch$/i.test(message) ||
    /^Load failed$/i.test(message) ||
    /^NetworkError\b/i.test(message)
  );
}

function inviteLinkFromResponse(data: unknown): string {
  if (!isRecord(data)) return "";
  return String(data.invite_link ?? data.inviteLink ?? "").trim();
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function booleanValue(value: unknown): boolean {
  return value === true || value === 1 || value === "1" || value === "true";
}

function numberValue(value: unknown): number {
  const number = Number(value ?? 0);
  return Number.isFinite(number) ? number : 0;
}

function normalizeHeadingText(value: string): string {
  return value.normalize("NFKD").toLocaleLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim();
}

function isGenericEmptyHeading(title: string, ui: I18nDictionary["social"]["ui"]): boolean {
  return [ui.nothingHereYet, ui.noActivityYet].some((value) => normalizeHeadingText(value) === normalizeHeadingText(title));
}

function titleCase(value: string): string {
  return value.replace(/(^|[_\s-])([a-z])/g, (_, prefix: string, letter: string) => `${prefix ? " " : ""}${letter.toUpperCase()}`).trim();
}

function articlePayloadFromComposer(value: string): { title: string; slug: string; excerpt: string; body: string } {
  const body = value.trim();
  const lines = body.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  const title = (lines[0] ?? "").slice(0, 220);
  const excerptSource = lines.slice(1).join(" ").trim() || body;
  return {
    title,
    slug: slugFromComposerTitle(title),
    excerpt: excerptSource.slice(0, 500),
    body
  };
}

function slugFromComposerTitle(value: string): string {
  return value
    .normalize("NFKD")
    .toLocaleLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 240);
}

function splitCsv(value: FormDataEntryValue | null): string[] {
  return String(value ?? "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

const pageMap: Record<string, SocialPageComponent> = {
  ContractView
};

export function mountOpenSNSignupForm(element: HTMLElement, props: { dictionary: I18nDictionary; localeCode: string; view: ViewCatalogItem; currentQuery: Record<string, string>; socialDefinition: OpenSNSocialDefinition }) {
  const dictionary = extendOpenSNDictionary(props.localeCode, props.dictionary);
  activeDictionary = dictionary;
  activeLocaleCode = props.localeCode;
  activeSocialDefinition = props.socialDefinition;
  createRoot(element).render(<AuthForm view={props.view} currentQuery={props.currentQuery} localeCode={props.localeCode} authReady={false} authUser={null} dictionary={dictionary} socialDefinition={props.socialDefinition} embedded />);
}

export function createOpenSNSocialApp(options: { resolveView?: (pathname: string) => ViewCatalogItem | null } = {}) {
  socialAppMounted = true;
  configuredViewResolver = options.resolveView;
  createInertiaApp<PageProps>({
    resolve: (name) => pageMap[name] ?? ContractView,
    setup({ el, App, props }) {
      if (el.hasChildNodes()) {
        hydrateRoot(el, <App {...props} />);
      } else {
        createRoot(el).render(<App {...props} />);
      }
    }
  });
}
